// src/domain/belop.ts
var BELOP_SKALA = 1000n;
var DESIMALER = 3;
var NULL_BELOP = 0n;
var DESIMALTEKST = /^([+-]?)(\d+)(?:[.,](\d{1,3}))?$/;
function belopFraDesimaltekst(tekst) {
  const treff = DESIMALTEKST.exec(tekst.trim());
  if (!treff) {
    throw new RangeError(
      `Ugyldig bel\xF8p: ${JSON.stringify(tekst)} (forventet desimaltall med maks ${DESIMALER} desimaler)`
    );
  }
  const [, fortegn = "", heltall = "0", desimaler = ""] = treff;
  const hele = BigInt(heltall) * BELOP_SKALA;
  const brok = BigInt(desimaler.padEnd(DESIMALER, "0") || "0");
  const sum2 = hele + brok;
  return fortegn === "-" ? -sum2 : sum2;
}
function belopTilDesimaltekst(belop) {
  const negativ = belop < 0n;
  const absolutt = negativ ? -belop : belop;
  const hele = absolutt / BELOP_SKALA;
  const brok = absolutt % BELOP_SKALA;
  return `${negativ ? "-" : ""}${hele.toString()}.${brok.toString().padStart(DESIMALER, "0")}`;
}
function belopFraTall(tall3) {
  if (!Number.isFinite(tall3)) {
    throw new RangeError(`Ugyldig bel\xF8p: ${tall3}`);
  }
  const skalert = tall3 * Number(BELOP_SKALA);
  const avrundet = skalert < 0 ? -Math.round(-skalert) : Math.round(skalert);
  return BigInt(avrundet);
}
function belopTilTall(belop) {
  return Number(belop) / Number(BELOP_SKALA);
}
function summerBelop(belop) {
  let sum2 = NULL_BELOP;
  for (const b of belop) sum2 += b;
  return sum2;
}
function fordelJevnt(total, antall) {
  if (!Number.isInteger(antall) || antall < 0) {
    throw new RangeError(`Antall deler m\xE5 v\xE6re et ikke-negativt heltall, fikk ${antall}`);
  }
  if (antall === 0) return [];
  const n = BigInt(antall);
  const grunn = total / n;
  const rest = total - grunn * n;
  const steg = rest < 0n ? -1n : 1n;
  let gjenstaende = rest < 0n ? -rest : rest;
  const deler = [];
  for (let i = 0; i < antall; i += 1) {
    if (gjenstaende > 0n) {
      deler.push(grunn + steg);
      gjenstaende -= 1n;
    } else {
      deler.push(grunn);
    }
  }
  return deler;
}
var FAKTOR_SKALA = 1000000000n;
function skalerBelop(belop, faktor) {
  if (!Number.isFinite(faktor)) {
    throw new RangeError(`Ugyldig faktor: ${faktor}`);
  }
  const skalertFaktor = BigInt(Math.round(faktor * Number(FAKTOR_SKALA)));
  const produkt = belop * skalertFaktor;
  const halv = FAKTOR_SKALA / 2n;
  return produkt >= 0n ? (produkt + halv) / FAKTOR_SKALA : -((-produkt + halv) / FAKTOR_SKALA);
}
function fordelEtterVekter(total, vekter) {
  if (vekter.length === 0) return [];
  if (vekter.some((v) => !Number.isFinite(v) || v < 0)) {
    throw new RangeError("Vekter m\xE5 v\xE6re endelige og ikke-negative.");
  }
  const vektsum = vekter.reduce((a, b) => a + b, 0);
  if (vektsum === 0) return fordelJevnt(total, vekter.length);
  const andeler = vekter.map((v) => Number(total) * v / vektsum);
  const grunn = andeler.map((a) => BigInt(Math.trunc(a)));
  const fordelt = grunn.reduce((a, b) => a + b, 0n);
  const rest = total - fordelt;
  const rekkefolge = andeler.map((a, i) => ({ i, brok: Math.abs(a - Math.trunc(a)) })).sort((a, b) => b.brok - a.brok || a.i - b.i);
  const deler = [...grunn];
  const steg = rest < 0n ? -1n : 1n;
  let gjenstaende = rest < 0n ? -rest : rest;
  for (const { i } of rekkefolge) {
    if (gjenstaende === 0n) break;
    deler[i] = (deler[i] ?? 0n) + steg;
    gjenstaende -= 1n;
  }
  return deler;
}

// src/domain/import/enhet.ts
function konverterBelop(belop, fra, til) {
  if (fra === til) return belop;
  if (fra === "kr" && til === "kr_1000") {
    const negativ = belop < 0n;
    const absolutt = negativ ? -belop : belop;
    const avrundet = (absolutt + 500n) / 1000n;
    return negativ ? -avrundet : avrundet;
  }
  return belop * 1000n;
}

// src/domain/omrade.ts
var omregn = (tekst, fra) => belopTilDesimaltekst(konverterBelop(belopFraDesimaltekst(tekst), fra, "kr_1000"));
function normaliserTall(tall3, fra) {
  if (fra === "kr_1000") return tall3;
  return {
    budsjett: omregn(tall3.budsjett, fra),
    regnskapHittil: omregn(tall3.regnskapHittil, fra),
    prognoseTotalt: omregn(tall3.prognoseTotalt, fra),
    avvik: omregn(tall3.avvik, fra),
    avvikProsent: tall3.avvikProsent,
    fjoraretTotalt: tall3.fjoraretTotalt === null ? null : omregn(tall3.fjoraretTotalt, fra),
    fjoraretHittil: tall3.fjoraretHittil === null ? null : omregn(tall3.fjoraretHittil, fra)
  };
}
var sum = (verdier) => verdier.reduce((s, v) => s + belopFraDesimaltekst(v), 0n);
function summerTall(alle) {
  const budsjett = sum(alle.map((t2) => t2.budsjett));
  const avvik = sum(alle.map((t2) => t2.avvik));
  const fjoraret = (velg) => {
    const verdier = alle.map(velg);
    if (verdier.some((v) => v === null)) return null;
    return belopTilDesimaltekst(sum(verdier.filter((v) => v !== null)));
  };
  return {
    budsjett: belopTilDesimaltekst(budsjett),
    regnskapHittil: belopTilDesimaltekst(sum(alle.map((t2) => t2.regnskapHittil))),
    prognoseTotalt: belopTilDesimaltekst(sum(alle.map((t2) => t2.prognoseTotalt))),
    avvik: belopTilDesimaltekst(avvik),
    // Null budsjett gir null prosent, ikke uendelig eller null. Samme regel som
    // ellers: et forholdstall uten nevner finnes ikke.
    avvikProsent: budsjett === 0n ? null : Number(avvik * 10000n / budsjett) / 100,
    fjoraretTotalt: fjoraret((t2) => t2.fjoraretTotalt),
    fjoraretHittil: fjoraret((t2) => t2.fjoraretHittil)
  };
}
function normaliserRad(rad, fra) {
  if (fra === "kr_1000") return rad;
  const tall3 = normaliserTall(rad, fra);
  return {
    ...rad,
    ...tall3,
    barn: rad.barn.map((b) => normaliserRad(b, fra))
  };
}
function summerRader(trer) {
  if (trer.length === 0) return [];
  const rekkefolge = [];
  const samlet = /* @__PURE__ */ new Map();
  for (const tre of trer) {
    for (const rad of tre) {
      const funnet = samlet.get(rad.id);
      if (funnet) {
        funnet.push(rad);
      } else {
        rekkefolge.push(rad.id);
        samlet.set(rad.id, [rad]);
      }
    }
  }
  return rekkefolge.flatMap((id) => {
    const rader = samlet.get(id) ?? [];
    const forste = rader[0];
    if (forste === void 0) return [];
    return {
      id,
      niva: forste.niva,
      kontonummer: forste.kontonummer,
      navn: forste.navn,
      ...summerTall(rader),
      barn: summerRader(rader.map((r) => r.barn))
    };
  });
}
function summerManeder(alle) {
  if (alle.length === 0) return [];
  const maneder = [...new Set(alle.flatMap((m) => m.map((x) => x.maned)))].sort((a, b) => a - b);
  return maneder.map((maned) => {
    const rader = alle.flatMap((m) => m.filter((x) => x.maned === maned));
    const bokfort = rader.filter((r) => r.regnskap !== null);
    return {
      maned,
      budsjett: belopTilDesimaltekst(sum(rader.map((r) => r.budsjett))),
      regnskap: bokfort.length === 0 ? null : belopTilDesimaltekst(sum(bokfort.map((r) => r.regnskap ?? "0.000"))),
      prognose: belopTilDesimaltekst(sum(rader.map((r) => r.prognose)))
    };
  });
}
function normaliserManed(maned, fra) {
  if (fra === "kr_1000") return maned;
  return {
    maned: maned.maned,
    budsjett: omregn(maned.budsjett, fra),
    regnskap: maned.regnskap === null ? null : omregn(maned.regnskap, fra),
    prognose: omregn(maned.prognose, fra)
  };
}
function minsteBokforte(maneder) {
  const med = maneder.filter((m) => m !== null);
  return med.length === 0 ? null : Math.min(...med);
}

// src/domain/enums.ts
var VALUTA_ENHETER = ["kr", "kr_1000"];
var IDENTITETSLEVERANDOR_TYPER = ["keycloak_intern", "oidc", "saml"];
var HOVEDKONTO_KATEGORIER = ["lonn", "utgifter", "inntekter"];
var NIVAER = [1, 2, 3];
var NIVA_HOVEDKONTO = 1;
var NIVA_ARTSGRUPPE = 2;
var NIVA_UNDERKONTO = 3;
var PROGNOSE_METODER = ["framskrivning", "rullerende", "normtall", "aktivitet"];
var ROLLER = ["leder", "okonom", "leser"];
var DATAKILDER = ["manuell", "import", "api"];
var MANEDER = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
function manedFraIndeks(indeks) {
  const maned = MANEDER[indeks];
  if (maned === void 0) throw new RangeError(`Ugyldig m\xE5nedsindeks: ${indeks}`);
  return maned;
}
function erManed(verdi) {
  return Number.isInteger(verdi) && verdi >= 1 && verdi <= 12;
}
var MANEDSNAVN = {
  1: "januar",
  2: "februar",
  3: "mars",
  4: "april",
  5: "mai",
  6: "juni",
  7: "juli",
  8: "august",
  9: "september",
  10: "oktober",
  11: "november",
  12: "desember"
};
var FUNKSJONSFLAGG = [
  "finansiell_oversikt",
  "prognose",
  "import_regnskap",
  "kpi_og_tiltak",
  "bemanning",
  "administrasjon",
  "innkjop",
  "rapportering",
  "budsjett",
  "moter",
  "investeringer",
  "prosjekter",
  "vedlikehold"
];

// src/domain/avvik.ts
function beregnAvvik(prognoseTotalt, budsjett) {
  return prognoseTotalt - budsjett;
}
function erOverBudsjett(prognoseTotalt, budsjett) {
  return beregnAvvik(prognoseTotalt, budsjett) > 0n;
}
function beregnAvvikProsent(prognoseTotalt, budsjett) {
  if (budsjett === 0n) return null;
  const avvik = beregnAvvik(prognoseTotalt, budsjett);
  return Number(avvik) / Math.abs(Number(budsjett)) * 100;
}

// src/domain/periode.ts
function losManed(maned, inndata) {
  if (inndata.regnskap !== null) {
    return { maned, belop: inndata.regnskap, kilde: "regnskap" };
  }
  return { maned, belop: inndata.prognose ?? NULL_BELOP, kilde: "prognose" };
}
function losAr(inndataPerManed) {
  return MANEDER.map(
    (maned) => losManed(maned, inndataPerManed.get(maned) ?? { regnskap: null, prognose: null })
  );
}
function beregnPrognoseTotalt(verdier) {
  return summerBelop(verdier.map((v) => v.belop));
}
function beregnRegnskapHittilIAr(verdier) {
  return summerBelop(verdier.filter((v) => v.kilde === "regnskap").map((v) => v.belop));
}
function sisteBokfortManed(verdier) {
  let siste = null;
  for (const verdi of verdier) {
    if (verdi.kilde === "regnskap") siste = verdi.maned;
  }
  return siste;
}
function erApenForPrognose(verdi) {
  return verdi.kilde === "prognose";
}

// src/domain/kaskade.ts
function dekker(overstyring, underkonto) {
  switch (overstyring.niva) {
    case NIVA_UNDERKONTO:
      return underkonto.id === overstyring.malId;
    case NIVA_ARTSGRUPPE:
      return underkonto.artsgruppeId === overstyring.malId;
    default:
      return underkonto.hovedkontoId === overstyring.malId;
  }
}
function lukkedeManeder(regnskap) {
  const lukkede = /* @__PURE__ */ new Set();
  for (const perManed of regnskap.values()) {
    for (const maned of perManed.keys()) lukkede.add(maned);
  }
  return [...lukkede].sort((a, b) => a - b);
}
function sammenlignUnderkonto(a, b) {
  if (a.sortering !== b.sortering) return a.sortering - b.sortering;
  const aNr = a.kontonummer ?? "";
  const bNr = b.kontonummer ?? "";
  if (aNr !== bNr) return aNr < bNr ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}
function fordelOverstyring(malBelop, underkontoer) {
  const sortert = [...underkontoer].sort(sammenlignUnderkonto);
  const deler = fordelJevnt(malBelop, sortert.length);
  const resultat = /* @__PURE__ */ new Map();
  sortert.forEach((underkonto, i) => {
    resultat.set(underkonto.id, deler[i] ?? NULL_BELOP);
  });
  return resultat;
}
function hent(tabell, underkontoId, maned) {
  return tabell.get(underkontoId)?.get(maned) ?? null;
}
function beregnPrognoseTabell(inndata) {
  const underkontoer = [...inndata.underkontoer].sort(sammenlignUnderkonto);
  const perKonto = /* @__PURE__ */ new Map();
  for (const underkonto of underkontoer) perKonto.set(underkonto.id, []);
  for (const maned of MANEDER) {
    const bokfort = /* @__PURE__ */ new Map();
    const apen = /* @__PURE__ */ new Map();
    for (const underkonto of underkontoer) {
      const faktisk = hent(inndata.regnskap, underkonto.id, maned);
      if (faktisk !== null) bokfort.set(underkonto.id, faktisk);
      else apen.set(underkonto.id, hent(inndata.basisPrognose, underkonto.id, maned) ?? NULL_BELOP);
    }
    const lukket = bokfort.size > 0;
    if (!lukket) {
      for (const overstyring of inndata.overstyringer) {
        if (overstyring.maned !== maned) continue;
        const medlemmer = underkontoer.filter((u2) => dekker(overstyring, u2));
        if (medlemmer.length === 0) continue;
        for (const [underkontoId, belop] of fordelOverstyring(overstyring.belop, medlemmer)) {
          apen.set(underkontoId, belop);
        }
      }
    }
    for (const underkonto of underkontoer) {
      const fastsatt = bokfort.has(underkonto.id) ? { belop: bokfort.get(underkonto.id) ?? NULL_BELOP, kilde: "regnskap" } : { belop: apen.get(underkonto.id) ?? NULL_BELOP, kilde: "prognose" };
      perKonto.get(underkonto.id)?.push({ maned, belop: fastsatt.belop, kilde: fastsatt.kilde });
    }
  }
  const rader = underkontoer.map((underkonto) => {
    const maneder = perKonto.get(underkonto.id) ?? [];
    return {
      underkontoId: underkonto.id,
      maneder,
      arstotal: beregnPrognoseTotalt(maneder)
    };
  });
  return { rader };
}
function summerTabell(tabell) {
  return summerBelop(tabell.rader.map((r) => r.arstotal));
}

// src/domain/oversikt.ts
function summerManeder2(tabell, underkontoId, tom2 = 12) {
  const perManed = tabell.get(underkontoId);
  if (!perManed) return NULL_BELOP;
  return summerBelop(MANEDER.filter((m) => m <= tom2).map((m) => perManed.get(m) ?? NULL_BELOP));
}
function lesInndata(regnskap, prognose, underkontoId) {
  const bokfort = regnskap.get(underkontoId);
  const anslag = prognose.get(underkontoId);
  return new Map(
    MANEDER.map((m) => [
      m,
      {
        // `?? null`, never `?? 0n`: a month with no row is not a month booked at
        // zero, and only the former may fall through to the forecast.
        regnskap: bokfort?.get(m) ?? null,
        prognose: anslag?.get(m) ?? null
      }
    ])
  );
}
function summerValgfri(verdier) {
  const tall3 = verdier.filter((v) => v !== null);
  return tall3.length === 0 ? null : summerBelop(tall3);
}
function summerBarn(barn) {
  const budsjett = summerBelop(barn.map((b) => b.budsjett));
  const prognoseTotalt = summerBelop(barn.map((b) => b.prognoseTotalt));
  return {
    budsjett,
    regnskapHittil: summerBelop(barn.map((b) => b.regnskapHittil)),
    prognoseTotalt,
    fjoraretTotalt: summerValgfri(barn.map((b) => b.fjoraretTotalt)),
    fjoraretHittil: summerValgfri(barn.map((b) => b.fjoraretHittil)),
    // Recomputed from the summed figures rather than added up from the children's
    // avvik. The two are algebraically identical — `beregnAvvik` is linear — and a
    // test asserts they stay that way. Going through the shared function means
    // there is still only ONE place that knows what an avvik is.
    avvik: beregnAvvik(prognoseTotalt, budsjett),
    avvikProsent: beregnAvvikProsent(prognoseTotalt, budsjett)
  };
}
function finnSisteBokfort(inndata) {
  let siste = null;
  for (const hovedkonto of inndata.hovedkontoer) {
    for (const artsgruppe of hovedkonto.artsgrupper) {
      for (const underkonto of artsgruppe.underkontoer) {
        const verdier = losAr(lesInndata(inndata.regnskap, inndata.prognose, underkonto.id));
        const her = sisteBokfortManed(verdier);
        if (her !== null && (siste === null || her > siste)) siste = her;
      }
    }
  }
  return siste;
}
function byggManeder(inndata, sisteBokfort) {
  const underkontoer = inndata.hovedkontoer.flatMap(
    (h) => h.artsgrupper.flatMap((a) => a.underkontoer)
  );
  const verdierPerKonto = underkontoer.map(
    (u2) => new Map(
      losAr(lesInndata(inndata.regnskap, inndata.prognose, u2.id)).map((v) => [v.maned, v.belop])
    )
  );
  let budsjettSum = NULL_BELOP;
  let regnskapSum = NULL_BELOP;
  let prognoseSum = NULL_BELOP;
  return MANEDER.map((maned) => {
    budsjettSum = summerBelop([
      budsjettSum,
      ...underkontoer.map((u2) => inndata.budsjett.get(u2.id)?.get(maned) ?? NULL_BELOP)
    ]);
    prognoseSum = summerBelop([
      prognoseSum,
      ...verdierPerKonto.map((v) => v.get(maned) ?? NULL_BELOP)
    ]);
    const bokfort = sisteBokfort !== null && maned <= sisteBokfort;
    if (bokfort) {
      regnskapSum = summerBelop([
        regnskapSum,
        ...underkontoer.map((u2) => inndata.regnskap.get(u2.id)?.get(maned) ?? NULL_BELOP)
      ]);
    }
    return {
      maned,
      budsjett: budsjettSum,
      regnskap: bokfort ? regnskapSum : null,
      prognose: prognoseSum
    };
  });
}
function byggOversikt(inndata) {
  const sisteBokfort = finnSisteBokfort(inndata);
  const harFjoraar = inndata.fjoraret !== void 0 && inndata.fjoraret.size > 0;
  const fjoraret = inndata.fjoraret ?? /* @__PURE__ */ new Map();
  const hovedkontoer = inndata.hovedkontoer.map((hovedkonto) => {
    const artsgrupper = hovedkonto.artsgrupper.map((artsgruppe) => {
      const underkontoer = artsgruppe.underkontoer.map((underkonto) => {
        const verdier = losAr(lesInndata(inndata.regnskap, inndata.prognose, underkonto.id));
        const budsjett = summerManeder2(inndata.budsjett, underkonto.id);
        const prognoseTotalt = beregnPrognoseTotalt(verdier);
        return {
          id: underkonto.id,
          niva: 3,
          kontonummer: underkonto.kontonummer,
          navn: underkonto.navn,
          budsjett,
          regnskapHittil: beregnRegnskapHittilIAr(verdier),
          prognoseTotalt,
          avvik: beregnAvvik(prognoseTotalt, budsjett),
          avvikProsent: beregnAvvikProsent(prognoseTotalt, budsjett),
          fjoraretTotalt: harFjoraar ? summerManeder2(fjoraret, underkonto.id) : null,
          // No month booked this year means nothing to compare «hittil» against
          // yet — zero months, not the whole of last year.
          fjoraretHittil: !harFjoraar ? null : sisteBokfort === null ? NULL_BELOP : summerManeder2(fjoraret, underkonto.id, sisteBokfort),
          barn: []
        };
      });
      return {
        id: artsgruppe.id,
        niva: 2,
        kontonummer: artsgruppe.kontonummer,
        navn: artsgruppe.navn,
        ...summerBarn(underkontoer),
        barn: underkontoer
      };
    });
    return {
      id: hovedkonto.id,
      niva: 1,
      kontonummer: hovedkonto.kontonummer,
      navn: hovedkonto.navn,
      ...summerBarn(artsgrupper),
      barn: artsgrupper
    };
  });
  return {
    hovedkontoer,
    sum: summerBarn(hovedkontoer),
    sisteBokfortManed: sisteBokfort,
    maneder: byggManeder(inndata, sisteBokfort)
  };
}
function flatUtTil(rader, detaljniva) {
  const ut = [];
  const gaTil = (rad) => {
    ut.push(rad);
    if (rad.niva < detaljniva) rad.barn.forEach(gaTil);
  };
  rader.forEach(gaTil);
  return ut;
}

// src/domain/metoder.ts
function hent2(tabell, underkontoId, maned) {
  return tabell.get(underkontoId)?.get(maned) ?? null;
}
function snitt(sum2, antall) {
  if (antall <= 0) return NULL_BELOP;
  const n = BigInt(antall);
  const halv = n / 2n;
  return sum2 >= 0n ? (sum2 + halv) / n : -((-sum2 + halv) / n);
}
function budsjettvekter(underkontoer, budsjett) {
  return underkontoer.map((u2) => {
    const arsbelop = summerBelop(MANEDER.map((m) => hent2(budsjett, u2.id, m) ?? NULL_BELOP));
    return Math.abs(Number(arsbelop));
  });
}
function tomTabell(underkontoer) {
  return new Map(underkontoer.map((u2) => [u2.id, /* @__PURE__ */ new Map()]));
}
function beregnBasis(valg, underkontoer, inndata) {
  const sortert = [...underkontoer].sort(sammenlignUnderkonto);
  const ut = tomTabell(sortert);
  switch (valg.metode) {
    case "framskrivning": {
      const faktor = 1 + valg.variabler.vekstrate;
      for (const underkonto of sortert) {
        const harHistorikk = MANEDER.some(
          (m) => hent2(inndata.fjorartsRegnskap, underkonto.id, m) !== null
        );
        for (const maned of MANEDER) {
          const verdi = harHistorikk ? skalerBelop(
            hent2(inndata.fjorartsRegnskap, underkonto.id, maned) ?? NULL_BELOP,
            faktor
          ) : hent2(inndata.budsjett, underkonto.id, maned) ?? NULL_BELOP;
          ut.get(underkonto.id)?.set(maned, verdi);
        }
      }
      return ut;
    }
    case "rullerende": {
      const { vindusManeder, vekstrate } = valg.variabler;
      if (!Number.isInteger(vindusManeder) || vindusManeder < 1 || vindusManeder > 12) {
        throw new RangeError(`Vindu m\xE5 v\xE6re 1\u201312 m\xE5neder, fikk ${vindusManeder}`);
      }
      const faktor = 1 + vekstrate;
      for (const underkonto of sortert) {
        const bokforte = [...MANEDER].reverse().map((m) => hent2(inndata.regnskapIAr, underkonto.id, m)).filter((b) => b !== null).slice(0, vindusManeder);
        for (const maned of MANEDER) {
          const verdi = bokforte.length > 0 ? skalerBelop(snitt(summerBelop(bokforte), bokforte.length), faktor) : hent2(inndata.budsjett, underkonto.id, maned) ?? NULL_BELOP;
          ut.get(underkonto.id)?.set(maned, verdi);
        }
      }
      return ut;
    }
    case "normtall": {
      const { grunnbemanningArsverk, manedskostnadPerArsverk, pleietyngde } = valg.variabler;
      if (grunnbemanningArsverk < 0) {
        throw new RangeError("Grunnbemanning kan ikke v\xE6re negativ.");
      }
      if (pleietyngde.some((p2) => p2.antallBrukere < 0 || p2.normtallArsverkPerBruker < 0)) {
        throw new RangeError("Antall brukere og normtall m\xE5 v\xE6re ikke-negative.");
      }
      const arsverk = grunnbemanningArsverk + pleietyngde.reduce((sum2, p2) => sum2 + p2.antallBrukere * p2.normtallArsverkPerBruker, 0);
      const manedsbelop = skalerBelop(manedskostnadPerArsverk, arsverk);
      return fordelNed(sortert, inndata.budsjett, manedsbelop, ut);
    }
    case "aktivitet": {
      const { volumPerManed, enhetspris } = valg.variabler;
      if (volumPerManed < 0) throw new RangeError("Volum kan ikke v\xE6re negativt.");
      const manedsbelop = skalerBelop(enhetspris, volumPerManed);
      return fordelNed(sortert, inndata.budsjett, manedsbelop, ut);
    }
  }
}
function fordelNed(underkontoer, budsjett, manedsbelop, ut) {
  const deler = fordelEtterVekter(manedsbelop, budsjettvekter(underkontoer, budsjett));
  for (const maned of MANEDER) {
    underkontoer.forEach((underkonto, i) => {
      ut.get(underkonto.id)?.set(maned, deler[i] ?? NULL_BELOP);
    });
  }
  return ut;
}
function beregnBasisPrognose(valgPerHovedkonto, inndata) {
  const perHovedkonto = /* @__PURE__ */ new Map();
  for (const underkonto of inndata.underkontoer) {
    const liste = perHovedkonto.get(underkonto.hovedkontoId) ?? [];
    liste.push(underkonto);
    perHovedkonto.set(underkonto.hovedkontoId, liste);
  }
  const samlet = /* @__PURE__ */ new Map();
  for (const [hovedkontoId, underkontoer] of perHovedkonto) {
    const valg = valgPerHovedkonto.get(hovedkontoId);
    if (!valg) {
      for (const underkonto of underkontoer) {
        const perManed = /* @__PURE__ */ new Map();
        for (const maned of MANEDER) {
          perManed.set(maned, hent2(inndata.budsjett, underkonto.id, maned) ?? NULL_BELOP);
        }
        samlet.set(underkonto.id, perManed);
      }
      continue;
    }
    for (const [underkontoId, perManed] of beregnBasis(valg, underkontoer, inndata)) {
      samlet.set(underkontoId, new Map(perManed));
    }
  }
  return samlet;
}

// src/domain/prognoseendring.ts
function kanonisk(verdi) {
  if (Array.isArray(verdi)) return verdi.map(kanonisk);
  if (typeof verdi === "object" && verdi !== null) {
    return Object.fromEntries(
      Object.keys(verdi).sort().map((k2) => [k2, kanonisk(verdi[k2])])
    );
  }
  return verdi;
}
var sammeBelop = (a, b) => belopFraDesimaltekst(a) === belopFraDesimaltekst(b);
function prognoseendringer(forrige, neste, laaste) {
  const laast = new Set(laaste);
  const nokkel2 = (o) => `${String(o.niva)}:${o.malId}:${String(o.maned)}`;
  const apne = (liste) => new Map(liste.filter((o) => !laast.has(o.maned)).map((o) => [nokkel2(o), o]));
  const fra = apne(forrige.overstyringer);
  const til = apne(neste.overstyringer);
  const overstyringer = [];
  const alle = [...til.values(), ...[...fra.values()].filter((o) => !til.has(nokkel2(o)))];
  for (const kilde2 of alle) {
    const a = fra.get(nokkel2(kilde2));
    const b = til.get(nokkel2(kilde2));
    const lik = a !== void 0 && b !== void 0 && sammeBelop(a.belop, b.belop) && a.kommentar === b.kommentar;
    if (lik) continue;
    overstyringer.push({
      hva: "overstyring",
      niva: kilde2.niva,
      malId: kilde2.malId,
      maned: kilde2.maned,
      fraBelop: a?.belop ?? null,
      tilBelop: b?.belop ?? null,
      kommentar: b?.kommentar ?? null
    });
  }
  overstyringer.sort(
    (x, y) => x.niva - y.niva || x.malId.localeCompare(y.malId) || x.maned - y.maned
  );
  const tekst = (m) => JSON.stringify(
    kanonisk({
      metode: m.metode,
      variabler: m.variabler,
      metodeLast: m.metodeLast,
      variablerLast: m.variablerLast
    })
  );
  const metodeFra = new Map(forrige.metodevalg.map((m) => [m.hovedkontoId, m]));
  const metodeTil = new Map(neste.metodevalg.map((m) => [m.hovedkontoId, m]));
  const metoder = [];
  for (const id of [.../* @__PURE__ */ new Set([...metodeFra.keys(), ...metodeTil.keys()])].sort()) {
    const a = metodeFra.get(id);
    const b = metodeTil.get(id);
    if (a && b && tekst(a) === tekst(b)) continue;
    metoder.push({
      hva: "metode",
      hovedkontoId: id,
      metodeFra: a?.metode ?? null,
      metodeTil: b?.metode ?? null
    });
  }
  return [...metoder, ...overstyringer];
}

// src/domain/levering.ts
var PROGNOSERYTMER = ["maned", "tertial"];
var LEVERINGSSTATUSER = ["levert", "godkjent", "sendt_tilbake", "erstattet"];
var TERTIALSLUTT = [4, 8, 12];
function leveringsperiode(rytme, sisteBokforteManed) {
  if (sisteBokforteManed === null) return null;
  if (rytme === "maned") return sisteBokforteManed;
  const slutt = TERTIALSLUTT.filter((m) => m <= sisteBokforteManed);
  return slutt.at(-1) ?? null;
}
function periodenavn(rytme, periode2) {
  if (rytme === "maned") return MANEDSNAVN[periode2];
  const nummer = TERTIALSLUTT.findIndex((m) => periode2 <= m) + 1;
  return `${nummer}. tertial`;
}
var FRISTDAG_MIN = 1;
var FRISTDAG_MAKS = 28;
var tosifret = (n) => String(n).padStart(2, "0");
function leveringsfrist(ar, periode2, fristdag) {
  if (fristdag === null) return null;
  const [aret, maned] = periode2 === 12 ? [ar + 1, 1] : [ar, periode2 + 1];
  return `${String(aret)}-${tosifret(maned)}-${tosifret(fristdag)}`;
}
function erForsinket(tilstand, frist, idag) {
  if (frist === null) return false;
  return (tilstand === "ikke_levert" || tilstand === "sendt_tilbake") && idag > frist;
}
var DATO = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Europe/Oslo",
  year: "numeric",
  month: "2-digit",
  day: "2-digit"
});
function datoIOslo(tidspunkt) {
  return DATO.format(tidspunkt);
}
function gjeldendeLevering(leveringer, periode2) {
  return leveringer.filter((l) => l.periode === periode2 && l.status !== "erstattet").reduce(
    (nyest, l) => nyest === void 0 || l.levert.tidspunkt > nyest.levert.tidspunkt ? l : nyest,
    void 0
  );
}
function periodetilstand(leveringer, periode2) {
  const status = gjeldendeLevering(leveringer, periode2)?.status;
  return status === void 0 || status === "erstattet" ? "ikke_levert" : status;
}
function kanLeverePeriode(leveringer, periode2) {
  return periodetilstand(leveringer, periode2) !== "godkjent";
}
var BEHANDLER = /* @__PURE__ */ new Set(["leder", "okonom"]);
function kanBehandleLeveringer(omradetilganger, kommunalomradeId) {
  return omradetilganger.some(
    (o) => o.kommunalomradeId === kommunalomradeId && BEHANDLER.has(o.rolle)
  );
}

// src/domain/oppgaver.ts
var LEVERER = /* @__PURE__ */ new Set(["leder", "okonom"]);
var REKKEFOLGE = { godkjenn: 0, rett: 1, lever: 2, mangler: 3 };
function utledOppgaver(bruker, virksomheter) {
  const ut = [];
  const mangler = /* @__PURE__ */ new Map();
  for (const v of virksomheter) {
    if (v.periode === null) continue;
    const periode2 = v.periode;
    const gjeldende = gjeldendeLevering(v.leveringer, periode2);
    const erSjef = kanBehandleLeveringer(bruker.omradetilganger, v.kommunalomradeId);
    if (erSjef) {
      if (gjeldende?.status === "levert" && gjeldende.levertAvId !== bruker.brukerId) {
        ut.push({
          type: "godkjenn",
          virksomhetId: v.id,
          virksomhetNavn: v.navn,
          kommunalomradeId: v.kommunalomradeId,
          ar: v.ar,
          periode: periode2,
          levert: gjeldende.levert
        });
      }
      if (gjeldende === void 0 || gjeldende.status === "sendt_tilbake") {
        const samlet = mangler.get(v.kommunalomradeId) ?? {
          type: "mangler",
          kommunalomradeId: v.kommunalomradeId,
          kommunalomradeNavn: v.kommunalomradeNavn,
          virksomheter: []
        };
        mangler.set(v.kommunalomradeId, {
          ...samlet,
          virksomheter: [...samlet.virksomheter, { id: v.id, navn: v.navn, ar: v.ar, periode: periode2 }]
        });
      }
      continue;
    }
    const rolle = bruker.tilganger.find((t2) => t2.virksomhetId === v.id)?.rolle;
    if (rolle === void 0 || !LEVERER.has(rolle)) continue;
    const felles2 = { virksomhetId: v.id, virksomhetNavn: v.navn, ar: v.ar, periode: periode2 };
    if (gjeldende === void 0) {
      ut.push({ type: "lever", ...felles2 });
    } else if (gjeldende.status === "sendt_tilbake" && gjeldende.behandlet !== null) {
      ut.push({ type: "rett", ...felles2, behandlet: gjeldende.behandlet });
    }
  }
  ut.push(...mangler.values());
  return ut.sort((a, b) => REKKEFOLGE[a.type] - REKKEFOLGE[b.type]);
}

// src/domain/rapport.ts
var PERIODETYPER = ["maned", "tertial", "ar"];
var RAPPORTTYPER = [...PERIODETYPER, "budsjett"];
function erPeriodetype(type) {
  return type !== "budsjett";
}
function harNummer(type) {
  return type === "maned" || type === "tertial";
}
var TERTIALSLUTT2 = [4, 8, 12];
function rapportperiode(type, nummer) {
  if (!harNummer(type)) return 12;
  if (nummer === null) return null;
  if (type === "maned") return erManed(nummer) ? nummer : null;
  return TERTIALSLUTT2[nummer - 1] ?? null;
}
function rapportnavn(type, periode2, ar) {
  if (type === "budsjett") {
    return `Budsjett ${String(ar)} og \xF8konomiplan ${String(ar)}\u2013${String(ar + 3)}`;
  }
  if (type === "ar") return `\xC5rsrapport ${String(ar)}`;
  if (type === "tertial") return `Tertialrapport ${String(periode2 / 4)}. tertial ${String(ar)}`;
  return `M\xE5nedsrapport ${MANEDSNAVN[periode2]} ${String(ar)}`;
}
function sammenligningsperiode(type, periode2) {
  if (type === "budsjett") return null;
  const forrige = type === "maned" ? periode2 - 1 : periode2 - 4;
  return erManed(forrige) ? forrige : null;
}
var tilTusen = (tekst, fra) => konverterBelop(belopFraDesimaltekst(tekst), fra, "kr_1000");
var tall = (budsjett, prognose) => ({
  budsjett: belopTilDesimaltekst(budsjett),
  prognose: belopTilDesimaltekst(prognose),
  avvik: belopTilDesimaltekst(beregnAvvik(prognose, budsjett)),
  avvikProsent: beregnAvvikProsent(prognose, budsjett)
});
var staende = (leveringer, periode2) => {
  const l = gjeldendeLevering(leveringer, periode2);
  return l?.status === "godkjent" || l?.status === "levert" ? l : void 0;
};
function avvikslinjer(l, enhet) {
  return l.hovedkontoer.flatMap((h) => {
    const f = l.forklaringer?.find((x) => x.hovedkontoId === h.id);
    if (f === void 0) return [];
    return [
      {
        hovedkonto: h.kontonummer === null ? h.navn : `${h.kontonummer} ${h.navn}`,
        avvik: belopTilDesimaltekst(
          beregnAvvik(tilTusen(h.prognose, enhet), tilTusen(h.budsjett, enhet))
        ),
        arsak: f.arsak,
        tiltak: f.tiltak,
        tiltakNavn: f.tiltakNavn
      }
    ];
  });
}
function regnVirksomhet(v, periode2, sammenlign) {
  const gjeldende = gjeldendeLevering(v.leveringer, periode2);
  const status = gjeldende === void 0 || gjeldende.status === "erstattet" ? "ikke_levert" : gjeldende.status;
  const iSummen = status === "godkjent" || status === "levert";
  const budsjett = gjeldende ? tilTusen(gjeldende.budsjett, v.valutaEnhet) : 0n;
  const prognose = gjeldende ? tilTusen(gjeldende.prognose, v.valutaEnhet) : 0n;
  const forrige = sammenlign === null ? void 0 : staende(v.leveringer, sammenlign);
  const endring = iSummen && forrige !== void 0 ? prognose - tilTusen(forrige.prognose, v.valutaEnhet) : null;
  return {
    linje: {
      id: v.id,
      navn: v.navn,
      status,
      tall: gjeldende ? tall(budsjett, prognose) : null,
      iSummen,
      endring: endring === null ? null : belopTilDesimaltekst(endring),
      levert: gjeldende?.levert ?? null,
      kommentar: gjeldende?.kommentar ?? null,
      behandlet: gjeldende?.behandlet ?? null,
      kommentarer: gjeldende ? [...gjeldende.kommentarer] : [],
      forklaringer: gjeldende ? avvikslinjer(gjeldende, v.valutaEnhet) : []
    },
    budsjett: iSummen ? budsjett : 0n,
    prognose: iSummen ? prognose : 0n,
    endring,
    hovedkontoer: iSummen && gjeldende ? gjeldende.hovedkontoer.map((h) => ({
      id: h.id,
      kontonummer: h.kontonummer,
      navn: h.navn,
      kategori: h.kategori,
      budsjett: tilTusen(h.budsjett, v.valutaEnhet),
      prognose: tilTusen(h.prognose, v.valutaEnhet)
    })) : []
  };
}
function summer(deler) {
  const budsjett = deler.reduce((s, d) => s + d.budsjett, 0n);
  const prognose = deler.reduce((s, d) => s + d.prognose, 0n);
  const endringer = deler.map((d) => d.endring).filter((e) => e !== null);
  const perKonto = /* @__PURE__ */ new Map();
  for (const h of deler.flatMap((d) => d.hovedkontoer)) {
    const forr = perKonto.get(h.id);
    perKonto.set(
      h.id,
      forr === void 0 ? h : { ...forr, budsjett: forr.budsjett + h.budsjett, prognose: forr.prognose + h.prognose }
    );
  }
  const hovedkontoer = [...perKonto.values()].sort(
    (a, b) => (a.kontonummer ?? a.navn).localeCompare(b.kontonummer ?? b.navn, "nb", { numeric: true })
  ).map((h) => ({
    ...h,
    budsjett: belopTilDesimaltekst(h.budsjett),
    prognose: belopTilDesimaltekst(h.prognose),
    avvik: belopTilDesimaltekst(beregnAvvik(h.prognose, h.budsjett))
  }));
  const status = deler.map((d) => d.linje.status);
  return {
    sum: tall(budsjett, prognose),
    endring: endringer.length === 0 ? null : belopTilDesimaltekst(endringer.reduce((s, e) => s + e, 0n)),
    dekning: {
      antall: deler.length,
      godkjent: status.filter((s) => s === "godkjent").length,
      venter: status.filter((s) => s === "levert").length,
      sendtTilbake: status.filter((s) => s === "sendt_tilbake").length,
      mangler: status.filter((s) => s === "ikke_levert").length
    },
    hovedkontoer
  };
}
function byggOkonomirapport(omrader, type, periode2) {
  const sammenlign = sammenligningsperiode(type, periode2);
  const alle = [];
  const deler = omrader.map((o) => {
    const regnet = o.virksomheter.map((v) => regnVirksomhet(v, periode2, sammenlign));
    alle.push(...regnet);
    return { id: o.id, navn: o.navn, ...summer(regnet), virksomheter: regnet.map((r) => r.linje) };
  });
  return { ...summer(alle), omrader: deler };
}

// src/domain/rapportkapittel.ts
var KAPITTELTYPER = [
  "tekst",
  "omrader",
  "okonomi_sammendrag",
  "okonomi_hovedkonto",
  "okonomi_kommentarer",
  "okonomi_budsjettendringer",
  "kpi",
  "budsjett_oversikt",
  "budsjett_tiltak",
  "budsjett_investering"
];
var KAPITTELTYPENAVN = {
  tekst: "Fritekst",
  omrader: "Kommunalomr\xE5dene",
  okonomi_sammendrag: "\xD8konomi: sammendrag",
  okonomi_hovedkonto: "\xD8konomi: per hovedkonto",
  okonomi_kommentarer: "\xD8konomi: kommentarer til leveringene",
  okonomi_budsjettendringer: "\xD8konomi: forslag til budsjettendringer",
  kpi: "N\xF8kkeltall fra KPI",
  budsjett_oversikt: "Budsjett: \xF8konomiplanen per omr\xE5de",
  budsjett_tiltak: "Budsjett: tiltakene",
  budsjett_investering: "Budsjett: investeringene"
};
var FELLES_TYPER = ["tekst", "omrader", "kpi"];
function kapitteltyperFor(type) {
  return KAPITTELTYPER.filter(
    (k2) => FELLES_TYPER.includes(k2) || (erPeriodetype(type) ? k2.startsWith("okonomi_") : k2.startsWith("budsjett_"))
  );
}
var MAKS_KAPITLER = 40;
var MAKS_UNDEROVERSKRIFTER = 10;
function nyttKapittel(type, nyId) {
  const felles2 = { id: nyId(), tittel: KAPITTELTYPENAVN[type], veiledning: null };
  switch (type) {
    case "omrader":
      return {
        ...felles2,
        type,
        underoverskrifter: [
          { id: nyId(), tittel: "\xD8konomi" },
          { id: nyId(), tittel: "Utfordringer og tiltak" }
        ],
        medOkonomi: true
      };
    case "kpi":
      return { ...felles2, type, kategorier: [] };
    case "budsjett_tiltak":
      return { ...felles2, type, medIkkeInnstilte: true };
    default:
      return { ...felles2, type };
  }
}
function standardmal(type, nyId) {
  const tekst = (tittel2, veiledning2) => ({
    id: nyId(),
    type: "tekst",
    tittel: tittel2,
    veiledning: veiledning2
  });
  const omrader = (tittel2, underoverskrifter) => ({
    id: nyId(),
    type: "omrader",
    tittel: tittel2,
    veiledning: "Hva har skjedd i perioden, hva er utfordringene, og hva gj\xF8res med dem?",
    underoverskrifter: underoverskrifter.map((t2) => ({ id: nyId(), tittel: t2 })),
    medOkonomi: true
  });
  const fast = (t2, tittel2) => ({
    id: nyId(),
    type: t2,
    tittel: tittel2,
    veiledning: null
  });
  if (type === "budsjett") {
    return [
      tekst("Innledning", "Hovedbildet: det viktigste i forslaget, og hva det bygger p\xE5."),
      tekst(
        "Kommunedirekt\xF8rens vurdering",
        "\xD8konomisk handlingsrom, prioriteringer og risiko i planperioden."
      ),
      { id: nyId(), type: "budsjett_oversikt", tittel: "\xD8konomiplanen", veiledning: null },
      omrader("Kommunalomr\xE5dene", ["Hovedutfordringer", "Prioriteringer og tiltak"]),
      {
        id: nyId(),
        type: "budsjett_tiltak",
        tittel: "Tiltakene",
        veiledning: null,
        medIkkeInnstilte: true
      },
      { id: nyId(), type: "budsjett_investering", tittel: "Investeringene", veiledning: null },
      tekst("Forslag til vedtak", "Vedtakspunktene kommunestyret skal ta stilling til.")
    ];
  }
  if (type === "maned") {
    return [
      tekst("Innledning", "Kort om m\xE5neden: det viktigste r\xE5dmannen og politikerne b\xF8r vite."),
      fast("okonomi_sammendrag", "\xD8konomi"),
      omrader("Kommunalomr\xE5dene", ["\xD8konomi", "Utfordringer"]),
      fast("okonomi_kommentarer", "Kommentarer til prognosene")
    ];
  }
  return [
    tekst("Innledning", "Form\xE5let med rapporten, og hovedbildet for perioden."),
    tekst(
      "Sentrale forhold i kommunen",
      "Befolkning, samfunnsutvikling, investeringer og andre forhold som p\xE5virker kommunen."
    ),
    fast("okonomi_sammendrag", "\xD8konomisk status"),
    fast("okonomi_hovedkonto", "\xD8konomi per hovedkonto"),
    omrader("Kommunalomr\xE5dene", [
      "\xD8konomi",
      "Tjenester og kvalitet",
      "Personell og sykefrav\xE6r",
      "Utfordringer og tiltak"
    ]),
    { id: nyId(), type: "kpi", tittel: "N\xF8kkeltall", veiledning: null, kategorier: [] },
    fast("okonomi_kommentarer", "Kommentarer til prognosene"),
    fast("okonomi_budsjettendringer", "Forslag til budsjettendringer"),
    tekst("Oppsummering", "Samlet vurdering, og eventuelle forslag til vedtak.")
  ];
}
function tekstfelt(kapitler) {
  return kapitler.flatMap((k2) => {
    if (k2.type === "tekst") return [{ kapittelId: k2.id, feltId: TEKSTFELT, perOmrade: false }];
    if (k2.type === "omrader") {
      return k2.underoverskrifter.map((u2) => ({ kapittelId: k2.id, feltId: u2.id, perOmrade: true }));
    }
    return [];
  });
}
var TEKSTFELT = "tekst";
function typefeil(type, kapitler) {
  const lov = kapitteltyperFor(type);
  const feil = kapitler.find((k2) => !lov.includes(k2.type));
  if (feil === void 0) return null;
  return type === "budsjett" ? `\xAB${feil.tittel}\xBB viser tall fra prognosen, og h\xF8rer ikke hjemme i budsjettet.` : `\xAB${feil.tittel}\xBB viser budsjettet, og h\xF8rer bare hjemme i budsjettdokumentet.`;
}
function oppsettfeil(kapitler) {
  if (kapitler.length > MAKS_KAPITLER)
    return `En rapport kan ha h\xF8yst ${String(MAKS_KAPITLER)} kapitler.`;
  const ider = kapitler.map((k2) => k2.id);
  if (new Set(ider).size !== ider.length) return "To kapitler har samme id.";
  for (const k2 of kapitler) {
    if (k2.type !== "omrader") continue;
    if (k2.underoverskrifter.length === 0) {
      return `\xAB${k2.tittel}\xBB m\xE5 ha minst \xE9n underoverskrift.`;
    }
    if (k2.underoverskrifter.length > MAKS_UNDEROVERSKRIFTER) {
      return `\xAB${k2.tittel}\xBB kan ha h\xF8yst ${String(MAKS_UNDEROVERSKRIFTER)} underoverskrifter.`;
    }
    const u2 = k2.underoverskrifter.map((x) => x.id);
    if (new Set(u2).size !== u2.length) return `To underoverskrifter i \xAB${k2.tittel}\xBB har samme id.`;
  }
  return null;
}

// src/domain/rapportflyt.ts
var OMRADETILSTANDER = ["ikke_startet", "i_arbeid", "ferdig"];
function omradetilstand(harTekst, ferdig) {
  if (ferdig) return "ferdig";
  return harTekst ? "i_arbeid" : "ikke_startet";
}
function rapportnummer(type, periode2) {
  if (!harNummer(type)) return null;
  return type === "tertial" ? periode2 / 4 : periode2;
}
var REKKEFOLGE2 = { skriv: 0, folg_opp: 1, foresla: 2 };
function utledRapportoppgaver(bruker, rapporter) {
  const ut = [];
  for (const r of rapporter) {
    const felles2 = {
      rapportId: r.rapportId,
      tittel: r.tittel,
      rapporttype: r.type,
      ar: r.ar,
      nummer: rapportnummer(r.type, r.periode),
      frist: r.frist
    };
    for (const o of r.omrader) {
      if (o.tilstand === "ferdig") continue;
      if (bruker.skriver.has(o.id)) {
        ut.push({
          ...felles2,
          oppgave: "skriv",
          omradeId: o.id,
          omradeNavn: o.navn,
          tilstand: o.tilstand,
          nyeForslag: o.nyeForslag
        });
      } else if (bruker.foreslar.has(o.id) && !bruker.harForeslatt.has(`${r.rapportId}/${o.id}`)) {
        ut.push({ ...felles2, oppgave: "foresla", omradeId: o.id, omradeNavn: o.navn });
      }
    }
    const ferdige = r.omrader.filter((o) => o.tilstand === "ferdig").length;
    if (bruker.styrer && ferdige < r.omrader.length) {
      ut.push({ ...felles2, oppgave: "folg_opp", ferdige, antall: r.omrader.length });
    }
  }
  return ut.sort((a, b) => REKKEFOLGE2[a.oppgave] - REKKEFOLGE2[b.oppgave]);
}

// src/domain/politisk.ts
function ankernokkel(anker) {
  return `${anker.kapittelId}/${anker.omradeId ?? "-"}/${anker.elementId ?? "-"}`;
}
function serUtsendelse(utvalg, utsendelse) {
  if (utvalg.length === 0) return false;
  return utsendelse.alleFolkevalgte || utvalg.includes(utsendelse.utvalgId);
}
var tall2 = (okonomi) => {
  if (okonomi === null || typeof okonomi !== "object") return JSON.stringify(okonomi);
  const { laget: _laget, ...resten } = okonomi;
  return JSON.stringify(resten);
};
function endredeAnkre(forrige, ny) {
  const endret = [];
  const elementer = (kapittelId3, omradeId, for_, na) => {
    for (const e of na.elementer ?? []) {
      const f = for_?.elementer?.find((x) => x.id === e.id);
      if (JSON.stringify(f) !== JSON.stringify(e)) {
        endret.push(ankernokkel({ kapittelId: kapittelId3, omradeId, elementId: e.id }));
      }
    }
  };
  const kapittel = (id) => ankernokkel({ kapittelId: id, omradeId: null, elementId: null });
  const okonomiEndret = tall2(forrige.okonomi) !== tall2(ny.okonomi);
  for (const k2 of ny.kapitler) {
    const f = forrige.kapitler.find((x) => x.id === k2.id && x.type === k2.type);
    if (f === void 0) {
      endret.push(kapittel(k2.id));
      continue;
    }
    switch (k2.type) {
      case "tekst":
        if (f.type === "tekst" && f.felt.tekst !== k2.felt.tekst) endret.push(kapittel(k2.id));
        elementer(k2.id, null, f.type === "tekst" ? f.felt : void 0, k2.felt);
        break;
      case "omrader":
        for (const o of k2.omrader) {
          const fo = f.type === "omrader" ? f.omrader.find((x) => x.id === o.id) : void 0;
          if (fo === void 0 || tall2(fo.okonomi) !== tall2(o.okonomi) || JSON.stringify(fo.budsjett ?? null) !== JSON.stringify(o.budsjett ?? null) || JSON.stringify(fo.kutt ?? null) !== JSON.stringify(o.kutt ?? null)) {
            endret.push(ankernokkel({ kapittelId: k2.id, omradeId: o.id, elementId: null }));
          }
          for (const felt2 of o.felt) {
            const ff = fo?.felt.find((x) => x.id === felt2.id);
            if (fo !== void 0 && ff?.tekst.tekst !== felt2.tekst.tekst) {
              endret.push(ankernokkel({ kapittelId: k2.id, omradeId: o.id, elementId: felt2.id }));
            }
            if (fo !== void 0) elementer(k2.id, o.id, ff?.tekst, felt2.tekst);
          }
        }
        break;
      case "kpi":
        if (f.type === "kpi" && JSON.stringify(f.omrader) !== JSON.stringify(k2.omrader)) {
          endret.push(kapittel(k2.id));
        }
        break;
      case "okonomi_budsjettendringer":
        if ("revisjoner" in f && JSON.stringify(f.revisjoner) !== JSON.stringify(k2.revisjoner)) {
          endret.push(kapittel(k2.id));
        }
        break;
      case "budsjett_oversikt":
      case "budsjett_tiltak":
      case "budsjett_investering":
        if ("budsjett" in f && JSON.stringify(f.budsjett) !== JSON.stringify(k2.budsjett)) {
          endret.push(kapittel(k2.id));
        }
        break;
      default:
        if (okonomiEndret) endret.push(kapittel(k2.id));
    }
  }
  return endret;
}
function utledPolitiskeOppgaver(bruker, utsendelser) {
  const les2 = [];
  const svar = [];
  for (const u2 of utsendelser) {
    if (u2.behandlet) continue;
    const felles2 = {
      utsendelseId: u2.utsendelseId,
      rapportId: u2.rapportId,
      tittel: u2.tittel,
      rapporttype: u2.type,
      ar: u2.ar,
      nummer: u2.nummer,
      frist: u2.frist,
      utvalgNavn: u2.utvalgNavn
    };
    if (serUtsendelse(bruker.utvalg, u2)) les2.push({ ...felles2, oppgave: "les" });
    const antall = u2.ubesvarte.filter(
      (t2) => t2.omradeId === null ? bruker.styrer : bruker.skriver.has(t2.omradeId)
    ).length;
    if (antall > 0) svar.push({ ...felles2, oppgave: "svar", antall });
  }
  return [...svar, ...les2];
}
var harElement = (felt2, id) => (felt2?.elementer ?? []).some((e) => e.id === id);
function gyldigAnker(innhold, anker) {
  const kapittel = innhold.kapitler.find((k2) => k2.id === anker.kapittelId);
  if (kapittel === void 0) return false;
  const { elementId } = anker;
  if (anker.omradeId === null) {
    return elementId === null || kapittel.type === "tekst" && harElement(kapittel.felt, elementId);
  }
  if (kapittel.type !== "omrader") return false;
  const omrade2 = kapittel.omrader?.find((o) => o.id === anker.omradeId);
  if (omrade2 === void 0) return false;
  return elementId === null || (omrade2.felt ?? []).some((f) => f.id === elementId || harElement(f.tekst, elementId));
}
function kanSvarePolitisk(bruker, omradeId) {
  if (bruker.erOkonomisjef || bruker.controllerOmrader.length > 0) return true;
  return omradeId !== null && bruker.omradetilganger.some(
    (o) => o.kommunalomradeId === omradeId && (o.rolle === "leder" || o.rolle === "okonom")
  );
}

// src/domain/rapportelement.ts
var ELEMENTTYPER = ["tabell", "figur", "bilde"];
var FIGURTYPER = ["stolpe", "linje", "kake"];
var ELEMENTNAVN = {
  tabell: "Tabell",
  figur: "Figur",
  bilde: "Bilde"
};
var MAKS_KOLONNER = 20;
var MAKS_RADER = 200;
var MAKS_CELLE = 500;
var MAKS_SERIER = 8;
var MAKS_KATEGORIER = 60;
var MAKS_BILDE = 2 * 1024 * 1024;
var BILDETYPER = ["image/png", "image/jpeg"];
function elementmerke(type, nummer) {
  return `[[${ELEMENTNAVN[type]} ${String(nummer)}]]`;
}
var MERKE = /^\s*\[\[(Tabell|Figur|Bilde) (\d+)\]\]\s*$/;
function lesElementmerke(linje) {
  const treff = MERKE.exec(linje);
  if (treff === null) return null;
  const navn2 = treff[1];
  const type = ELEMENTTYPER.find((t2) => ELEMENTNAVN[t2] === navn2);
  return type === void 0 ? null : { type, nummer: Number(treff[2]) };
}
function lesLimtInnTabell(tekst) {
  const linjer = tekst.replace(/\r\n?/g, "\n").split("\n");
  while (linjer.length > 0 && (linjer.at(-1) ?? "").trim() === "") linjer.pop();
  const rader = linjer.map((l) => l.split("	").map((c) => c.trim()));
  const bredde = Math.max(0, ...rader.map((r) => r.length));
  return rader.map((r) => [...r, ...Array(bredde - r.length).fill("")]);
}
function tallFraCelle(celle4) {
  const renset = celle4.replace(/\s/g, "").replace(/%$/, "").replace(/^−/, "-").replace(",", ".");
  if (renset === "") return null;
  if (!/^-?\d+(\.\d+)?$/.test(renset)) return void 0;
  return Number(renset);
}

// src/domain/tekstblokker.ts
var PUNKT = /^\s*[-•]\s+/;
function blokk(linjer) {
  return linjer.every((l) => PUNKT.test(l)) ? { type: "liste", punkter: linjer.map((l) => l.replace(PUNKT, "")) } : { type: "avsnitt", tekst: linjer.map((l) => l.trim()).join(" ") };
}
function tekstblokker(tekst) {
  const blokker = [];
  for (const del of tekst.replace(/\r\n/g, "\n").split(/\n\s*\n/)) {
    const linjer = del.split("\n").map((l) => l.trimEnd()).filter((l) => l.trim() !== "");
    let samlet = [];
    for (const linje of linjer) {
      const merke = lesElementmerke(linje);
      if (merke === null) {
        samlet.push(linje);
        continue;
      }
      if (samlet.length > 0) blokker.push(blokk(samlet));
      samlet = [];
      blokker.push({ type: "merke", elementtype: merke.type, nummer: merke.nummer });
    }
    if (samlet.length > 0) blokker.push(blokk(samlet));
  }
  return blokker;
}
function medElementer(tekst, elementer) {
  const brukt = /* @__PURE__ */ new Set();
  const ut = [];
  for (const b of tekstblokker(tekst)) {
    if (b.type !== "merke") {
      ut.push(b);
      continue;
    }
    const element = elementer.find((e) => e.type === b.elementtype && e.nummer === b.nummer);
    if (element === void 0) {
      ut.push({ type: "avsnitt", tekst: elementmerke(b.elementtype, b.nummer) });
    } else if (!brukt.has(element)) {
      brukt.add(element);
      ut.push({ type: "element", element });
    }
  }
  for (const e of elementer) if (!brukt.has(e)) ut.push({ type: "element", element: e });
  return ut;
}

// src/domain/figur.ts
var FIGURFLATE = { bredde: 640, hoyde: 300, venstre: 64, hoyre: 16, topp: 16, bunn: 44 };
var plott = {
  bredde: FIGURFLATE.bredde - FIGURFLATE.venstre - FIGURFLATE.hoyre,
  hoyde: FIGURFLATE.hoyde - FIGURFLATE.topp - FIGURFLATE.bunn
};
function figurskala(verdier, onsket = 5) {
  const tall3 = verdier.filter((v) => v !== null);
  let min = Math.min(0, ...tall3);
  let max = Math.max(0, ...tall3);
  if (min === max) max = 1;
  const grovt = (max - min) / onsket;
  const potens = 10 ** Math.floor(Math.log10(grovt));
  const steg = ([1, 2, 2.5, 5, 10].find((f) => f * potens >= grovt) ?? 10) * potens;
  min = Math.floor(min / steg) * steg;
  max = Math.ceil(max / steg) * steg;
  const merker = [];
  for (let v = min; v <= max + steg / 2; v += steg) merker.push(Number(v.toPrecision(12)));
  const y = (verdi) => FIGURFLATE.topp + plott.hoyde - (verdi - min) / (max - min) * plott.hoyde;
  return { min, max, merker, y };
}
function figurX(i, antall) {
  return FIGURFLATE.venstre + plott.bredde / antall * (i + 0.5);
}
function figurstolper(serier, s) {
  const antall = serier[0]?.length ?? 0;
  const gruppe = plott.bredde / Math.max(antall, 1);
  const bredde = gruppe * 0.8 / Math.max(serier.length, 1);
  const null_ = s.y(0);
  return serier.flatMap(
    (verdier, serie) => verdier.flatMap((verdi, kategori) => {
      if (verdi === null) return [];
      const topp = s.y(Math.max(verdi, 0));
      return [
        {
          serie,
          kategori,
          x: FIGURFLATE.venstre + kategori * gruppe + gruppe * 0.1 + serie * bredde,
          y: topp,
          bredde,
          hoyde: Math.abs(s.y(verdi) - null_),
          verdi
        }
      ];
    })
  );
}
function linjestykker(verdier, s) {
  const stykker = [];
  let punkter = [];
  verdier.forEach((verdi, i) => {
    if (verdi === null) {
      if (punkter.length > 0) stykker.push(punkter.join(" "));
      punkter = [];
      return;
    }
    punkter.push(`${rund(figurX(i, verdier.length))},${rund(s.y(verdi))}`);
  });
  if (punkter.length > 0) stykker.push(punkter.join(" "));
  return stykker;
}
var FIGURKAKE = { x: 150, y: FIGURFLATE.hoyde / 2, r: 120 };
function kakestykker(verdier) {
  const sum2 = verdier.reduce((a, v) => a + (v ?? 0), 0);
  if (sum2 <= 0) return [];
  const punkt = (vinkel) => `${rund(FIGURKAKE.x + FIGURKAKE.r * Math.sin(vinkel))},${rund(FIGURKAKE.y - FIGURKAKE.r * Math.cos(vinkel))}`;
  let start = 0;
  const ut = [];
  verdier.forEach((verdi, kategori) => {
    if (verdi === null || verdi <= 0) return;
    const andel = verdi / sum2;
    const slutt = start + andel * 2 * Math.PI;
    const sti = andel >= 1 ? `M ${String(FIGURKAKE.x - FIGURKAKE.r)},${String(FIGURKAKE.y)} a ${String(FIGURKAKE.r)},${String(FIGURKAKE.r)} 0 1,0 ${String(2 * FIGURKAKE.r)},0 a ${String(FIGURKAKE.r)},${String(FIGURKAKE.r)} 0 1,0 ${String(-2 * FIGURKAKE.r)},0 Z` : `M ${String(FIGURKAKE.x)},${String(FIGURKAKE.y)} L ${punkt(start)} A ${String(FIGURKAKE.r)},${String(FIGURKAKE.r)} 0 ${andel > 0.5 ? "1" : "0"},1 ${punkt(slutt)} Z`;
    ut.push({ kategori, sti, andel });
    start = slutt;
  });
  return ut;
}
var rund = (n) => String(Math.round(n * 10) / 10);
var TALL = new Intl.NumberFormat("nb-NO", { maximumFractionDigits: 2 });
function formaterFigurtall(verdi, enhet = null) {
  if (verdi === null) return "\u2013";
  const tall3 = TALL.format(verdi);
  if (enhet === null || enhet === "") return tall3;
  return enhet === "%" ? `${tall3} %` : `${tall3} ${enhet}`;
}
function figurnavnHvert(antall) {
  return Math.max(1, Math.ceil(antall / 12));
}
var SERIEFARGER = ["#17226d", "#20358f", "#284cac", "#3766c0", "#4c80cd", "#6297d6"];
function seriefargeHex(i, antall) {
  const trinn = SERIEFARGER.length;
  const steg = antall <= 1 ? Math.ceil(trinn / 2) : 1 + Math.round(i * (trinn - 1) / (antall - 1));
  return SERIEFARGER[Math.min(Math.max(steg, 1), trinn) - 1] ?? "#17226d";
}
var RUTE = "#dde1e8";
var AKSE = "#79818f";
var DEMPET = "#525a67";
var vask = (tekst) => tekst.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
function figurSvg(f) {
  const deler = [];
  const kake = f.figurtype === "kake";
  const bredde = kake ? 2 * FIGURKAKE.x : FIGURFLATE.bredde;
  if (kake) {
    for (const k2 of kakestykker(f.serier[0]?.verdier ?? [])) {
      const farge = seriefargeHex(k2.kategori, f.kategorier.length);
      deler.push(`<path d="${k2.sti}" fill="${farge}" stroke="#ffffff" stroke-width="1.5"/>`);
    }
  } else {
    const s = figurskala(f.serier.flatMap((x) => x.verdier));
    for (const m of s.merker) {
      const y = String(s.y(m));
      deler.push(
        `<line x1="${String(FIGURFLATE.venstre)}" x2="${String(FIGURFLATE.bredde - FIGURFLATE.hoyre)}" y1="${y}" y2="${y}" stroke="${m === 0 ? AKSE : RUTE}" stroke-width="1"/>`,
        `<text x="${String(FIGURFLATE.venstre - 8)}" y="${String(s.y(m) + 4)}" text-anchor="end" font-size="11" fill="${DEMPET}">${vask(formaterFigurtall(m))}</text>`
      );
    }
    const hvert = figurnavnHvert(f.kategorier.length);
    f.kategorier.forEach((k2, i) => {
      if (i % hvert !== 0) return;
      const navn2 = k2.length > 14 ? `${k2.slice(0, 13)}\u2026` : k2;
      deler.push(
        `<text x="${String(figurX(i, f.kategorier.length))}" y="${String(FIGURFLATE.hoyde - FIGURFLATE.bunn + 18)}" text-anchor="middle" font-size="11" fill="${DEMPET}">${vask(navn2)}</text>`
      );
    });
    if (f.figurtype === "stolpe") {
      for (const b of figurstolper(
        f.serier.map((x) => x.verdier),
        s
      )) {
        deler.push(
          `<rect x="${String(b.x)}" y="${String(b.y)}" width="${String(b.bredde)}" height="${String(b.hoyde)}" fill="${seriefargeHex(b.serie, f.serier.length)}"/>`
        );
      }
    } else {
      f.serier.forEach((x, i) => {
        const farge = seriefargeHex(i, f.serier.length);
        for (const punkter of linjestykker(x.verdier, s)) {
          deler.push(
            `<polyline points="${punkter}" fill="none" stroke="${farge}" stroke-width="2.5" stroke-linejoin="round"/>`
          );
        }
        x.verdier.forEach((v, j) => {
          if (v === null) return;
          deler.push(
            `<circle cx="${String(figurX(j, x.verdier.length))}" cy="${String(s.y(v))}" r="3.5" fill="${farge}"/>`
          );
        });
      });
    }
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${String(bredde)} ${String(FIGURFLATE.hoyde)}">${deler.join("")}</svg>`;
}

// src/domain/rapporttekster.ts
function dekningstekst(dekning) {
  const { antall, godkjent, venter, sendtTilbake, mangler } = dekning;
  if (antall === 0) return { tekst: "Ingen virksomheter \xE5 rapportere for.", komplett: false };
  if (godkjent === antall && antall === 1) {
    return { tekst: "Virksomheten har levert, og leveringen er godkjent.", komplett: true };
  }
  if (godkjent === antall) {
    return {
      tekst: `Alle ${String(antall)} virksomhetene har levert, og alle er godkjent.`,
      komplett: true
    };
  }
  const deler = [
    venter > 0 ? `${String(venter)} venter p\xE5 godkjenning` : null,
    sendtTilbake > 0 ? `${String(sendtTilbake)} er sendt tilbake` : null,
    mangler > 0 ? `${String(mangler)} har ikke levert` : null
  ].filter((d) => d !== null);
  return {
    tekst: `Summen bygger p\xE5 ${String(godkjent + venter)} av ${String(antall)} virksomheter. ${deler.join(", ")}.`,
    komplett: false
  };
}
function sammenligningstekst(rapport) {
  if (rapport.sammenlignetMed === null) return null;
  return rapport.type === "maned" ? MANEDSNAVN[rapport.sammenlignetMed] : `${String(rapport.sammenlignetMed / 4)}. tertial`;
}

// src/domain/budsjett.ts
var BUDSJETTFASER = ["rammer", "innspill", "forslag", "vedtatt"];
var FASENAVN = {
  rammer: "Rammer",
  innspill: "Innspill fra omr\xE5dene",
  forslag: "Kommunedirekt\xF8rens forslag",
  vedtatt: "Vedtatt"
};
var TILTAKSTYPER = ["innsparing", "behov"];
var TILTAKSSTATUSER = ["foreslatt", "innstilt", "ikke_innstilt"];
var ENDRINGSKATEGORIER = ["tidligere_vedtak", "demografi", "annet"];
var PLANAR = 4;
function planar(budsjettar) {
  return [budsjettar, budsjettar + 1, budsjettar + 2, budsjettar + 3];
}
var NULL_FIREAAR = [NULL_BELOP, NULL_BELOP, NULL_BELOP, NULL_BELOP];
function summerFireaar(rader) {
  return rader.reduce(
    (a, r) => [a[0] + r[0], a[1] + r[1], a[2] + r[2], a[3] + r[3]],
    NULL_FIREAAR
  );
}
function prisjuster(grunnlag, prosent2) {
  return skalerBelop(grunnlag, 1 + prosent2 / 100);
}
function tiltakseffekt(t2) {
  if (t2.type === "behov") return t2.belop;
  return [-t2.belop[0], -t2.belop[1], -t2.belop[2], -t2.belop[3]];
}
function omraderamme(input) {
  const justert = prisjuster(input.grunnlag, input.prisvekst);
  const endringer = summerFireaar(input.endringer.map((e) => e.belop));
  const ramme = [
    justert + endringer[0],
    justert + endringer[1],
    justert + endringer[2],
    justert + endringer[3]
  ];
  const tiltak = summerFireaar(
    input.tiltak.filter((t2) => t2.status === "innstilt").map(tiltakseffekt)
  );
  return {
    grunnlag: input.grunnlag,
    justert,
    endringer,
    ramme,
    tiltak,
    forslag: summerFireaar([ramme, tiltak])
  };
}
function ubalanse(tilFordeling, forslag, investering = NULL_FIREAAR) {
  if (tilFordeling === null) return null;
  const sum2 = summerFireaar([...forslag, investering]);
  return [
    tilFordeling[0] - sum2[0],
    tilFordeling[1] - sum2[1],
    tilFordeling[2] - sum2[2],
    tilFordeling[3] - sum2[3]
  ];
}
function kanEndreTiltak(fase, bruker, omradeId) {
  if (fase === "vedtatt") return false;
  if (bruker.styrer) return true;
  return fase === "innspill" && bruker.skriver.has(omradeId);
}
function omradeneSer(fase) {
  return fase !== "rammer";
}

// src/domain/investering.ts
var MOMSANDEL = 0.2;
var per = (f) => [f(0), f(1), f(2), f(3)];
function finansiering(p2) {
  const moms = per(
    (i) => p2.momskompensasjon ? skalerBelop(p2.utgift[i] ?? NULL_BELOP, MOMSANDEL) : NULL_BELOP
  );
  return {
    momskompensasjon: moms,
    tilskudd: p2.tilskudd,
    egenkapital: p2.egenkapital,
    lan: per(
      (i) => (p2.utgift[i] ?? NULL_BELOP) - (moms[i] ?? NULL_BELOP) - (p2.tilskudd[i] ?? NULL_BELOP) - (p2.egenkapital[i] ?? NULL_BELOP)
    )
  };
}
function overfinansiert(f) {
  return f.lan.some((l) => l < NULL_BELOP);
}
function kapitalkostnad(lan, rentePst, avdragstid) {
  const tid = BigInt(Math.max(1, Math.round(avdragstid)));
  return per((ar) => {
    let sum2 = NULL_BELOP;
    for (let t2 = 0; t2 < ar; t2 += 1) {
      const opptak = lan[t2] ?? NULL_BELOP;
      if (opptak <= NULL_BELOP) continue;
      const avdrag = opptak / tid;
      const rest = opptak - avdrag * BigInt(ar - t2 - 1);
      if (rest <= NULL_BELOP) continue;
      sum2 += avdrag + skalerBelop(rest, rentePst / 100);
    }
    return sum2;
  });
}
function regnProsjekt(p2, rentePst) {
  const f = finansiering(p2);
  return {
    utgift: p2.utgift,
    ...f,
    kapitalkostnad: kapitalkostnad(f.lan, rentePst, p2.avdragstid),
    driftskonsekvens: p2.driftskonsekvens
  };
}
function investeringsoversikt(prosjekter, rentePst) {
  const regnet = prosjekter.filter((p2) => p2.status === "innstilt").map((p2) => regnProsjekt(p2, rentePst));
  const sum2 = (felt2) => summerFireaar(regnet.map((r) => r[felt2]));
  return {
    utgift: sum2("utgift"),
    momskompensasjon: sum2("momskompensasjon"),
    tilskudd: sum2("tilskudd"),
    egenkapital: sum2("egenkapital"),
    lan: sum2("lan"),
    kapitalkostnad: sum2("kapitalkostnad"),
    driftskonsekvens: sum2("driftskonsekvens")
  };
}
function driftsbelastning(r) {
  return summerFireaar([r.kapitalkostnad, r.driftskonsekvens]);
}
var STANDARD_AVDRAGSTID = 40;
var MAKS_AVDRAGSTID = 50;
function kanEndreProsjekt(fase, bruker, omradeId) {
  if (fase === "vedtatt") return false;
  if (bruker.styrer) return true;
  return fase === "innspill" && omradeId !== null && bruker.skriver.has(omradeId);
}

// src/domain/budsjettforslag.ts
var FORSLAGSTYPER = ["parti", "vedtak"];
function erMed(tiltak, valg) {
  return valg.get(tiltak.id) ?? tiltak.status === "innstilt";
}
function forslagsramme(input) {
  return summerFireaar([
    input.ramme,
    ...input.tiltak.filter((t2) => erMed(t2, input.valg)).map(tiltakseffekt),
    ...input.linjer
  ]);
}
function forslagsbalanse(tilFordeling, sentrale, omrader) {
  if (tilFordeling === null) return null;
  const inn = summerFireaar([tilFordeling, ...sentrale]);
  const ut = summerFireaar(omrader);
  return [inn[0] - ut[0], inn[1] - ut[1], inn[2] - ut[2], inn[3] - ut[3]];
}
function iBalanse(balanse) {
  return balanse?.every((b) => b >= NULL_BELOP) ?? false;
}
function regnForslag(g, f) {
  const valg = new Map(f.valg.map((v) => [v.tiltakId, v.med]));
  const omrader = g.omrader.map((o) => ({
    id: o.id,
    ramme: forslagsramme({
      ramme: o.ramme,
      tiltak: o.tiltak,
      valg,
      linjer: f.linjer.filter((l) => l.omradeId === o.id).map((l) => l.belop)
    })
  }));
  const sentrale = f.linjer.filter((l) => l.omradeId === null).map((l) => l.belop);
  const rammer = omrader.map((o) => o.ramme);
  const prosjektvalg = new Map((f.prosjektvalg ?? []).map((v) => [v.prosjektId, v.med]));
  const i = g.investeringer;
  const valgte = (i?.prosjekter ?? []).filter((p2) => prosjektvalg.get(p2.id) ?? p2.status === "innstilt").map((p2) => ({ ...p2, status: "innstilt" }));
  const oversikt = investeringsoversikt(valgte, i?.lanerente ?? 0);
  const investering = driftsbelastning(oversikt);
  const balanse = forslagsbalanse(g.tilFordeling, sentrale, [...rammer, investering]);
  return {
    omrader,
    sum: summerFireaar(rammer),
    tilFordeling: g.tilFordeling === null ? null : summerFireaar([g.tilFordeling, ...sentrale]),
    ubalanse: balanse,
    investeringsutgift: oversikt.utgift,
    investering
  };
}
function kanEndreForslag(fase, styrer) {
  return styrer && fase === "forslag";
}

// src/domain/gevinstplan.ts
var GEVINSTVURDERINGER = ["i_rute", "etter_plan", "svikter", "stanset"];
var GEVINSTVURDERINGSNAVN = {
  i_rute: "I rute",
  etter_plan: "Etter plan",
  svikter: "Svikter",
  stanset: "Stanset"
};
function planlagtGevinstHittil(vedtatt, fraManed, tilManed, tilOgMed) {
  if (tilOgMed === null) return NULL_BELOP;
  const fra = fraManed ?? 1;
  const til = Math.max(tilManed ?? 12, fra);
  if (tilOgMed < fra) return NULL_BELOP;
  const maneder = BigInt(til - fra + 1);
  const gatt = BigInt(Math.min(tilOgMed, til) - fra + 1);
  return vedtatt * gatt / maneder;
}
function gevinststatus(g, tilOgMed) {
  const planlagtHittil = planlagtGevinstHittil(g.vedtatt, g.fraManed, g.tilManed, tilOgMed);
  const realisert = g.realisert ?? NULL_BELOP;
  const forventet = g.stanset ? NULL_BELOP : g.forventet ?? g.vedtatt;
  const udekket = forventet < g.vedtatt ? g.vedtatt - forventet : NULL_BELOP;
  const vurdering = g.stanset ? "stanset" : udekket > NULL_BELOP ? "svikter" : realisert < planlagtHittil ? "etter_plan" : "i_rute";
  return { vedtatt: g.vedtatt, planlagtHittil, realisert, forventet, udekket, vurdering };
}
function summerGevinster(statuser) {
  const sum2 = (felt2) => summerBelop(statuser.map(felt2));
  const antall = { i_rute: 0, etter_plan: 0, svikter: 0, stanset: 0 };
  for (const s of statuser) antall[s.vurdering] += 1;
  return {
    vedtatt: sum2((s) => s.vedtatt),
    planlagtHittil: sum2((s) => s.planlagtHittil),
    realisert: sum2((s) => s.realisert),
    forventet: sum2((s) => s.forventet),
    udekket: sum2((s) => s.udekket),
    antall
  };
}

// src/domain/avviksforklaring.ts
var STANDARD_FORKLARINGSTERSKEL = {
  prosent: 5,
  belop: 100000000n
};
var abs = (b) => b < NULL_BELOP ? -b : b;
function trengerForklaring(budsjett, prognose, enhet, terskel) {
  if (terskel === null) return false;
  const avvik = abs(beregnAvvik(prognose, budsjett));
  if (avvik === NULL_BELOP) return false;
  if (konverterBelop(avvik, enhet, "kr") < terskel.belop) return false;
  if (budsjett === NULL_BELOP) return true;
  return avvik * 100000n >= BigInt(Math.round(terskel.prosent * 1e3)) * abs(budsjett);
}
function forklaringsplikt(hovedkontoer, enhet, terskel) {
  return hovedkontoer.filter(
    (h) => trengerForklaring(
      belopFraDesimaltekst(h.budsjett),
      belopFraDesimaltekst(h.prognose),
      enhet,
      terskel
    )
  );
}
function manglerForklaring(plikt, forklaringer) {
  const forklart = new Set(forklaringer.map((f) => f.hovedkontoId));
  return plikt.filter((h) => !forklart.has(h.id));
}

// src/domain/tiltaksgap.ts
var GAPDEKNINGER = ["ingen_merforbruk", "dekket", "delvis", "udekket"];
var GAPDEKNINGSNAVN = {
  ingen_merforbruk: "Ingen merforbruk",
  dekket: "Dekket",
  delvis: "Delvis dekket",
  udekket: "Ikke dekket"
};
function gjenstaendeGevinst(t2) {
  if (t2.stanset) return NULL_BELOP;
  const forventet = t2.forventet ?? t2.vedtatt ?? NULL_BELOP;
  const rest = forventet - (t2.realisert ?? NULL_BELOP);
  return rest > NULL_BELOP ? rest : NULL_BELOP;
}
function tiltaksgap(avvik, gjenstaende) {
  if (avvik <= NULL_BELOP) {
    return {
      avvik,
      gjenstaende,
      udekket: NULL_BELOP,
      dekningProsent: null,
      dekning: "ingen_merforbruk"
    };
  }
  const dekket = gjenstaende < avvik ? gjenstaende : avvik;
  const promille = Number(dekket * 1000n / avvik);
  return {
    avvik,
    gjenstaende,
    udekket: avvik - dekket,
    dekningProsent: promille / 10,
    dekning: dekket === avvik ? "dekket" : dekket > NULL_BELOP ? "delvis" : "udekket"
  };
}
function summerGap(deler) {
  return tiltaksgap(
    summerBelop(deler.map((d) => d.avvik)),
    summerBelop(deler.map((d) => d.gjenstaende))
  );
}

// src/domain/posteringsvarsel.ts
var VARSELGRENSE = 50000000n;
var MINSTE_HISTORIKK = 3;
var abs2 = (b) => b < NULL_BELOP ? -b : b;
function median(belop) {
  const sortert = [...belop].sort((a, b) => a < b ? -1 : a > b ? 1 : 0);
  const midt = Math.floor(sortert.length / 2);
  if (sortert.length === 0) return NULL_BELOP;
  if (sortert.length % 2 === 1) return sortert[midt] ?? NULL_BELOP;
  return ((sortert[midt - 1] ?? NULL_BELOP) + (sortert[midt] ?? NULL_BELOP)) / 2n;
}
function uvanligPostering(g, enhet) {
  const historikk = g.historikk.slice(-12);
  if (historikk.length < MINSTE_HISTORIKK) return null;
  const vanlig = median(historikk);
  const avvik = abs2(g.maned - vanlig);
  if (konverterBelop(avvik, enhet, "kr") < VARSELGRENSE) return null;
  if (avvik < 2n * abs2(vanlig)) return null;
  if (g.fjoraret !== null && 2n * abs2(g.maned - g.fjoraret) <= abs2(g.maned)) return null;
  return vanlig;
}
var periode = (ar, maned) => ar * 12 + maned;
function uvanligePosteringer(input) {
  const na = periode(input.ar, input.maned);
  const for_ = input.bokforte.map((b) => periode(b.ar, b.maned)).filter((p2) => p2 < na).sort((a, b) => a - b);
  const harFjoraret = for_.includes(periode(input.ar - 1, input.maned));
  const perKonto = /* @__PURE__ */ new Map();
  for (const r of input.rader) {
    const m = perKonto.get(r.underkontoId) ?? /* @__PURE__ */ new Map();
    m.set(periode(r.ar, r.maned), (m.get(periode(r.ar, r.maned)) ?? NULL_BELOP) + r.belop);
    perKonto.set(r.underkontoId, m);
  }
  const ut = [];
  for (const [underkontoId, maneder] of perKonto) {
    const belop = maneder.get(na);
    if (belop === void 0 || belop === NULL_BELOP) continue;
    const fjoraret = harFjoraret ? maneder.get(periode(input.ar - 1, input.maned)) ?? NULL_BELOP : null;
    const vanlig = uvanligPostering(
      { maned: belop, historikk: for_.map((p2) => maneder.get(p2) ?? NULL_BELOP), fjoraret },
      input.enhet
    );
    if (vanlig !== null) ut.push({ underkontoId, belop, vanlig, fjoraret });
  }
  return ut.sort((a, b) => {
    const da = abs2(a.belop - a.vanlig);
    const db = abs2(b.belop - b.vanlig);
    return da > db ? -1 : da < db ? 1 : 0;
  });
}

// src/domain/treffsikkerhet.ts
var TREFFRETNINGER = ["treffer", "for_lav", "for_hoy", "blandet"];
var TREFFRETNINGSNAVN = {
  treffer: "Treffer",
  for_lav: "Sp\xE5r for lavt",
  for_hoy: "Sp\xE5r for h\xF8yt",
  blandet: "Bommer begge veier"
};
var TREFFGRENSE_PROSENT = 2;
var abs3 = (b) => b < NULL_BELOP ? -b : b;
function prosent(teller, nevner) {
  if (nevner === NULL_BELOP) return null;
  const n = abs3(nevner);
  const t2 = teller * 1000n;
  const kvote = t2 / n;
  const rest = abs3(t2 % n);
  const rundet = 2n * rest >= n ? kvote + (t2 < NULL_BELOP ? -1n : 1n) : kvote;
  return Number(rundet) / 10;
}
function treffsikkerhet(leveringer, utfall) {
  const punkter = [...leveringer].sort((a, b) => a.periode - b.periode).map((l) => {
    const bom = l.prognose - utfall;
    return { periode: l.periode, prognose: l.prognose, bom, bomProsent: prosent(bom, utfall) };
  });
  if (punkter.length === 0) return { punkter, snittBomProsent: null, retning: null };
  const prosenter = punkter.map((p2) => p2.bomProsent).filter((p2) => p2 !== null);
  const snitt2 = prosenter.length === 0 ? null : Math.round(prosenter.reduce((s, p2) => s + Math.abs(p2), 0) / prosenter.length * 10) / 10;
  const lave = prosenter.filter((p2) => p2 < -TREFFGRENSE_PROSENT).length;
  const hoye = prosenter.filter((p2) => p2 > TREFFGRENSE_PROSENT).length;
  const retning = snitt2 === null ? null : snitt2 <= TREFFGRENSE_PROSENT ? "treffer" : 3 * lave >= 2 * prosenter.length ? "for_lav" : 3 * hoye >= 2 * prosenter.length ? "for_hoy" : "blandet";
  return { punkter, snittBomProsent: snitt2, retning };
}

// src/domain/tilgang.ts
function utledVirksomhetstilganger(direkte, omrader, virksomheter) {
  const rollePerOmrade = new Map(omrader.map((o) => [o.kommunalomradeId, o.rolle]));
  const resultat = /* @__PURE__ */ new Map();
  for (const v of virksomheter) {
    const rolle = rollePerOmrade.get(v.kommunalomradeId);
    if (rolle !== void 0) resultat.set(v.id, rolle);
  }
  for (const t2 of direkte) {
    resultat.set(t2.virksomhetId, t2.rolle);
  }
  return [...resultat].map(([virksomhetId, rolle]) => ({ virksomhetId, rolle }));
}
function harOmradetilgang(omrader, kommunalomradeId) {
  return omrader.some((o) => o.kommunalomradeId === kommunalomradeId);
}
function styrerRapporten(bruker) {
  return bruker.erOkonomisjef || bruker.controllerOmrader.length > 0;
}
var REDIGERER_VISNING = /* @__PURE__ */ new Set(["leder", "okonom"]);
function kanRedigereVisning(bruker, omfang) {
  if (bruker.erPlattformadmin || bruker.kanAdministrere) return true;
  if ("virksomhetId" in omfang) {
    const tilgang = bruker.tilganger.find((t2) => t2.virksomhetId === omfang.virksomhetId);
    return tilgang !== void 0 && REDIGERER_VISNING.has(tilgang.rolle);
  }
  const omrade2 = bruker.omradetilganger.find((o) => o.kommunalomradeId === omfang.kommunalomradeId);
  return omrade2 !== void 0 && REDIGERER_VISNING.has(omrade2.rolle);
}

// src/domain/grupper.ts
var GRUPPETYPER = ["motearena", "prosjekt"];
var GRUPPETYPENAVN = {
  motearena: "M\xF8tearena",
  prosjekt: "Prosjektgruppe"
};
var GRUPPEROLLER = ["leder", "medlem"];
var AKSJONSSTATUSER = ["apen", "lukket"];
var ANSVARLIG = /* @__PURE__ */ new Set(["leder", "okonom"]);
function kanOppretteGruppe(b, omfang) {
  if (b.erOkonomisjef || b.kanAdministrere) return true;
  if (omfang.type === "kommune") return false;
  if (omfang.type === "omrade") {
    return b.omradetilganger.some(
      (o) => o.kommunalomradeId === omfang.id && ANSVARLIG.has(o.rolle)
    );
  }
  return b.tilganger.some((t2) => t2.virksomhetId === omfang.id && ANSVARLIG.has(t2.rolle));
}
function kanOppretteNoenGruppe(b) {
  return b.erOkonomisjef || b.kanAdministrere || b.omradetilganger.some((o) => ANSVARLIG.has(o.rolle)) || b.tilganger.some((t2) => ANSVARLIG.has(t2.rolle));
}
function kanVaereMedlem(b) {
  return b.erOkonomisjef || b.kanAdministrere || styrerRapporten(b) || b.tilganger.length > 0 || b.omradetilganger.length > 0;
}
function harLeder(medlemmer) {
  return medlemmer.some((m) => m.rolle === "leder");
}
function erForfalt(p2, idag) {
  return p2.status === "apen" && p2.frist !== null && p2.frist < idag;
}

// src/domain/prosjektfremdrift.ts
var MILEPAELSTATUSER = ["planlagt", "pagaende", "ferdig"];
var MILEPAELSTATUSNAVN = {
  planlagt: "Planlagt",
  pagaende: "P\xE5g\xE5r",
  ferdig: "Ferdig"
};
var PROSJEKTVURDERINGER = ["i_rute", "risiko", "forsinket", "ikke_startet"];
var PROSJEKTVURDERINGSNAVN = {
  i_rute: "I rute",
  risiko: "Risiko",
  forsinket: "Forsinket",
  ikke_startet: "Ingenting \xE5 m\xE5le enn\xE5"
};
function erForsinketMilepael(m, idag) {
  return m.status !== "ferdig" && m.frist !== null && m.frist < idag;
}
function prosjektfremdrift(g, idag) {
  const aktive = g.tiltak.filter((t2) => !t2.stanset);
  const forventet = summerBelop(aktive.map((t2) => t2.forventet ?? NULL_BELOP));
  const realisert = summerBelop(aktive.map((t2) => t2.realisert ?? NULL_BELOP));
  const telling3 = (s) => g.kpier.filter((k2) => k2.status === s).length;
  const milepaeler = {
    ferdige: g.milepaeler.filter((m) => m.status === "ferdig").length,
    totalt: g.milepaeler.length,
    forsinkede: g.milepaeler.filter((m) => erForsinketMilepael(m, idag)).length
  };
  const kpier = {
    god: telling3("god"),
    varsel: telling3("varsel"),
    kritisk: telling3("kritisk"),
    utenVurdering: telling3("ingen_vurdering")
  };
  const tomt = g.milepaeler.length === 0 && g.tiltak.length === 0 && g.kpier.length === 0;
  const vurdering = tomt ? "ikke_startet" : milepaeler.forsinkede > 0 ? "forsinket" : kpier.kritisk > 0 || g.tiltak.some((t2) => t2.stanset) ? "risiko" : "i_rute";
  return {
    milepaeler,
    gevinst: {
      forventet,
      realisert,
      prosent: forventet > NULL_BELOP ? Number(realisert * 100n / forventet) : null
    },
    kpier,
    vurdering
  };
}

// src/domain/prosjektportefolje.ts
var PROSJEKTFASER = ["ide", "planlegging", "gjennomforing", "avsluttet"];
var PROSJEKTFASENAVN = {
  ide: "Id\xE9",
  planlegging: "Planlegging",
  gjennomforing: "Gjennomf\xF8ring",
  avsluttet: "Avsluttet"
};
var RISIKONIVAER = ["lav", "middels", "hoy"];
var RISIKONAVN = {
  lav: "Lav",
  middels: "Middels",
  hoy: "H\xF8y"
};
var PORTEFOLJESTATUSER = [
  "forsinket",
  "risiko",
  "i_rute",
  "ikke_startet",
  "avsluttet"
];
var PORTEFOLJESTATUSNAVN = {
  forsinket: "Forsinket",
  risiko: "Risiko",
  i_rute: "I rute",
  ikke_startet: "Ikke startet",
  avsluttet: "Avsluttet"
};
function vurderPortefoljeprosjekt(g, idag) {
  if (g.fase === "avsluttet") return { status: "avsluttet", grunner: [] };
  const forsinket = [];
  if (g.fremdrift === "forsinket") forsinket.push("En milep\xE6l har passert fristen.");
  if (g.sluttdato !== null && g.sluttdato < idag) forsinket.push("Sluttdatoen er passert.");
  const risiko = [];
  if (g.investering?.sprekker === true) risiko.push("Sluttprognosen er over rammen.");
  if (g.investering?.status === "merforbruk") risiko.push("Merforbruk p\xE5 investeringen i \xE5r.");
  if (g.risiko === "hoy") risiko.push("Prosjektlederen vurderer risikoen som h\xF8y.");
  if (g.fremdrift === "risiko")
    risiko.push("En KPI er over terskelen, eller et tiltak er stanset.");
  const grunner = [...forsinket, ...risiko];
  if (forsinket.length > 0) return { status: "forsinket", grunner };
  if (risiko.length > 0) return { status: "risiko", grunner };
  if (g.fremdrift === "ikke_startet" && g.investering === null) {
    return { status: "ikke_startet", grunner: [] };
  }
  return { status: "i_rute", grunner: [] };
}
function serPortefoljeprosjekt(leser, prosjekt, erMedlem) {
  if (leser.styrer || erMedlem) return true;
  if (prosjekt.virksomhetId !== null) {
    return leser.virksomheter.has(prosjekt.virksomhetId) || prosjekt.virksomhetensOmradeId !== null && leser.omrader.has(prosjekt.virksomhetensOmradeId);
  }
  return prosjekt.omradeId !== null && leser.omrader.has(prosjekt.omradeId);
}
function tellPortefolje(statuser) {
  const telling3 = {
    forsinket: 0,
    risiko: 0,
    i_rute: 0,
    ikke_startet: 0,
    avsluttet: 0
  };
  for (const s of statuser) telling3[s] += 1;
  return telling3;
}

// src/domain/budsjettibruk.ts
var OVERFORINGSAVVIK = ["ikke_fordelt", "ikke_budsjettert", "avvik"];
function overforingsavvik(input) {
  const avvik = [];
  for (const o of input.omrader) {
    if (o.udisponert !== NULL_BELOP)
      avvik.push({ type: "ikke_fordelt", navn: o.navn, belop: o.udisponert });
  }
  for (const v of input.virksomheter) {
    if (v.ramme === null) continue;
    if (!v.harDetaljer) {
      if (v.ramme !== NULL_BELOP)
        avvik.push({ type: "ikke_budsjettert", navn: v.navn, belop: v.ramme });
      continue;
    }
    const rest = v.ramme - v.detaljsum;
    if (rest !== NULL_BELOP) avvik.push({ type: "avvik", navn: v.navn, belop: rest });
  }
  return avvik;
}
function kanTaIBruk(fase, styrer, tattIBruk) {
  return styrer && fase === "vedtatt" && !tattIBruk;
}
function eksportbelop(belop) {
  return belopTilDesimaltekst(belop).replace(/\.?0+$/, "").replace(".", ",");
}

// src/domain/budsjettoppgaver.ts
function utledBudsjettoppgaver(g) {
  if (g.fase === "rammer" || g.tattIBruk) return [];
  const felles2 = { ar: g.ar, frist: g.frist };
  return [
    ...g.omrader.filter((o) => o.udisponert !== 0n).map((o) => ({
      ...felles2,
      oppgave: "fordel",
      omradeId: o.id,
      omrade: o.navn,
      ikkeFordelt: o.udisponert
    })),
    // Uten en ramme er det ingenting å budsjettere mot ennå: oppgaven kommer
    // når kommunalsjefen har fordelt.
    ...g.virksomheter.filter((v) => v.ramme !== 0n && v.ramme !== v.detaljsum).map((v) => ({
      ...felles2,
      oppgave: "detaljbudsjett",
      virksomhetId: v.id,
      virksomhet: v.navn,
      ikkeBudsjettert: v.ramme - v.detaljsum
    }))
  ];
}

// src/domain/investeringsoppfolging.ts
var INVESTERINGSSTATUSER = [
  "ikke_meldt",
  "merforbruk",
  "mindreforbruk",
  "i_rute"
];
var INVESTERINGSSTATUSNAVN = {
  ikke_meldt: "Ikke meldt",
  merforbruk: "Merforbruk",
  mindreforbruk: "Mindreforbruk",
  i_rute: "I rute"
};
function vurderInvestering(t2, terskel) {
  const forbruksprosent = t2.budsjett === NULL_BELOP ? null : Number(t2.regnskap * 100n / t2.budsjett);
  const p2 = t2.prognose;
  if (p2 === null) {
    return {
      status: "ikke_meldt",
      avvik: null,
      ubrukt: NULL_BELOP,
      forbruksprosent,
      sluttavvik: null,
      sprekker: false
    };
  }
  const avvik = beregnAvvik(p2.ar, t2.budsjett);
  const sluttavvik = p2.slutt === null || t2.kostnadsramme === null ? null : beregnAvvik(p2.slutt, t2.kostnadsramme);
  const status = trengerForklaring(t2.budsjett, p2.ar, "kr", terskel) ? avvik > NULL_BELOP ? "merforbruk" : "mindreforbruk" : "i_rute";
  return {
    status,
    avvik,
    ubrukt: avvik < NULL_BELOP ? -avvik : NULL_BELOP,
    forbruksprosent,
    sluttavvik,
    sprekker: sluttavvik !== null && sluttavvik > NULL_BELOP
  };
}
function investeringKreverForklaring(budsjett, arsprognose, kostnadsramme, sluttprognose, terskel) {
  if (trengerForklaring(budsjett, arsprognose, "kr", terskel)) return true;
  if (kostnadsramme === null || sluttprognose === null) return false;
  return sluttprognose > kostnadsramme && trengerForklaring(kostnadsramme, sluttprognose, "kr", terskel);
}
function investeringSkalMeldes(rytme, sisteForteManed, meldtPeriode) {
  const periode2 = leveringsperiode(rytme, sisteForteManed);
  if (periode2 === null) return null;
  return meldtPeriode !== null && meldtPeriode >= periode2 ? null : periode2;
}
function kanMeldeInvestering(bruker, omradeId) {
  if (bruker.styrer) return true;
  return omradeId !== null && bruker.skriver.has(omradeId);
}
function somKontooverskrift(overskrift) {
  const bytt = {
    Prosjekt: "Konto",
    Prosjektnr: "Kontonr",
    Prosjektnavn: "Kontonavn"
  };
  return overskrift.map((h) => bytt[h.trim()] ?? h);
}

// src/domain/vedlikehold.ts
var FAGOMRADER = ["bygg", "vei", "vann_avlop", "kjoretoy"];
var FAGOMRADENAVN = {
  bygg: "Bygg og anlegg",
  vei: "Vei",
  vann_avlop: "Vann og avl\xF8p",
  kjoretoy: "Kj\xF8ret\xF8y"
};
var ALVORLIGHETER = ["lav", "middels", "hoy", "kritisk"];
var ALVORLIGHETSNAVN = {
  lav: "Lav",
  middels: "Middels",
  hoy: "H\xF8y",
  kritisk: "Kritisk"
};
var AVVIKSSTATUSER = ["ny", "under_arbeid", "lukket"];
var AVVIKSSTATUSNAVN = {
  ny: "Ny",
  under_arbeid: "Under arbeid",
  lukket: "Lukket"
};
function fagomraderFor(b) {
  if (b.styrer) return [...FAGOMRADER];
  return FAGOMRADER.filter((f) => b.avdelinger.some((a) => a.fagomrade === f));
}
function serObjekt(b, o) {
  if (b.styrer) return true;
  for (const v of [o.avdelingId, o.brukesAvId]) {
    if (v !== null && b.virksomheter.has(v)) return true;
  }
  for (const omrade2 of [o.avdelingensOmradeId, o.brukerensOmradeId]) {
    if (omrade2 !== null && b.omrader.has(omrade2)) return true;
  }
  return false;
}
function kanForvalte(b, o) {
  if (b.styrer) return true;
  if (o.avdelingId !== null && b.avdelinger.some((a) => a.virksomhetId === o.avdelingId && a.skriver)) {
    return true;
  }
  return o.avdelingensOmradeId !== null && b.skriverOmrader.has(o.avdelingensOmradeId);
}
function serFrittAvvik(b, fagomrade, meldtAv) {
  return b.styrer || meldtAv === b.brukerId || b.avdelinger.some((a) => a.fagomrade === fagomrade);
}
function kanBehandleFrittAvvik(b, fagomrade) {
  return b.styrer || b.avdelinger.some((a) => a.fagomrade === fagomrade && a.skriver);
}
function sorterAvvik(avvik, meldt) {
  const apen = (s) => s === "lukket" ? 1 : 0;
  const alvor = (a) => ALVORLIGHETER.indexOf(a);
  return [...avvik].sort(
    (x, y) => apen(x.status) - apen(y.status) || alvor(y.alvorlighet) - alvor(x.alvorlighet) || meldt(x).localeCompare(meldt(y))
  );
}

// src/domain/avvikskatalog.ts
var AVVIKSIKONER = [
  "flamme",
  "drape",
  "hammer",
  "varsel",
  "lyn",
  "vind",
  "traer",
  "tilgjengelighet",
  "insekt",
  "las",
  "sporsmal",
  "veiarbeid",
  "skilt",
  "gatelys",
  "bolger",
  "gjerde",
  "snofnugg",
  "fotspor",
  "blad",
  "draper",
  "stopp",
  "kum",
  "kolbe",
  "trykk",
  "regn",
  "fabrikk",
  "brannslukker",
  "bil",
  "skiftenokkel",
  "dekk"
];
var t = (nokkel2, navn2, ikon, alvorlighet, undertyper = []) => ({ nokkel: nokkel2, navn: navn2, ikon, alvorlighet, undertyper });
var u = (nokkel2, navn2, alvorlighet) => alvorlighet === void 0 ? { nokkel: nokkel2, navn: navn2 } : { nokkel: nokkel2, navn: navn2, alvorlighet };
var ANNET = t("annet", "Annet", "sporsmal", "middels");
var AVVIKSKATALOG = {
  bygg: [
    t("brannsikkerhet", "Brann", "flamme", "hoy", [
      u("romningsvei", "R\xF8mningsvei sperret", "kritisk"),
      u("branndor", "Brannd\xF8r holdt \xE5pen eller defekt"),
      u("brannalarm", "Brannalarm i feil", "kritisk"),
      u("nodlys", "N\xF8dlys eller ledesystem"),
      u("slokkeutstyr", "Slokkeutstyr mangler eller er utg\xE5tt"),
      u("brannskille", "Hull i brannskille")
    ]),
    t("lekkasje", "Vann og fukt", "drape", "middels", [
      u("lekkasje", "Lekkasje", "hoy"),
      u("fukt_mugg", "Fukt eller mugg"),
      u("sluk_takrenne", "Tett sluk eller takrenne"),
      u("frostskade", "Frostskade")
    ]),
    t("skade", "Skade og h\xE6rverk", "hammer", "lav", [
      u("glass", "Knust glass", "middels"),
      u("dor_las", "D\xF8r eller l\xE5s"),
      u("fasade_tak", "Fasade eller tak"),
      u("tagging", "Tagging"),
      u("innbrudd", "Innbrudd", "hoy")
    ]),
    t("personsikkerhet", "Personsikkerhet", "varsel", "hoy", [
      u("fallfare", "Fallfare"),
      u("rekkverk", "L\xF8st eller manglende rekkverk"),
      u("sno_is_tak", "Sn\xF8 og is p\xE5 tak", "kritisk"),
      u("glatt", "Glatt adkomst", "middels")
    ]),
    t("tekniske_anlegg", "Tekniske anlegg", "lyn", "middels", [
      u("heis", "Heis"),
      u("elektrisk", "Elektrisk feil", "hoy"),
      u("varme", "Varme"),
      u("sanitar", "Sanit\xE6ranlegg"),
      u("legionella", "Legionella", "hoy"),
      u("nodstrom", "N\xF8dstr\xF8m")
    ]),
    t("inneklima", "Inneklima", "vind", "middels", [
      u("temperatur", "Temperatur"),
      u("ventilasjon", "Ventilasjon"),
      u("lukt", "Lukt"),
      u("stoy", "St\xF8y"),
      u("radon", "Radon", "hoy")
    ]),
    t("uteomrade", "Uteomr\xE5de", "traer", "lav", [
      u("lekeapparat", "Lekeapparat", "hoy"),
      u("fallunderlag", "Fallunderlag", "middels"),
      u("gjerde_port", "Gjerde eller port"),
      u("utebelysning", "Utebelysning")
    ]),
    t("tilgjengelighet", "Tilgjengelighet", "tilgjengelighet", "middels", [
      u("automatdor", "Automatisk d\xF8r"),
      u("rampe_loft", "Rampe eller l\xF8fteplattform"),
      u("ledelinje", "Ledelinjer og merking", "lav")
    ]),
    t("skadedyr", "Skadedyr", "insekt", "middels", [
      u("gnagere", "Mus eller rotter"),
      u("insekter", "Insekter")
    ]),
    t("sikring", "Sikring", "las", "middels", [
      u("adgang", "Adgangskontroll"),
      u("innbruddsalarm", "Innbruddsalarm"),
      u("nokler", "N\xF8kler og l\xE5ssystem")
    ]),
    ANNET
  ],
  vei: [
    t("hull", "Hull og skader i dekket", "veiarbeid", "middels", [
      u("hull", "Hull"),
      u("sprekker", "Sprekker eller setninger"),
      u("spor", "Spor", "lav")
    ]),
    t("skilt", "Skilt og oppmerking", "skilt", "middels", [
      u("skilt", "Skilt mangler eller er skadet"),
      u("oppmerking", "Slitt oppmerking", "lav")
    ]),
    t("belysning", "Gatelys", "gatelys", "lav", [
      u("lys_ute", "Lys ute"),
      u("stolpe", "Skadet stolpe", "hoy")
    ]),
    t("drenering", "Drenering og overvann", "bolger", "middels", [
      u("sluk", "Tett sluk"),
      u("groft", "Gr\xF8ft eller stikkrenne"),
      u("vann_i_veien", "Vann over veien", "hoy")
    ]),
    t("rekkverk", "Rekkverk og autovern", "gjerde", "hoy"),
    t("vinterdrift", "Vinterdrift", "snofnugg", "middels", [
      u("broyting", "Br\xF8yting"),
      u("stroing", "Str\xF8ing", "hoy"),
      u("snofonn", "Sn\xF8fonn som tar sikten")
    ]),
    t("fortau", "Fortau og gang- og sykkelvei", "fotspor", "middels", [
      u("kantstein", "Kantstein"),
      u("ujevnt", "Ujevnt dekke")
    ]),
    t("vegetasjon", "Vegetasjon og sikt", "blad", "lav", [
      u("sikt", "Sikt i kryss", "middels"),
      u("veltet_tre", "Veltet tre", "hoy")
    ]),
    ANNET
  ],
  vann_avlop: [
    t("lekkasje", "Vannlekkasje", "draper", "hoy", [
      u("ledningsbrudd", "Ledningsbrudd", "kritisk"),
      u("lekkasje", "Lekkasje"),
      u("stikkledning", "Stikkledning", "middels")
    ]),
    t("tilstopping", "Tilstopping", "stopp", "middels", [
      u("avlop", "Avl\xF8p"),
      u("overvann", "Overvann")
    ]),
    t("kum", "Kum og kumlokk", "kum", "hoy", [
      u("kumlokk", "Kumlokk l\xF8st eller mangler", "kritisk"),
      u("skadet_kum", "Skadet kum")
    ]),
    t("vannkvalitet", "Vannkvalitet", "kolbe", "hoy", [
      u("misfarget", "Misfarget vann"),
      u("lukt_smak", "Lukt eller smak")
    ]),
    t("trykk", "Vanntrykk", "trykk", "middels", [
      u("lavt", "Lavt trykk"),
      u("ingen_vann", "Ingen vann", "hoy")
    ]),
    t("oversvommelse", "Oversv\xF8mmelse", "regn", "hoy", [
      u("tilbakeslag", "Tilbakeslag i kjeller"),
      u("overflate", "Vann p\xE5 overflaten", "middels")
    ]),
    t("pumpestasjon", "Pumpestasjon og renseanlegg", "fabrikk", "hoy", [
      u("stans", "Stans", "kritisk"),
      u("alarm", "Alarm"),
      u("lukt", "Lukt", "middels")
    ]),
    t("hydrant", "Brannkum og hydrant", "brannslukker", "hoy"),
    ANNET
  ],
  kjoretoy: [
    t("skade", "Skade", "bil", "middels"),
    t("service", "Service", "skiftenokkel", "lav"),
    t("dekk", "Dekk", "dekk", "middels"),
    ANNET
  ]
};
var AVVIKSTYPER = {
  bygg: AVVIKSKATALOG.bygg.map((x) => x.nokkel),
  vei: AVVIKSKATALOG.vei.map((x) => x.nokkel),
  vann_avlop: AVVIKSKATALOG.vann_avlop.map((x) => x.nokkel),
  kjoretoy: AVVIKSKATALOG.kjoretoy.map((x) => x.nokkel)
};
function erAvvikstype(fagomrade, type) {
  return AVVIKSTYPER[fagomrade].includes(type);
}
function avvikstype(fagomrade, type) {
  return AVVIKSKATALOG[fagomrade].find((x) => x.nokkel === type) ?? {
    ...ANNET,
    nokkel: type,
    navn: type
  };
}
function erUndertype(fagomrade, type, undertype) {
  return undertype === null || avvikstype(fagomrade, type).undertyper.some((x) => x.nokkel === undertype);
}
function undertypenavn(fagomrade, type, undertype) {
  if (undertype === null) return null;
  return avvikstype(fagomrade, type).undertyper.find((x) => x.nokkel === undertype)?.navn ?? null;
}
function foreslattAlvorlighet(fagomrade, type, undertype) {
  const ty = avvikstype(fagomrade, type);
  return ty.undertyper.find((x) => x.nokkel === undertype)?.alvorlighet ?? ty.alvorlighet;
}
var FRISTDAGER = {
  kritisk: 0,
  hoy: 7,
  middels: 30,
  lav: 90
};
var ALVORLIGHETSKRITERIUM = {
  kritisk: "Fare for liv og helse, eller stans i en viktig funksjon. Sikres straks.",
  hoy: "Brudd p\xE5 et lovkrav, eller fare for personskade eller f\xF8lgeskade.",
  middels: "Redusert funksjon, men ikke farlig.",
  lav: "Kosmetisk, eller en ulempe. Tas med i det planlagte vedlikeholdet."
};
function standardFrist(alvorlighet, meldt) {
  const [ar = 0, maned = 1, dag = 1] = meldt.split("-").map(Number);
  const d = new Date(Date.UTC(ar, maned - 1, dag + FRISTDAGER[alvorlighet]));
  return d.toISOString().slice(0, 10);
}
function avvikErForfalt(a, idag) {
  return a.status !== "lukket" && a.frist !== null && a.frist < idag;
}
function typeOgUndertype(fagomrade, type, undertype) {
  const under = undertypenavn(fagomrade, type, undertype);
  return under === null ? avvikstype(fagomrade, type).navn : `${avvikstype(fagomrade, type).navn}: ${under}`;
}
var dato = (iso) => iso === null ? "ingen" : iso.split("-").reverse().join(".");
function avviksendringer(fagomrade, for_, etter) {
  const ut = [];
  if (for_.tittel !== etter.tittel) ut.push(`Tittel: \xAB${for_.tittel}\xBB \u2192 \xAB${etter.tittel}\xBB`);
  if (for_.beskrivelse !== etter.beskrivelse) ut.push("Beskrivelsen er endret");
  const typeFor = typeOgUndertype(fagomrade, for_.type, for_.undertype);
  const typeEtter = typeOgUndertype(fagomrade, etter.type, etter.undertype);
  if (typeFor !== typeEtter) ut.push(`Type: ${typeFor} \u2192 ${typeEtter}`);
  if (for_.alvorlighet !== etter.alvorlighet) {
    ut.push(
      `Alvorlighet: ${ALVORLIGHETSNAVN[for_.alvorlighet]} \u2192 ${ALVORLIGHETSNAVN[etter.alvorlighet]}`
    );
  }
  if (for_.frist !== etter.frist) ut.push(`Frist: ${dato(for_.frist)} \u2192 ${dato(etter.frist)}`);
  if (for_.sikret !== etter.sikret) {
    ut.push(etter.sikret ? "Midlertidig sikret" : "Ikke lenger sikret");
  }
  return ut;
}

// src/domain/kontroller.ts
var BRANN = "Forskrift om brannforebygging";
var EL = "Forskrift om elektriske lavspenningsanlegg";
var KONTROLLKATALOG = [
  // Bygg og anlegg: brann.
  k("bygg", "brannalarm", "Brannalarmanlegg", 12, BRANN, "brannsikkerhet", [
    p("sentral", "Brannsentralen er uten feil og utkoblinger", "brannalarm"),
    p("detektorer", "Detektorene er pr\xF8vd og uten skader", "brannalarm"),
    p("alarmorganer", "Klokker og sirener h\xF8res i hele bygget", "brannalarm"),
    p("overforing", "Alarmen g\xE5r videre til 110-sentralen", "brannalarm", "kritisk"),
    p("dokumentasjon", "Kontrollboken og serviceavtalen er oppdatert", null, "lav")
  ]),
  k("bygg", "romningsvei", "R\xF8mningsveier", 1, BRANN, "brannsikkerhet", [
    p("fri", "R\xF8mningsveiene er frie for lagrede ting", "romningsvei", "kritisk"),
    p("dorer", "Utgangsd\xF8rene kan \xE5pnes innenfra uten n\xF8kkel", "romningsvei", "kritisk"),
    p("merking", "Ledeskiltene er synlige og tente", "nodlys")
  ]),
  k("bygg", "nodlys", "N\xF8dlys og ledesystem", 12, BRANN, "brannsikkerhet", [
    p("lys", "N\xF8dlysarmaturene lyser ved str\xF8mbrudd", "nodlys"),
    p("skilt", "Ledeskiltene er synlige og tente", "nodlys"),
    p("varighet", "Batteriene holder i minst \xE9n time", "nodlys")
  ]),
  k("bygg", "sprinkler", "Sprinkleranlegg", 12, BRANN, "brannsikkerhet", [
    p("trykk", "Trykket er innenfor det anlegget krever", null, "kritisk"),
    p("ventiler", "Hovedventilene st\xE5r \xE5pne og er sikret", null, "kritisk"),
    p("hoder", "Sprinklerhodene er hele og ikke dekket til", null),
    p("alarm", "Str\xF8mningsalarmen er pr\xF8vd", "brannalarm")
  ]),
  k("bygg", "slokkeutstyr", "H\xE5ndslokkeutstyr", 12, BRANN, "brannsikkerhet", [
    p("plassering", "Utstyret st\xE5r der det skal, og er merket", "slokkeutstyr"),
    p("kontrollert", "Apparatene er kontrollert og ikke utg\xE5tt", "slokkeutstyr"),
    p("slanger", "Brannslangene er hele og n\xE5r fram", "slokkeutstyr")
  ]),
  k("bygg", "branndorer", "Brannd\xF8rer og brannskiller", 12, BRANN, "brannsikkerhet", [
    p("lukker", "D\xF8rene lukker og g\xE5r i l\xE5s av seg selv", "branndor"),
    p("holdes_apne", "Ingen d\xF8rer holdes \xE5pne med kiler eller lignende", "branndor"),
    p("tetning", "Pakninger og terskler er hele", "branndor"),
    p("gjennomforinger", "Gjennomf\xF8ringer i brannskillene er tettet", "brannskille")
  ]),
  k("bygg", "romningsovelse", "R\xF8mnings\xF8velse", 12, BRANN, "brannsikkerhet", [
    p("gjennomfort", "\xD8velsen er gjennomf\xF8rt med dem som er i bygget", null),
    p("tid", "Alle var ute innen den planlagte tiden", "romningsvei"),
    p("oppmote", "Oppm\xF8teplassen ble brukt, og alle ble telt opp", null)
  ]),
  // Tekniske anlegg.
  k("bygg", "heis", "Heis", 12, "Plan- og bygningsloven", "tekniske_anlegg", [
    p("nodtelefon", "N\xF8dtelefonen virker", "heis", "hoy"),
    p("stopp", "Heisen stopper i niv\xE5 med etasjen", "heis"),
    p("dorer", "D\xF8rene og sikkerhetsbryterne virker", "heis", "hoy"),
    p("sertifikat", "Gyldig kontrollsertifikat henger i heisen", "heis", "lav")
  ]),
  k("bygg", "elanlegg", "Elektrisk anlegg", 12, EL, "tekniske_anlegg", [
    p("tavler", "Tavlerommene er ryddige og l\xE5st", "elektrisk", "lav"),
    p("jordfeil", "Jordfeilbryterne er pr\xF8vd", "elektrisk"),
    p("skader", "Ingen synlige skader p\xE5 kabler, brytere eller stikk", "elektrisk"),
    p("varmgang", "Ingen tegn til varmgang, som lukt eller misfarging", "elektrisk", "kritisk")
  ]),
  k("bygg", "termografi", "Termografi av el-tavler", 36, EL, "tekniske_anlegg", [
    p("varmgang", "Ingen punkter med unormal temperatur", "elektrisk", "hoy")
  ]),
  k("bygg", "nodstrom", "N\xF8dstr\xF8msaggregat", 3, null, "tekniske_anlegg", [
    p("provekjoring", "Aggregatet starter og tar last", "nodstrom", "hoy"),
    p("drivstoff", "Drivstoffet holder for den planlagte driftstiden", "nodstrom")
  ]),
  k(
    "bygg",
    "legionella",
    "Legionella",
    12,
    "Forskrift om milj\xF8rettet helsevern",
    "tekniske_anlegg",
    [
      p("temperatur", "Varmtvannet holder minst 60 \xB0C ut fra berederne", "legionella"),
      p("dusjer", "Lite brukte dusjer og tappesteder er spylt", "legionella"),
      p("prove", "Pr\xF8ve er tatt der det er risiko", "legionella")
    ]
  ),
  k(
    "bygg",
    "varmepumpe",
    "Varmepumpe og kuldeanlegg",
    12,
    "Forskrift om fluorholdige klimagasser",
    "tekniske_anlegg",
    [
      p("lekkasje", "Ingen lekkasje av kuldemedium", "varme", "hoy"),
      p("logg", "Anleggsloggen er oppdatert", null, "lav")
    ]
  ),
  // Inneklima.
  k("bygg", "ventilasjon", "Ventilasjonsanlegg", 12, "Arbeidsplassforskriften", "inneklima", [
    p("filter", "Filtrene er byttet", "ventilasjon"),
    p("luftmengder", "Luftmengdene er som prosjektert", "ventilasjon"),
    p("aggregat", "Aggregatet g\xE5r uten unormal lyd", "ventilasjon"),
    p("driftstider", "Driftstidene passer bruken av bygget", "ventilasjon", "lav")
  ]),
  k("bygg", "radon", "Radonm\xE5ling", 60, "Str\xE5levernforskriften", "inneklima", [
    p("tiltaksniva", "M\xE5lingene er under tiltaksniv\xE5et p\xE5 100 Bq/m\xB3", "radon"),
    p("grense", "Ingen rom er over grenseverdien p\xE5 200 Bq/m\xB3", "radon", "hoy")
  ]),
  // Uteområder og tilgjengelighet.
  k(
    "bygg",
    "lekeplass",
    "Lekeplassutstyr, \xE5rlig hovedinspeksjon",
    12,
    "Forskrift om sikkerhet ved lekeplassutstyr",
    "uteomrade",
    [
      p("apparater", "Apparatene er hele, uten l\xF8se deler eller skarpe kanter", "lekeapparat"),
      p(
        "klemfare",
        "Ingen \xE5pninger der hode eller fingre kan sette seg fast",
        "lekeapparat",
        "kritisk"
      ),
      p("fester", "Fundamenter og fester er stabile", "lekeapparat"),
      p("fallunderlag", "Fallunderlaget har riktig dybde og dekker fallsonen", "fallunderlag"),
      p("gjerde", "Gjerde og port er hele", "gjerde_port")
    ]
  ),
  k("bygg", "lekeplass_drift", "Lekeplass, operativ inspeksjon", 3, "NS-EN 1176-7", "uteomrade", [
    p("slitasje", "Ingen unormal slitasje p\xE5 bevegelige deler", "lekeapparat"),
    p("fallunderlag", "Fallunderlaget er jevnt og uten fremmedlegemer", "fallunderlag"),
    p("rydding", "Ingen glass, spr\xF8yter eller h\xE6rverk", null, "hoy")
  ]),
  k("bygg", "automatdorer", "Automatiske d\xF8rer og porter", 12, null, "tilgjengelighet", [
    p("klemsikring", "Sensorene og klemsikringen stopper d\xF8ren", "automatdor", "hoy"),
    p("apning", "D\xF8ren \xE5pner og lukker som den skal", "automatdor"),
    p("nodapning", "D\xF8ren kan \xE5pnes for h\xE5nd ved str\xF8mbrudd", "automatdor", "hoy")
  ]),
  // Bygningen og arbeidsmiljøet.
  k("bygg", "tak", "Tak og taknedl\xF8p", 12, null, "skade", [
    p("tekking", "Taktekkingen er hel", "fasade_tak"),
    p("beslag", "Beslag og gjennomf\xF8ringer er tette", "fasade_tak"),
    p("renner", "Takrenner og nedl\xF8p er rensket", null),
    p("sikring", "Sn\xF8fangere og takstiger er festet", null)
  ]),
  k("bygg", "sno_tak", "Sn\xF8 og is p\xE5 tak", 12, null, "personsikkerhet", [
    p("ras", "Ingen sn\xF8 eller is som kan rase ned der folk g\xE5r", "sno_is_tak", "kritisk"),
    p("snofangere", "Sn\xF8fangerne er hele", null)
  ]),
  k("bygg", "vernerunde", "Vernerunde", 12, "Arbeidsmilj\xF8loven", "personsikkerhet", [
    p("adkomst", "Adkomst og trapper er trygge", "glatt"),
    p("rekkverk", "Rekkverk og h\xE5ndlister er festet", "rekkverk"),
    p("fallfare", "Ingen \xE5pne kanter eller hull med fallfare", "fallfare"),
    p("orden", "Det er ryddig i fellesarealene", null, "lav")
  ]),
  k("bygg", "tilstandsanalyse", "Tilstandsanalyse (NS 3424)", 60, null, "skade", [
    p("vurdert", "Alle bygningsdeler er vurdert i vedlikeholdsplanen", null, "lav")
  ]),
  // Vei.
  k("vei", "gatelys", "Gatelys", 12, null, "belysning", [
    p("lys", "Alle lyspunktene lyser", "lys_ute"),
    p("stolper", "Stolper og fundamenter st\xE5r st\xF8dig", "stolpe", "hoy"),
    p("kabelskap", "Kabelskapene er hele og l\xE5st", null, "middels")
  ]),
  k("vei", "skilt", "Skilt og oppmerking", 12, "Skiltforskriften", "skilt", [
    p("skilt", "Skiltene st\xE5r og er synlige", "skilt"),
    p("oppmerking", "Oppmerkingen er synlig", "oppmerking")
  ]),
  k("vei", "rekkverk", "Rekkverk og autovern", 12, null, "rekkverk", [
    p("skader", "Ingen p\xE5kj\xF8rte eller l\xF8se elementer", null),
    p("ender", "Endeforankringene er hele", null)
  ]),
  k("vei", "sluk_stikkrenner", "Sluk og stikkrenner", 12, null, "drenering", [
    p("sluk", "Slukene er t\xF8mt og tar unna vann", "sluk"),
    p("stikkrenner", "Stikkrennene er \xE5pne i begge ender", "groft")
  ]),
  // Vann og avløp.
  k("vann_avlop", "hydrant", "Brannkummer og hydranter", 12, BRANN, "hydrant", [
    p("tilgjengelig", "Hydranten er merket og lett \xE5 komme til", null),
    p("virker", "Hydranten er pr\xF8vd og gir vann", null, "kritisk"),
    p("frost", "Hydranten er t\xF8mt og sikret mot frost", null)
  ]),
  k("vann_avlop", "kummer", "Kummer og kumlokk", 12, null, "kum", [
    p("lokk", "Lokkene ligger stabilt og i niv\xE5 med veien", "kumlokk", "kritisk"),
    p("kum", "Kummen er uten skader", "skadet_kum")
  ]),
  k("vann_avlop", "pumpestasjon", "Pumpestasjon", 1, null, "pumpestasjon", [
    p("pumper", "Pumpene g\xE5r uten unormal lyd", null),
    p("alarm", "Alarmene er pr\xF8vd og n\xE5r vakten", "alarm", "hoy"),
    p("niva", "Niv\xE5m\xE5lingen stemmer", null),
    p("lukt", "Ingen lukt utenfor stasjonen", "lukt")
  ]),
  k("vann_avlop", "hoydebasseng", "H\xF8ydebasseng", 12, "Drikkevannsforskriften", "vannkvalitet", [
    p("sikring", "Luker og ventiler er l\xE5st og hele", null, "hoy"),
    p("inspeksjon", "Bassenget er inspisert og rengjort ved behov", null),
    p("prove", "Vannpr\xF8ven er innenfor kravene", "lukt_smak")
  ])
];
function k(fagomrade, nokkel2, navn2, intervallManeder, hjemmel, avvikstype2, punkter) {
  return { nokkel: nokkel2, navn: navn2, fagomrade, intervallManeder, hjemmel, avvikstype: avvikstype2, punkter };
}
function p(nokkel2, tekst, undertype, alvorlighet) {
  return alvorlighet === void 0 ? { nokkel: nokkel2, tekst, undertype } : { nokkel: nokkel2, tekst, undertype, alvorlighet };
}
function kontrolltyper(rader) {
  const fraKatalog = KONTROLLKATALOG.map((kat) => {
    const rad = rader.find((r) => r.nokkel === kat.nokkel);
    return rad === void 0 ? { ...kat, aktiv: true, egen: false } : {
      ...kat,
      navn: rad.navn,
      intervallManeder: rad.intervallManeder,
      hjemmel: rad.hjemmel,
      aktiv: rad.aktiv,
      egen: false
    };
  });
  const egne = rader.filter((r) => !erKatalognokkel(r.nokkel)).map((r) => ({ ...r, punkter: [], egen: true })).sort((a, b) => a.navn.localeCompare(b.navn, "nb"));
  return [...fraKatalog, ...egne];
}
function erKatalognokkel(nokkel2) {
  return KONTROLLKATALOG.some((kat) => kat.nokkel === nokkel2);
}
var KONTROLLRESULTATER = ["godkjent", "mangler"];
var KONTROLLRESULTATNAVN = {
  godkjent: "Godkjent",
  mangler: "Mangler funnet"
};
var PUNKTRESULTATER = ["ok", "avvik", "ikke_relevant"];
var PUNKTRESULTATNAVN = {
  ok: "I orden",
  avvik: "Avvik",
  ikke_relevant: "Ikke relevant"
};
function sjekklisteresultat(svar) {
  return svar.some((x) => x.resultat === "avvik") ? "mangler" : "godkjent";
}
function erKomplettSjekkliste(punkter, svar) {
  const besvart = svar.map((x) => x.nokkel);
  return besvart.length === punkter.length && new Set(besvart).size === besvart.length && punkter.every((pk) => besvart.includes(pk.nokkel));
}
function punktavvikstittel(kontroll, punkt) {
  const tittel2 = `${kontroll}: ${punkt} \u2013 ikke i orden`;
  return tittel2.length <= 150 ? tittel2 : `${tittel2.slice(0, 149)}\u2026`;
}
function nesteFrist(utfort, intervallManeder) {
  const [ar = 0, maned = 1, dag = 1] = utfort.split("-").map(Number);
  const indeks = ar * 12 + (maned - 1) + intervallManeder;
  const nyttAr = Math.floor(indeks / 12);
  const nyManed = indeks % 12 + 1;
  const sisteDag = new Date(Date.UTC(nyttAr, nyManed, 0)).getUTCDate();
  const to = (n) => String(n).padStart(2, "0");
  return `${String(nyttAr)}-${to(nyManed)}-${to(Math.min(dag, sisteDag))}`;
}
var VARSEL_DAGER = 30;
var FRISTTILSTANDER = ["over_fristen", "snart", "i_rute"];
var FRISTTILSTANDNAVN = {
  over_fristen: "Over fristen",
  snart: "Snart",
  i_rute: "I rute"
};
function dagerTil(frist, idag) {
  return Math.round((Date.parse(frist) - Date.parse(idag)) / (24 * 3600 * 1e3));
}
function fristtilstand(frist, idag) {
  const dager = dagerTil(frist, idag);
  if (dager < 0) return "over_fristen";
  return dager <= VARSEL_DAGER ? "snart" : "i_rute";
}

// src/domain/avviksoppfolging.ts
function oppfolgingFra(idag, maneder) {
  const [ar = 0, maned = 1] = idag.split("-").map(Number);
  const indeks = ar * 12 + (maned - 1) - (maneder - 1);
  const m = String(indeks % 12 + 1).padStart(2, "0");
  return `${String(Math.floor(indeks / 12))}-${m}-01`;
}
function tell(avvik, nokkel2, fra, idag) {
  const ut = /* @__PURE__ */ new Map();
  for (const a of avvik) {
    const iPerioden = a.meldt >= fra;
    const apen = a.status !== "lukket";
    if (!iPerioden && !apen) continue;
    const k2 = nokkel2(a);
    const t2 = ut.get(k2) ?? { meldt: 0, apne: 0, forfalte: 0, kostnad: NULL_BELOP };
    ut.set(k2, {
      meldt: t2.meldt + (iPerioden ? 1 : 0),
      apne: t2.apne + (apen ? 1 : 0),
      forfalte: t2.forfalte + (avvikErForfalt(a, idag) ? 1 : 0),
      kostnad: t2.kostnad + (iPerioden ? a.kostnad : NULL_BELOP)
    });
  }
  return ut;
}
var etterAntall = (x, y) => y.meldt - x.meldt || y.apne - x.apne;
function avviksoppfolging(avvik, idag, maneder = 12) {
  const fra = oppfolgingFra(idag, maneder);
  const apne = avvik.filter((a) => a.status !== "lukket");
  const meldt = avvik.filter((a) => a.meldt >= fra);
  const lukket = avvik.filter((a) => a.lukket !== null && a.lukket >= fra);
  const medFrist = lukket.filter((a) => a.frist !== null);
  const perType = [...tell(avvik, (a) => `${a.fagomrade}/${a.type}`, fra, idag).entries()].map(
    ([k2, t2]) => {
      const [fagomrade, type] = k2.split("/");
      return { fagomrade, type, ...t2 };
    }
  );
  const objekter = new Map(
    avvik.flatMap((a) => a.objekt === null ? [] : [[a.objekt.id, a.objekt]])
  );
  const perObjekt = [...tell(avvik, (a) => a.objekt?.id ?? null, fra, idag).entries()].map(
    ([id, t2]) => ({ objekt: id === null ? null : objekter.get(id) ?? null, ...t2 })
  );
  const grupper = /* @__PURE__ */ new Map();
  for (const a of meldt) {
    if (a.objekt === null) continue;
    const k2 = [a.objekt.id, a.fagomrade, a.type, a.undertype ?? ""].join("|");
    const g = grupper.get(k2);
    grupper.set(
      k2,
      g === void 0 ? {
        objekt: a.objekt,
        fagomrade: a.fagomrade,
        type: a.type,
        undertype: a.undertype,
        antall: 1,
        sist: a.meldt
      } : { ...g, antall: g.antall + 1, sist: a.meldt > g.sist ? a.meldt : g.sist }
    );
  }
  const gjentakende = [...grupper.values()].filter((g) => g.antall >= 2).sort((x, y) => y.antall - x.antall || y.sist.localeCompare(x.sist));
  const perManed = Array.from({ length: maneder }, (_, i) => {
    const maned = oppfolgingFra(idag, maneder - i).slice(0, 7);
    return {
      maned,
      meldt: meldt.filter((a) => a.meldt.startsWith(maned)).length,
      lukket: lukket.filter((a) => a.lukket?.startsWith(maned) === true).length
    };
  });
  return {
    fra,
    til: idag,
    apne: apne.length,
    forfalte: apne.filter((a) => avvikErForfalt(a, idag)).length,
    sikret: apne.filter((a) => a.sikret).length,
    meldt: meldt.length,
    lukket: lukket.length,
    lukketInnenFrist: medFrist.filter((a) => (a.lukket ?? "") <= (a.frist ?? "")).length,
    lukketMedFrist: medFrist.length,
    snittDagerTilLukket: lukket.length === 0 ? null : Math.round(
      lukket.reduce((s, a) => s + dagerTil(a.lukket ?? a.meldt, a.meldt), 0) / lukket.length * 10
    ) / 10,
    kostnad: meldt.reduce((s, a) => s + a.kostnad, NULL_BELOP),
    perType: perType.sort(etterAntall),
    perObjekt: perObjekt.sort(etterAntall),
    gjentakende,
    perManed
  };
}

// src/domain/tilstand.ts
var BYGNINGSDELGRUPPER = {
  "2": "Bygning",
  "3": "VVS",
  "4": "Elkraft",
  "5": "Tele og automatisering",
  "6": "Andre installasjoner",
  "7": "Utend\xF8rs"
};
var BYGNINGSDELER = [
  ["21", "Grunn og fundamenter"],
  ["22", "B\xE6resystemer"],
  ["23", "Yttervegger"],
  ["24", "Innervegger"],
  ["25", "Dekker"],
  ["26", "Yttertak"],
  ["27", "Fast inventar"],
  ["28", "Trapper og balkonger"],
  ["31", "Sanit\xE6r"],
  ["32", "Varme"],
  ["33", "Brannslokking"],
  ["36", "Luftbehandling"],
  ["37", "Komfortkj\xF8ling"],
  ["41", "Basisinstallasjoner for elkraft"],
  ["43", "Lavspent forsyning"],
  ["44", "Lys"],
  ["46", "Reservekraft"],
  ["54", "Alarm- og signalsystemer"],
  ["56", "Automatisering"],
  ["62", "Person- og varetransport"],
  ["71", "Bearbeidet terreng"],
  ["72", "Utend\xF8rs konstruksjoner"],
  ["73", "Utend\xF8rs VVS"],
  ["74", "Utend\xF8rs elkraft"],
  ["76", "Veier og plasser"],
  ["77", "Parker og hager"]
].map(([kode, navn2]) => ({ kode, navn: navn2, gruppe: kode.slice(0, 1) }));
function erBygningsdel(kode) {
  return BYGNINGSDELER.some((d) => d.kode === kode);
}
var TILSTANDSGRADER = [0, 1, 2, 3];
var TILSTANDSGRADNAVN = {
  0: "TG0 Ingen symptomer",
  1: "TG1 Svake symptomer",
  2: "TG2 Middels kraftige symptomer",
  3: "TG3 Kraftige symptomer"
};
var KONSEKVENSGRADNAVN = {
  0: "KG0 Ingen konsekvenser",
  1: "KG1 Sm\xE5 konsekvenser",
  2: "KG2 Middels store konsekvenser",
  3: "KG3 Store konsekvenser"
};
function vedlikeholdsplan(tiltak) {
  const ar = [...new Set(tiltak.map((t2) => t2.ar))].sort(
    (a, b) => a === null ? 1 : b === null ? -1 : a - b
  );
  return ar.map((a) => {
    const iAret = tiltak.filter((t2) => t2.ar === a).sort(
      (x, y) => y.tilstandsgrad - x.tilstandsgrad || (y.konsekvensgrad ?? -1) - (x.konsekvensgrad ?? -1)
    );
    return {
      ar: a,
      sum: summerBelop(iAret.map((t2) => t2.kostnad ?? 0n)),
      tiltak: iAret
    };
  });
}

// src/domain/sak.ts
var SAKSTYPER = ["avvik", "kontroll", "aksjonspunkt", "tiltak", "arbeidsordre"];
var SAKSTYPENAVN = {
  avvik: "Avvik",
  kontroll: "Kontroll",
  aksjonspunkt: "Aksjonspunkt",
  tiltak: "Tiltak",
  arbeidsordre: "Arbeidsordre"
};
var SAKSPREFIKS = {
  avvik: "AVV",
  kontroll: "KTR",
  aksjonspunkt: "AKS",
  tiltak: "TLT",
  arbeidsordre: "AO"
};
function saksnummer(type, nummer) {
  return `${SAKSPREFIKS[type]}-${String(nummer)}`;
}
function erSakstype(v) {
  return SAKSTYPER.includes(v);
}
var PRIORITETER = ["lav", "middels", "hoy", "kritisk"];
var PRIORITETSNAVN = {
  lav: "Lav",
  middels: "Middels",
  hoy: "H\xF8y",
  kritisk: "Kritisk"
};
var REAKSJONER = ["\u{1F44D}", "\u2764\uFE0F", "\u{1F389}", "\u{1F440}", "\u2705", "\u{1F62E}"];
function erReaksjon(v) {
  return REAKSJONER.includes(v);
}
var MAKS_VEDLEGG = 10 * 1024 * 1024;
var VEDLEGGSTYPER = {
  "image/jpeg": "Bilde",
  "image/png": "Bilde",
  "image/webp": "Bilde",
  "application/pdf": "PDF",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "Word",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "Excel",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": "PowerPoint",
  "text/plain": "Tekst"
};
var OFFICE = {
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation"
};
var starterMed = (b, signatur, fra = 0) => signatur.every((x, i) => b[fra + i] === x);
function gjenkjennFiltype(bytes, filnavn) {
  const endelse = filnavn.toLowerCase().split(".").pop() ?? "";
  if (starterMed(bytes, [255, 216, 255])) return "image/jpeg";
  if (starterMed(bytes, [137, 80, 78, 71, 13, 10, 26, 10])) return "image/png";
  if (starterMed(bytes, [82, 73, 70, 70]) && starterMed(bytes, [87, 69, 66, 80], 8)) {
    return "image/webp";
  }
  if (starterMed(bytes, [37, 80, 68, 70, 45])) return "application/pdf";
  if (starterMed(bytes, [80, 75, 3, 4])) return OFFICE[endelse] ?? null;
  if (endelse === "txt" && !bytes.slice(0, 8192).includes(0)) return "text/plain";
  return null;
}
function erBilde(mimetype) {
  return mimetype.startsWith("image/");
}
function filstorrelse(bytes) {
  if (bytes < 1024) return `${String(bytes)} B`;
  if (bytes < 1024 * 1024) return `${String(Math.round(bytes / 1024))} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
}
function fremdrift(deloppgaver) {
  if (deloppgaver.length === 0) return null;
  return Math.round(deloppgaver.filter((d) => d.ferdig).length / deloppgaver.length * 100);
}
function tidstekst(minutter) {
  const t2 = Math.floor(minutter / 60);
  const m = minutter % 60;
  if (t2 === 0) return `${String(m)} min`;
  return m === 0 ? `${String(t2)} t` : `${String(t2)} t ${String(m)} min`;
}
function omtalte(tekst, personer) {
  const lengstForst = [...personer].sort((a, b) => b.navn.length - a.navn.length);
  const funnet = /* @__PURE__ */ new Map();
  let rest = tekst;
  for (const p2 of lengstForst) {
    const merke = `@${p2.navn}`;
    if (rest.includes(merke)) {
      funnet.set(p2.id, p2);
      rest = rest.split(merke).join(" ");
    }
  }
  return personer.filter((p2) => funnet.has(p2.id));
}
function ryddEtiketter(etiketter) {
  const ut = [];
  for (const e of etiketter) {
    const t2 = e.trim().replace(/\s+/g, " ");
    if (t2 !== "" && !ut.some((x) => x.toLowerCase() === t2.toLowerCase())) ut.push(t2);
  }
  return ut.slice(0, 10);
}

// src/domain/formatering.ts
function formaterBelop(verdi) {
  const [hel, des] = belopTilDesimaltekst(verdi).split(".");
  const negativ = hel?.startsWith("-") ?? false;
  const siffer = (negativ ? (hel ?? "").slice(1) : hel ?? "0").replace(
    /\B(?=(\d{3})+(?!\d))/g,
    " "
  );
  const gruppert = `${negativ ? "\u2212" : ""}${siffer}`;
  return des === void 0 ? gruppert : `${gruppert},${des}`;
}

// src/domain/arbeidsordre.ts
var ARBEIDSORDRESTATUSER = ["ny", "pagar", "utfort", "avbrutt"];
var ARBEIDSORDRESTATUSNAVN = {
  ny: "Ny",
  pagar: "P\xE5begynt",
  utfort: "Utf\xF8rt",
  avbrutt: "Avbrutt"
};
var ARBEIDSORDREHANDLING = {
  ny: "Gjen\xE5pne",
  pagar: "Meld p\xE5begynt",
  utfort: "Meld utf\xF8rt",
  avbrutt: "Avbryt ordren"
};
var OVERGANGER = {
  ny: ["pagar", "utfort", "avbrutt"],
  pagar: ["utfort", "avbrutt"],
  // En ordre meldt utført for tidlig kan tas opp igjen.
  utfort: ["pagar"],
  avbrutt: ["ny"]
};
function nesteArbeidsordrestatuser(s) {
  return OVERGANGER[s];
}
function kanGaTil(fra, til) {
  return OVERGANGER[fra].includes(til);
}
function erApenOrdre(s) {
  return s === "ny" || s === "pagar";
}
var OPPHAV = ["manuell", "avvik", "kontroll", "tilstand"];
var OPPHAVNAVN = {
  manuell: "Lagt inn",
  avvik: "Avvik",
  kontroll: "Kontroll",
  tilstand: "Vedlikeholdsplanen"
};
var KOSTNADSTYPER = ["arbeid", "materiell"];
var KOSTNADSTYPENAVN = {
  arbeid: "Arbeid",
  materiell: "Materiell"
};
var ANTALL_SKALA = 100n;
var ANTALL = /^(\d{1,7})(?:[.,](\d{1,2}))?$/;
function antallFraTekst(tekst) {
  const treff = ANTALL.exec(tekst.trim());
  if (!treff) throw new RangeError(`Ugyldig antall: ${JSON.stringify(tekst)}`);
  const [, hele = "0", brok = ""] = treff;
  return BigInt(hele) * ANTALL_SKALA + BigInt(brok.padEnd(2, "0"));
}
function antallTilTekst(hundredeler) {
  return `${String(hundredeler / ANTALL_SKALA)}.${String(hundredeler % ANTALL_SKALA).padStart(2, "0")}`;
}
function linjesum(antall, enhetspris) {
  const produkt = antallFraTekst(antall) * enhetspris;
  const negativ = produkt < 0n;
  const abs5 = negativ ? -produkt : produkt;
  const avrundet = (abs5 + ANTALL_SKALA / 2n) / ANTALL_SKALA;
  return negativ ? -avrundet : avrundet;
}
function kronetekst(b) {
  return `${formaterBelop(b).replace(/,000$/, "").replace(/(,\d\d)0$/, "$1")} kr`;
}
function kostnad(linjer) {
  const sum2 = (t2) => summerBelop(linjer.filter((l) => l.type === t2).map((l) => linjesum(l.antall, l.enhetspris)));
  const arbeid = sum2("arbeid");
  const materiell = sum2("materiell");
  return { arbeid, materiell, total: arbeid + materiell };
}
function overEstimat(estimat, faktisk) {
  if (estimat === null || estimat === 0n) return null;
  return Number((faktisk - estimat) * 100n / estimat);
}
function ordrear(o) {
  return Number((o.utfort ?? o.frist ?? o.opprettet).slice(0, 4));
}
function kostnadsoversikt(plan, ordrer) {
  const tellende = ordrer.filter((o) => o.status !== "avbrutt");
  const ar = [.../* @__PURE__ */ new Set([...plan.map((p2) => p2.ar), ...tellende.map((o) => o.ar)])].sort(
    (a, b) => b - a
  );
  const nokkel2 = (o) => o?.id ?? "";
  return ar.map((a) => {
    const rader = /* @__PURE__ */ new Map();
    const rad = (objekt) => rader.get(nokkel2(objekt)) ?? { objekt, planlagt: 0n, estimat: 0n, faktisk: 0n, ordrer: 0 };
    for (const p2 of plan.filter((x) => x.ar === a)) {
      const r = rad(p2.objekt);
      rader.set(nokkel2(p2.objekt), { ...r, planlagt: r.planlagt + p2.kostnad });
    }
    for (const o of tellende.filter((x) => x.ar === a)) {
      const r = rad(o.objekt);
      rader.set(nokkel2(o.objekt), {
        ...r,
        estimat: r.estimat + (o.estimat ?? 0n),
        faktisk: r.faktisk + o.faktisk,
        ordrer: r.ordrer + 1
      });
    }
    const sortert = [...rader.values()].sort(
      (x, y) => x.objekt === null ? 1 : y.objekt === null ? -1 : x.objekt.navn.localeCompare(y.objekt.navn, "nb")
    );
    return {
      ar: a,
      rader: sortert,
      sum: {
        planlagt: summerBelop(sortert.map((r) => r.planlagt)),
        estimat: summerBelop(sortert.map((r) => r.estimat)),
        faktisk: summerBelop(sortert.map((r) => r.faktisk)),
        ordrer: sortert.reduce((s, r) => s + r.ordrer, 0)
      }
    };
  });
}

// src/domain/entreprenor.ts
var FIRMAROLLER = ["utforer", "leser"];
var FIRMAROLLENAVN = {
  utforer: "Utf\xF8rer",
  leser: "Leser"
};
var FIRMASVAR = ["akseptert", "avslatt"];
var FIRMASVARNAVN = {
  akseptert: "P\xE5tatt",
  avslatt: "Avsl\xE5tt"
};
function entreprenorKanGaTil(fra, til) {
  if (til === "pagar") return fra === "ny" || fra === "utfort";
  if (til === "utfort") return fra === "ny" || fra === "pagar";
  return false;
}
var VEKTER = [3, 2, 7, 6, 5, 4, 3, 2];
function erOrgnr(orgnr) {
  if (!/^\d{9}$/.test(orgnr)) return false;
  const siffer = Array.from({ length: 9 }, (_, i) => Number(orgnr.charAt(i)));
  const sum2 = VEKTER.reduce((s, v, i) => s + v * (siffer[i] ?? 0), 0);
  const rest = sum2 % 11;
  const kontroll = rest === 0 ? 0 : 11 - rest;
  return kontroll !== 10 && kontroll === siffer[8];
}
function utforertekst(firma, svar) {
  if (svar === null) return `Sendt til ${firma}, venter p\xE5 svar`;
  return `${FIRMASVARNAVN[svar]} av ${firma}`;
}

// src/domain/detaljbudsjett.ts
var ANTALL_MANEDER = 12;
var NULL_TOLV = [
  NULL_BELOP,
  NULL_BELOP,
  NULL_BELOP,
  NULL_BELOP,
  NULL_BELOP,
  NULL_BELOP,
  NULL_BELOP,
  NULL_BELOP,
  NULL_BELOP,
  NULL_BELOP,
  NULL_BELOP,
  NULL_BELOP
];
var KORTE_MANEDSNAVN = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "Mai",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Okt",
  "Nov",
  "Des"
];
function somTolv(liste) {
  if (liste.length !== ANTALL_MANEDER)
    throw new RangeError(`Ventet ${String(ANTALL_MANEDER)} m\xE5neder.`);
  return liste;
}
function summerTolv(rader) {
  return somTolv(NULL_TOLV.map((_, i) => summerBelop(rader.map((r) => r[i] ?? NULL_BELOP))));
}
function arssum(maneder) {
  return summerBelop(maneder);
}
function periodiser(arsbelop, profil) {
  if (profil === null) return somTolv(fordelJevnt(arsbelop, ANTALL_MANEDER));
  const retning = arssum(profil) < 0n ? -1 : 1;
  const vekter = profil.map((m) => Math.max(0, Number(m) * retning));
  return somTolv(fordelEtterVekter(arsbelop, vekter));
}
function udisponert(forslag, fordelinger) {
  const sum2 = summerFireaar(fordelinger);
  return [forslag[0] - sum2[0], forslag[1] - sum2[1], forslag[2] - sum2[2], forslag[3] - sum2[3]];
}
function detaljavvik(ramme, rader) {
  if (ramme === null) return null;
  return ramme - arssum(summerTolv(rader));
}
function kanDetaljere(fase) {
  return fase !== "rammer";
}
function kanFordele(fase, rolle, omradeId) {
  if (!kanDetaljere(fase)) return false;
  return rolle.styrer || rolle.skriver.has(omradeId);
}
function kanBudsjettereVirksomhet(fase, rolle, virksomhet2) {
  if (!kanDetaljere(fase)) return false;
  return rolle.styrer || rolle.skriver.has(virksomhet2.omradeId) || rolle.virksomheter.has(virksomhet2.id);
}

// src/domain/budsjettrevisjon.ts
function periodiserFra(arsbelop, fraManed) {
  const fra = Math.min(Math.max(Math.round(fraManed), 1), ANTALL_MANEDER);
  const deler = fordelJevnt(arsbelop, ANTALL_MANEDER - fra + 1);
  return Array.from(
    { length: ANTALL_MANEDER },
    (_, i) => i + 1 < fra ? NULL_BELOP : deler[i + 1 - fra] ?? NULL_BELOP
  );
}
function revisjonssum(linjer) {
  const omrader = /* @__PURE__ */ new Map();
  let netto = NULL_BELOP;
  for (const l of linjer) {
    const sum2 = arssum(l.maneder);
    omrader.set(l.omradeId, (omrader.get(l.omradeId) ?? NULL_BELOP) + sum2);
    netto += sum2;
  }
  return { omrader, netto };
}
function kanEndreRevisjon(styrer, budsjettetIBruk, vedtatt) {
  return styrer && budsjettetIBruk && !vedtatt;
}

// src/domain/administrasjon.ts
var SYSTEMROLLER = ["plattformadmin", "kommuneadmin", "bruker"];
function kanAdministrere(systemrolle) {
  return systemrolle === "kommuneadmin" || systemrolle === "plattformadmin";
}
function villeLaastUteKommunen(brukere, endring) {
  const etterpa = brukere.map(
    (b) => b.id === endring.brukerId ? {
      ...b,
      systemrolle: endring.systemrolle ?? b.systemrolle,
      aktiv: endring.aktiv ?? b.aktiv
    } : b
  );
  return !etterpa.some((b) => b.aktiv && kanAdministrere(b.systemrolle));
}
function kanTildeleSystemrolle(tildeler, tildelt) {
  if (!kanAdministrere(tildeler)) return false;
  if (tildelt === "plattformadmin") return tildeler === "plattformadmin";
  return true;
}
function finnUgyldigeTildelinger(tildelinger) {
  const sett = /* @__PURE__ */ new Set();
  const duplikater = /* @__PURE__ */ new Set();
  const ukjenteRoller = /* @__PURE__ */ new Set();
  for (const t2 of tildelinger) {
    if (sett.has(t2.virksomhetId)) duplikater.add(t2.virksomhetId);
    sett.add(t2.virksomhetId);
    if (!ROLLER.includes(t2.rolle)) ukjenteRoller.add(t2.rolle);
  }
  return { duplikater: [...duplikater], ukjenteRoller: [...ukjenteRoller] };
}

// src/domain/import/rutenett.ts
var MANEDSETIKETTER = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "Mai",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Okt",
  "Nov",
  "Des"
];
function tolkNorsktall(verdi) {
  let tekst = verdi.trim();
  if (tekst === "") return null;
  let negativ = false;
  if (/^\(.*\)$/.test(tekst)) {
    negativ = true;
    tekst = tekst.slice(1, -1).trim();
  }
  tekst = tekst.replace(/\s/g, "");
  if (tekst.startsWith("\u2212")) tekst = `-${tekst.slice(1)}`;
  const sisteKomma = tekst.lastIndexOf(",");
  const sistePunktum = tekst.lastIndexOf(".");
  if (sisteKomma >= 0 && sistePunktum >= 0) {
    const desimal = sisteKomma > sistePunktum ? "," : ".";
    const tusen = desimal === "," ? "." : ",";
    tekst = tekst.split(tusen).join("").replace(desimal, ".");
  } else if (sisteKomma >= 0) {
    tekst = tekst.replace(",", ".");
  }
  if (!/^[+-]?\d+(\.\d+)?$/.test(tekst)) return null;
  const [hel = "0", des = ""] = tekst.replace("-", "").split(".");
  const avrundet = des.length > 3 ? (Math.round(Number(`${hel}.${des}`) * 1e3) / 1e3).toFixed(3) : `${hel}.${des.padEnd(3, "0")}`;
  const belop = belopFraDesimaltekst(avrundet);
  const erNegativ = negativ !== tekst.startsWith("-");
  return erNegativ ? -belop : belop;
}
function normaliser(tekst) {
  return tekst.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/ø/g, "o").replace(/æ/g, "a").replace(/å/g, "a").replace(/[^a-z0-9 ]+/g, " ").replace(/\s+/g, " ").trim();
}

// src/domain/kpi/maaltall.ts
var MAALTALL_SKALA = 1000n;
var NULL_MAALTALL = 0n;
var MAALEENHETER = [
  "prosent",
  "kroner",
  "antall",
  "arsverk",
  "dogn",
  "forholdstall"
];
var ENHETSSYMBOL = {
  prosent: "%",
  kroner: "kr",
  antall: "",
  arsverk: "\xE5rsverk",
  dogn: "d\xF8gn",
  forholdstall: ""
};
var ENHETSDESIMALER = {
  prosent: 1,
  kroner: 0,
  antall: 0,
  arsverk: 1,
  dogn: 0,
  forholdstall: 2
};
function kanSummeres(enhet) {
  return enhet !== "prosent" && enhet !== "forholdstall";
}
var DESIMALER2 = /^(-)?(\d+)(?:[.,](\d{1,3}))?$/;
function maaltallFraDesimaltekst(tekst) {
  const treff = DESIMALER2.exec(tekst.trim());
  if (!treff) throw new RangeError(`Ugyldig m\xE5ltall: ${tekst}`);
  const [, fortegn, hel, desimaler = ""] = treff;
  const tusendeler = BigInt((hel ?? "0") + desimaler.padEnd(3, "0"));
  return fortegn === "-" ? -tusendeler : tusendeler;
}
function maaltallTilDesimaltekst(verdi) {
  const negativ = verdi < 0n;
  const absolutt = negativ ? -verdi : verdi;
  const hel = absolutt / MAALTALL_SKALA;
  const rest = (absolutt % MAALTALL_SKALA).toString().padStart(3, "0");
  return `${negativ ? "-" : ""}${hel.toString()}.${rest}`;
}
function maaltallFraBelop(belop) {
  return maaltallFraDesimaltekst(belopTilDesimaltekst(belop));
}
function andelIProsent(teller, nevner) {
  if (nevner === 0n) return null;
  const skalert = teller * 100n * MAALTALL_SKALA;
  const halv = nevner < 0n ? -nevner / 2n : nevner / 2n;
  const positivtResultat = skalert >= 0n === nevner > 0n;
  const absolutt = (abs4(skalert) + abs4(halv)) / abs4(nevner);
  return positivtResultat ? absolutt : -absolutt;
}
function abs4(verdi) {
  return verdi < 0n ? -verdi : verdi;
}
function formaterMaaltall(verdi, enhet) {
  const desimaler = ENHETSDESIMALER[enhet];
  const negativ = verdi < 0n;
  const absolutt = negativ ? -verdi : verdi;
  const deler = 10n ** BigInt(3 - desimaler);
  const avrundet = (absolutt + deler / 2n) / deler;
  const skala = 10n ** BigInt(desimaler);
  const hel = (avrundet / skala).toString().replace(/\B(?=(\d{3})+(?!\d))/g, "\xA0");
  const rest = desimaler === 0 ? "" : `,${(avrundet % skala).toString().padStart(desimaler, "0")}`;
  return `${negativ ? "\u2212" : ""}${hel}${rest}`;
}
function formaterMedEnhet(verdi, enhet) {
  const symbol = ENHETSSYMBOL[enhet];
  const tall3 = formaterMaaltall(verdi, enhet);
  return symbol === "" ? tall3 : `${tall3}\xA0${symbol}`;
}

// src/domain/bemanning/tall.ts
var TOM_MAANED = {
  budsjetterteArsverk: NULL_MAALTALL,
  besatteArsverk: NULL_MAALTALL,
  overtidstimer: NULL_MAALTALL,
  fravarsdagsverk: NULL_MAALTALL,
  korttidsdagsverk: NULL_MAALTALL,
  muligeDagsverk: NULL_MAALTALL
};
function utled(m) {
  return {
    vakans: m.budsjetterteArsverk - m.besatteArsverk,
    vakansgrad: andelIProsent(m.budsjetterteArsverk - m.besatteArsverk, m.budsjetterteArsverk),
    sykefravar: andelIProsent(m.fravarsdagsverk, m.muligeDagsverk),
    korttidsfravar: andelIProsent(m.korttidsdagsverk, m.muligeDagsverk)
  };
}
function summer2(deler) {
  return deler.reduce(
    (sum2, d) => ({
      budsjetterteArsverk: sum2.budsjetterteArsverk + d.budsjetterteArsverk,
      besatteArsverk: sum2.besatteArsverk + d.besatteArsverk,
      overtidstimer: sum2.overtidstimer + d.overtidstimer,
      fravarsdagsverk: sum2.fravarsdagsverk + d.fravarsdagsverk,
      korttidsdagsverk: sum2.korttidsdagsverk + d.korttidsdagsverk,
      muligeDagsverk: sum2.muligeDagsverk + d.muligeDagsverk
    }),
    { ...TOM_MAANED }
  );
}
function snittArsverk(maneder) {
  const med = maneder.filter((m) => m.budsjetterteArsverk !== 0n || m.besatteArsverk !== 0n);
  if (med.length === 0) return { budsjetterte: NULL_MAALTALL, besatte: NULL_MAALTALL };
  const antall = BigInt(med.length);
  return {
    budsjetterte: med.reduce((s, m) => s + m.budsjetterteArsverk, 0n) / antall,
    besatte: med.reduce((s, m) => s + m.besatteArsverk, 0n) / antall
  };
}
function registrerteManeder(maneder) {
  return MANEDER.filter((m) => {
    const v = maneder.get(m);
    return v !== void 0 && (v.muligeDagsverk !== 0n || v.budsjetterteArsverk !== 0n);
  });
}
function tallFraTekst(tekst) {
  return maaltallFraDesimaltekst(tekst);
}

// src/domain/bemanning/import.ts
var FELTNAVN = {
  budsjetterteArsverk: [
    "budsjetterte arsverk",
    "budsjettert arsverk",
    "planlagte arsverk",
    "arsverk budsjett"
  ],
  besatteArsverk: ["besatte arsverk", "besatt arsverk", "faktiske arsverk", "arsverk besatt"],
  overtidstimer: ["overtidstimer", "overtid timer", "overtid"],
  fravarsdagsverk: ["fravarsdagsverk", "fravar dagsverk", "sykefravarsdagsverk"],
  korttidsdagsverk: ["korttidsdagsverk", "korttid dagsverk", "korttidsfravar"],
  muligeDagsverk: ["mulige dagsverk", "mulig dagsverk", "avtalte dagsverk"]
};
var KODENAVN = ["kode", "stillingskode", "gruppekode"];
var NAVNENAVN = ["stillingsgruppe", "gruppe", "navn", "stilling"];
var MANEDNAVN = ["maned", "mnd", "periode"];
function tolkManed(verdi) {
  const tekst = normaliser(verdi);
  if (tekst === "") return null;
  const tall3 = Number(tekst);
  if (Number.isInteger(tall3) && tall3 >= 1 && tall3 <= 12) return tall3;
  const kort = MANEDSETIKETTER.findIndex((e) => normaliser(e) === tekst.slice(0, 3));
  if (kort >= 0) return kort + 1;
  return null;
}
function finnKolonner(overskrift) {
  let kode = null;
  let navn2 = null;
  let maned = null;
  const felt2 = {};
  const ukjente = [];
  overskrift.forEach((ra, i) => {
    const n = normaliser(ra);
    if (n === "") return;
    if (kode === null && KODENAVN.includes(n)) {
      kode = i;
      return;
    }
    if (navn2 === null && NAVNENAVN.includes(n)) {
      navn2 = i;
      return;
    }
    if (maned === null && MANEDNAVN.includes(n)) {
      maned = i;
      return;
    }
    for (const [f, navnene] of Object.entries(FELTNAVN)) {
      if (felt2[f] === void 0 && navnene.includes(n)) {
        felt2[f] = i;
        return;
      }
    }
    ukjente.push(ra.trim());
  });
  return { kode, navn: navn2, maned, felt: felt2, ukjente };
}
function lesBemanningsfil(rutenett) {
  const ignorerteRader = [];
  const rader = [];
  const overskriftIndeks = rutenett.findIndex((rad) => {
    const k2 = finnKolonner(rad);
    return k2.maned !== null && Object.keys(k2.felt).length > 0;
  });
  if (overskriftIndeks < 0) {
    return {
      rader: [],
      ignorerteRader: [
        { linje: 1, innhold: "", arsak: "Fant ingen overskriftsrad med m\xE5ned og m\xE5ltall." }
      ],
      ukjenteKolonner: [],
      manederIFilen: []
    };
  }
  const kol = finnKolonner(rutenett[overskriftIndeks] ?? []);
  const maneder = /* @__PURE__ */ new Set();
  for (let i = overskriftIndeks + 1; i < rutenett.length; i += 1) {
    const rad = rutenett[i] ?? [];
    const linje = i + 1;
    const innhold = rad.join(" ").trim();
    if (innhold === "") continue;
    const kode = kol.kode === null ? "" : (rad[kol.kode] ?? "").trim();
    const navn2 = kol.navn === null ? "" : (rad[kol.navn] ?? "").trim();
    if (kode === "" && navn2 === "") {
      ignorerteRader.push({
        linje,
        innhold,
        arsak: "Raden har verken kode eller stillingsgruppe."
      });
      continue;
    }
    const maned = kol.maned === null ? null : tolkManed(rad[kol.maned] ?? "");
    if (maned === null) {
      ignorerteRader.push({ linje, innhold, arsak: "Fant ingen m\xE5ned p\xE5 raden." });
      continue;
    }
    const les2 = (f) => {
      const i2 = kol.felt[f];
      if (i2 === void 0) return NULL_MAALTALL;
      return tolkNorsktall(rad[i2] ?? "") ?? NULL_MAALTALL;
    };
    const maal = {
      budsjetterteArsverk: les2("budsjetterteArsverk"),
      besatteArsverk: les2("besatteArsverk"),
      overtidstimer: les2("overtidstimer"),
      fravarsdagsverk: les2("fravarsdagsverk"),
      korttidsdagsverk: les2("korttidsdagsverk"),
      muligeDagsverk: les2("muligeDagsverk")
    };
    const harTall = Object.values(maal).some((v) => v !== 0n);
    if (!harTall) {
      ignorerteRader.push({ linje, innhold, arsak: "Raden har ingen tall." });
      continue;
    }
    if (maal.fravarsdagsverk > maal.muligeDagsverk) {
      ignorerteRader.push({
        linje,
        innhold,
        arsak: "Frav\xE6rsdagsverk er st\xF8rre enn mulige dagsverk."
      });
      continue;
    }
    if (maal.korttidsdagsverk > maal.fravarsdagsverk) {
      ignorerteRader.push({
        linje,
        innhold,
        arsak: "Korttidsfrav\xE6r er st\xF8rre enn det samlede frav\xE6ret."
      });
      continue;
    }
    maneder.add(maned);
    rader.push({
      linje,
      kode: kode === "" ? navn2 : kode,
      navn: navn2 === "" ? kode : navn2,
      maned,
      maal
    });
  }
  return {
    rader,
    ignorerteRader,
    ukjenteKolonner: kol.ukjente,
    manederIFilen: [...maneder].sort((a, b) => a - b)
  };
}

// src/domain/innkjop/orgnummer.ts
var VEKTER2 = [3, 2, 7, 6, 5, 4, 3, 2];
function normaliserOrgnummer(tekst) {
  const rent = tekst.replace(/[\s.–—-]/g, "");
  if (!/^\d{9}$/.test(rent)) return null;
  return erGyldigOrgnummer(rent) ? rent : null;
}
function erGyldigOrgnummer(nummer) {
  if (!/^\d{9}$/.test(nummer)) return false;
  let sum2 = 0;
  for (const [i, vekt] of VEKTER2.entries()) {
    sum2 += Number(nummer.charAt(i)) * vekt;
  }
  const rest = sum2 % 11;
  const kontroll = rest === 0 ? 0 : 11 - rest;
  if (kontroll === 10) return false;
  return kontroll === Number(nummer.charAt(8));
}
function formaterOrgnummer(nummer) {
  if (!/^\d{9}$/.test(nummer)) return nummer;
  return `${nummer.slice(0, 3)} ${nummer.slice(3, 6)} ${nummer.slice(6)}`;
}

// src/domain/innkjop/avstemming.ts
function avstem(bokfortPerKonto, forklartPerKonto) {
  const kontoer = [.../* @__PURE__ */ new Set([...bokfortPerKonto.keys(), ...forklartPerKonto.keys()])].sort();
  const rader = kontoer.map((underkontoId) => {
    const bokfort = bokfortPerKonto.get(underkontoId) ?? 0n;
    const forklart = forklartPerKonto.get(underkontoId) ?? 0n;
    return { underkontoId, bokfort, forklart, uforklart: bokfort - forklart };
  });
  const bokfortTotalt = rader.reduce((sum2, r) => sum2 + r.bokfort, 0n);
  const forklartTotalt = rader.reduce((sum2, r) => sum2 + r.forklart, 0n);
  return {
    rader,
    bokfortTotalt,
    forklartTotalt,
    uforklartTotalt: bokfortTotalt - forklartTotalt,
    andelForklart: andelIProsent(forklartTotalt, bokfortTotalt)
  };
}

// src/domain/tekst.ts
function foldTekst(tekst) {
  return tekst.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/ø/g, "o").replace(/æ/g, "a").replace(/å/g, "a");
}

// src/domain/import/datoformat.ts
var SKRASTREK = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/;
var lesbar = (dag, maned, ar) => `${String(dag)}. ${MANEDSNAVN[maned]} ${ar}`;
function vurderDatoformat(verdier) {
  let dagForst = null;
  let manedForst = null;
  let tvetydig = null;
  let finnes = false;
  for (const verdi of verdier) {
    const tekst = verdi.trim();
    const treff = SKRASTREK.exec(tekst);
    if (treff === null) continue;
    const a = Number(treff[1]);
    const b = Number(treff[2]);
    const ar = treff[3] ?? "";
    if (a < 1 || b < 1 || a > 31 || b > 31) continue;
    finnes = true;
    if (a > 12 && b <= 12) dagForst ??= tekst;
    else if (b > 12 && a <= 12) manedForst ??= tekst;
    else if (a <= 12 && b <= 12 && a !== b) tvetydig ??= { tekst, a, b, ar };
  }
  if (dagForst !== null && manedForst !== null) {
    return {
      rekkefolge: "blandet",
      feil: `Filen har datoer med skr\xE5strek i to rekkef\xF8lger: \xAB${dagForst}\xBB har dagen f\xF8rst, \xAB${manedForst}\xBB har m\xE5neden f\xF8rst. Da kan ingen av de andre leses sikkert, og filen er ikke lest. Rett datoene, eller lagre dem som 2026-03-25, og last filen opp igjen.`,
      merknad: null
    };
  }
  if (manedForst !== null) {
    return {
      rekkefolge: "maned_forst",
      feil: `Datoene st\xE5r med m\xE5neden f\xF8rst, som i amerikansk format: \xAB${manedForst}\xBB kan ikke ha dagen f\xF8rst. Filen er ikke lest \u2014 en dato som 03/04/2026 ville ellers havnet i feil m\xE5ned. Lagre filen med norske regionsinnstillinger, eller med datoer som 2026-03-25, og last den opp igjen.`,
      merknad: null
    };
  }
  if (dagForst !== null) return { rekkefolge: "dag_forst", feil: null, merknad: null };
  if (tvetydig !== null) {
    const { tekst, a, b, ar } = tvetydig;
    return {
      rekkefolge: "tvetydig",
      feil: null,
      merknad: `Datoene med skr\xE5strek er lest med dagen f\xF8rst, som er vanlig i Norge. Filen har ingen dato som avgj\xF8r det: \xAB${tekst}\xBB er lest som ${lesbar(a, b, ar)}, ikke ${lesbar(b, a, ar)}. Stemmer ikke det, er filen lagret med amerikanske innstillinger og m\xE5 lagres p\xE5 nytt f\xF8r den importeres.`
    };
  }
  return {
    rekkefolge: finnes ? "dag_forst" : "ingen_skrastrek",
    feil: null,
    merknad: null
  };
}

// src/domain/innkjop/journal.ts
var KOLONNENAVN = {
  orgnummer: ["organisasjonsnummer", "orgnummer", "orgnr", "org.nr", "org nr"],
  leverandor: ["leverandor", "leverandornavn", "navn", "kreditor"],
  konto: ["konto", "kontonummer", "kontonr", "art", "artskonto"],
  belop: ["belop", "sum", "nettobelop", "belop eks mva", "nettosum"],
  dato: ["fakturadato", "bilagsdato", "dato", "posteringsdato"],
  fakturanummer: ["fakturanummer", "fakturanr", "bilagsnummer", "bilagsnr", "fakturanr."]
};
function erJournalfeil(r) {
  return "feil" in r;
}
function finnKolonner2(rad) {
  const funnet = {};
  rad.forEach((celle4, i) => {
    const tekst = foldTekst(celle4.trim());
    if (tekst === "") return;
    for (const [kolonne, navn2] of Object.entries(KOLONNENAVN)) {
      if (funnet[kolonne] === void 0 && navn2.some((n) => tekst === foldTekst(n))) {
        funnet[kolonne] = i;
      }
    }
  });
  return funnet;
}
function finnesDatoen(dato3) {
  const d = /* @__PURE__ */ new Date(`${dato3}T00:00:00Z`);
  return Number.isNaN(d.getTime()) || d.toISOString().slice(0, 10) !== dato3 ? null : dato3;
}
function tolkDato(verdi) {
  const tekst = verdi.trim();
  const [, isoAr, isoMnd, isoDag] = /^(\d{4})-(\d{2})-(\d{2})/.exec(tekst) ?? [];
  if (isoAr !== void 0 && isoMnd !== void 0 && isoDag !== void 0) {
    return finnesDatoen(`${isoAr}-${isoMnd}-${isoDag}`);
  }
  const [, raDag, raMnd, raAr] = /^(\d{1,2})[./](\d{1,2})[./](\d{4})/.exec(tekst) ?? [];
  if (raDag === void 0 || raMnd === void 0 || raAr === void 0) return null;
  const dag = raDag.padStart(2, "0");
  const maned = raMnd.padStart(2, "0");
  if (Number(maned) < 1 || Number(maned) > 12 || Number(dag) < 1 || Number(dag) > 31) return null;
  return finnesDatoen(`${raAr}-${maned}-${dag}`);
}
function analyserJournal(rutenett) {
  let overskrift = -1;
  let kolonner = {};
  for (let i = 0; i < rutenett.length; i += 1) {
    const funnet = finnKolonner2(rutenett[i] ?? []);
    if (funnet.belop !== void 0 && funnet.konto !== void 0) {
      overskrift = i;
      kolonner = funnet;
      break;
    }
  }
  if (overskrift === -1) {
    return {
      feil: "Fant ingen overskriftsrad med b\xE5de en konto- og en bel\xF8pskolonne. Filen m\xE5 ha kolonneoverskrifter."
    };
  }
  if (kolonner.orgnummer === void 0) {
    return {
      feil: "Fant ingen kolonne med organisasjonsnummer. Leverand\xF8rer identifiseres p\xE5 nummer, ikke p\xE5 navn."
    };
  }
  const les2 = (rad, kolonne) => kolonner[kolonne] === void 0 ? "" : (rad[kolonner[kolonne]] ?? "").trim();
  const datoformat = vurderDatoformat(
    rutenett.slice(overskrift + 1).map((rad) => les2(rad, "dato"))
  );
  if (datoformat.feil !== null) return { feil: datoformat.feil };
  const rader = [];
  const avviste = [];
  for (let i = overskrift + 1; i < rutenett.length; i += 1) {
    const rad = rutenett[i] ?? [];
    const linje = i + 1;
    if (rad.every((c) => c.trim() === "")) continue;
    const raOrg = les2(rad, "orgnummer");
    const orgnummer = normaliserOrgnummer(raOrg);
    if (orgnummer === null) {
      avviste.push({ linje, feil: "ugyldig_orgnummer", verdi: raOrg });
      continue;
    }
    const leverandor = les2(rad, "leverandor");
    if (leverandor === "") {
      avviste.push({ linje, feil: "mangler_leverandornavn", verdi: raOrg });
      continue;
    }
    const kontonummer = les2(rad, "konto");
    if (kontonummer === "") {
      avviste.push({ linje, feil: "mangler_konto", verdi: leverandor });
      continue;
    }
    const raBelop = les2(rad, "belop");
    const belop = tolkNorsktall(raBelop);
    if (belop === null) {
      avviste.push({ linje, feil: "ugyldig_belop", verdi: raBelop });
      continue;
    }
    const fakturanummer = les2(rad, "fakturanummer");
    rader.push({
      linje,
      orgnummer,
      leverandor,
      kontonummer,
      belop,
      dato: tolkDato(les2(rad, "dato")),
      fakturanummer: fakturanummer === "" ? null : fakturanummer.slice(0, 50)
    });
  }
  return { rader, avviste, datomerknad: datoformat.merknad };
}

// src/domain/drift.ts
var DRIFTSTATUSER = ["ok", "advarsel", "kritisk"];
var GB = 1024 ** 3;
var DISK_KRITISK_BYTES = 2 * GB;
var DISK_ADVARSEL_BYTES = 5 * GB;
var DISK_KRITISK_ANDEL = 0.05;
var DISK_ADVARSEL_ANDEL = 0.1;
function vurderDiskplass(ledigBytes, totaltBytes) {
  if (totaltBytes <= 0) return "ok";
  const andel = ledigBytes / totaltBytes;
  if (ledigBytes < DISK_KRITISK_BYTES || andel < DISK_KRITISK_ANDEL) return "kritisk";
  if (ledigBytes < DISK_ADVARSEL_BYTES || andel < DISK_ADVARSEL_ANDEL) return "advarsel";
  return "ok";
}
function formaterBytes(bytes) {
  const enheter = ["B", "kB", "MB", "GB", "TB"];
  let verdi = Math.abs(bytes);
  let enhet = "B";
  for (const kandidat of enheter) {
    enhet = kandidat;
    if (verdi < 1024 || kandidat === "TB") break;
    verdi /= 1024;
  }
  const desimaler = enhet === "B" || verdi >= 100 ? 0 : 1;
  const tall3 = (bytes < 0 ? -verdi : verdi).toFixed(desimaler).replace(".", ",");
  return `${tall3} ${enhet}`;
}

// src/schemas/drift.ts
import { z } from "zod";
var driftstatusSchema = z.enum(DRIFTSTATUSER);
var driftSchema = z.object({
  disk: z.object({
    /** Hele filsystemet der containerne og databasevolumet ligger. */
    totaltBytes: z.number().int().nonnegative(),
    /** Ledig for en vanlig bruker — altså ikke det root kan bruke opp. */
    ledigBytes: z.number().int().nonnegative(),
    status: driftstatusSchema
  }),
  /** Hvor stor selve kommunedatabasen er, som andel av det som fyller disken. */
  databaseBytes: z.number().int().nonnegative(),
  /** Når målingen ble gjort. Den er et øyeblikksbilde, ikke en tidsserie. */
  maalt: z.iso.datetime()
});

// src/schemas/kommuner.ts
import { z as z3 } from "zod";

// src/schemas/felles.ts
import { z as z2 } from "zod";
var uuidSchema = z2.uuid();
var arSchema = z2.number().int().min(2e3).max(2100).describe("Regnskaps\xE5r");
var manedSchema = z2.literal(MANEDER).describe("M\xE5ned, 1\u201312");
var arSporringSchema = z2.coerce.number().int().min(2e3).max(2100).describe("Regnskaps\xE5r, fra sp\xF8rrestreng");
var manedSporringSchema = z2.coerce.number().int().refine((n) => MANEDER.includes(n), {
  message: "M\xE5ned m\xE5 v\xE6re 1\u201312."
}).describe("M\xE5ned 1\u201312, fra sp\xF8rrestreng");
var belopSchema = z2.string().regex(/^[+-]?\d+(\.\d{1,3})?$/, "Bel\xF8p m\xE5 v\xE6re et desimaltall med maks 3 desimaler").describe('Bel\xF8p som desimaltekst, f.eks. "-1234.500"');
var isodatoSchema = z2.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Dato m\xE5 v\xE6re p\xE5 formen \xC5\xC5\xC5\xC5-MM-DD").describe('Dato uten klokkeslett, f.eks. "2026-05-01"');
var prosentSchema = z2.string().regex(/^\d{1,3}(\.\d{1,2})?$/, "Sats m\xE5 v\xE6re et tall med maks to desimaler, 0\u2013999,99").describe('Sats i prosent som desimaltekst, f.eks. "14.10"');
var kategoriSchema = z2.enum(HOVEDKONTO_KATEGORIER);
var identitetsleverandorTypeSchema = z2.enum(IDENTITETSLEVERANDOR_TYPER);
var valutaEnhetSchema = z2.enum(VALUTA_ENHETER);
var rolleSchema = z2.enum(ROLLER);
var pagineringSchema = z2.object({
  side: z2.coerce.number().int().min(1).default(1),
  antall: z2.coerce.number().int().min(1).max(200).default(50)
});
function sideSvarSchema(element) {
  return z2.object({
    data: z2.array(element),
    side: z2.number().int(),
    antall: z2.number().int(),
    totalt: z2.number().int()
  });
}
var feilSvarSchema = z2.object({
  statusKode: z2.number().int(),
  feilkode: z2.string().describe('Stabil maskinlesbar kode, f.eks. "validering_feilet"'),
  melding: z2.string().describe("Melding trygg \xE5 vise til bruker \u2014 aldri interne detaljer"),
  detaljer: z2.array(z2.object({ felt: z2.string(), melding: z2.string() })).optional(),
  sporingsId: z2.string().describe("Korrelerer svaret med serverloggen")
});

// src/schemas/kommuner.ts
var nyKommuneSchema = z3.object({
  navn: z3.string().trim().min(2, "Skriv navnet p\xE5 kommunen.").max(150),
  /** Ni siffer, eller ingenting. Tomt felt er ikke en feil — det ettersendes. */
  orgNummer: z3.string().trim().regex(/^\d{9}$/, "Organisasjonsnummer er ni siffer.").nullable().default(null),
  kommunalomrade: z3.string().trim().min(2, "Skriv navnet p\xE5 kommunalomr\xE5det.").max(150).default("Helse og omsorg"),
  virksomhet: z3.string().trim().min(2, "Skriv navnet p\xE5 den f\xF8rste virksomheten.").max(150),
  /**
   * Feil enhet gir tall som er tusen ganger for store eller små gjennom hele
   * løsningen, så den velges bevisst framfor å ha en stille standard.
   */
  valutaEnhet: valutaEnhetSchema,
  adminEpost: z3.string().trim().max(190).regex(/^[^@\s]+@[^@\s]+\.[^@\s]+$/, "Skriv en gyldig e-postadresse."),
  adminNavn: z3.string().trim().min(2, "Skriv navnet p\xE5 administratoren.").max(150),
  /** Minst én modul — en kommune uten moduler ser en tom meny og lurer på hvorfor. */
  moduler: z3.array(z3.enum(FUNKSJONSFLAGG)).min(1, "Velg minst \xE9n modul.")
});
var kommuneoppforingSchema = z3.object({
  id: uuidSchema,
  navn: z3.string(),
  orgNummer: z3.string().nullable(),
  aktiv: z3.boolean(),
  erLeverandor: z3.boolean(),
  opprettet: z3.iso.datetime(),
  antallBrukere: z3.number().int().nonnegative()
});

// src/schemas/integrasjoner.ts
import { z as z4 } from "zod";
var integrasjonsfeltSchema = z4.object({
  /** Maskinnavnet adapteren slår opp på. Snake_case, som i leverandørens API. */
  nokkel: z4.string().regex(/^[a-z][a-z0-9_]{0,39}$/, "Feltn\xF8kkelen m\xE5 v\xE6re sm\xE5 bokstaver, tall og understrek."),
  /** Det administratoren ser. «Klient-ID», ikke «client_id». */
  etikett: z4.string().min(1).max(80),
  /** Hvor verdien hentes fra hos leverandøren. Uten dette er feltet en gjetning. */
  hjelp: z4.string().max(200).optional(),
  /**
   * Om verdien er en hemmelighet eller bare en identifikator.
   *
   * Begge krypteres — det koster ingenting og sparer oss for å ta stilling per
   * felt. Men de oppfører seg ulikt på skjermen: en client_id skal kunne leses
   * mens den tastes, for den skrives feil like ofte som alt annet og det finnes
   * ingen måte å kontrollere den på etterpå. En client_secret skal ikke.
   */
  hemmelig: z4.boolean()
});
var integrasjonssystemSchema = z4.object({
  nokkel: z4.string(),
  navn: z4.string(),
  beskrivelse: z4.string(),
  /**
   * Minst ett. Et system uten felter ville gitt et skjema uten nøkkelfelt, og
   * dermed en tilkobling som stilltiende er uautentisert — verre enn et system
   * som ikke finnes i listen.
   */
  felter: z4.array(integrasjonsfeltSchema).min(1),
  /**
   * Hvor lenge vi venter på svar fra DETTE systemet.
   *
   * Sto som 30 s for alle. Det er ikke galt for alle — det er bare ikke riktig
   * for alle: Unit4 tillater 120 s på tunge uttrekk, og et helt regnskapsår er
   * et tungt uttrekk. En grense som er for kort gir en feil som ser ut som at
   * nøkkelen er utløpt.
   */
  tidsgrenseMs: z4.number().int().min(1e3).max(3e5)
});
var basisUrlSchema = z4.string().trim().max(500).regex(/^https:\/\/[^\s]+$/, "Adressen m\xE5 begynne med https://");
var hemmeligheterSchema = z4.record(z4.string().regex(/^[a-z][a-z0-9_]{0,39}$/), z4.string().min(1).max(4e3)).refine((m) => Object.keys(m).length > 0, "Minst \xE9n verdi m\xE5 oppgis.");
var nyIntegrasjonSchema = z4.object({
  systemNokkel: z4.string().trim().min(1).max(60),
  /** Skiller to tilkoblinger til samme system — «Visma test» og «Visma drift». */
  navn: z4.string().trim().min(2).max(150),
  basisUrl: basisUrlSchema,
  hemmeligheter: hemmeligheterSchema
});
var settHemmelighetSchema = z4.object({ hemmeligheter: hemmeligheterSchema });
var settIntegrasjonAktivSchema = z4.object({ aktiv: z4.boolean() });
var integrasjonSchema = z4.object({
  id: uuidSchema,
  systemNokkel: z4.string(),
  navn: z4.string(),
  basisUrl: z4.string(),
  aktiv: z4.boolean(),
  /** At den er satt — ikke hva den er. */
  harHemmelighet: z4.boolean(),
  hemmelighetSatt: z4.iso.datetime().nullable(),
  sistBrukt: z4.iso.datetime().nullable(),
  /**
   * Hvordan det gikk sist, og hvorfor.
   *
   * Står her fordi den som kan rette en feil adresse eller en utløpt nøkkel er
   * kommunens egen administrator — og hun har ikke serverloggen. Uten dette er
   * «henter ikke data» en tilstand bare leverandøren kan feilsøke, per kommune.
   */
  sistStatus: z4.enum(["ok", "feil"]).nullable(),
  sistFeil: z4.string().nullable()
});
var integrasjonsoppsettSchema = z4.object({
  tilgjengelig: z4.boolean(),
  systemer: z4.array(integrasjonssystemSchema)
});

// src/schemas/klientfeil.ts
import { z as z5 } from "zod";
var klientfeilSchema = z5.object({
  /**
   * Feilens egen melding, kuttet. Lang nok til å kjenne igjen en `TypeError`,
   * kort nok til at ingen kan fylle loggen med én forespørsel.
   */
  melding: z5.string().trim().min(1).max(300),
  /**
   * React sin komponentstabel — hvilke komponenter som lå over feilen.
   * Valgfri, fordi den bare finnes i utviklingsbygg med lesbare navn.
   */
  komponentstabel: z5.string().max(4e3).optional(),
  /** Hvilken skjerm brukeren sto på. Ofte nok alene til å finne feilen. */
  sti: z5.string().trim().max(500)
});
var klientfeilSvarSchema = z5.object({ sporingsId: z5.string() });

// src/schemas/dimensjoner.ts
import { z as z6 } from "zod";

// src/domain/import/dimensjon.ts
var DIMENSJONER = ["ansvar", "tjeneste", "prosjekt", "avdeling"];
var DIMENSJONSNAVN = {
  ansvar: "Ansvar",
  tjeneste: "Tjeneste",
  prosjekt: "Prosjekt",
  avdeling: "Avdeling"
};
function erDimensjon(verdi) {
  return DIMENSJONER.includes(verdi);
}
function normaliserDimensjonsverdi(verdi) {
  const rensket = verdi.trim().toUpperCase();
  if (!/^\d+$/.test(rensket)) return rensket;
  return rensket.replace(/^0+(?=\d)/, "");
}
function dimensjonsnokkel(dimensjon, verdi) {
  return `${dimensjon}:${normaliserDimensjonsverdi(verdi)}`;
}
function finnVirksomhet(verdier, oppslag) {
  const kandidater = [];
  const provde = [];
  for (const dimensjon of DIMENSJONER) {
    const verdi = verdier[dimensjon];
    if (verdi === void 0 || verdi.trim() === "") continue;
    provde.push(dimensjon);
    const virksomhetId = oppslag.get(dimensjonsnokkel(dimensjon, verdi));
    if (virksomhetId !== void 0) kandidater.push({ dimensjon, virksomhetId });
  }
  const [forste] = kandidater;
  if (forste === void 0) return { utfall: "ingen", provde };
  const uenige = kandidater.some((k2) => k2.virksomhetId !== forste.virksomhetId);
  if (uenige) return { utfall: "konflikt", kandidater };
  return { utfall: "treff", virksomhetId: forste.virksomhetId, dimensjon: forste.dimensjon };
}

// src/schemas/dimensjoner.ts
var dimensjonsverdiSchema = z6.string().trim().min(1, "Koden kan ikke v\xE6re tom.").max(60);
var nyDimensjonskoblingSchema = z6.object({
  dimensjon: z6.enum(DIMENSJONER),
  verdi: dimensjonsverdiSchema,
  virksomhetId: uuidSchema
});
var endreDimensjonskoblingSchema = z6.object({ virksomhetId: uuidSchema });
var dimensjonskoblingSchema = z6.object({
  id: uuidSchema,
  dimensjon: z6.enum(DIMENSJONER),
  /** Slik kommunen skrev den. */
  verdi: z6.string(),
  /** Slik oppslaget treffer den. Vises når de to er ulike, så «04100 leses som
   *  4100» er synlig i stedet for å være en overraskelse. */
  verdiNokkel: z6.string(),
  virksomhetId: uuidSchema,
  virksomhetNavn: z6.string()
});

// src/domain/kpi/status.ts
var RETNINGER = ["hoyere_er_bedre", "lavere_er_bedre"];
var STATUSER = ["god", "varsel", "kritisk", "ingen_vurdering"];
var AGGREGERINGER = ["sum", "vektet_snitt", "ingen"];
function terskelErGyldig({ retning, maal, terskel }) {
  if (maal === null || terskel === null) return true;
  return retning === "lavere_er_bedre" ? terskel >= maal : terskel <= maal;
}
function minstLikeBra(verdi, referanse, retning) {
  return retning === "lavere_er_bedre" ? verdi <= referanse : verdi >= referanse;
}
function beregnStatus(verdi, maalsetting) {
  const { retning, maal, terskel } = maalsetting;
  if (verdi === null || maal === null) return "ingen_vurdering";
  if (minstLikeBra(verdi, maal, retning)) return "god";
  if (terskel === null) return "varsel";
  return minstLikeBra(verdi, terskel, retning) ? "varsel" : "kritisk";
}
var UTVIKLINGER = ["bedre", "uendret", "verre", "ukjent"];
function beregnEndring(verdi, forrige, retning) {
  if (verdi === null || forrige === null) return { differanse: null, utvikling: "ukjent" };
  const differanse = verdi - forrige;
  if (differanse === 0n) return { differanse, utvikling: "uendret" };
  const stiger = differanse > 0n;
  const bedre = retning === "hoyere_er_bedre" ? stiger : !stiger;
  return { differanse, utvikling: bedre ? "bedre" : "verre" };
}
function aggreger(verdier, vekter, aggregering, enhet) {
  if (aggregering === "ingen" || verdier.length === 0) return null;
  if (aggregering === "sum") {
    if (!kanSummeres(enhet)) return null;
    return verdier.reduce((sum2, v) => sum2 + v, 0n);
  }
  if (vekter.length !== verdier.length) {
    throw new RangeError(
      `Vektet snitt trenger like mange vekter (${vekter.length}) som verdier (${verdier.length}).`
    );
  }
  const vektsum = vekter.reduce((sum2, v) => sum2 + v, 0n);
  if (vektsum === 0n) return null;
  const produkt = verdier.reduce((sum2, v, i) => sum2 + v * (vekter[i] ?? 0n), 0n);
  const halv = vektsum / 2n;
  return produkt >= 0n ? (produkt + halv) / vektsum : -((-produkt + halv) / vektsum);
}

// src/domain/kpi/kontovalg.ts
var UTVALGSROLLER = ["omfang", "teller", "nevner"];
function losOppKontovalg(plan, valg) {
  const underAv = new Map(
    plan.artsgrupper.map((a) => [a.id, a.underkontoer.map((u2) => u2.id)])
  );
  const valgte = /* @__PURE__ */ new Set();
  for (const v of valg) {
    if (v.niva === "underkonto") {
      valgte.add(v.id);
      continue;
    }
    for (const id of underAv.get(v.id) ?? []) valgte.add(id);
  }
  return valgte;
}
function losOppStandard(plan, standard) {
  if (standard.slag === "ingen") return /* @__PURE__ */ new Set();
  const valgte = /* @__PURE__ */ new Set();
  for (const gruppe of plan.artsgrupper) {
    if (standard.slag === "kategorier" && !standard.kategorier.includes(gruppe.kategori)) continue;
    for (const konto of gruppe.underkontoer) valgte.add(konto.id);
  }
  return valgte;
}
function losOppRolle(plan, valg, standard) {
  if (valg === void 0 || valg.length === 0) return losOppStandard(plan, standard);
  return losOppKontovalg(plan, valg);
}
function summerValgte(belopPerUnderkonto, valgte) {
  let sum2 = 0n;
  for (const id of valgte) sum2 += belopPerUnderkonto.get(id) ?? 0n;
  return sum2;
}
function foreslaKontoer(plan, ord, kategorier) {
  if (ord.length === 0) return [];
  const folde = ord.map(foldTekst).filter((o) => o !== "");
  const treffer = (konto) => {
    const tekst = foldTekst(`${konto.kontonummer ?? ""} ${konto.kontonavn}`);
    return folde.some((o) => tekst.includes(o));
  };
  const forslag = [];
  for (const gruppe of plan.artsgrupper) {
    if (!kategorier.includes(gruppe.kategori)) continue;
    const traff = gruppe.underkontoer.filter(treffer);
    if (traff.length === 0) continue;
    if (traff.length === gruppe.underkontoer.length) {
      forslag.push({ niva: "artsgruppe", id: gruppe.id });
      continue;
    }
    for (const konto of traff) forslag.push({ niva: "underkonto", id: konto.id });
  }
  return forslag;
}

// src/domain/kpi/typer.ts
var KPI_KATEGORIER = ["okonomi", "bemanning", "produksjon", "annet"];
var sumFor = (tabell, utvalg, rolle) => summerValgte(tabell, utvalg.get(rolle) ?? /* @__PURE__ */ new Set());
var tom = (utvalg, rolle) => (utvalg.get(rolle)?.size ?? 0) === 0;
var OMFANG_ALT = {
  rolle: "omfang",
  navn: "Kontoer som regnes med",
  forklaring: "Standard er hele kontoplanen. Snevres den inn, m\xE5ler kortet bare den delen av driften \u2014 nyttig for \xE5 f\xF8lge ett ansvarsomr\xE5de, men da er tallet ikke lenger virksomhetens samlede avvik.",
  standard: { slag: "alle" },
  forslagsord: [],
  forslagskategorier: []
};
var NEVNER_ALL_LONN = {
  rolle: "nevner",
  navn: "Grunnlaget andelen m\xE5les av",
  forklaring: "Standard er alle kontoer i l\xF8nnskategorien. Nevneren er HELE l\xF8nnen, ikke l\xF8nn uten den delen telleren m\xE5ler \u2014 er en firedel av l\xF8nnen vikar, er andelen 25 % og ikke 33 %.",
  standard: { slag: "kategorier", kategorier: ["lonn"] },
  forslagsord: [],
  forslagskategorier: []
};
var KPI_TYPER = [
  {
    nokkel: "avvik_prosent",
    navn: "Avvik mot budsjett",
    forklaring: "Prognose for hele \xE5ret mot vedtatt budsjett, i prosent. Positivt betyr over budsjett \u2014 samme fortegnsregel som overalt ellers i systemet.",
    kategori: "okonomi",
    enhet: "prosent",
    retning: "lavere_er_bedre",
    aggregering: "ingen",
    foreslattMaal: "0",
    foreslattTerskel: "3",
    utvalg: [OMFANG_ALT],
    beregn: ({ perUnderkonto }, utvalg) => {
      const b = sumFor(perUnderkonto.budsjett, utvalg, "omfang");
      const p2 = sumFor(perUnderkonto.prognose, utvalg, "omfang");
      return andelIProsent(p2 - b, b < 0n ? -b : b);
    }
  },
  {
    nokkel: "avvik_kroner",
    navn: "Avvik mot budsjett i kroner",
    forklaring: "Samme avvik som over, i kroner. Det tallet som faktisk m\xE5 dekkes inn.",
    kategori: "okonomi",
    enhet: "kroner",
    retning: "lavere_er_bedre",
    aggregering: "sum",
    foreslattMaal: "0",
    foreslattTerskel: null,
    utvalg: [OMFANG_ALT],
    // Null uten budsjett, av samme grunn som forbruksgrad er null uten regnskap:
    // et avvik fra ingenting er ikke et avvik, det er bare totalen. Uten dette
    // ville et år uten budsjett gitt et selvsikkert tall — og en trend mot fjoråret
    // som utelukkende måler at fjoråret manglet budsjett.
    beregn: ({ perUnderkonto }, utvalg) => {
      const b = sumFor(perUnderkonto.budsjett, utvalg, "omfang");
      if (b === 0n) return null;
      return maaltallFraBelop(sumFor(perUnderkonto.prognose, utvalg, "omfang") - b);
    }
  },
  {
    nokkel: "forbruksgrad",
    navn: "Forbruksgrad",
    forklaring: "Bokf\xF8rt regnskap mot budsjett for samme periode. Over 100 % betyr at perioden har kostet mer enn den var budsjettert til. M\xE5les ikke for m\xE5neder uten regnskap.",
    kategori: "okonomi",
    enhet: "prosent",
    retning: "lavere_er_bedre",
    aggregering: "ingen",
    foreslattMaal: "100",
    foreslattTerskel: "105",
    utvalg: [OMFANG_ALT],
    beregn: ({ perUnderkonto, harRegnskap }, utvalg) => harRegnskap ? andelIProsent(
      sumFor(perUnderkonto.regnskap, utvalg, "omfang"),
      sumFor(perUnderkonto.budsjett, utvalg, "omfang")
    ) : null
  },
  {
    nokkel: "lonnsandel",
    navn: "L\xF8nnsandel",
    forklaring: "L\xF8nn som andel av samlede utgifter, alts\xE5 l\xF8nn delt p\xE5 l\xF8nn pluss \xF8vrige utgifter. Inntekter holdes utenfor, ellers ville andelen svinget med refusjonene.",
    kategori: "okonomi",
    enhet: "prosent",
    retning: "lavere_er_bedre",
    aggregering: "vektet_snitt",
    foreslattMaal: "70",
    foreslattTerskel: "75",
    utvalg: [
      {
        rolle: "teller",
        navn: "L\xF8nnskontoer",
        forklaring: "Standard er alle kontoer i l\xF8nnskategorien.",
        standard: { slag: "kategorier", kategorier: ["lonn"] },
        forslagsord: [],
        forslagskategorier: []
      },
      {
        rolle: "nevner",
        navn: "Grunnlaget l\xF8nnen m\xE5les av",
        forklaring: "Standard er l\xF8nn pluss \xF8vrige utgifter. Inntekter holdes utenfor \u2014 ellers ville andelen svingt med refusjonene i stedet for med l\xF8nnen.",
        standard: { slag: "kategorier", kategorier: ["lonn", "utgifter"] },
        forslagsord: [],
        forslagskategorier: []
      }
    ],
    beregn: ({ perUnderkonto }, utvalg) => andelIProsent(
      sumFor(perUnderkonto.prognose, utvalg, "teller"),
      sumFor(perUnderkonto.prognose, utvalg, "nevner")
    )
  },
  {
    nokkel: "sykefravar_totalt",
    navn: "Sykefrav\xE6r totalt",
    forklaring: "Samlet sykefrav\xE6r i prosent av avtalte dagsverk. Registreres manuelt til bemanningsmodulen er p\xE5 plass.",
    kategori: "bemanning",
    enhet: "prosent",
    retning: "lavere_er_bedre",
    aggregering: "vektet_snitt",
    foreslattMaal: "8.5",
    foreslattTerskel: "9.5",
    utvalg: [],
    // Fraværsdagsverk av mulige dagsverk — samme brøk Bemanning viser, av
    // samme tall, så de to skjermene ikke kan svare forskjellig.
    beregn: ({ bemanning }) => bemanning === null ? null : andelIProsent(bemanning.fravarsdagsverk, bemanning.muligeDagsverk)
  },
  {
    nokkel: "sykefravar_korttid",
    navn: "Korttidsfrav\xE6r",
    forklaring: "Egenmeldt og legemeldt frav\xE6r til og med 16 dager, av mulige dagsverk. Hentes fra Bemanning. Skilles ut fordi det er den delen virksomheten selv kan p\xE5virke.",
    kategori: "bemanning",
    enhet: "prosent",
    retning: "lavere_er_bedre",
    aggregering: "vektet_snitt",
    foreslattMaal: "2.5",
    foreslattTerskel: "3.5",
    utvalg: [],
    beregn: ({ bemanning }) => bemanning === null ? null : andelIProsent(bemanning.korttidsdagsverk, bemanning.muligeDagsverk)
  },
  {
    nokkel: "vikarandel",
    navn: "Vikarandel av l\xF8nn",
    forklaring: "Vikarutgifter som andel av samlet l\xF8nn. Regnes fra kontoplanen: de kontoene virksomheten f\xF8rer vikar p\xE5, delt p\xE5 hele l\xF8nnskategorien.",
    kategori: "bemanning",
    enhet: "prosent",
    retning: "lavere_er_bedre",
    aggregering: "vektet_snitt",
    foreslattMaal: "8",
    foreslattTerskel: "12",
    utvalg: [
      {
        rolle: "teller",
        navn: "Vikarkontoer",
        forklaring: "Kontoene vikarutgifter f\xF8res p\xE5. Velg gjerne hele artsgruppen framfor kontoene enkeltvis \u2014 da f\xF8lger nye kontoer under den med av seg selv ved neste import.",
        standard: { slag: "ingen" },
        forslagsord: ["vikar"],
        forslagskategorier: ["lonn"]
      },
      NEVNER_ALL_LONN
    ],
    // Ikke målbar uten et utvalg. Et tomt utvalg som ga 0 % ville sett ut som
    // en virksomhet helt uten bruk av dette — et tall verdt en gratulasjon den
    // ikke har fortjent.
    beregn: ({ perUnderkonto }, utvalg) => tom(utvalg, "teller") ? null : andelIProsent(
      sumFor(perUnderkonto.prognose, utvalg, "teller"),
      sumFor(perUnderkonto.prognose, utvalg, "nevner")
    )
  },
  {
    nokkel: "overtidsandel",
    navn: "Overtidsandel av l\xF8nn",
    forklaring: "Overtid som andel av samlet l\xF8nn. Regnes fra kontoplanen, p\xE5 samme m\xE5te som vikarandelen.",
    kategori: "bemanning",
    enhet: "prosent",
    retning: "lavere_er_bedre",
    aggregering: "vektet_snitt",
    foreslattMaal: "2",
    foreslattTerskel: "4",
    utvalg: [
      {
        rolle: "teller",
        navn: "Overtidskontoer",
        forklaring: "Kontoene overtid f\xF8res p\xE5. Velg gjerne hele artsgruppen framfor kontoene enkeltvis \u2014 da f\xF8lger nye kontoer under den med av seg selv ved neste import.",
        standard: { slag: "ingen" },
        forslagsord: ["overtid"],
        forslagskategorier: ["lonn"]
      },
      NEVNER_ALL_LONN
    ],
    // Ikke målbar uten et utvalg. Et tomt utvalg som ga 0 % ville sett ut som
    // en virksomhet helt uten bruk av dette — et tall verdt en gratulasjon den
    // ikke har fortjent.
    beregn: ({ perUnderkonto }, utvalg) => tom(utvalg, "teller") ? null : andelIProsent(
      sumFor(perUnderkonto.prognose, utvalg, "teller"),
      sumFor(perUnderkonto.prognose, utvalg, "nevner")
    )
  },
  {
    nokkel: "arsverk",
    navn: "\xC5rsverk i turnus",
    forklaring: "Besatte \xE5rsverk i turnus, snittet over de registrerte m\xE5nedene. Hentes fra Bemanning. F\xF8lges uten m\xE5l \u2014 tallet er grunnlag for de andre, ikke noe som skal opp eller ned i seg selv.",
    kategori: "bemanning",
    enhet: "arsverk",
    retning: "hoyere_er_bedre",
    aggregering: "sum",
    foreslattMaal: null,
    foreslattTerskel: null,
    utvalg: [],
    // Allerede snittet i grunnlaget. Å summere årsverk over tolv måneder gir
    // et tall tolv ganger for stort — stort nok til å se ut som en skrivefeil,
    // men ikke alltid stort nok til at noen oppdager det.
    beregn: ({ bemanning }) => bemanning?.arsverk ?? null
  },
  {
    nokkel: "belegg",
    navn: "Belegg",
    forklaring: "Andel av plassene som er i bruk. Den ene i settet der h\xF8yere er bedre \u2014 nyttig p\xE5minnelse om at retning er en egenskap ved KPI-en, ikke ved tallet.",
    kategori: "produksjon",
    enhet: "prosent",
    retning: "hoyere_er_bedre",
    aggregering: "vektet_snitt",
    foreslattMaal: "98",
    foreslattTerskel: "95",
    utvalg: [],
    beregn: null
  }
];
var ETTER_NOKKEL = new Map(KPI_TYPER.map((t2) => [t2.nokkel, t2]));
function finnKpiType(nokkel2) {
  return ETTER_NOKKEL.get(nokkel2);
}
function erBeregnet(type) {
  return type.beregn !== null;
}

// src/domain/import/hierarki.ts
var INNRYKK = /^[-.\s]*/;
function innrykkbredde(ra) {
  return (INNRYKK.exec(ra)?.[0] ?? "").length;
}
function kodeOgNavn(ra) {
  const renset = ra.replace(INNRYKK, "").trim();
  const treff = /^(\d+)\s*(.*)$/.exec(renset);
  if (!treff) return { kode: "", navn: renset };
  return { kode: treff[1] ?? "", navn: (treff[2] ?? "").trim() };
}
function rang(verdier, verdi) {
  const distinkte = [...new Set(verdier)].sort((a, b) => a - b);
  return distinkte.indexOf(verdi);
}
function utledNivaer(rader) {
  const oppdelt = rader.map((r) => ({ ...r, ...kodeOgNavn(r.ra), innrykk: innrykkbredde(r.ra) }));
  const innrykk = oppdelt.map((r) => r.innrykk);
  const brukInnrykk = new Set(innrykk).size > 1;
  const lengder = oppdelt.filter((r) => r.kode !== "").map((r) => r.kode.length);
  const medDybde = oppdelt.map((r) => ({
    ...r,
    dybde: brukInnrykk ? rang(innrykk, r.innrykk) : r.kode === "" ? 0 : rang(lengder, r.kode.length)
  }));
  return medDybde.map((r, i) => {
    const neste = medDybde[i + 1];
    return {
      linje: r.linje,
      ra: r.ra,
      dybde: r.dybde,
      kode: r.kode,
      navn: r.navn,
      // A row has children exactly when the row immediately below it is deeper.
      // That is what separates a subtotal from an account, without needing to
      // know how many digits either of them has.
      erBlad: neste === void 0 || neste.dybde <= r.dybde
    };
  });
}
function finnForelder(rader, indeks) {
  const meg = rader[indeks];
  if (!meg) return null;
  for (let i = indeks - 1; i >= 0; i -= 1) {
    const kandidat = rader[i];
    if (kandidat && kandidat.dybde < meg.dybde) return kandidat;
  }
  return null;
}
function finnForfedre(rader, indeks) {
  const forfedre = [];
  let gjeldende = indeks;
  for (; ; ) {
    const forelder = finnForelder(rader, gjeldende);
    if (forelder === null) break;
    forfedre.unshift(forelder);
    gjeldende = rader.indexOf(forelder);
    if (gjeldende < 0) break;
  }
  return forfedre;
}

// src/domain/import/analyse.ts
var STANDARDVALG = { budsjettkilde: "budsjett" };
function erFeil(resultat) {
  return "feil" in resultat;
}
function celle(rad, indeks) {
  if (rad === void 0 || indeks === void 0 || indeks < 0) return "";
  return (rad[indeks] ?? "").trim();
}
function raCelle(rad, indeks) {
  return rad?.[indeks] ?? "";
}
function finnKolonner3(overskrift, etikett) {
  return MANEDSETIKETTER.map((m) => {
    const i = overskrift.indexOf(etikett(m));
    return i >= 0 ? i : null;
  });
}
function finnSisteManed(kolonner, datarader) {
  let siste = -1;
  kolonner.forEach((kolonne, i) => {
    if (kolonne === null) return;
    const harTall = datarader.some((rad) => {
      const verdi = tolkNorsktall(celle(rad, kolonne));
      return verdi !== null && verdi !== 0n;
    });
    if (harTall) siste = i;
  });
  return siste;
}
function lesManeder(rad, kolonner, tilOgMed) {
  const maneder = /* @__PURE__ */ new Map();
  for (let i = 0; i <= tilOgMed; i += 1) {
    const kolonne = kolonner[i];
    if (kolonne === null || kolonne === void 0) continue;
    maneder.set(manedFraIndeks(i), tolkNorsktall(celle(rad, kolonne)) ?? 0n);
  }
  return maneder;
}
function tolkManedsmal(rutenett, valg) {
  const overskrift = (rutenett[0] ?? []).map((h) => h.trim());
  if (overskrift[0] !== "Kontonr") return null;
  const navnKolonne = overskrift.indexOf("Kontonavn");
  const regnskapKolonner = finnKolonner3(overskrift, (m) => m);
  if (regnskapKolonner.some((k2) => k2 === null)) {
    return {
      feil: "M\xE5nedsmalen m\xE5 ha alle tolv m\xE5nedskolonnene (Jan til Des). Last ned malen p\xE5 nytt hvis du er usikker."
    };
  }
  const datarader = rutenett.slice(1).filter((r) => celle(r, 0) !== "");
  const siste = finnSisteManed(regnskapKolonner, datarader);
  if (siste < 0) {
    return { feil: "Fant ingen m\xE5neder med tall i filen. Er alle bel\xF8pene null?" };
  }
  const rader = [];
  const ignorerte = [];
  datarader.forEach((rad, i) => {
    const kontonummer = celle(rad, 0);
    if (!/^\d+$/.test(kontonummer)) {
      ignorerte.push({
        linje: i + 2,
        innhold: kontonummer,
        arsak: "Kontonummeret er ikke et tall."
      });
      return;
    }
    rader.push({
      kontonummer,
      navn: celle(rad, navnKolonne >= 0 ? navnKolonne : void 0),
      regnskap: lesManeder(rad, regnskapKolonner, siste),
      // The template carries actuals only; it has nowhere to put a budget.
      budsjett: /* @__PURE__ */ new Map(),
      foreslattArtsgruppeKontonummer: null,
      foreslattArtsgruppeNavn: null,
      foreslattHovedkontoKontonummer: null,
      foreslattHovedkontoNavn: null
    });
  });
  return {
    format: "manedsmal",
    sisteManedMedRegnskap: manedFraIndeks(siste),
    sisteManedMedBudsjett: null,
    budsjettkilde: valg.budsjettkilde,
    rader,
    ignorerteRader: ignorerte
  };
}
function tolkErpRapport(rutenett, valg) {
  const overskrift = (rutenett[0] ?? []).map((h) => h.trim());
  if (overskrift[0] !== "Konto") return null;
  const regnskapKolonner = finnKolonner3(overskrift, (m) => `${m} Regnskap`);
  const budsjettKolonner = finnKolonner3(
    overskrift,
    (m) => valg.budsjettkilde === "opprinnelig" ? `${m} Opprinnelig budsjett` : `${m} Budsjett`
  );
  const harRegnskapkolonner = regnskapKolonner.some((k2) => k2 !== null);
  const harBudsjettkolonner = budsjettKolonner.some((k2) => k2 !== null);
  if (!harRegnskapkolonner && !harBudsjettkolonner) {
    return {
      feil: "Fant verken \xAB<m\xE5ned> Regnskap\xBB- eller \xAB<m\xE5ned> Budsjett\xBB-kolonner i rapporten. Eksporter rapporten med m\xE5nedskolonner, eller bruk m\xE5nedsmalen i stedet."
    };
  }
  const datarader = rutenett.slice(1).filter((r) => celle(r, 0) !== "");
  const sisteRegnskap = finnSisteManed(regnskapKolonner, datarader);
  const sisteBudsjett = finnSisteManed(budsjettKolonner, datarader);
  if (sisteRegnskap < 0 && sisteBudsjett < 0) {
    return { feil: "Fant ingen m\xE5neder med tall i rapporten. Er alle bel\xF8pene null?" };
  }
  const rader = [];
  const ignorerte = [];
  const nivaer = utledNivaer(datarader.map((rad, i) => ({ linje: i + 2, ra: raCelle(rad, 0) })));
  nivaer.forEach((niva, i) => {
    if (!niva.erBlad) return;
    if (niva.kode === "") {
      ignorerte.push({
        linje: niva.linje,
        innhold: niva.ra.trim(),
        arsak: "Ingen kontokode i Konto-kolonnen."
      });
      return;
    }
    const rad = datarader[i];
    if (rad === void 0) return;
    const forfedre = finnForfedre(nivaer, i);
    const forelder = forfedre[forfedre.length - 1] ?? null;
    const besteforelder = forfedre.length >= 2 ? forfedre[forfedre.length - 2] ?? null : null;
    rader.push({
      kontonummer: niva.kode,
      navn: niva.navn,
      regnskap: sisteRegnskap >= 0 ? lesManeder(rad, regnskapKolonner, sisteRegnskap) : /* @__PURE__ */ new Map(),
      budsjett: sisteBudsjett >= 0 ? lesManeder(rad, budsjettKolonner, sisteBudsjett) : /* @__PURE__ */ new Map(),
      // The group the FILE puts this account in. Matched against the kommune's
      // real artsgrupper later; if it matches nothing, the user picks.
      foreslattArtsgruppeKontonummer: forelder?.kode === "" ? null : forelder?.kode ?? null,
      foreslattArtsgruppeNavn: forelder?.navn ?? null,
      foreslattHovedkontoKontonummer: besteforelder?.kode === "" ? null : besteforelder?.kode ?? null,
      foreslattHovedkontoNavn: besteforelder?.navn ?? null
    });
  });
  return {
    format: "erp_rapport",
    sisteManedMedRegnskap: sisteRegnskap >= 0 ? manedFraIndeks(sisteRegnskap) : null,
    sisteManedMedBudsjett: sisteBudsjett >= 0 ? manedFraIndeks(sisteBudsjett) : null,
    budsjettkilde: valg.budsjettkilde,
    rader,
    ignorerteRader: ignorerte
  };
}
function analyserRutenett(rutenett, valg = STANDARDVALG) {
  if (rutenett.length === 0) return { feil: "Filen er tom." };
  const manedsmal = tolkManedsmal(rutenett, valg);
  if (manedsmal !== null) return manedsmal;
  const erp = tolkErpRapport(rutenett, valg);
  if (erp !== null) return erp;
  return {
    feil: "Kjente ikke igjen formatet. Filen m\xE5 enten v\xE6re m\xE5nedsmalen (kolonnene Kontonr, Kontonavn, Jan \u2026 Des) eller en m\xE5nedsrapport fra fagsystemet (en Konto-kolonne med \xAB<m\xE5ned> Regnskap\xBB-kolonner)."
  };
}

// src/domain/import/kontroll.ts
function celle2(rad, indeks) {
  if (rad === void 0 || indeks === null || indeks < 0) return "";
  return (rad[indeks] ?? "").trim();
}
function kolonneIndeks(overskrift, navn2) {
  const i = overskrift.indexOf(navn2);
  return i >= 0 ? i : null;
}
function manedskolonner(overskrift, suffiks) {
  return MANEDSETIKETTER.map((m) => kolonneIndeks(overskrift, `${m} ${suffiks}`));
}
function les(rad, kolonner) {
  return summerBelop(kolonner.map((k2) => tolkNorsktall(celle2(rad, k2)) ?? 0n));
}
function sjekkArssum(rutenett, navn2, budsjettkilde) {
  const overskrift = (rutenett[0] ?? []).map((h) => h.trim());
  const suffiks = navn2 === "Budsjett" && budsjettkilde === "opprinnelig" ? "Opprinnelig budsjett" : navn2;
  const arKolonne = kolonneIndeks(overskrift, `\xC5r ${suffiks}`);
  if (arKolonne === null) return null;
  const kolonner = manedskolonner(overskrift, suffiks);
  if (kolonner.every((k2) => k2 === null)) return null;
  const avvik = [];
  let sjekket = 0;
  for (const rad of rutenett.slice(1)) {
    const konto = celle2(rad, 0);
    if (konto === "") continue;
    const ar = tolkNorsktall(celle2(rad, arKolonne));
    if (ar === null) continue;
    sjekket += 1;
    const sum2 = les(rad, kolonner);
    if (sum2 !== ar) {
      avvik.push(`${konto.trim()}: m\xE5neder ${formaterBelop(sum2)}, men \xAB\xC5r ${suffiks}\xBB sier ${formaterBelop(ar)}`);
    }
  }
  if (sjekket === 0) return null;
  if (avvik.length === 0) {
    return {
      alvor: "ok",
      tittel: `${suffiks}: m\xE5nedene stemmer med \xE5rskolonnen`,
      detalj: `Alle ${sjekket} rader summerer n\xF8yaktig til sin egen \xAB\xC5r ${suffiks}\xBB-kolonne. Da er det de riktige m\xE5nedskolonnene som er lest.`
    };
  }
  return {
    alvor: "feil",
    tittel: `${suffiks}: ${avvik.length} av ${sjekket} rader stemmer ikke med \xE5rskolonnen`,
    detalj: "De tolv m\xE5nedskolonnene summerer ikke til det filen selv oppgir som \xE5ret. Det betyr som regel at en akkumulert kolonne (\xABhi\xE5.\xBB) er lest som en m\xE5ned, eller at filen er redigert slik at radene ikke lenger henger sammen. Ikke importer f\xF8r dette er avklart.",
    eksempler: avvik.slice(0, 8)
  };
}
function sjekkGruppesummer(rutenett, analyse) {
  if (analyse.format !== "erp_rapport") return null;
  const overskrift = (rutenett[0] ?? []).map((h) => h.trim());
  const kolonner = manedskolonner(overskrift, "Regnskap");
  if (kolonner.every((k2) => k2 === null)) return null;
  const datarader = rutenett.slice(1).filter((r) => celle2(r, 0) !== "");
  const nivaer = utledNivaer(datarader.map((rad, i) => ({ linje: i + 2, ra: rad[0] ?? "" })));
  const avvik = [];
  let sjekket = 0;
  nivaer.forEach((niva, i) => {
    if (niva.erBlad) return;
    let sum2 = 0n;
    for (let j = i + 1; j < nivaer.length; j += 1) {
      const under = nivaer[j];
      if (under === void 0 || under.dybde <= niva.dybde) break;
      if (!under.erBlad) continue;
      const rad = datarader[j];
      if (rad !== void 0) sum2 += les(rad, kolonner);
    }
    const egen = les(datarader[i] ?? [], kolonner);
    sjekket += 1;
    if (sum2 !== egen) {
      avvik.push(`${niva.kode} ${niva.navn}: egen linje ${formaterBelop(egen)}, kontoene under ${formaterBelop(sum2)}`);
    }
  });
  if (sjekket === 0) return null;
  if (avvik.length === 0) {
    return {
      alvor: "ok",
      tittel: `Niv\xE5ene henger sammen (${sjekket} sumlinjer)`,
      detalj: "Hver sumlinje er n\xF8yaktig summen av kontoene under den. Niv\xE5ene i filen er alts\xE5 lest riktig, og ingen sumlinje blir importert som om den var en konto."
    };
  }
  return {
    alvor: "advarsel",
    tittel: `${avvik.length} av ${sjekket} sumlinjer stemmer ikke med kontoene under`,
    detalj: "Sumlinjen og kontoene under den er ikke like. Det er normalt hvis rapporten er filtrert, men det kan ogs\xE5 bety at innrykket i Konto-kolonnen er g\xE5tt tapt \u2014 for eksempel ved redigering i Excel \u2014 slik at niv\xE5ene leses feil. Sjekk noen av linjene under f\xF8r du importerer.",
    eksempler: avvik.slice(0, 8)
  };
}
function sjekkDubletter(analyse) {
  const sett = /* @__PURE__ */ new Map();
  for (const rad of analyse.rader) sett.set(rad.kontonummer, (sett.get(rad.kontonummer) ?? 0) + 1);
  const doble = [...sett].filter(([, n]) => n > 1);
  if (doble.length === 0) return null;
  return {
    alvor: "feil",
    tittel: `${doble.length} kontonummer finnes flere ganger`,
    detalj: "Importen skriver \xE9n linje per konto og m\xE5ned, s\xE5 den siste raden vinner og de andre forsvinner uten spor. Sl\xE5 sammen radene i filen f\xF8r du importerer.",
    eksempler: doble.map(([nr, n]) => `${nr}: ${n} rader`).slice(0, 8)
  };
}
function ubrukteKolonner(rutenett, analyse) {
  const overskrift = (rutenett[0] ?? []).map((h) => h.trim());
  if (overskrift.length === 0) return null;
  const suffiks = analyse.budsjettkilde === "opprinnelig" ? "Opprinnelig budsjett" : "Budsjett";
  const brukt = /* @__PURE__ */ new Set();
  for (const m of MANEDSETIKETTER) {
    brukt.add(`${m} Regnskap`);
    brukt.add(`${m} ${suffiks}`);
  }
  brukt.add(overskrift[0] ?? "");
  const ubrukt = overskrift.filter((h) => h !== "" && !brukt.has(h));
  if (ubrukt.length === 0) return null;
  return {
    alvor: "info",
    tittel: `${ubrukt.length} kolonner leses ikke`,
    detalj: "Disse kolonnene finnes i filen, men importen r\xF8rer dem ikke. S\xE6rlig \xABhi\xE5.\xBB-kolonnene er akkumulerte tall som ville bl\xE5st opp hver m\xE5ned med alt som kom f\xF8r, s\xE5 de skal ikke leses.",
    eksempler: [...new Set(ubrukt)].slice(0, 24)
  };
}
function sjekkManglendeBudsjett(analyse) {
  const utenBudsjett = analyse.rader.filter(
    // `size > 0` var ikke nok: en konto kan ha tolv månedsverdier som alle er
    // null, og da er «har regnskap, men ingen budsjett» feil på begge halvdeler.
    // I en ekte kommunefil var 15 av 54 treff slike rene nullrader — støy som
    // gjør at man slutter å lese advarselen.
    (r) => [...r.regnskap.values()].some((v) => v !== 0n) && [...r.budsjett.values()].every((b) => b === 0n)
  );
  if (utenBudsjett.length === 0) return null;
  return {
    alvor: "advarsel",
    tittel: `${utenBudsjett.length} kontoer har regnskap, men ingen budsjett`,
    detalj: "De vil vises som rene avvik i Finansiell oversikt, fordi det ikke finnes noe \xE5 m\xE5le dem mot. Det kan v\xE6re riktig \u2014 en konto det ikke er budsjettert p\xE5 \u2014 men er ogs\xE5 det man ser n\xE5r feil budsjettkolonne er valgt. Pr\xF8v \xE5 bytte mellom \xABBudsjett\xBB og \xABOpprinnelig budsjett\xBB og se om tallet endrer seg.",
    eksempler: utenBudsjett.slice(0, 8).map((r) => `${r.kontonummer} ${r.navn}`)
  };
}
function kontroller(rutenett, analyse) {
  const funn = [
    sjekkArssum(rutenett, "Regnskap", analyse.budsjettkilde),
    sjekkArssum(rutenett, "Budsjett", analyse.budsjettkilde),
    sjekkGruppesummer(rutenett, analyse),
    sjekkDubletter(analyse),
    sjekkManglendeBudsjett(analyse),
    ubrukteKolonner(rutenett, analyse)
  ];
  const rangering = { feil: 0, advarsel: 1, ok: 2, info: 3 };
  return funn.filter((f) => f !== null).sort((a, b) => rangering[a.alvor] - rangering[b.alvor]);
}

// src/domain/import/plassering.ts
function summerAr(maneder) {
  return summerBelop(MANEDER.map((m) => maneder.get(m) ?? 0n));
}
function plasser(rader, kontoplan) {
  const underkontoPerNummer = new Map(kontoplan.underkontoer.map((u2) => [u2.kontonummer, u2]));
  const hovedkontoPerNummer = new Map(
    kontoplan.hovedkontoer.filter((h) => h.kontonummer !== null).map((h) => [h.kontonummer, h])
  );
  const artsgruppePerNummer = new Map(
    kontoplan.artsgrupper.filter((a) => a.kontonummer !== null).map((a) => [a.kontonummer, a])
  );
  const sett = /* @__PURE__ */ new Set();
  const duplikater = /* @__PURE__ */ new Set();
  for (const rad of rader) {
    if (sett.has(rad.kontonummer)) duplikater.add(rad.kontonummer);
    sett.add(rad.kontonummer);
  }
  const treff = [];
  const utenTreff = [];
  for (const rad of rader) {
    const kjent = underkontoPerNummer.get(rad.kontonummer);
    if (kjent) {
      treff.push({
        rad,
        underkontoId: kjent.underkontoId,
        arssumRegnskap: summerAr(rad.regnskap),
        arssumBudsjett: summerAr(rad.budsjett)
      });
      continue;
    }
    const foreslatt = rad.foreslattArtsgruppeKontonummer !== null ? artsgruppePerNummer.get(rad.foreslattArtsgruppeKontonummer) : void 0;
    const filHovedkonto = rad.foreslattHovedkontoKontonummer !== null ? hovedkontoPerNummer.get(rad.foreslattHovedkontoKontonummer) : void 0;
    utenTreff.push({
      rad,
      arssumRegnskap: summerAr(rad.regnskap),
      arssumBudsjett: summerAr(rad.budsjett),
      foreslattArtsgruppeId: foreslatt?.artsgruppeId ?? null,
      foreslattArtsgruppeNavn: foreslatt?.navn ?? null,
      // The hovedkonto of the MATCHED artsgruppe when there is one; otherwise
      // the one the file named, so the row always says where it thinks it goes.
      foreslattHovedkontoNavn: foreslatt?.hovedkontoNavn ?? filHovedkonto?.navn ?? null,
      filArtsgruppeKontonummer: rad.foreslattArtsgruppeKontonummer,
      filArtsgruppeNavn: rad.foreslattArtsgruppeNavn,
      filHovedkontoKontonummer: rad.foreslattHovedkontoKontonummer,
      filHovedkontoNavn: rad.foreslattHovedkontoNavn,
      filHovedkontoId: filHovedkonto?.hovedkontoId ?? null
    });
  }
  return { treff, utenTreff, duplikater: [...duplikater] };
}

// src/domain/lonn/fil.ts
var FELTNAVN2 = {
  ansattnummer: ["ansattnummer", "ansatt nr", "ansattnr", "ansatt id", "personalnummer"],
  navn: ["navn", "ansatt", "ansattnavn"],
  eksternId: [
    "arbeidsforhold",
    "arbeidsforholdid",
    "stillingsid",
    "forholdsid",
    "stillingsforhold"
  ],
  gruppekode: ["stillingskode", "kode", "gruppekode"],
  stillingsprosent: ["stillingsprosent", "stilling prosent", "prosent", "stillingsstorrelse"],
  arslonnFullStilling: ["arslonn", "arslonn full stilling", "brutto arslonn", "grunnlonn", "lonn"],
  fraDato: ["fra dato", "fradato", "startdato", "ansatt fra", "tiltradt"],
  tilDato: ["til dato", "tildato", "sluttdato", "ansatt til", "fratradt"]
};
var PAAKREVDE = [
  "ansattnummer",
  "navn",
  "gruppekode",
  "stillingsprosent",
  "arslonnFullStilling",
  "fraDato"
];
function finnLonnskolonner(overskrift) {
  const felt2 = {};
  const ukjente = [];
  overskrift.forEach((r\u00E5, i) => {
    const n = normaliser(r\u00E5);
    if (n === "") return;
    for (const [f, navnene] of Object.entries(FELTNAVN2)) {
      if (felt2[f] === void 0 && navnene.includes(n)) {
        felt2[f] = i;
        return;
      }
    }
    ukjente.push(r\u00E5.trim());
  });
  return { felt: felt2, ukjente };
}
function tolkDesimal(verdi) {
  const tekst = verdi.trim().replace(/\s/g, "").replace(/%$/, "");
  if (tekst === "") return null;
  try {
    return belopFraDesimaltekst(tekst);
  } catch {
    return null;
  }
}
function lesLonnsfil(rutenett) {
  const ignorerteRader = [];
  const rader = [];
  const overskrift = rutenett[0];
  if (!overskrift) {
    return {
      rader: [],
      ignorerteRader: [],
      ukjenteKolonner: [],
      manglendeKolonner: [...PAAKREVDE],
      datofeil: null,
      datomerknad: null
    };
  }
  const { felt: felt2, ukjente } = finnLonnskolonner(overskrift);
  const manglende = PAAKREVDE.filter((f) => felt2[f] === void 0);
  if (manglende.length > 0) {
    return {
      rader: [],
      ignorerteRader: [],
      ukjenteKolonner: ukjente,
      manglendeKolonner: manglende,
      datofeil: null,
      datomerknad: null
    };
  }
  const hent3 = (rad, f) => felt2[f] === void 0 ? "" : (rad[felt2[f]] ?? "").trim();
  const datoformat = vurderDatoformat(
    rutenett.slice(1).flatMap((rad) => [hent3(rad, "fraDato"), hent3(rad, "tilDato")])
  );
  if (datoformat.feil !== null) {
    return {
      rader: [],
      ignorerteRader: [],
      ukjenteKolonner: ukjente,
      manglendeKolonner: [],
      datofeil: datoformat.feil,
      datomerknad: null
    };
  }
  rutenett.slice(1).forEach((rad, i) => {
    const linje = i + 2;
    const innhold = rad.join(";");
    if (rad.every((c) => c.trim() === "")) return;
    const ansattnummer = hent3(rad, "ansattnummer");
    const navn2 = hent3(rad, "navn");
    const gruppekode = hent3(rad, "gruppekode");
    const stillingsprosent = tolkDesimal(hent3(rad, "stillingsprosent"));
    const arslonn = tolkDesimal(hent3(rad, "arslonnFullStilling"));
    const fraDato = tolkDato(hent3(rad, "fraDato"));
    const mangler = [];
    if (ansattnummer === "") mangler.push("ansattnummer");
    if (navn2 === "") mangler.push("navn");
    if (gruppekode === "") mangler.push("stillingskode");
    if (stillingsprosent === null) mangler.push("stillingsprosent");
    if (arslonn === null) mangler.push("\xE5rsl\xF8nn");
    if (fraDato === null) mangler.push("fra-dato");
    if (mangler.length > 0 || stillingsprosent === null || arslonn === null || fraDato === null) {
      ignorerteRader.push({
        linje,
        innhold,
        arsak: `Mangler eller kunne ikke lese: ${mangler.join(", ")}.`
      });
      return;
    }
    const raTil = hent3(rad, "tilDato");
    const tilDato = raTil === "" ? null : tolkDato(raTil);
    if (raTil !== "" && tilDato === null) {
      ignorerteRader.push({ linje, innhold, arsak: `Kunne ikke lese sluttdatoen \xAB${raTil}\xBB.` });
      return;
    }
    const eksternId = hent3(rad, "eksternId");
    rader.push({
      linje,
      ansattnummer,
      navn: navn2,
      eksternId: eksternId === "" ? null : eksternId,
      gruppekode,
      stillingsprosent,
      arslonnFullStilling: arslonn,
      fraDato,
      tilDato
    });
  });
  return {
    rader,
    ignorerteRader,
    ukjenteKolonner: ukjente,
    manglendeKolonner: [],
    datofeil: null,
    datomerknad: datoformat.merknad
  };
}

// src/domain/lonn/hjemmelfil.ts
var FELTNAVN3 = {
  betegnelse: ["betegnelse", "stilling", "stillingsbetegnelse", "navn"],
  gruppekode: ["stillingskode", "kode", "gruppekode"],
  stillingsprosent: ["stillingsprosent", "prosent", "stillingsstorrelse"],
  fraDato: ["vedtatt fra", "fra dato", "fradato", "gjelder fra", "opprettet"],
  tilDato: ["inndratt fra", "til dato", "tildato", "gjelder til", "inndratt"]
};
var PAAKREVDE2 = ["betegnelse", "gruppekode", "stillingsprosent", "fraDato"];
function finnHjemmelkolonner(overskrift) {
  const felt2 = {};
  const ukjente = [];
  overskrift.forEach((r\u00E5, i) => {
    const n = normaliser(r\u00E5);
    if (n === "") return;
    for (const [f, navnene] of Object.entries(FELTNAVN3)) {
      if (felt2[f] === void 0 && navnene.includes(n)) {
        felt2[f] = i;
        return;
      }
    }
    ukjente.push(r\u00E5.trim());
  });
  return { felt: felt2, ukjente };
}
function tolkProsent(verdi) {
  const tekst = verdi.trim().replace(/\s/g, "").replace(/%$/, "");
  if (tekst === "") return null;
  try {
    return belopFraDesimaltekst(tekst);
  } catch {
    return null;
  }
}
function lesHjemmelfil(rutenett) {
  const ignorerteRader = [];
  const rader = [];
  const overskrift = rutenett[0];
  if (!overskrift) {
    return {
      rader: [],
      ignorerteRader: [],
      ukjenteKolonner: [],
      manglendeKolonner: [...PAAKREVDE2],
      datofeil: null,
      datomerknad: null
    };
  }
  const { felt: felt2, ukjente } = finnHjemmelkolonner(overskrift);
  const manglende = PAAKREVDE2.filter((f) => felt2[f] === void 0);
  if (manglende.length > 0) {
    return {
      rader: [],
      ignorerteRader: [],
      ukjenteKolonner: ukjente,
      manglendeKolonner: manglende,
      datofeil: null,
      datomerknad: null
    };
  }
  const hent3 = (rad, f) => felt2[f] === void 0 ? "" : (rad[felt2[f]] ?? "").trim();
  const datoformat = vurderDatoformat(
    rutenett.slice(1).flatMap((rad) => [hent3(rad, "fraDato"), hent3(rad, "tilDato")])
  );
  if (datoformat.feil !== null) {
    return {
      rader: [],
      ignorerteRader: [],
      ukjenteKolonner: ukjente,
      manglendeKolonner: [],
      datofeil: datoformat.feil,
      datomerknad: null
    };
  }
  rutenett.slice(1).forEach((rad, i) => {
    const linje = i + 2;
    const innhold = rad.join(";");
    if (rad.every((c) => c.trim() === "")) return;
    const betegnelse = hent3(rad, "betegnelse");
    const gruppekode = hent3(rad, "gruppekode");
    const stillingsprosent = tolkProsent(hent3(rad, "stillingsprosent"));
    const fraDato = tolkDato(hent3(rad, "fraDato"));
    const mangler = [];
    if (betegnelse === "") mangler.push("betegnelse");
    if (gruppekode === "") mangler.push("stillingskode");
    if (stillingsprosent === null) mangler.push("stillingsprosent");
    if (fraDato === null) mangler.push("vedtatt fra");
    if (mangler.length > 0 || stillingsprosent === null || fraDato === null) {
      ignorerteRader.push({
        linje,
        innhold,
        arsak: `Mangler eller kunne ikke lese: ${mangler.join(", ")}.`
      });
      return;
    }
    const raTil = hent3(rad, "tilDato");
    const tilDato = raTil === "" ? null : tolkDato(raTil);
    if (raTil !== "" && tilDato === null) {
      ignorerteRader.push({ linje, innhold, arsak: `Kunne ikke lese datoen \xAB${raTil}\xBB.` });
      return;
    }
    if (tilDato !== null && tilDato < fraDato) {
      ignorerteRader.push({
        linje,
        innhold,
        arsak: `Inndratt ${tilDato} er f\xF8r hjemmelen ble vedtatt ${fraDato}.`
      });
      return;
    }
    rader.push({ linje, betegnelse, gruppekode, stillingsprosent, fraDato, tilDato });
  });
  return {
    rader,
    ignorerteRader,
    ukjenteKolonner: ukjente,
    manglendeKolonner: [],
    datofeil: null,
    datomerknad: datoformat.merknad
  };
}
function hjemmelnokkel(rad) {
  return `${rad.gruppekode.toUpperCase()}:${normaliser(rad.betegnelse)}`;
}

// src/domain/lonn/versjonering.ts
function dagenFor(dato3) {
  const d = /* @__PURE__ */ new Date(`${dato3}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}
function dagenEtter(dato3) {
  const d = /* @__PURE__ */ new Date(`${dato3}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}
function velgBlant(kandidater) {
  const lopende = kandidater.filter((e) => e.tilDato === null);
  const [forste, andre] = lopende;
  if (andre !== void 0) return { treff: null, tvetydig: true };
  if (forste !== void 0) return { treff: forste, tvetydig: false };
  const sortert = [...kandidater].sort(
    (a, b) => a.fraDato < b.fraDato ? -1 : a.fraDato > b.fraDato ? 1 : 0
  );
  return { treff: sortert.at(-1) ?? null, tvetydig: false };
}
function finnTilsvarende(rad, eksisterende) {
  if (rad.eksternId !== null) {
    return velgBlant(eksisterende.filter((e) => e.eksternId === rad.eksternId));
  }
  return velgBlant(eksisterende.filter((e) => e.gruppekode === rad.gruppekode));
}
function ulikeFelt(rad, forhold) {
  const ulike = [];
  if (rad.stillingsprosent !== forhold.stillingsprosent) ulike.push("stillingsprosent");
  if (rad.arslonnFullStilling !== forhold.arslonnFullStilling) ulike.push("arslonnFullStilling");
  if (rad.gruppekode !== forhold.gruppekode) ulike.push("gruppekode");
  return ulike;
}
function vurderRad(rad, eksisterende, virkningsdato) {
  const { treff, tvetydig } = finnTilsvarende(rad, eksisterende);
  if (tvetydig) {
    return {
      utfall: "avvist",
      grunn: "Flere forhold passer p\xE5 denne raden. Uten en entydig id fra l\xF8nnssystemet kan vi ikke vite hvilket den gjelder."
    };
  }
  if (!treff) {
    return { utfall: "ny" };
  }
  if (treff.tilDato !== null) {
    if (rad.tilDato === treff.tilDato && ulikeFelt(rad, treff).length === 0) {
      return { utfall: "uendret", forholdId: treff.id };
    }
    return {
      utfall: "avvist",
      grunn: `Forholdet er allerede avsluttet ${treff.tilDato} hos oss. Skal det gjen\xE5pnes, h\xF8rer det som et nytt forhold.`
    };
  }
  if (rad.tilDato !== null) {
    if (rad.tilDato < treff.fraDato) {
      return {
        utfall: "avvist",
        grunn: `Sluttdatoen ${rad.tilDato} ligger f\xF8r forholdet begynte ${treff.fraDato}.`
      };
    }
    return { utfall: "avsluttet", forholdId: treff.id, tilDato: rad.tilDato };
  }
  const endrede = ulikeFelt(rad, treff);
  if (endrede.length === 0) return { utfall: "uendret", forholdId: treff.id };
  if (virkningsdato <= treff.fraDato) {
    return {
      utfall: "avvist",
      grunn: `Virkningsdatoen ${virkningsdato} er ikke etter at forholdet begynte ${treff.fraDato}. Endringen ville lukket en periode f\xF8r den startet.`
    };
  }
  return {
    utfall: "endret",
    forholdId: treff.id,
    endrede,
    lukkesDato: dagenFor(virkningsdato)
  };
}

// src/domain/lonn/hjemmelsdekning.ts
var TOM = {
  dekket: NULL_BELOP,
  ledig: NULL_BELOP,
  ledigSiden: null,
  blirLedigFra: null
};
function aktivPa(p2, dato3) {
  return p2.fraDato <= dato3 && (p2.tilDato === null || p2.tilDato >= dato3);
}
function grensedatoer(hjemmel, dekninger) {
  const grenser = /* @__PURE__ */ new Set([hjemmel.fraDato]);
  for (const d of dekninger) {
    if (d.fraDato > hjemmel.fraDato) grenser.add(d.fraDato);
    if (d.tilDato !== null) grenser.add(dagenEtter(d.tilDato));
  }
  return [...grenser].filter((g) => g >= hjemmel.fraDato && (hjemmel.tilDato === null || g <= hjemmel.tilDato)).sort();
}
function hjemmelsdekning(hjemmel, dekninger, referansedato) {
  if (hjemmel.tilDato !== null && referansedato > hjemmel.tilDato) return TOM;
  const dekningPa = (dato3) => summerBelop(dekninger.filter((d) => aktivPa(d, dato3)).map((d) => d.stillingsprosent));
  const grenser = grensedatoer(hjemmel, dekninger);
  const underdekket = (dato3) => dekningPa(dato3) < hjemmel.stillingsprosent;
  if (referansedato < hjemmel.fraDato) {
    return {
      ...TOM,
      blirLedigFra: grenser.find((g) => underdekket(g)) ?? null
    };
  }
  const dekket = dekningPa(referansedato);
  const ledig = hjemmel.stillingsprosent - dekket;
  if (ledig <= NULL_BELOP) {
    return {
      dekket,
      ledig,
      ledigSiden: null,
      blirLedigFra: grenser.find((g) => g > referansedato && underdekket(g)) ?? null
    };
  }
  const iDag = grenser.filter((g) => g <= referansedato);
  let siden = hjemmel.fraDato;
  for (let i = iDag.length - 1; i >= 0; i -= 1) {
    const grense = iDag[i];
    if (grense === void 0 || !underdekket(grense)) break;
    siden = grense;
  }
  return { dekket, ledig, ledigSiden: siden, blirLedigFra: null };
}

// src/domain/lonn/kostnad.ts
var FERIEMANED = 6;
function gjeldendeOppgjor(oppgjor, periode2) {
  return oppgjor.filter(
    (o) => o.stillingsgruppeId === null || o.stillingsgruppeId === periode2.stillingsgruppeId
  ).filter((o) => o.virkningDato > periode2.fraDato).map(({ virkningDato, prosent: prosent2 }) => ({ virkningDato, prosent: prosent2 }));
}
function dagerIManeden(ar, maned) {
  return new Date(Date.UTC(ar, maned, 0)).getUTCDate();
}
function dato2(ar, maned, dag) {
  return `${String(ar)}-${String(maned).padStart(2, "0")}-${String(dag).padStart(2, "0")}`;
}
function dagnummer(iso) {
  return Math.round((/* @__PURE__ */ new Date(`${iso}T00:00:00Z`)).getTime() / 864e5);
}
function andelAvManeden(periode2, ar, maned) {
  const dager = dagerIManeden(ar, maned);
  const forste = dato2(ar, maned, 1);
  const siste = dato2(ar, maned, dager);
  const start = periode2.fraDato > forste ? periode2.fraDato : forste;
  const slutt = periode2.tilDato !== null && periode2.tilDato < siste ? periode2.tilDato : siste;
  if (start > slutt) return 0;
  return (dagnummer(slutt) - dagnummer(start) + 1) / dager;
}
function vekstfaktor(ar, maned, oppgjor, glidningProsent) {
  const sisteIManeden = dato2(ar, maned, dagerIManeden(ar, maned));
  const fraOppgjor = oppgjor.reduce(
    (faktor, o) => o.virkningDato <= sisteIManeden ? faktor * (1 + o.prosent / 100) : faktor,
    1
  );
  const fraGlidning = 1 + glidningProsent / 100 * (maned / 12);
  return fraOppgjor * fraGlidning;
}
function feriepengemaned(stilling, ar) {
  if (andelAvManeden(stilling, ar, FERIEMANED) > 0) return FERIEMANED;
  const aktive = MANEDER.filter((m) => andelAvManeden(stilling, ar, m) > 0);
  const for_ferien = aktive.filter((m) => m < FERIEMANED);
  return for_ferien.at(-1) ?? null;
}
function beregnArskostnad(stilling, ar, forutsetninger, oppgjor = []) {
  const stillingsandel = Number(stilling.stillingsprosent) / 1e5;
  const arslonn = skalerBelop(stilling.arslonnFullStilling, stillingsandel);
  const basis = fordelJevnt(arslonn, 12);
  const lonnFor = MANEDER.map((maned, i) => {
    const andel = andelAvManeden(stilling, ar, maned);
    if (andel === 0) return NULL_BELOP;
    const faktor = vekstfaktor(ar, maned, oppgjor, forutsetninger.glidningProsent);
    return skalerBelop(basis[i] ?? NULL_BELOP, faktor * andel);
  });
  const grunnlag = summerBelop(lonnFor);
  const sats = stilling.feriepengeProsent ?? forutsetninger.feriepengeProsent;
  const utbetalesI = feriepengemaned(stilling, ar);
  const feriepenger = utbetalesI === null ? NULL_BELOP : skalerBelop(grunnlag, sats / 100);
  return MANEDER.map((maned, i) => {
    const trekkes = utbetalesI === FERIEMANED && maned === FERIEMANED;
    const lonn = trekkes ? NULL_BELOP : lonnFor[i] ?? NULL_BELOP;
    const feriepengerNa = maned === utbetalesI ? feriepenger : NULL_BELOP;
    const pensjon = skalerBelop(lonn + feriepengerNa, forutsetninger.pensjonProsent / 100);
    const arbeidsgiveravgift = skalerBelop(
      lonn + feriepengerNa + pensjon,
      forutsetninger.arbeidsgiveravgiftProsent / 100
    );
    return {
      maned,
      lonn,
      feriepenger: feriepengerNa,
      pensjon,
      arbeidsgiveravgift,
      sum: lonn + feriepengerNa + pensjon + arbeidsgiveravgift
    };
  });
}
function summerKostnad(maneder) {
  return {
    lonn: summerBelop(maneder.map((m) => m.lonn)),
    feriepenger: summerBelop(maneder.map((m) => m.feriepenger)),
    pensjon: summerBelop(maneder.map((m) => m.pensjon)),
    arbeidsgiveravgift: summerBelop(maneder.map((m) => m.arbeidsgiveravgift)),
    sum: summerBelop(maneder.map((m) => m.sum))
  };
}
function leggSammen(alle) {
  return MANEDER.map((maned, i) => {
    const raden = alle.map((k2) => k2[i]).filter((m) => m !== void 0);
    return {
      maned,
      lonn: summerBelop(raden.map((m) => m.lonn)),
      feriepenger: summerBelop(raden.map((m) => m.feriepenger)),
      pensjon: summerBelop(raden.map((m) => m.pensjon)),
      arbeidsgiveravgift: summerBelop(raden.map((m) => m.arbeidsgiveravgift)),
      sum: summerBelop(raden.map((m) => m.sum))
    };
  });
}

// src/domain/lonn/arsverk.ts
function arsverkForManed(andeler, ar, maned) {
  return andeler.reduce((sum2, a) => {
    const andel = andelAvManeden(a, ar, maned);
    if (andel === 0) return sum2;
    return sum2 + skalerBelop(a.stillingsprosent, andel / 100);
  }, NULL_MAALTALL);
}
function arsverkPerManed(andeler, ar) {
  return MANEDER.map((maned) => arsverkForManed(andeler, ar, maned));
}
function snittArsverkAvManeder(maneder) {
  if (maneder.length === 0) return NULL_MAALTALL;
  return maneder.reduce((s, m) => s + m, NULL_MAALTALL) / BigInt(maneder.length);
}
function vakansPerManed(hjemler, forhold, ar) {
  const budsjetterte = arsverkPerManed(hjemler, ar);
  const besatte = arsverkPerManed(forhold, ar);
  return MANEDER.map((_, i) => (budsjetterte[i] ?? NULL_MAALTALL) - (besatte[i] ?? NULL_MAALTALL));
}

// src/domain/lonn/prognosekobling.ts
var LONNSPOSTER = ["lonn", "feriepenger", "pensjon", "arbeidsgiveravgift"];
function belopFor(maned, post) {
  if (post === "lonn") return maned.lonn;
  if (post === "feriepenger") return maned.feriepenger;
  if (post === "pensjon") return maned.pensjon;
  return maned.arbeidsgiveravgift;
}
function fordelPaKontoer(maneder, kobling) {
  const tabell = /* @__PURE__ */ new Map();
  for (const post of LONNSPOSTER) {
    const kontoId = kobling[post];
    const perManed = tabell.get(kontoId) ?? /* @__PURE__ */ new Map();
    for (const maned of MANEDER) {
      const kostnad2 = maneder.find((m) => m.maned === maned);
      const belop = kostnad2 === void 0 ? NULL_BELOP : belopFor(kostnad2, post);
      perManed.set(maned, (perManed.get(maned) ?? NULL_BELOP) + belop);
    }
    tabell.set(kontoId, perManed);
  }
  return tabell;
}
function arssumPerKonto(tabell) {
  const sum2 = /* @__PURE__ */ new Map();
  for (const [kontoId, perManed] of tabell) {
    let total = NULL_BELOP;
    for (const belop of perManed.values()) total += belop;
    sum2.set(kontoId, total);
  }
  return sum2;
}

// src/schemas/auth.ts
import { z as z7 } from "zod";
var virksomhetTilgangSchema = z7.object({
  virksomhetId: uuidSchema,
  virksomhetNavn: z7.string(),
  kommunalomradeId: uuidSchema,
  rolle: rolleSchema
});
var omradeTilgangSchema = z7.object({
  kommunalomradeId: uuidSchema,
  kommunalomradeNavn: z7.string(),
  rolle: rolleSchema
});
var megSchema = z7.object({
  brukerId: uuidSchema,
  navn: z7.string(),
  epost: z7.email(),
  kommuneId: uuidSchema,
  kommuneNavn: z7.string(),
  /** Platform operator, not a kommune role — must still pick one kommune to see data. */
  erPlattformadmin: z7.boolean(),
  /** Kalleren tilhører leverandørens kommune, og kan administrere plattformen. */
  erLeverandor: z7.boolean().default(false),
  /**
   * May administer users and access in their own kommune. Sent so the client can
   * hide what it cannot use — the server still refuses regardless, since a menu
   * that hides a route is not a permission check.
   */
  kanAdministrere: z7.boolean(),
  /**
   * Økonomisjef: ser økonomirapporten for hele kommunen, på tvers av områdene.
   * En tildeling på kommunenivå, ikke en systemrolle — den gir innsyn i tall,
   * ikke rett til å administrere noe.
   */
  erOkonomisjef: z7.boolean().default(false),
  /**
   * Områdene kalleren er controller for. En controller styrer rapporten sammen
   * med økonomisjefen, og skriver i områdekapitlene for disse områdene.
   */
  controllerOmrader: z7.array(uuidSchema).default([]),
  /**
   * Utvalgene kalleren sitter i som folkevalgt. En folkevalgt leser og
   * kommenterer rapportene som er sendt til utvalget — og endrer ingenting.
   */
  utvalg: z7.array(z7.object({ id: uuidSchema, navn: z7.string() })).default([]),
  tilganger: z7.array(virksomhetTilgangSchema),
  /** Områdene kalleren er tildelt. Bare disse tilbys som omfang i velgeren. */
  omradetilganger: z7.array(omradeTilgangSchema),
  /**
   * Firmaet, når kalleren er en entreprenørbruker. Hen ser bare
   * arbeidsordrene firmaet har fått, i vedlikeholdsappen. Se
   * `domain/entreprenor.ts`.
   */
  entreprenor: z7.object({
    firmaId: uuidSchema,
    firmanavn: z7.string(),
    rolle: z7.enum(FIRMAROLLER)
  }).nullable().default(null)
});

// src/schemas/kontoplan.ts
import { z as z8 } from "zod";
var underkontoSchema = z8.object({
  id: uuidSchema,
  kontonummer: z8.string().nullable(),
  kontonavn: z8.string(),
  sortering: z8.number().int(),
  aktiv: z8.boolean()
});
var artsgruppeSchema = z8.object({
  id: uuidSchema,
  kontonummer: z8.string().nullable().describe("KOSTRA-kodet artsgruppe, 10\u201319"),
  navn: z8.string(),
  sortering: z8.number().int(),
  aktiv: z8.boolean(),
  underkontoer: z8.array(underkontoSchema)
});
var hovedkontoSchema = z8.object({
  id: uuidSchema,
  kontonummer: z8.string().nullable(),
  navn: z8.string(),
  kategori: kategoriSchema,
  sortering: z8.number().int(),
  aktiv: z8.boolean(),
  artsgrupper: z8.array(artsgruppeSchema)
});
var kontoplanSchema = z8.object({
  kommuneId: uuidSchema,
  hovedkontoer: z8.array(hovedkontoSchema)
});

// src/schemas/oversikt.ts
import { z as z9 } from "zod";
var detaljnivaSchema = z9.literal(NIVAER).describe("1 = hovedkonto, 2 = artsgruppe, 3 = underkonto");
var oversiktTallSchema = z9.object({
  budsjett: belopSchema,
  regnskapHittil: belopSchema,
  prognoseTotalt: belopSchema,
  /** prognose_totalt − budsjett. Positivt betyr over budsjett. */
  avvik: belopSchema,
  avvikProsent: z9.number().nullable(),
  /**
   * Fjorårets bokførte regnskap for hele året, og for de samme månedene som i
   * år er bokført til. Null betyr at fjoråret ikke finnes i systemet — noe
   * annet enn at det ble ført null kroner.
   */
  fjoraretTotalt: belopSchema.nullable(),
  fjoraretHittil: belopSchema.nullable()
});
var oversiktRadSchema = z9.lazy(
  () => oversiktTallSchema.extend({
    id: z9.string(),
    niva: detaljnivaSchema,
    kontonummer: z9.string().nullable(),
    navn: z9.string(),
    barn: z9.array(oversiktRadSchema)
  })
);
var oversiktManedSchema = z9.object({
  maned: manedSchema,
  /** Akkumulert til og med måneden. */
  budsjett: belopSchema,
  /** Null fra og med første måned uten bokførte tall — kurven skal stoppe der. */
  regnskap: belopSchema.nullable(),
  prognose: belopSchema
});
var oversiktsdataSchema = z9.object({
  /** Om beløpene er i kroner eller tusen kroner — styrer hvordan de vises. */
  valutaEnhet: valutaEnhetSchema,
  ar: arSchema,
  /**
   * Siste måned med bokførte tall, eller null for et år uten regnskap ennå.
   * Skjermen bruker den til å si "regnskap til og med juli, prognose resten".
   */
  sisteBokfortManed: manedSchema.nullable(),
  /** Nivået skjermen åpner på. Brukeren kan endre det uten et nytt kall. */
  standardDetaljniva: detaljnivaSchema,
  hovedkontoer: z9.array(oversiktRadSchema),
  sum: oversiktTallSchema,
  /** Året akkumulert, for linjediagrammet. Summert over alle kontoer. */
  maneder: z9.array(oversiktManedSchema)
});
var finansiellOversiktSchema = oversiktsdataSchema.extend({
  virksomhetId: uuidSchema,
  virksomhetNavn: z9.string()
});
var finansiellOversiktSporringSchema = z9.object({
  ar: arSporringSchema
});
var omradevirksomhetSchema = z9.object({
  virksomhetId: uuidSchema,
  navn: z9.string(),
  /**
   * Enheten virksomheten selv fører i.
   *
   * Tallene er normalisert til tusen kroner uansett, men enheten oppgis fordi
   * den forklarer hvorfor et tall her kan se annerledes ut enn i Finansiell
   * oversikt for samme virksomhet alene.
   */
  egenEnhet: valutaEnhetSchema,
  /**
   * Virksomhetens EGEN siste bokførte måned.
   *
   * Virksomheter i samme område kan ligge ulikt an. Er én bokført til juli og
   * en annen til mars, er områdets tall for april til juli en delsum — og det
   * er ikke synlig av totalen alene.
   */
  sisteBokfortManed: manedSchema.nullable(),
  tall: oversiktTallSchema
});
var omradeoversiktSchema = oversiktsdataSchema.extend({
  kommunalomradeId: uuidSchema,
  navn: z9.string(),
  /**
   * Virksomhetene som er med, hver for seg.
   *
   * Både for «Per virksomhet»-visualiseringen og for å kunne se hvem som ligger
   * bak på bokføringen.
   */
  virksomheter: z9.array(omradevirksomhetSchema),
  /**
   * Hvor mange virksomheter i området kalleren ikke ser.
   *
   * Summen dekker bare det man har tilgang til. En kommunalsjef som leser et
   * ufullstendig totaltall som fullstendig er verre stilt enn en som ikke fikk
   * tallet — så tallet må følge med, ikke bare et flagg.
   */
  antallUtelatt: z9.number().int()
});
var omradeoversiktSporringSchema = z9.object({ ar: arSporringSchema });

// src/schemas/oversiktsoppsett.ts
import { z as z10 } from "zod";
var nokkel = z10.string().min(1).max(120);
var hoyst = (verdi) => z10.record(nokkel, verdi).refine((r) => Object.keys(r).length <= 100, {
  message: "For mange oppf\xF8ringer."
});
var framstillingSchema = z10.enum([
  "tall",
  "staaende",
  "liggende",
  "linje",
  "areal",
  "blandet",
  "sirkel"
]);
var variantSchema = z10.string().regex(/^[a-z0-9_]{1,30}$/);
var oversiktsoppsettSchema = z10.object({
  valgte: z10.array(nokkel).max(100),
  storrelser: hoyst(z10.enum(["liten", "stor"])),
  tilpassede: hoyst(
    z10.object({
      kolonner: z10.number().int().min(2).max(12),
      hoyde: z10.number().int().min(10).max(60)
    })
  ),
  plasser: hoyst(
    z10.object({ kol: z10.number().int().min(0).max(11), rad: z10.number().int().min(0).max(5e3) })
  ),
  framstillinger: hoyst(framstillingSchema),
  serieformer: hoyst(z10.record(nokkel, z10.enum(["stolpe", "linje", "areal"]))),
  /** Fra før variantene fantes har et lagret oppsett ingen; da er det tomt. */
  varianter: hoyst(variantSchema).default({})
});
var fellesVisningSchema = z10.object({
  /** Null: ingen har satt opp visningen ennå, og standardoppsettet gjelder. */
  oppsett: oversiktsoppsettSchema.nullable(),
  kanRedigere: z10.boolean(),
  /** Hvilken lagring visningen er. 0 før noen har satt den opp. */
  versjon: z10.number().int().min(0),
  sistEndret: z10.object({
    tidspunkt: z10.iso.datetime(),
    /** Null når kontoen som endret den ikke finnes lenger. */
    av: z10.string().nullable()
  }).nullable()
});
var lagreFellesVisningSchema = z10.object({
  versjon: z10.number().int().min(0),
  oppsett: oversiktsoppsettSchema
});

// src/schemas/prognose.ts
import { z as z11 } from "zod";
var framskrivningVariablerSchema = z11.object({
  /** 0.02 = 2 %. Bounded to a range a real budget process could defend. */
  vekstrate: z11.number().min(-0.5).max(1)
});
var rullerendeVariablerSchema = z11.object({
  vindusManeder: z11.number().int().min(1).max(12),
  vekstrate: z11.number().min(-0.5).max(1)
});
var pleietyngdeSchema = z11.object({
  navn: z11.string().min(1).max(100),
  antallBrukere: z11.number().int().min(0).max(1e4),
  normtallArsverkPerBruker: z11.number().min(0).max(10)
});
var normtallVariablerSchema = z11.object({
  grunnbemanningArsverk: z11.number().min(0).max(1e4),
  manedskostnadPerArsverk: belopSchema,
  pleietyngde: z11.array(pleietyngdeSchema).max(20)
});
var aktivitetVariablerSchema = z11.object({
  volumPerManed: z11.number().min(0).max(1e6),
  enhetspris: belopSchema
});
var laser = {
  metodeLast: z11.boolean().default(false),
  variablerLast: z11.boolean().default(false)
};
var metodevalgSchema = z11.discriminatedUnion("metode", [
  z11.object({
    hovedkontoId: uuidSchema,
    metode: z11.literal("framskrivning"),
    variabler: framskrivningVariablerSchema,
    ...laser
  }),
  z11.object({
    hovedkontoId: uuidSchema,
    metode: z11.literal("rullerende"),
    variabler: rullerendeVariablerSchema,
    ...laser
  }),
  z11.object({
    hovedkontoId: uuidSchema,
    metode: z11.literal("normtall"),
    variabler: normtallVariablerSchema,
    ...laser
  }),
  z11.object({
    hovedkontoId: uuidSchema,
    metode: z11.literal("aktivitet"),
    variabler: aktivitetVariablerSchema,
    ...laser
  })
]);
var nivaSchema = z11.literal(NIVAER);
var overstyringSchema = z11.object({
  niva: nivaSchema,
  /** hovedkonto.id, artsgruppe.id or underkonto.id, matching `niva`. */
  malId: uuidSchema,
  maned: manedSchema,
  /** The TARGET for that level and month — never a delta. */
  belop: belopSchema,
  kommentar: z11.string().max(500).nullable().default(null)
});
var prognoseManedSchema = z11.object({
  maned: manedSchema,
  /** The RESOLVED value: booked actual, override, or basis — whichever wins. */
  belop: belopSchema,
  /** `regnskap` means booked and not editable; `prognose` means still open. */
  kilde: z11.enum(["regnskap", "prognose"]),
  /**
   * What the chosen method produced for this month, BEFORE any override.
   *
   * Sent so the browser can re-run the very same cascade locally while the user
   * edits, and show the effect of an override immediately without a round trip.
   * That is what makes "edit locally, save explicitly" possible while still
   * guaranteeing the displayed figure is the figure that gets persisted — the
   * client and the server call the same `beregnPrognoseTabell`.
   */
  basis: belopSchema,
  /**
   * Budsjettet for denne måneden, slik det er periodisert i budsjettimporten.
   *
   * Sendt med for cellepanelet: «hva var planen for oktober» er det første
   * spørsmålet noen stiller om et tall de er i ferd med å overstyre. Årsbudsjettet
   * delt på tolv ville vært feil for alle kontoer med sesong.
   */
  budsjett: belopSchema,
  /**
   * Fjorårets regnskap for samme måned. Null når det ikke finnes et tall —
   * ikke null kroner, som ville påstått at kontoen var tom i fjor.
   */
  fjoraret: belopSchema.nullable()
});
var prognoseRadSchema = z11.object({
  underkontoId: uuidSchema,
  kontonummer: z11.string().nullable(),
  navn: z11.string(),
  artsgruppeId: uuidSchema,
  hovedkontoId: uuidSchema,
  /** Needed client-side: distribution order must match the server's exactly. */
  sortering: z11.number().int(),
  maneder: z11.array(prognoseManedSchema).length(12),
  arstotal: belopSchema,
  budsjett: belopSchema,
  avvik: belopSchema
});
var prognoseHovedkontoSchema = z11.object({
  id: uuidSchema,
  kontonummer: z11.string().nullable(),
  navn: z11.string(),
  kategori: kategoriSchema,
  /** null until someone has chosen a method — then the basis is the budget. */
  metodevalg: metodevalgSchema.nullable(),
  artsgrupper: z11.array(
    z11.object({
      id: uuidSchema,
      kontonummer: z11.string().nullable(),
      navn: z11.string(),
      underkontoIder: z11.array(uuidSchema)
    })
  )
});
var prognoseVisningSchema = z11.object({
  virksomhetId: uuidSchema,
  virksomhetNavn: z11.string(),
  valutaEnhet: valutaEnhetSchema,
  ar: arSchema,
  sisteBokfortManed: manedSchema.nullable(),
  /**
   * Måneder som er lukket fordi det er ført regnskap i dem.
   *
   * Låsingen gjelder HELE virksomheten, ikke konto for konto. En måned der noen
   * konto er bokført er en avsluttet periode, og da skal ingenting i den kunne
   * endres — heller ikke på en konto som tilfeldigvis manglet i importfilen.
   * Håndheves tre steder, med én definisjon: kaskaden ignorerer overstyringer i
   * lukkede måneder ved beregning, tjenesten forkaster dem ved lagring, og
   * importen sletter dem for månedene den bokfører.
   */
  laasteManeder: z11.array(manedSchema),
  /**
   * Hvilken lagring skjermen bygger på. 0 før prognosen er lagret for året.
   *
   * Sendes tilbake ved lagring. Har noen andre lagret i mellomtiden, er
   * versjonen en annen, og lagringen avvises i stedet for å skrive over det de
   * gjorde. Se `lagrePrognoseSchema`.
   */
  versjon: z11.number().int().min(0),
  /** Den siste lagringen: når, og av hvem. Null før prognosen er lagret for året. */
  sistLagret: z11.object({ tidspunkt: z11.iso.datetime(), av: z11.string().nullable() }).nullable(),
  hovedkontoer: z11.array(prognoseHovedkontoSchema),
  rader: z11.array(prognoseRadSchema),
  overstyringer: z11.array(overstyringSchema),
  sum: z11.object({
    budsjett: belopSchema,
    prognoseTotalt: belopSchema,
    avvik: belopSchema,
    avvikProsent: z11.number().nullable()
  })
});
var lagrePrognoseSchema = z11.object({
  ar: arSchema,
  /**
   * Versjonen lagringen bygger på, fra `PrognoseVisning.versjon`.
   *
   * Hele årets tilstand erstattes ved lagring, så uten den ville den som
   * lagret sist, stille ha fjernet det en kollega lagret i mellomtiden — og
   * endringsloggen ville vist at det var hun som gjorde det.
   */
  versjon: z11.number().int().min(0),
  metodevalg: z11.array(metodevalgSchema).max(3),
  overstyringer: z11.array(overstyringSchema).max(2e3)
});
var prognoseSporringSchema = z11.object({
  ar: arSporringSchema
});
var prognoseMetoderSchema = z11.enum(PROGNOSE_METODER);
var endringsfelles = {
  id: uuidSchema,
  tidspunkt: z11.iso.datetime(),
  brukerNavn: z11.string()
};
var prognoseendringSchema = z11.discriminatedUnion("hva", [
  z11.object({
    ...endringsfelles,
    hva: z11.literal("overstyring"),
    niva: nivaSchema,
    malId: uuidSchema,
    maned: manedSchema,
    /** Null: det var ingen overstyring før. */
    fraBelop: belopSchema.nullable(),
    /** Null: overstyringen er fjernet, og cellen er tilbake på grunnlaget. */
    tilBelop: belopSchema.nullable(),
    kommentar: z11.string().nullable()
  }),
  z11.object({
    ...endringsfelles,
    hva: z11.literal("metode"),
    hovedkontoId: uuidSchema,
    /** Null: ingen metode, altså budsjettet som grunnlag. */
    metodeFra: z11.string().nullable(),
    metodeTil: z11.string().nullable()
  })
]);
var prognoseendringerSchema = z11.array(prognoseendringSchema);

// src/schemas/levering.ts
import { z as z12 } from "zod";
var prognoserytmeSchema = z12.enum(PROGNOSERYTMER);
var fristdagSchema = z12.number().int().min(FRISTDAG_MIN).max(FRISTDAG_MAKS).nullable();
var leveringsstatusSchema = z12.enum(LEVERINGSSTATUSER);
var hendelseSchema = z12.object({
  tidspunkt: z12.iso.datetime(),
  /** Navnet. Null når brukeren er slettet — leveringen blir stående likevel. */
  av: z12.string().nullable()
});
var kommentartekst = z12.string().trim().min(1).max(1e3);
var leveringskommentarSchema = hendelseSchema.extend({
  id: uuidSchema,
  tekst: z12.string()
});
var forklaringstekst = z12.string().trim().min(1).max(1e3);
var avviksforklaringSchema = z12.object({
  hovedkontoId: uuidSchema,
  arsak: forklaringstekst,
  /** Hva som gjøres med det. Null er at ingenting gjøres, og da sier forklaringen hvorfor. */
  tiltak: forklaringstekst.nullable().default(null),
  /** Et tiltak i tiltaksmodulen forklaringen peker på, også et vedtatt kutt. */
  tiltakId: uuidSchema.nullable().default(null)
});
var levertForklaringSchema = avviksforklaringSchema.extend({
  tiltakNavn: z12.string().nullable().default(null)
});
var prognoseleveringSchema = z12.object({
  id: uuidSchema,
  virksomhetId: uuidSchema,
  ar: arSchema,
  /** Måneden perioden slutter med: 8 er august, eller 2. tertial. */
  periode: manedSchema,
  status: leveringsstatusSchema,
  levert: hendelseSchema,
  /** Det den som leverte, skrev med. */
  kommentar: z12.string().nullable(),
  /** Prognosens versjon da den ble levert. 0 er en prognose som aldri er lagret. */
  versjon: z12.number().int().min(0),
  budsjett: belopSchema,
  prognose: belopSchema,
  avvik: belopSchema,
  /** Øyeblikksbildet per hovedkonto: det som ble meldt, ikke det som står der nå. */
  hovedkontoer: z12.array(
    z12.object({
      id: uuidSchema,
      kontonummer: z12.string().nullable(),
      navn: z12.string(),
      kategori: kategoriSchema,
      budsjett: belopSchema,
      prognose: belopSchema
    })
  ),
  /** Forklaringene på avvikene, slik de ble levert. Tomt for leveringer fra før kravet. */
  forklaringer: z12.array(levertForklaringSchema).default([]),
  /** Godkjent eller sendt tilbake: av hvem, når, og hvorfor. */
  behandlet: hendelseSchema.extend({ kommentar: z12.string().nullable() }).nullable(),
  kommentarer: z12.array(leveringskommentarSchema)
});
var virksomhetsleveringerSchema = z12.object({
  virksomhetId: uuidSchema,
  virksomhetNavn: z12.string(),
  valutaEnhet: valutaEnhetSchema,
  /** Perioden som kan leveres nå. Null før regnskapet for den første er ført. */
  periode: manedSchema.nullable(),
  /** Årets leveringer, nyeste først — også de som er erstattet eller sendt tilbake. */
  leveringer: z12.array(prognoseleveringSchema),
  /** Om kalleren kan godkjenne, sende tilbake og kommentere. Se `kanBehandleLeveringer`. */
  kanBehandle: z12.boolean()
});
var forklaringsterskelSchema = z12.object({
  prosent: z12.number().min(0).max(100),
  /** I kroner. */
  belop: belopSchema
}).nullable();
var prognoseleveringerSchema = virksomhetsleveringerSchema.extend({
  ar: arSchema,
  rytme: prognoserytmeSchema,
  fristdag: fristdagSchema,
  /** Om kalleren kan levere: leder eller økonom på virksomheten. */
  kanLevere: z12.boolean(),
  forklaringsterskel: forklaringsterskelSchema.default(null),
  /**
   * Hovedkontoene i den LAGREDE prognosen som må forklares når den leveres.
   * Tom når ingenting kan leveres nå. I virksomhetens enhet.
   */
  forklaringsplikt: z12.array(
    z12.object({
      hovedkontoId: uuidSchema,
      kontonummer: z12.string().nullable(),
      navn: z12.string(),
      budsjett: belopSchema,
      prognose: belopSchema,
      avvik: belopSchema
    })
  ).default([]),
  /** Årets tiltak for virksomheten, også de vedtatte kuttene, til å peke på. */
  tiltakvalg: z12.array(z12.object({ id: uuidSchema, navn: z12.string() })).default([])
});
var omradeleveringerSchema = z12.object({
  kommunalomradeId: uuidSchema,
  navn: z12.string(),
  ar: arSchema,
  rytme: prognoserytmeSchema,
  fristdag: fristdagSchema,
  virksomheter: z12.array(virksomhetsleveringerSchema)
});
var leverPrognoseSchema = z12.object({
  ar: arSchema,
  /**
   * Prognosens versjon slik skjermen viser den.
   *
   * Leveringen er av det som er LAGRET. Har noen lagret siden skjermen ble
   * hentet, er det ikke det brukeren så på, og leveringen avvises framfor å
   * melde tall de ikke har sett. 0 er en prognose som aldri er lagret: den
   * står på budsjettet, og også det kan være det virksomheten melder.
   */
  versjon: z12.number().int().min(0),
  kommentar: kommentartekst.nullable().default(null),
  forklaringer: z12.array(avviksforklaringSchema).max(200).default([]).refine((f) => new Set(f.map((x) => x.hovedkontoId)).size === f.length, {
    message: "\xC9n forklaring per hovedkonto."
  })
});
var behandleLeveringSchema = z12.object({
  utfall: z12.enum(["godkjent", "sendt_tilbake"]),
  kommentar: kommentartekst.nullable().default(null)
}).refine((b) => b.utfall === "godkjent" || b.kommentar !== null, {
  message: "Skriv hvorfor leveringen sendes tilbake.",
  path: ["kommentar"]
});
var kommenterLeveringSchema = z12.object({ tekst: kommentartekst });
var leveringsoppsettSchema = z12.object({
  rytme: prognoserytmeSchema,
  fristdag: fristdagSchema,
  /** Uten feltet: kravet står som det er. Se `domain/avviksforklaring.ts`. */
  forklaringsterskel: forklaringsterskelSchema.optional()
});
var treffsikkerhetSchema = z12.object({
  ar: arSchema,
  virksomheter: z12.array(
    z12.object({
      id: uuidSchema,
      navn: z12.string(),
      valutaEnhet: valutaEnhetSchema,
      /** Regnskapet er ført til og med desember: utfallet er fasit. */
      avsluttet: z12.boolean(),
      /** Regnskapet for hele året. Null før året er avsluttet. */
      utfall: belopSchema.nullable(),
      /** De leveringene som sto for hver periode: godkjent, eller levert og ventende. */
      punkter: z12.array(
        z12.object({
          periode: manedSchema,
          prognose: belopSchema,
          bom: belopSchema,
          bomProsent: z12.number().nullable()
        })
      ),
      snittBomProsent: z12.number().nullable(),
      retning: z12.enum(TREFFRETNINGER).nullable()
    })
  )
});

// src/schemas/oppgaver.ts
import { z as z13 } from "zod";
var hendelse = z13.object({ tidspunkt: z13.iso.datetime(), av: z13.string().nullable() });
var virksomhet = { virksomhetId: uuidSchema, virksomhetNavn: z13.string(), ar: arSchema };
var oppgaveSchema = z13.discriminatedUnion("type", [
  z13.object({
    type: z13.literal("godkjenn"),
    ...virksomhet,
    kommunalomradeId: uuidSchema,
    periode: manedSchema,
    levert: hendelse
  }),
  z13.object({
    type: z13.literal("rett"),
    ...virksomhet,
    periode: manedSchema,
    behandlet: hendelse.extend({ kommentar: z13.string().nullable() })
  }),
  z13.object({ type: z13.literal("lever"), ...virksomhet, periode: manedSchema }),
  z13.object({
    type: z13.literal("mangler"),
    kommunalomradeId: uuidSchema,
    kommunalomradeNavn: z13.string(),
    virksomheter: z13.array(
      z13.object({ id: uuidSchema, navn: z13.string(), ar: arSchema, periode: manedSchema })
    )
  })
]);
var oppgaverSchema = z13.object({
  rytme: prognoserytmeSchema,
  fristdag: fristdagSchema,
  oppgaver: z13.array(oppgaveSchema)
});

// src/schemas/posteringsvarsel.ts
import { z as z14 } from "zod";
var hendelse2 = z14.object({ tidspunkt: z14.iso.datetime(), av: z14.string().nullable() });
var posteringsvarselSchema = z14.object({
  underkontoId: uuidSchema,
  kontonummer: z14.string().nullable(),
  kontonavn: z14.string(),
  /** Beløpene i virksomhetens enhet. */
  belop: belopSchema,
  vanlig: belopSchema,
  /** Samme måned i fjor. Null når fjoråret ikke er bokført. */
  fjoraret: belopSchema.nullable(),
  /** Lederen har sett på posteringen. Null er at ingen har det ennå. */
  kvittert: hendelse2.extend({ kommentar: z14.string().nullable() }).nullable()
});
var posteringsvarslerSchema = z14.object({
  virksomhetId: uuidSchema,
  ar: arSchema,
  /** Måneden som er sjekket: den siste bokførte. Null før noe er bokført. */
  maned: manedSchema.nullable(),
  valutaEnhet: valutaEnhetSchema,
  /** Om kalleren kan kvittere: leder eller økonom på virksomheten. */
  kanKvittere: z14.boolean(),
  varsler: z14.array(posteringsvarselSchema)
});
var posteringsvarselSporringSchema = z14.object({ ar: arSporringSchema });
var kvitterVarselSchema = z14.object({
  ar: arSchema,
  maned: manedSchema,
  underkontoId: uuidSchema,
  /** Hva lederen fant: «Riktig, to måneders husleie», eller «Feilført, rettes». */
  kommentar: z14.string().trim().min(1).max(1e3).nullable().default(null)
});
var varseloppgaverSchema = z14.object({
  oppgaver: z14.array(
    z14.object({
      virksomhetId: uuidSchema,
      virksomhetNavn: z14.string(),
      ar: arSchema,
      maned: manedSchema,
      valutaEnhet: valutaEnhetSchema,
      antall: z14.number().int().min(1),
      /** Den største, til oppgaveteksten. */
      storste: z14.object({ kontonavn: z14.string(), belop: belopSchema, vanlig: belopSchema })
    })
  )
});

// src/schemas/grupper.ts
import { z as z16 } from "zod";

// src/schemas/kpi.ts
import { z as z15 } from "zod";
var maaltallSchema = z15.string().regex(/^-?\d+\.\d{3}$/, "M\xE5ltall m\xE5 ha n\xF8yaktig tre desimaler, som \xAB7.800\xBB.");
var maaltallInnSchema = z15.string().regex(/^-?\d+([.,]\d{1,3})?$/, "Skriv et tall, for eksempel 8,5.");
var maaleenhetSchema = z15.enum(MAALEENHETER);
var retningSchema = z15.enum(RETNINGER);
var aggregeringSchema = z15.enum(AGGREGERINGER);
var kpiStatusSchema = z15.enum(STATUSER);
var utviklingSchema = z15.enum(UTVIKLINGER);
var kpiKategoriSchema = z15.enum(KPI_KATEGORIER);
var kpiKildeSchema = z15.enum(["beregnet", "manuell"]);
var kontovalgSchema = z15.object({
  niva: z15.enum(["artsgruppe", "underkonto"]),
  id: uuidSchema
});
var utvalgsrolleSchema = z15.enum(UTVALGSROLLER);
var lagretRolleutvalgSchema = z15.object({
  kontovalg: z15.array(kontovalgSchema).default([]),
  /**
   * `foreslatt` betyr at importen gjettet. Skillet må bæres helt ut til kortet:
   * en gjetning og et valg gir like troverdige tall å se på, og den ene av dem
   * har ingen sett på ennå.
   */
  opphav: z15.enum(["valgt", "foreslatt"]).default("valgt")
});
var kpiParametreLagretSchema = z15.object({
  roller: z15.partialRecord(utvalgsrolleSchema, lagretRolleutvalgSchema).default({})
});
var kpiParametreFlatSchema = z15.object({
  kontovalg: z15.array(kontovalgSchema)
});
var kpiUtvalgSchema = z15.object({
  rolle: utvalgsrolleSchema,
  navn: z15.string(),
  forklaring: z15.string(),
  /** Hva standarden er, i ord — «alle kontoer i lønn». */
  standardTekst: z15.string(),
  /**
   * Underkontoene standarden løser seg opp til.
   *
   * Sendes med selv om ingen har valgt noe, slik at velgeren kan HUKE AV dem:
   * «alle kontoer i lønn» er ikke etterprøvbart som setning, men det er
   * etterprøvbart som avhukinger i kontoplanen. Tom når standarden er «ingen».
   */
  standardKontoer: z15.array(uuidSchema),
  /**
   * Om rollen HAR en standard å falle tilbake på.
   *
   * Skilt fra at `standardKontoer` er tom: en rolle med standarden «alle
   * kontoer i lønn» har ingen kontoer i en kommune som ennå ikke har ført lønn,
   * og det er noe annet enn en rolle som ikke har noen standard i det hele
   * tatt. Bare den første kan tilbakestilles.
   */
  harStandard: z15.boolean(),
  kontovalg: z15.array(kontovalgSchema),
  /** True når kontoene er importens gjetning og ingen har sett på dem ennå. */
  erForeslatt: z15.boolean(),
  /** True når rollen står tom OG standarden er «ingen» — da måler den ingenting. */
  maaSettesOpp: z15.boolean()
});
var kpiSporringSchema = z15.object({
  ar: arSporringSchema,
  periode: z15.enum(["ar", "maned"]).default("ar"),
  maned: manedSporringSchema.nullable().default(null)
});
var tiltakSporringSchema = z15.object({ ar: arSporringSchema });
var kpiPeriodeSchema = z15.enum(["ar", "maned"]);
var kpiPunktSchema = z15.object({
  maned: manedSchema,
  verdi: maaltallSchema.nullable()
});
var kpiKortSchema = z15.object({
  id: uuidSchema,
  typeNokkel: z15.string(),
  navn: z15.string(),
  forklaring: z15.string(),
  kategori: kpiKategoriSchema,
  enhet: maaleenhetSchema,
  kilde: kpiKildeSchema,
  retning: retningSchema,
  aggregering: aggregeringSchema,
  maal: maaltallSchema.nullable(),
  terskel: maaltallSchema.nullable(),
  /** The value for the period being shown — null when not measured. */
  verdi: maaltallSchema.nullable(),
  status: kpiStatusSchema,
  /** Change from the previous period, and what that change means. */
  differanse: maaltallSchema.nullable(),
  utvikling: utviklingSchema,
  /** Always the twelve months, whichever period is selected. */
  serie: z15.array(kpiPunktSchema),
  opphav: z15.string(),
  antallTiltak: z15.number().int(),
  /** Kontorollene kortet måler over. Tom for typene uten kontoer. */
  utvalg: z15.array(kpiUtvalgSchema)
});
var kpiTypeValgSchema = z15.object({
  nokkel: z15.string(),
  navn: z15.string(),
  forklaring: z15.string(),
  kategori: kpiKategoriSchema,
  enhet: maaleenhetSchema,
  retning: retningSchema,
  erBeregnet: z15.boolean(),
  foreslattMaal: maaltallInnSchema.nullable(),
  foreslattTerskel: maaltallInnSchema.nullable(),
  harUtvalg: z15.boolean()
});
var kpiVisningSchema = z15.object({
  virksomhetId: uuidSchema,
  virksomhetNavn: z15.string(),
  ar: arSporringSchema,
  periode: kpiPeriodeSchema,
  /** Set only when `periode` is 'maned'. */
  maned: manedSchema.nullable(),
  /** The last month with actuals — what the monthly view opens on. */
  sisteBokfortManed: manedSchema.nullable(),
  kort: z15.array(kpiKortSchema),
  tilgjengeligeTyper: z15.array(kpiTypeValgSchema)
});
var lagreKpiSchema = z15.object({
  typeNokkel: z15.string().min(1).max(60),
  maal: maaltallInnSchema.nullable(),
  terskel: maaltallInnSchema.nullable(),
  aktiv: z15.boolean().default(true),
  /**
   * Kontoutvalget per rolle. Taket er ikke vilkårlig: en kommune som fører
   * vikar på hundre kontoer velger artsgruppen, ikke hundre kontoer, og en
   * liste lengre enn kontoplanen ville uansett vært feil. Serveren avviser i
   * tillegg id-er som ikke finnes i kontoplanen, og roller typen ikke har.
   */
  roller: z15.partialRecord(utvalgsrolleSchema, z15.array(kontovalgSchema).max(200)).default({})
});
var lagreMaalingSchema = z15.object({
  ar: arSchema,
  maned: manedSchema.nullable(),
  verdi: maaltallInnSchema,
  kommentar: z15.string().max(500).nullable().default(null)
});
var tiltakStatusSchema = z15.enum(["planlagt", "pagaende", "fullfort", "stanset"]);
var tiltakSchema = z15.object({
  id: uuidSchema,
  ar: arSchema,
  navn: z15.string(),
  beskrivelse: z15.string().nullable(),
  status: tiltakStatusSchema,
  fremdriftProsent: z15.number().int().min(0).max(100),
  fraManed: manedSchema.nullable(),
  tilManed: manedSchema.nullable(),
  /** Kroner, as a decimal string. Positive means a saving. */
  forventetGevinst: z15.string().nullable(),
  realisertGevinst: z15.string().nullable(),
  ansvarligId: uuidSchema.nullable(),
  ansvarligNavn: z15.string().nullable(),
  /** The KPIs this measure is meant to move. */
  kpiIder: z15.array(uuidSchema),
  kpiNavn: z15.array(z15.string()),
  /** Kroner. Satt når tiltaket er et vedtatt kutt; se `domain/gevinstplan.ts`. */
  vedtattGevinst: z15.string().nullable(),
  /** Kuttet kom fra budsjettvedtaket i løsningen, ikke lagt inn for hånd. */
  fraBudsjett: z15.boolean()
});
var lagreTiltakSchema = z15.object({
  ar: arSchema,
  navn: z15.string().min(1).max(200),
  beskrivelse: z15.string().max(5e3).nullable().default(null),
  status: tiltakStatusSchema.default("planlagt"),
  fremdriftProsent: z15.number().int().min(0).max(100).default(0),
  fraManed: manedSchema.nullable().default(null),
  tilManed: manedSchema.nullable().default(null),
  forventetGevinst: z15.string().nullable().default(null),
  realisertGevinst: z15.string().nullable().default(null),
  ansvarligId: uuidSchema.nullable().default(null),
  kpiIder: z15.array(uuidSchema).default([])
});
var tiltakVisningSchema = z15.object({
  virksomhetId: uuidSchema,
  ar: arSchema,
  tiltak: z15.array(tiltakSchema),
  /** Sum of expected gains across measures that are not stopped. */
  sumForventetGevinst: z15.string(),
  sumRealisertGevinst: z15.string()
});
var gevinstvurderingSchema = z15.enum(GEVINSTVURDERINGER);
var gevinsttallSchema = z15.object({
  /** Kroner, som desimaltekst. */
  vedtatt: z15.string(),
  planlagtHittil: z15.string(),
  realisert: z15.string(),
  forventet: z15.string(),
  udekket: z15.string()
});
var vedtattKuttSchema = gevinsttallSchema.extend({
  id: uuidSchema,
  navn: z15.string(),
  beskrivelse: z15.string().nullable(),
  /** Null for et kutt på området som helhet. */
  virksomhet: z15.object({ id: uuidSchema, navn: z15.string() }).nullable(),
  status: tiltakStatusSchema,
  fremdriftProsent: z15.number().int().min(0).max(100),
  fraManed: manedSchema.nullable(),
  tilManed: manedSchema.nullable(),
  ansvarligNavn: z15.string().nullable(),
  fraBudsjett: z15.boolean(),
  vurdering: gevinstvurderingSchema,
  /** Om kalleren kan melde gevinst på kuttet. */
  kanMelde: z15.boolean()
});
var gevinstsumSchema = gevinsttallSchema.extend({
  antall: z15.object({
    i_rute: z15.number().int(),
    etter_plan: z15.number().int(),
    svikter: z15.number().int(),
    stanset: z15.number().int()
  })
});
var vedtatteKuttOmradeSchema = z15.object({
  id: uuidSchema,
  navn: z15.string(),
  sum: gevinstsumSchema,
  kutt: z15.array(vedtattKuttSchema)
});
var vedtatteKuttSchema = z15.object({
  ar: arSchema,
  /** Måneden regnskapet er ført til; det planlagte er regnet hit. */
  tilOgMedManed: manedSchema.nullable(),
  /** Om kalleren kan legge inn og endre det vedtatte: økonomisjefen og controllerne. */
  kanStyre: z15.boolean(),
  sum: gevinstsumSchema,
  omrader: z15.array(vedtatteKuttOmradeSchema)
});
var vedtatteKuttSporringSchema = z15.object({ ar: arSporringSchema });
var meldGevinstSchema = z15.object({
  status: tiltakStatusSchema,
  fremdriftProsent: z15.number().int().min(0).max(100),
  realisertGevinst: z15.string().nullable(),
  forventetGevinst: z15.string().nullable()
});
var lagreVedtattKuttSchema = z15.object({
  ar: arSchema,
  navn: z15.string().min(1).max(200),
  beskrivelse: z15.string().max(5e3).nullable().default(null),
  virksomhetId: uuidSchema.nullable().default(null),
  omradeId: uuidSchema.nullable().default(null),
  vedtattGevinst: z15.string(),
  fraManed: manedSchema.nullable().default(null),
  tilManed: manedSchema.nullable().default(null)
}).refine((k2) => k2.virksomhetId === null !== (k2.omradeId === null), {
  message: "Et vedtatt kutt h\xF8rer til \xE9n virksomhet eller ett omr\xE5de."
});
var tiltaksgapSchema = z15.object({
  avvik: belopSchema,
  gjenstaende: belopSchema,
  udekket: belopSchema,
  dekningProsent: z15.number().min(0).max(100).nullable(),
  dekning: z15.enum(GAPDEKNINGER)
});
var tiltaksdekningSchema = z15.object({
  ar: arSchema,
  sum: tiltaksgapSchema,
  omrader: z15.array(
    z15.object({
      id: uuidSchema,
      navn: z15.string(),
      sum: tiltaksgapSchema,
      /** Gjenstående gevinst fra tiltak på området som helhet, ikke på en virksomhet. */
      paOmradet: belopSchema,
      virksomheter: z15.array(
        tiltaksgapSchema.extend({
          id: uuidSchema,
          navn: z15.string(),
          /** Tiltakene som teller: alle som ikke er stanset. */
          antallTiltak: z15.number().int().min(0)
        })
      )
    })
  )
});

// src/schemas/grupper.ts
var hendelse3 = z16.object({ tidspunkt: z16.iso.datetime(), av: z16.string().nullable() });
var person = z16.object({ id: uuidSchema, navn: z16.string() });
var gruppetypeSchema = z16.enum(GRUPPETYPER);
var grupperolleSchema = z16.enum(GRUPPEROLLER);
var aksjonsstatusSchema = z16.enum(AKSJONSSTATUSER);
var gruppeomfangSchema = z16.discriminatedUnion("type", [
  z16.object({ type: z16.literal("kommune"), navn: z16.string() }),
  z16.object({ type: z16.literal("omrade"), id: uuidSchema, navn: z16.string() }),
  z16.object({ type: z16.literal("virksomhet"), id: uuidSchema, navn: z16.string() })
]);
var motelenke = z16.object({ id: uuidSchema, dato: z16.iso.date(), tittel: z16.string() });
var aksjonspunktSchema = z16.object({
  id: uuidSchema,
  mote: motelenke,
  tekst: z16.string(),
  ansvarlig: person.nullable(),
  frist: z16.iso.date().nullable(),
  status: aksjonsstatusSchema,
  lukket: hendelse3.extend({ kommentar: z16.string().nullable() }).nullable(),
  /** Den ansvarlige og gruppens ledere kan lukke og åpne punktet igjen. */
  kanLukke: z16.boolean()
});
var gruppeoversiktSchema = z16.object({
  kanOpprette: z16.object({
    kommune: z16.boolean(),
    omrader: z16.array(person),
    virksomheter: z16.array(person)
  }),
  grupper: z16.array(
    z16.object({
      id: uuidSchema,
      navn: z16.string(),
      type: gruppetypeSchema,
      omfang: gruppeomfangSchema,
      rolle: grupperolleSchema,
      antallMedlemmer: z16.number().int().min(1),
      nesteMote: motelenke.nullable(),
      sisteMote: motelenke.nullable(),
      apneAksjonspunkter: z16.number().int().min(0)
    })
  )
});
var gruppeSchema = z16.object({
  id: uuidSchema,
  navn: z16.string(),
  type: gruppetypeSchema,
  beskrivelse: z16.string(),
  omfang: gruppeomfangSchema,
  versjon: z16.number().int().min(1),
  /** Om kalleren leder gruppen: endrer den og medlemmene. */
  kanStyre: z16.boolean(),
  medlemmer: z16.array(person.extend({ epost: z16.string(), rolle: grupperolleSchema })),
  moter: z16.array(
    motelenke.extend({ harReferat: z16.boolean(), apneAksjonspunkter: z16.number().int().min(0) })
  ),
  /** De åpne, og de som er lukket de siste 30 dagene. */
  aksjonspunkter: z16.array(aksjonspunktSchema)
});
var navn = z16.string().trim().min(1).max(150);
var nyGruppeSchema = z16.object({
  navn,
  type: gruppetypeSchema,
  beskrivelse: z16.string().trim().max(2e3).default(""),
  omradeId: uuidSchema.nullable().default(null),
  virksomhetId: uuidSchema.nullable().default(null)
}).refine((g) => g.omradeId === null || g.virksomhetId === null, {
  message: "En gruppe h\xF8rer til kommunen, ett omr\xE5de eller \xE9n virksomhet."
});
var endreGruppeSchema = z16.object({
  versjon: z16.number().int().min(1),
  navn,
  type: gruppetypeSchema,
  beskrivelse: z16.string().trim().max(2e3)
});
var settMedlemmerSchema = z16.object({
  medlemmer: z16.array(z16.object({ brukerId: uuidSchema, rolle: grupperolleSchema })).min(1).max(200).refine((m) => new Set(m.map((x) => x.brukerId)).size === m.length, {
    message: "Hvert medlem \xE9n gang."
  }).refine((m) => m.some((x) => x.rolle === "leder"), {
    message: "Gruppen m\xE5 ha minst \xE9n leder."
  })
});
var gruppekandidaterSchema = z16.array(person.extend({ epost: z16.string() }));
var moteSchema = z16.object({
  id: uuidSchema,
  gruppe: person,
  dato: z16.iso.date(),
  tittel: z16.string(),
  agenda: z16.string(),
  referat: z16.string(),
  versjon: z16.number().int().min(1),
  sistEndret: hendelse3.nullable(),
  /** Om kalleren kan slette møtet: gruppens ledere. */
  kanSlette: z16.boolean(),
  aksjonspunkter: z16.array(aksjonspunktSchema),
  /** Medlemmene, til å velge en ansvarlig. */
  medlemmer: z16.array(person)
});
var nyttMoteSchema = z16.object({
  dato: z16.iso.date(),
  tittel: z16.string().trim().min(1).max(150),
  agenda: z16.string().max(2e4).default("")
});
var lagreMoteSchema = z16.object({
  versjon: z16.number().int().min(1),
  dato: z16.iso.date(),
  tittel: z16.string().trim().min(1).max(150),
  agenda: z16.string().max(2e4),
  referat: z16.string().max(5e4)
});
var nyttAksjonspunktSchema = z16.object({
  tekst: z16.string().trim().min(1).max(1e3),
  ansvarligId: uuidSchema.nullable().default(null),
  frist: z16.iso.date().nullable().default(null)
});
var endreAksjonspunktSchema = z16.object({
  status: aksjonsstatusSchema,
  kommentar: z16.string().trim().min(1).max(1e3).nullable().default(null)
});
var aksjonsoppgaverSchema = z16.object({
  oppgaver: z16.array(
    z16.object({
      id: uuidSchema,
      tekst: z16.string(),
      frist: z16.iso.date().nullable(),
      gruppe: person,
      mote: motelenke
    })
  )
});
var milepaelstatusSchema = z16.enum(MILEPAELSTATUSER);
var milepaelSchema = z16.object({
  id: uuidSchema,
  navn: z16.string(),
  frist: z16.iso.date().nullable(),
  status: milepaelstatusSchema,
  ferdig: z16.iso.datetime().nullable(),
  forsinket: z16.boolean()
});
var tiltaksvalg = z16.object({
  id: uuidSchema,
  ar: arSchema,
  navn: z16.string(),
  /** Virksomheten, eller området for et tiltak på området. */
  sted: z16.string()
});
var kpivalg = z16.object({ id: uuidSchema, navn: z16.string(), sted: z16.string() });
var prosjektfremdriftSchema = z16.object({
  gruppeId: uuidSchema,
  /** Året tiltakene og KPI-ene måles for. */
  ar: arSchema,
  /** Gruppens ledere endrer milepælene og koblingene. */
  kanStyre: z16.boolean(),
  vurdering: z16.enum(PROSJEKTVURDERINGER),
  milepaeler: z16.object({
    ferdige: z16.number().int().min(0),
    totalt: z16.number().int().min(0),
    forsinkede: z16.number().int().min(0),
    liste: z16.array(milepaelSchema)
  }),
  gevinst: z16.object({
    /** Kroner. */
    forventet: belopSchema,
    realisert: belopSchema,
    prosent: z16.number().int().nullable()
  }),
  tiltak: z16.array(
    tiltaksvalg.extend({
      status: tiltakStatusSchema,
      fremdriftProsent: z16.number().int().min(0).max(100),
      forventet: belopSchema.nullable(),
      realisert: belopSchema.nullable()
    })
  ),
  kpier: z16.array(
    kpivalg.extend({
      enhet: maaleenhetSchema,
      verdi: maaltallSchema.nullable(),
      maal: maaltallSchema.nullable(),
      status: kpiStatusSchema
    })
  ),
  kpiTelling: z16.object({
    god: z16.number().int(),
    varsel: z16.number().int(),
    kritisk: z16.number().int(),
    utenVurdering: z16.number().int()
  }),
  /** Det lederen kan koble til: i gruppens område og innenfor egen tilgang. Null for andre. */
  valg: z16.object({ tiltak: z16.array(tiltaksvalg), kpier: z16.array(kpivalg) }).nullable()
});
var lagreMilepaelSchema = z16.object({
  navn: z16.string().trim().min(1).max(200),
  frist: z16.iso.date().nullable().default(null),
  status: milepaelstatusSchema.default("planlagt")
});
var settKoblingerSchema = z16.object({
  tiltak: z16.array(z16.object({ ar: arSchema, id: uuidSchema })).max(100),
  kpier: z16.array(uuidSchema).max(100)
});

// src/schemas/prosjektportefolje.ts
import { z as z17 } from "zod";
var prosjektfaseSchema = z17.enum(PROSJEKTFASER);
var risikonivaSchema = z17.enum(RISIKONIVAER);
var portefoljestatusSchema = z17.enum(PORTEFOLJESTATUSER);
var telling = z17.object({
  forsinket: z17.number().int(),
  risiko: z17.number().int(),
  i_rute: z17.number().int(),
  ikke_startet: z17.number().int(),
  avsluttet: z17.number().int()
});
var portefoljeprosjektSchema = z17.object({
  id: uuidSchema,
  navn: z17.string(),
  omfang: gruppeomfangSchema,
  /** Gruppens ledere: prosjektlederne. */
  ledere: z17.array(z17.string()),
  fase: prosjektfaseSchema,
  startdato: z17.iso.date().nullable(),
  sluttdato: z17.iso.date().nullable(),
  risiko: risikonivaSchema.nullable(),
  statuskommentar: z17.string().nullable(),
  statusEndret: z17.object({ tidspunkt: z17.iso.datetime(), av: z17.string().nullable() }).nullable(),
  status: portefoljestatusSchema,
  /** Hvorfor statusen er som den er, med den alvorligste grunnen først. */
  grunner: z17.array(z17.string()),
  milepaeler: z17.object({
    ferdige: z17.number().int(),
    totalt: z17.number().int(),
    forsinkede: z17.number().int(),
    neste: z17.object({ navn: z17.string(), frist: z17.iso.date().nullable() }).nullable()
  }),
  gevinst: z17.object({
    forventet: belopSchema,
    realisert: belopSchema,
    prosent: z17.number().int().nullable()
  }),
  kpier: z17.object({
    god: z17.number().int(),
    varsel: z17.number().int(),
    kritisk: z17.number().int(),
    utenVurdering: z17.number().int()
  }),
  investering: z17.object({
    id: uuidSchema,
    kode: z17.string(),
    navn: z17.string(),
    budsjett: belopSchema,
    regnskap: belopSchema,
    arsprognose: belopSchema.nullable(),
    kostnadsramme: belopSchema.nullable(),
    sluttprognose: belopSchema.nullable(),
    status: z17.enum(INVESTERINGSSTATUSER),
    sprekker: z17.boolean()
  }).nullable(),
  /** Medlemmene kan åpne gruppen; de andre ser bare porteføljen. */
  erMedlem: z17.boolean(),
  /** Gruppens ledere og den som styrer, endrer fase, datoer, risiko og kobling. */
  kanEndre: z17.boolean(),
  versjon: z17.number().int()
});
var portefoljeSchema = z17.object({
  ar: arSchema,
  telling,
  prosjekter: z17.array(portefoljeprosjektSchema),
  /** Investeringene kalleren kan koble et prosjekt til. */
  investeringsvalg: z17.array(z17.object({ id: uuidSchema, kode: z17.string(), navn: z17.string() }))
});
var lagreProsjektinfoSchema = z17.object({
  fase: prosjektfaseSchema,
  startdato: z17.iso.date().nullable(),
  sluttdato: z17.iso.date().nullable(),
  risiko: risikonivaSchema.nullable(),
  statuskommentar: z17.string().trim().max(500).nullable(),
  investeringId: uuidSchema.nullable(),
  versjon: z17.number().int()
}).refine((d) => d.startdato === null || d.sluttdato === null || d.sluttdato >= d.startdato, {
  message: "Sluttdatoen kan ikke v\xE6re f\xF8r startdatoen.",
  path: ["sluttdato"]
});

// src/schemas/rapport.ts
import { z as z18 } from "zod";
var rapporttypeSchema = z18.enum(RAPPORTTYPER);
var periodetypeSchema = z18.enum(PERIODETYPER);
var rapportsporringSchema = z18.object({
  ar: arSporringSchema,
  type: rapporttypeSchema,
  nummer: z18.coerce.number().int().optional()
}).refine((s) => rapportperiode(s.type, s.nummer ?? null) !== null, {
  message: "Velg en m\xE5ned (1\u201312) eller et tertial (1\u20133) som finnes.",
  path: ["nummer"]
});
var okonomisporringSchema = z18.object({
  ar: arSporringSchema,
  type: periodetypeSchema,
  nummer: z18.coerce.number().int().optional()
}).refine((s) => rapportperiode(s.type, s.nummer ?? null) !== null, {
  message: "Velg en m\xE5ned (1\u201312) eller et tertial (1\u20133) som finnes.",
  path: ["nummer"]
});
var hendelse4 = z18.object({ tidspunkt: z18.iso.datetime(), av: z18.string().nullable() });
var rapporttallSchema = z18.object({
  budsjett: belopSchema,
  prognose: belopSchema,
  avvik: belopSchema,
  avvikProsent: z18.number().nullable()
});
var dekningSchema = z18.object({
  antall: z18.number().int().min(0),
  godkjent: z18.number().int().min(0),
  venter: z18.number().int().min(0),
  sendtTilbake: z18.number().int().min(0),
  mangler: z18.number().int().min(0)
});
var hovedkontolinjeSchema = z18.object({
  id: uuidSchema,
  kontonummer: z18.string().nullable(),
  navn: z18.string(),
  kategori: kategoriSchema,
  budsjett: belopSchema,
  prognose: belopSchema,
  avvik: belopSchema
});
var virksomhetslinjeSchema = z18.object({
  id: uuidSchema,
  navn: z18.string(),
  status: z18.enum(["godkjent", "levert", "sendt_tilbake", "ikke_levert"]),
  tall: rapporttallSchema.nullable(),
  iSummen: z18.boolean(),
  endring: belopSchema.nullable(),
  levert: hendelse4.nullable(),
  kommentar: z18.string().nullable(),
  behandlet: hendelse4.extend({ kommentar: z18.string().nullable() }).nullable(),
  kommentarer: z18.array(hendelse4.extend({ tekst: z18.string() })),
  /** Avvikene virksomheten forklarte, med avviket i tusen kroner. Tomt i eldre rapporter. */
  forklaringer: z18.array(
    z18.object({
      hovedkonto: z18.string(),
      avvik: belopSchema,
      arsak: z18.string(),
      tiltak: z18.string().nullable(),
      tiltakNavn: z18.string().nullable()
    })
  ).default([])
});
var summerSchema = {
  sum: rapporttallSchema,
  endring: belopSchema.nullable(),
  dekning: dekningSchema,
  hovedkontoer: z18.array(hovedkontolinjeSchema)
};
var okonomirapportSchema = z18.object({
  /** «Tertialrapport 2. tertial 2026». */
  tittel: z18.string(),
  /** Området, eller kommunen. */
  omfang: z18.object({
    type: z18.enum(["omrade", "kommune"]),
    id: uuidSchema,
    navn: z18.string()
  }),
  type: periodetypeSchema,
  ar: arSchema,
  periode: manedSchema,
  /** Perioden endringen er regnet mot. Null for den første i året. */
  sammenlignetMed: manedSchema.nullable(),
  /** Når rapporten ble laget. Den bygger på leveringene slik de sto da. */
  laget: z18.iso.datetime(),
  ...summerSchema,
  omrader: z18.array(
    z18.object({
      id: uuidSchema,
      navn: z18.string(),
      ...summerSchema,
      virksomheter: z18.array(virksomhetslinjeSchema)
    })
  )
});
var rapportomfangSchema = z18.object({
  omrader: z18.array(z18.object({ id: uuidSchema, navn: z18.string() })),
  kommune: z18.object({ id: uuidSchema, navn: z18.string() }).nullable()
});

// src/schemas/rapportbygger.ts
import { z as z20 } from "zod";

// src/schemas/budsjett.ts
import { z as z19 } from "zod";
var hendelse5 = z19.object({ tidspunkt: z19.iso.datetime(), av: z19.string().nullable() });
var fireaarSchema = z19.tuple([belopSchema, belopSchema, belopSchema, belopSchema]);
var ikkeNegativt = belopSchema.refine(
  (b) => !b.startsWith("-"),
  "Bel\xF8pet kan ikke v\xE6re negativt."
);
var fireaarIkkeNegativt = z19.tuple([ikkeNegativt, ikkeNegativt, ikkeNegativt, ikkeNegativt]);
var budsjettfaseSchema = z19.enum(BUDSJETTFASER);
var tiltakstypeSchema = z19.enum(TILTAKSTYPER);
var tiltaksstatusSchema = z19.enum(TILTAKSSTATUSER);
var endringskategoriSchema = z19.enum(ENDRINGSKATEGORIER);
var budsjettendringSchema = z19.object({
  id: uuidSchema,
  omradeId: uuidSchema,
  kategori: endringskategoriSchema,
  tekst: z19.string(),
  belop: fireaarSchema,
  laget: hendelse5
});
var budsjetttiltakSchema = z19.object({
  id: uuidSchema,
  omradeId: uuidSchema,
  virksomhet: z19.object({ id: uuidSchema, navn: z19.string() }).nullable(),
  type: tiltakstypeSchema,
  tittel: z19.string(),
  beskrivelse: z19.string(),
  /** Hva tiltaket betyr for tjenestene og innbyggerne. */
  konsekvens: z19.string(),
  /** Positive tall; typen sier om det spares eller brukes. */
  belop: fireaarSchema,
  status: tiltaksstatusSchema,
  laget: hendelse5,
  endret: hendelse5.nullable(),
  versjon: z19.number().int().min(1),
  /** Om kalleren kan endre tiltaket nå. */
  kanEndre: z19.boolean()
});
var tolvSchema = z19.array(belopSchema).length(12);
var budsjettfordelingSchema = z19.object({
  virksomhet: z19.object({ id: uuidSchema, navn: z19.string() }),
  belop: fireaarSchema,
  /** Summen av virksomhetens detaljbudsjett for budsjettåret. */
  detaljsum: belopSchema,
  /** Null før noe er fordelt til virksomheten. */
  versjon: z19.number().int().min(1).nullable(),
  /** Om kalleren kan åpne virksomhetens detaljbudsjett. */
  kanBudsjettere: z19.boolean()
});
var budsjettomradeSchema = z19.object({
  id: uuidSchema,
  navn: z19.string(),
  /** Fjorårets budsjett for området. */
  grunnlag: belopSchema,
  /** Grunnlaget i budsjettårets kroner. */
  justert: belopSchema,
  endringer: fireaarSchema,
  ramme: fireaarSchema,
  /** Effekten av de innstilte tiltakene. */
  tiltak: fireaarSchema,
  forslag: fireaarSchema,
  endringsliste: z19.array(budsjettendringSchema),
  tiltaksliste: z19.array(budsjetttiltakSchema),
  /** Om kalleren kan legge inn tiltak for området nå. */
  kanForeslaTiltak: z19.boolean(),
  virksomheter: z19.array(z19.object({ id: uuidSchema, navn: z19.string() })),
  /** Forslaget fordelt på virksomhetene, én rad per aktiv virksomhet. */
  fordeling: z19.array(budsjettfordelingSchema),
  /** Forslaget minus det som er fordelt. */
  udisponert: fireaarSchema,
  /** Om kalleren kan fordele områdets ramme nå. */
  kanFordele: z19.boolean(),
  /** Rammen slik kommunestyret vedtok den. Null før vedtaket. */
  vedtatt: fireaarSchema.nullable()
});
var budsjettprosessSchema = z19.object({
  prosess: z19.object({
    id: uuidSchema,
    ar: arSchema,
    planar: z19.tuple([arSchema, arSchema, arSchema, arSchema]),
    fase: budsjettfaseSchema,
    /** Lønns- og prisveksten grunnlaget justeres med, i prosent. */
    prisvekst: prosentSchema,
    /** Det som er til fordeling til driften, per år. Null før det er satt. */
    tilFordeling: fireaarSchema.nullable(),
    /** Fristen for områdenes innspill. */
    frist: z19.iso.date().nullable(),
    grunnlagOppdatert: hendelse5,
    versjon: z19.number().int().min(1),
    /** Kommunestyrets vedtak. Null før det er registrert. */
    vedtak: z19.object({ dato: z19.iso.date(), saksnummer: z19.string().nullable(), registrert: hendelse5 }).nullable(),
    /**
     * Når detaljbudsjettet ble kommunens budsjett. Etter det er fordelingen og
     * detaljbudsjettet låst, og importer endrer ikke budsjettet for året.
     */
    tattIBruk: hendelse5.nullable()
  }).nullable(),
  omrader: z19.array(budsjettomradeSchema),
  /** Summen av områdenes forslag. */
  sum: fireaarSchema,
  ubalanse: fireaarSchema.nullable(),
  /**
   * Det de innstilte investeringene koster driften — renter, avdrag og andre
   * konsekvenser — og som er trukket fra i `ubalanse`. Null for den som ikke
   * styrer.
   */
  investering: fireaarSchema.nullable(),
  /** Om kalleren styrer budsjettet: økonomisjefen eller en controller. */
  kanStyre: z19.boolean(),
  /**
   * Virksomhetene kalleren leder eller fører økonomien for, og kan budsjettere
   * per konto og måned — utenom dem som står under områdene over.
   */
  egneVirksomheter: z19.array(z19.object({ id: uuidSchema, navn: z19.string() }))
});
var budsjettprosesserSchema = z19.object({
  prosesser: z19.array(z19.object({ ar: arSchema, fase: budsjettfaseSchema })),
  kanStyre: z19.boolean()
});
var opprettBudsjettSchema = z19.object({ ar: arSchema });
var endreBudsjettSchema = z19.object({
  versjon: z19.number().int().min(1),
  fase: budsjettfaseSchema.optional(),
  prisvekst: prosentSchema.optional(),
  tilFordeling: fireaarSchema.nullable().optional(),
  frist: z19.iso.date().nullable().optional()
});
var nyBudsjettendringSchema = z19.object({
  omradeId: uuidSchema,
  kategori: endringskategoriSchema,
  tekst: z19.string().trim().min(1).max(300),
  belop: fireaarSchema
});
var tiltaksfelt = {
  virksomhetId: uuidSchema.nullable().default(null),
  type: tiltakstypeSchema,
  tittel: z19.string().trim().min(1).max(200),
  beskrivelse: z19.string().trim().max(5e3).default(""),
  konsekvens: z19.string().trim().max(5e3).default(""),
  belop: fireaarIkkeNegativt
};
var nyttBudsjetttiltakSchema = z19.object({ omradeId: uuidSchema, ...tiltaksfelt });
var endreBudsjetttiltakSchema = z19.object({
  versjon: z19.number().int().min(1),
  ...tiltaksfelt
});
var tiltaksstatusInnSchema = z19.object({ status: tiltaksstatusSchema });
var settBudsjettfordelingSchema = z19.object({
  belop: fireaarSchema,
  /** Null når virksomheten ikke har fått noe fordelt før. */
  versjon: z19.number().int().min(1).nullable()
});
var detaljradSchema = z19.object({
  underkonto: z19.object({
    id: uuidSchema,
    kontonummer: z19.string().nullable(),
    navn: z19.string(),
    kategori: kategoriSchema
  }),
  maneder: tolvSchema,
  /** Fjorårets budsjett for kontoen, i tusen kroner. Profilen månedene kan følge. */
  fjoraar: tolvSchema,
  /** Null når kontoen bare står i fjorårets budsjett, og ikke er budsjettert ennå. */
  versjon: z19.number().int().min(1).nullable()
});
var detaljbudsjettSchema = z19.object({
  ar: arSchema,
  fase: budsjettfaseSchema,
  /** Lønns- og prisveksten fjorårets budsjett justeres med. */
  prisvekst: prosentSchema,
  virksomhet: z19.object({
    id: uuidSchema,
    navn: z19.string(),
    omradeId: uuidSchema,
    omradeNavn: z19.string()
  }),
  /** Det virksomheten har fått, per år i økonomiplanen. Null før noe er fordelt. */
  fordeling: fireaarSchema.nullable(),
  rader: z19.array(detaljradSchema),
  sum: tolvSchema,
  /** Rammen for budsjettåret minus summen av kontoene. Null uten ramme. */
  avvik: belopSchema.nullable(),
  kanEndre: z19.boolean()
});
var settDetaljradSchema = z19.object({
  maneder: tolvSchema,
  /** Null når kontoen ikke er budsjettert før. */
  versjon: z19.number().int().min(1).nullable()
});
var forslagstypeSchema = z19.enum(FORSLAGSTYPER);
var forslagslinjeSchema = z19.object({
  id: uuidSchema,
  /** Området linjen gjelder. Null er en endring i det som er til fordeling. */
  omradeId: uuidSchema.nullable(),
  tekst: z19.string(),
  belop: fireaarSchema,
  laget: hendelse5
});
var forslagstall = {
  omrader: z19.array(z19.object({ id: uuidSchema, ramme: fireaarSchema })),
  sum: fireaarSchema,
  /** Det som er til fordeling, med forslagets endringer i det. */
  tilFordeling: fireaarSchema.nullable(),
  /** Etter det investeringene forslaget har med, koster driften. */
  ubalanse: fireaarSchema.nullable(),
  /** Brutto utgift i investeringsprosjektene forslaget har med. */
  investeringsutgift: fireaarSchema,
  /** Det prosjektene forslaget har med, koster driften. */
  investering: fireaarSchema
};
var budsjettforslagSchema = z19.object({
  id: uuidSchema,
  type: forslagstypeSchema,
  navn: z19.string(),
  merknad: z19.string(),
  versjon: z19.number().int().min(1),
  /** Tiltakene forslaget tar ut eller inn, mot kommunedirektørens innstilling. */
  valg: z19.array(z19.object({ tiltakId: uuidSchema, med: z19.boolean() })),
  /** Investeringsprosjektene forslaget tar ut eller inn. */
  prosjektvalg: z19.array(z19.object({ prosjektId: uuidSchema, med: z19.boolean() })),
  linjer: z19.array(forslagslinjeSchema),
  laget: hendelse5,
  endret: hendelse5.nullable(),
  ...forslagstall
});
var budsjettforslageneSchema = z19.object({
  omrader: z19.array(z19.object({ id: uuidSchema, navn: z19.string() })),
  /** Tiltakene forslagene kan ta ut og inn: de som er innstilt eller vurdert. */
  tiltak: z19.array(
    z19.object({
      id: uuidSchema,
      omradeId: uuidSchema,
      tittel: z19.string(),
      type: tiltakstypeSchema,
      status: tiltaksstatusSchema,
      virksomhet: z19.string().nullable(),
      belop: fireaarSchema
    })
  ),
  /** Investeringsprosjektene forslagene kan ta ut og inn. */
  prosjekter: z19.array(
    z19.object({
      id: uuidSchema,
      navn: z19.string(),
      omradeId: uuidSchema.nullable(),
      status: tiltaksstatusSchema,
      utgift: fireaarSchema
    })
  ),
  kommunedirektoren: z19.object(forslagstall),
  forslag: z19.array(budsjettforslagSchema),
  /** Om kalleren kan legge inn og endre forslag nå. */
  kanEndre: z19.boolean()
});
var nyttBudsjettforslagSchema = z19.object({
  type: forslagstypeSchema,
  navn: z19.string().trim().min(1).max(150),
  merknad: z19.string().trim().max(5e3).default(""),
  /**
   * Forslaget det nye starter fra: tiltakene og linjene kopieres. Null er
   * kommunedirektørens forslag.
   */
  grunnlag: uuidSchema.nullable().default(null)
});
var endreBudsjettforslagSchema = z19.object({
  versjon: z19.number().int().min(1),
  navn: z19.string().trim().min(1).max(150),
  merknad: z19.string().trim().max(5e3)
});
var settForslagsvalgSchema = z19.object({
  /** Null følger kommunedirektørens innstilling. */
  med: z19.boolean().nullable()
});
var nyForslagslinjeSchema = z19.object({
  omradeId: uuidSchema.nullable(),
  tekst: z19.string().trim().min(1).max(300),
  belop: fireaarSchema
});
var registrerVedtakSchema = z19.object({
  dato: z19.iso.date(),
  saksnummer: z19.string().trim().max(50).nullable().default(null)
});
var opprettetBudsjettforslagSchema = z19.object({ id: uuidSchema });
var budsjettibrukSchema = z19.object({
  kanTaIBruk: z19.boolean(),
  tattIBruk: hendelse5.nullable(),
  /** Det som ikke går opp for budsjettåret. Tomt når alt er fordelt og budsjettert. */
  avvik: z19.array(
    z19.object({ type: z19.enum(OVERFORINGSAVVIK), navn: z19.string(), belop: belopSchema })
  ),
  /** Virksomhetene som har et detaljbudsjett, og som får budsjettlinjer. */
  virksomheter: z19.number().int().min(0),
  /**
   * Virksomhetene med et detaljbudsjett, men uten ansvar i kontostrengen. Filen
   * til økonomisystemet får ingen ansvarskode for dem, og kan ikke leses inn.
   */
  utenAnsvar: z19.array(z19.string())
});
var taIBrukSchema = z19.object({
  /** Ta i bruk selv om noe ikke går opp. Uten den avvises det. */
  tillatAvvik: z19.boolean().default(false)
});
var budsjettoppgaveSchema = z19.discriminatedUnion("oppgave", [
  z19.object({
    oppgave: z19.literal("fordel"),
    ar: arSchema,
    omradeId: uuidSchema,
    omrade: z19.string(),
    frist: z19.iso.date().nullable(),
    ikkeFordelt: belopSchema
  }),
  z19.object({
    oppgave: z19.literal("detaljbudsjett"),
    ar: arSchema,
    virksomhetId: uuidSchema,
    virksomhet: z19.string(),
    frist: z19.iso.date().nullable(),
    ikkeBudsjettert: belopSchema
  })
]);
var budsjettoppgaverSchema = z19.object({ oppgaver: z19.array(budsjettoppgaveSchema) });

// src/schemas/rapportbygger.ts
var tittel = z20.string().trim().min(1).max(150);
var veiledning = z20.string().trim().max(500).nullable().default(null);
var kapittelId = z20.string().min(1).max(64);
var felles = { id: kapittelId, tittel, veiledning };
var kapittelSchema = z20.discriminatedUnion("type", [
  z20.object({ ...felles, type: z20.literal("tekst") }),
  z20.object({
    ...felles,
    type: z20.literal("omrader"),
    underoverskrifter: z20.array(z20.object({ id: kapittelId, tittel })).min(1).max(MAKS_UNDEROVERSKRIFTER),
    medOkonomi: z20.boolean()
  }),
  z20.object({ ...felles, type: z20.literal("okonomi_sammendrag") }),
  z20.object({ ...felles, type: z20.literal("okonomi_hovedkonto") }),
  z20.object({ ...felles, type: z20.literal("okonomi_kommentarer") }),
  z20.object({ ...felles, type: z20.literal("okonomi_budsjettendringer") }),
  z20.object({
    ...felles,
    type: z20.literal("kpi"),
    kategorier: z20.array(z20.enum(KPI_KATEGORIER)).max(KPI_KATEGORIER.length)
  }),
  z20.object({ ...felles, type: z20.literal("budsjett_oversikt") }),
  z20.object({ ...felles, type: z20.literal("budsjett_tiltak"), medIkkeInnstilte: z20.boolean() }),
  z20.object({ ...felles, type: z20.literal("budsjett_investering") })
]);
var kapitlerSchema = z20.array(kapittelSchema).max(MAKS_KAPITLER).superRefine((kapitler, ctx) => {
  const feil = oppsettfeil(kapitler);
  if (feil !== null) ctx.addIssue({ code: "custom", message: feil });
});
var hendelse6 = z20.object({ tidspunkt: z20.iso.datetime(), av: z20.string().nullable() });
var rapportmalSchema = z20.object({
  type: rapporttypeSchema,
  kapitler: z20.array(kapittelSchema),
  /** 0 før malen er lagret: da er det standardmalen som vises. */
  versjon: z20.number().int().min(0),
  sistEndret: hendelse6.nullable()
});
var lagreOppsettSchema = z20.object({
  versjon: z20.number().int().min(0),
  kapitler: kapitlerSchema
});
var elementtittel = z20.string().trim().max(200);
var kilde = z20.string().trim().max(300).nullable().default(null);
var celle3 = z20.string().max(MAKS_CELLE);
var tabellobjekt = z20.object({
  type: z20.literal("tabell"),
  tittel: elementtittel,
  kolonner: z20.array(celle3).min(1).max(MAKS_KOLONNER),
  rader: z20.array(z20.array(celle3)).max(MAKS_RADER),
  kilde
});
var tabellregler = (s) => s.refine((t2) => t2.rader.every((r) => r.length === t2.kolonner.length), {
  message: "Hver rad m\xE5 ha like mange celler som tabellen har kolonner."
});
var figurobjekt = z20.object({
  type: z20.literal("figur"),
  tittel: elementtittel,
  figurtype: z20.enum(FIGURTYPER),
  /** Hva tallene er målt i: «kr 1000», «%», «årsverk». */
  enhet: z20.string().trim().max(30).nullable().default(null),
  kategorier: z20.array(z20.string().trim().min(1).max(100)).min(1).max(MAKS_KATEGORIER),
  serier: z20.array(
    z20.object({
      navn: z20.string().trim().min(1).max(100),
      verdier: z20.array(z20.number().nullable())
    })
  ).min(1).max(MAKS_SERIER),
  kilde
});
var figurregler = (s) => s.refine((f) => f.serier.every((x) => x.verdier.length === f.kategorier.length), {
  message: "Hver serie m\xE5 ha ett tall for hver kategori."
}).refine((f) => f.figurtype !== "kake" || f.serier.length === 1, {
  message: "Et kakediagram viser \xE9n serie."
}).refine(
  (f) => f.figurtype !== "kake" || (f.serier[0]?.verdier ?? []).every((v) => v === null || v >= 0),
  { message: "Et kakediagram kan ikke vise negative tall." }
);
var tabellinnhold = tabellregler(tabellobjekt);
var figurinnhold = figurregler(figurobjekt);
var bildeinnhold = z20.object({
  type: z20.literal("bilde"),
  bildeId: uuidSchema,
  /** Bildeteksten, under bildet. */
  tittel: elementtittel,
  /** Hva bildet viser, for den som ikke ser det. Påkrevd. */
  alt: z20.string().trim().min(1, "Beskriv hva bildet viser.").max(500),
  kilde
});
var elementinnholdSchema = z20.discriminatedUnion("type", [
  tabellinnhold,
  figurinnhold,
  bildeinnhold
]);
var elementfelles = {
  id: uuidSchema,
  /** Tabell 1, Figur 1: nummeret per felt og type. Står fast. */
  nummer: z20.number().int().min(1),
  versjon: z20.number().int().min(1)
};
var rapportelementSchema = z20.discriminatedUnion("type", [
  tabellregler(tabellobjekt.extend(elementfelles)),
  figurregler(figurobjekt.extend(elementfelles)),
  bildeinnhold.extend(elementfelles)
]);
var tekstfeltSchema = z20.object({
  tekst: z20.string(),
  /** 0 før noe er skrevet. Sendes tilbake ved lagring, mot samtidige endringer. */
  versjon: z20.number().int().min(0),
  sistEndret: hendelse6.nullable(),
  kanRedigere: z20.boolean(),
  /** Tabellene, figurene og bildene i feltet, i rekkefølge. */
  elementer: z20.array(rapportelementSchema).default([])
});
var omradetilstandSchema = z20.enum(OMRADETILSTANDER);
var forslagSchema = z20.object({
  id: uuidSchema,
  virksomhet: z20.object({ id: uuidSchema, navn: z20.string() }),
  tekst: z20.string(),
  laget: hendelse6,
  status: z20.enum(["ny", "brukt", "avvist"]),
  /** Om kalleren selv har foreslått det. */
  egen: z20.boolean()
});
var kpiKortISammendrag = z20.object({
  navn: z20.string(),
  kategori: kpiKategoriSchema,
  enhet: maaleenhetSchema,
  verdi: maaltallSchema.nullable(),
  maal: maaltallSchema.nullable(),
  status: kpiStatusSchema
});
var omradedelSchema = okonomirapportSchema.shape.omrader.element;
var budsjettomradetallSchema = z20.object({
  grunnlag: belopSchema,
  justert: belopSchema,
  endringer: fireaarSchema,
  ramme: fireaarSchema,
  tiltak: fireaarSchema,
  forslag: fireaarSchema
});
var planarSchema = z20.tuple([arSchema, arSchema, arSchema, arSchema]);
var innholdskapittelSchema = z20.discriminatedUnion("type", [
  z20.object({ ...felles, type: z20.literal("tekst"), felt: tekstfeltSchema }),
  z20.object({
    ...felles,
    type: z20.literal("omrader"),
    underoverskrifter: z20.array(z20.object({ id: kapittelId, tittel })),
    medOkonomi: z20.boolean(),
    omrader: z20.array(
      z20.object({
        id: uuidSchema,
        navn: z20.string(),
        felt: z20.array(
          z20.object({
            id: kapittelId,
            tittel,
            tekst: tekstfeltSchema,
            forslag: z20.array(forslagSchema).default([])
          })
        ),
        okonomi: omradedelSchema.nullable(),
        /** Budsjettet for området, i budsjettdokumentet. */
        budsjett: budsjettomradetallSchema.nullable().default(null),
        /**
         * De vedtatte kuttene i området, planlagt hittil regnet til rapportens
         * periode. Null når rapporten ikke viser økonomi, eller KPI og tiltak
         * ikke er slått på.
         */
        kutt: vedtatteKuttOmradeSchema.nullable().default(null),
        tilstand: omradetilstandSchema.default("ikke_startet"),
        ferdig: hendelse6.nullable().default(null),
        /** Om kalleren kan markere kapitlet som ferdig, eller åpne det igjen. */
        kanMarkere: z20.boolean().default(false),
        /** Virksomhetene kalleren kan foreslå tekst for, som avdelingsleder. */
        kanForeslaFor: z20.array(z20.object({ id: uuidSchema, navn: z20.string() })).default([])
      })
    )
  }),
  z20.object({ ...felles, type: z20.literal("okonomi_sammendrag") }),
  z20.object({ ...felles, type: z20.literal("okonomi_hovedkonto") }),
  z20.object({ ...felles, type: z20.literal("okonomi_kommentarer") }),
  z20.object({
    ...felles,
    type: z20.literal("okonomi_budsjettendringer"),
    /** Budsjettrevisjonene som legges fram i rapporten. Se `domain/budsjettrevisjon.ts`. */
    revisjoner: z20.array(
      z20.object({
        nummer: z20.number().int().min(1),
        navn: z20.string(),
        /** Summen av endringene. Null er finansiert. */
        netto: belopSchema,
        vedtak: z20.object({ dato: z20.iso.date(), saksnummer: z20.string().nullable() }).nullable(),
        omrader: z20.array(z20.object({ navn: z20.string(), endring: belopSchema })),
        linjer: z20.array(
          z20.object({
            omrade: z20.string(),
            virksomhet: z20.string(),
            konto: z20.string(),
            tekst: z20.string(),
            sum: belopSchema
          })
        )
      })
    )
  }),
  z20.object({
    ...felles,
    type: z20.literal("kpi"),
    kategorier: z20.array(kpiKategoriSchema),
    /** Null når KPI-modulen ikke er slått på for kommunen. */
    omrader: z20.array(
      z20.object({
        id: uuidSchema,
        navn: z20.string(),
        virksomheter: z20.array(
          z20.object({ id: uuidSchema, navn: z20.string(), kort: z20.array(kpiKortISammendrag) })
        )
      })
    ).nullable()
  }),
  z20.object({
    ...felles,
    type: z20.literal("budsjett_oversikt"),
    /** Null når budsjettet for året ikke er opprettet. */
    budsjett: z20.object({
      planar: planarSchema,
      prisvekst: prosentSchema,
      omrader: z20.array(
        z20.object({
          id: uuidSchema,
          navn: z20.string(),
          grunnlag: belopSchema,
          ramme: fireaarSchema,
          tiltak: fireaarSchema,
          forslag: fireaarSchema
        })
      ),
      sum: fireaarSchema,
      tilFordeling: fireaarSchema.nullable(),
      /**
       * Det som er til fordeling minus summen og investeringenes kostnad for
       * driften. Negativt må kuttes.
       */
      ubalanse: fireaarSchema.nullable(),
      /** Det de innstilte investeringene koster driften. Null i eldre versjoner. */
      investering: fireaarSchema.nullable().default(null)
    }).nullable()
  }),
  z20.object({
    ...felles,
    type: z20.literal("budsjett_tiltak"),
    medIkkeInnstilte: z20.boolean(),
    budsjett: z20.object({
      planar: planarSchema,
      omrader: z20.array(
        z20.object({
          id: uuidSchema,
          navn: z20.string(),
          tiltak: z20.array(
            z20.object({
              id: uuidSchema,
              tittel: z20.string(),
              type: tiltakstypeSchema,
              status: tiltaksstatusSchema,
              virksomhet: z20.string().nullable(),
              beskrivelse: z20.string(),
              konsekvens: z20.string(),
              belop: fireaarSchema
            })
          )
        })
      )
    }).nullable()
  }),
  z20.object({
    ...felles,
    type: z20.literal("budsjett_investering"),
    /** Null når budsjettet for året ikke er opprettet. */
    budsjett: z20.object({
      planar: planarSchema,
      lanerente: prosentSchema,
      prosjekter: z20.array(
        z20.object({
          id: uuidSchema,
          navn: z20.string(),
          omrade: z20.string().nullable(),
          beskrivelse: z20.string(),
          utgift: fireaarSchema,
          lan: fireaarSchema,
          driftskonsekvens: fireaarSchema
        })
      ),
      sum: z20.object({
        utgift: fireaarSchema,
        momskompensasjon: fireaarSchema,
        tilskudd: fireaarSchema,
        egenkapital: fireaarSchema,
        lan: fireaarSchema,
        kapitalkostnad: fireaarSchema,
        driftskonsekvens: fireaarSchema
      }),
      driftsbelastning: fireaarSchema
    }).nullable()
  })
]);
var rapportinnholdSchema = z20.object({
  tittel: z20.string(),
  type: rapporttypeSchema,
  ar: arSchema,
  periode: manedSchema,
  kommune: z20.object({ id: uuidSchema, navn: z20.string() }),
  laget: z20.iso.datetime(),
  /** Økonomirapporten for kommunen. Null for den som bare ser sitt eget område. */
  okonomi: okonomirapportSchema.nullable(),
  kapitler: z20.array(innholdskapittelSchema)
});
var rapportversjonsoppforingSchema = z20.object({
  versjon: z20.number().int().min(1),
  fastsatt: hendelse6
});
var rapportutkastSchema = z20.object({
  rapport: z20.object({
    id: uuidSchema,
    versjon: z20.number().int().min(1),
    innhold: rapportinnholdSchema,
    oppsett: z20.array(kapittelSchema),
    /** Om kalleren styrer rapporten: økonomisjefen eller en controller. */
    kanStyre: z20.boolean(),
    /** Fristen for områdenes kapitler, som dato. */
    frist: z20.iso.date().nullable(),
    /** Hvor langt hvert område har kommet, for områdene kalleren ser. */
    status: z20.array(
      z20.object({
        id: uuidSchema,
        navn: z20.string(),
        tilstand: omradetilstandSchema,
        ferdig: hendelse6.nullable(),
        nyeForslag: z20.number().int().min(0)
      })
    ),
    versjoner: z20.array(rapportversjonsoppforingSchema)
  }).nullable(),
  kanOpprette: z20.boolean()
});
var opprettRapportSchema = z20.object({
  ar: arSchema,
  type: rapporttypeSchema,
  nummer: z20.number().int().nullable()
}).describe("Rapporten for perioden, opprettet fra malen.");
var lagreTekstSchema = z20.object({
  kapittelId,
  /** Området, for et områdekapittel. Null for fritekst. */
  omradeId: uuidSchema.nullable(),
  feltId: kapittelId,
  versjon: z20.number().int().min(0),
  tekst: z20.string().max(2e4)
});
var nyttElementSchema = z20.object({
  kapittelId,
  omradeId: uuidSchema.nullable(),
  feltId: kapittelId,
  element: elementinnholdSchema
});
var endreElementSchema = z20.object({
  versjon: z20.number().int().min(1),
  element: elementinnholdSchema
});
var flyttElementSchema = z20.object({ retning: z20.enum(["opp", "ned"]) });
var opplastetBildeSchema = z20.object({ bildeId: uuidSchema });
var rapportversjonSchema = rapportversjonsoppforingSchema.extend({
  innhold: rapportinnholdSchema
});
var settFristSchema = z20.object({ frist: z20.iso.date().nullable() });
var markerFerdigSchema = z20.object({ ferdig: z20.boolean() });
var nyttForslagSchema = z20.object({
  kapittelId,
  omradeId: uuidSchema,
  feltId: kapittelId,
  virksomhetId: uuidSchema,
  tekst: z20.string().trim().min(1).max(2e4)
});
var behandleForslagSchema = z20.object({ status: z20.enum(["brukt", "avvist"]) });
var oppgaveFelles = {
  rapportId: uuidSchema,
  tittel: z20.string(),
  rapporttype: rapporttypeSchema,
  ar: arSchema,
  nummer: z20.number().int().nullable(),
  frist: z20.iso.date().nullable()
};
var rapportoppgaverSchema = z20.object({
  oppgaver: z20.array(
    z20.discriminatedUnion("oppgave", [
      z20.object({
        ...oppgaveFelles,
        oppgave: z20.literal("skriv"),
        omradeId: uuidSchema,
        omradeNavn: z20.string(),
        tilstand: z20.enum(["ikke_startet", "i_arbeid"]),
        nyeForslag: z20.number().int().min(0)
      }),
      z20.object({
        ...oppgaveFelles,
        oppgave: z20.literal("folg_opp"),
        ferdige: z20.number().int().min(0),
        antall: z20.number().int().min(0)
      }),
      z20.object({
        ...oppgaveFelles,
        oppgave: z20.literal("foresla"),
        omradeId: uuidSchema,
        omradeNavn: z20.string()
      }),
      // Den politiske behandlingen. `frist` er fristen for kommentarer.
      z20.object({
        ...oppgaveFelles,
        oppgave: z20.literal("les"),
        utsendelseId: uuidSchema,
        utvalgNavn: z20.string()
      }),
      z20.object({
        ...oppgaveFelles,
        oppgave: z20.literal("svar"),
        utsendelseId: uuidSchema,
        utvalgNavn: z20.string(),
        antall: z20.number().int().min(1)
      })
    ])
  )
});

// src/schemas/politisk.ts
import { z as z21 } from "zod";
var hendelse7 = z21.object({ tidspunkt: z21.iso.datetime(), av: z21.string().nullable() });
var kapittelId2 = z21.string().min(1).max(64);
var ankerSchema = z21.object({
  kapittelId: kapittelId2,
  omradeId: uuidSchema.nullable(),
  elementId: kapittelId2.nullable()
});
var kommentartekst2 = z21.string().trim().min(1).max(1e4);
var kommentarsvarSchema = z21.object({
  id: uuidSchema,
  tekst: z21.string(),
  laget: hendelse7,
  /** Svart av administrasjonen, ikke av en folkevalgt. */
  fraAdministrasjonen: z21.boolean()
});
var politiskKommentarSchema = z21.object({
  id: uuidSchema,
  anker: ankerSchema,
  tekst: z21.string(),
  laget: hendelse7,
  svar: z21.array(kommentarsvarSchema),
  /** Om administrasjonen har svart. */
  besvart: z21.boolean(),
  /** Om kalleren kan svare i tråden. */
  kanSvare: z21.boolean()
});
var utsendelseSchema = z21.object({
  id: uuidSchema,
  rapportId: uuidSchema,
  tittel: z21.string(),
  rapporttype: rapporttypeSchema,
  ar: arSchema,
  nummer: z21.number().int().nullable(),
  versjon: z21.number().int().min(1),
  utvalg: z21.object({ id: uuidSchema, navn: z21.string() }),
  /** Tilgjengelig for alle folkevalgte i kommunen, ikke bare utvalget. */
  alleFolkevalgte: z21.boolean(),
  /** Fristen for kommentarer, som dato. */
  frist: z21.iso.date().nullable(),
  sendt: hendelse7,
  behandlet: z21.object({
    dato: z21.iso.date(),
    saksnummer: z21.string().nullable(),
    registrert: hendelse7
  }).nullable(),
  antallKommentarer: z21.number().int().min(0),
  ubesvarte: z21.number().int().min(0)
});
var utsendelserSchema = z21.object({
  utsendelser: z21.array(utsendelseSchema),
  /** Utvalgene rapporten kan sendes til. Tom for den som ikke styrer rapporten. */
  utvalg: z21.array(z21.object({ id: uuidSchema, navn: z21.string() }))
});
var utsendelseDetaljSchema = utsendelseSchema.extend({
  innhold: rapportinnholdSchema,
  fastsatt: hendelse7,
  kommentarer: z21.array(politiskKommentarSchema),
  /**
   * Den forrige versjonen av rapporten som ble sendt til politisk behandling,
   * og det som er endret siden den, som ankre. Se `endredeAnkre`.
   */
  forrige: z21.object({ utsendelseId: uuidSchema, versjon: z21.number().int() }).nullable(),
  endret: z21.array(z21.string()),
  /** Om kalleren kan skrive en ny kommentar: en folkevalgt, før behandlingen. */
  kanKommentere: z21.boolean(),
  /** Om kalleren kan registrere behandlingen: den som styrer rapporten. */
  kanRegistrere: z21.boolean()
});
var sendTilBehandlingSchema = z21.object({
  versjon: z21.number().int().min(1),
  utvalgId: uuidSchema,
  alleFolkevalgte: z21.boolean().default(false),
  frist: z21.iso.date().nullable().default(null)
});
var fastsettSchema = z21.object({
  behandling: sendTilBehandlingSchema.omit({ versjon: true }).nullable().default(null)
});
var nyKommentarSchema = z21.object({ anker: ankerSchema, tekst: kommentartekst2 });
var nyttKommentarsvarSchema = z21.object({ tekst: kommentartekst2 });
var registrerBehandlingSchema = z21.object({
  dato: z21.iso.date(),
  saksnummer: z21.string().trim().min(1).max(50).nullable().default(null)
});

// src/schemas/investering.ts
import { z as z22 } from "zod";
var hendelse8 = z22.object({ tidspunkt: z22.iso.datetime(), av: z22.string().nullable() });
var ikkeNegativt2 = belopSchema.refine(
  (b) => !b.startsWith("-"),
  "Bel\xF8pet kan ikke v\xE6re negativt."
);
var fireIkkeNegativt = z22.tuple([ikkeNegativt2, ikkeNegativt2, ikkeNegativt2, ikkeNegativt2]);
var finansieringSchema = z22.object({
  momskompensasjon: fireaarSchema,
  tilskudd: fireaarSchema,
  egenkapital: fireaarSchema,
  /** Det som lånes: resten. Negativt er overfinansiert. */
  lan: fireaarSchema
});
var investeringsprosjektSchema = z22.object({
  id: uuidSchema,
  /** Null er et prosjekt for hele kommunen. */
  omrade: z22.object({ id: uuidSchema, navn: z22.string() }).nullable(),
  navn: z22.string(),
  beskrivelse: z22.string(),
  status: tiltaksstatusSchema,
  /** Brutto utgift, inkludert merverdiavgift. */
  utgift: fireaarSchema,
  momskompensasjon: z22.boolean(),
  avdragstid: z22.number().int().min(1).max(MAKS_AVDRAGSTID),
  /** Andre driftskonsekvenser: pluss er mer kostnad, minus en innsparing. */
  driftskonsekvens: fireaarSchema,
  finansiering: finansieringSchema,
  overfinansiert: z22.boolean(),
  /** Renter og avdrag på prosjektets lån. */
  kapitalkostnad: fireaarSchema,
  laget: hendelse8,
  endret: hendelse8.nullable(),
  versjon: z22.number().int().min(1),
  kanEndre: z22.boolean()
});
var investeringsbudsjettSchema = z22.object({
  /** Renten nye lån regnes med, i prosent. */
  lanerente: prosentSchema,
  prosjekter: z22.array(investeringsprosjektSchema),
  /** De innstilte prosjektene summert. */
  sum: finansieringSchema.extend({
    utgift: fireaarSchema,
    kapitalkostnad: fireaarSchema,
    driftskonsekvens: fireaarSchema
  }),
  /** Det investeringene belaster driften med: renter, avdrag og andre konsekvenser. */
  driftsbelastning: fireaarSchema,
  /** Områdene kalleren kan melde inn prosjekter for nå. */
  omrader: z22.array(z22.object({ id: uuidSchema, navn: z22.string() })),
  /** Om kalleren kan melde inn prosjekter for hele kommunen, innstille og sette renten. */
  kanStyre: z22.boolean()
});
var prosjektfelt = {
  navn: z22.string().trim().min(1).max(200),
  beskrivelse: z22.string().trim().max(5e3).default(""),
  utgift: fireIkkeNegativt,
  tilskudd: fireIkkeNegativt,
  egenkapital: fireIkkeNegativt,
  momskompensasjon: z22.boolean().default(true),
  avdragstid: z22.number().int().min(1).max(MAKS_AVDRAGSTID),
  driftskonsekvens: fireaarSchema
};
var nyttInvesteringsprosjektSchema = z22.object({
  omradeId: uuidSchema.nullable(),
  ...prosjektfelt
});
var endreInvesteringsprosjektSchema = z22.object({
  versjon: z22.number().int().min(1),
  ...prosjektfelt
});
var settLanerenteSchema = z22.object({ lanerente: prosentSchema });

// src/schemas/investeringsoppfolging.ts
import { z as z24 } from "zod";

// src/schemas/import.ts
import { z as z23 } from "zod";
var valutaenhetSchema = z23.enum(["kr", "kr_1000"]);
var budsjettkildeSchema = z23.enum(["budsjett", "opprinnelig"]);
var importManedSchema = z23.object({
  maned: manedSchema,
  /** Already converted to the virksomhet's storage unit. */
  belop: belopSchema
});
var radgrunnlag = {
  kontonummer: z23.string().min(1).max(20),
  navn: z23.string().max(150),
  regnskap: z23.array(importManedSchema).max(12),
  budsjett: z23.array(importManedSchema).max(12),
  arssumRegnskap: belopSchema,
  arssumBudsjett: belopSchema
};
var importTreffSchema = z23.object({
  ...radgrunnlag,
  underkontoId: uuidSchema,
  /** What the account is called in the kontoplan, when the file disagrees. */
  kjentNavn: z23.string()
});
var importUtenTreffSchema = z23.object({
  ...radgrunnlag,
  /** An artsgruppe that already exists, derived from the file's own hierarchy. */
  foreslattArtsgruppeId: uuidSchema.nullable(),
  foreslattArtsgruppeNavn: z23.string().nullable(),
  foreslattHovedkontoNavn: z23.string().nullable(),
  /**
   * What the FILE said, whether or not it matched anything the kommune has.
   * Present so a genuinely new artsgruppe can be created with the number and
   * name the file gave, under the hovedkonto the file named — the alternative
   * was that such an account could not be imported at all.
   */
  filArtsgruppeKontonummer: z23.string().nullable(),
  filArtsgruppeNavn: z23.string().nullable(),
  filHovedkontoKontonummer: z23.string().nullable(),
  filHovedkontoNavn: z23.string().nullable(),
  filHovedkontoId: uuidSchema.nullable()
});
var ignorertRadSchema = z23.object({
  linje: z23.number().int(),
  innhold: z23.string(),
  arsak: z23.string()
});
var analyserImportSchema = z23.object({
  ar: arSporringSchema,
  filEnhet: valutaenhetSchema.default("kr"),
  budsjettkilde: budsjettkildeSchema.default("budsjett")
});
var importAnalyseSchema = z23.object({
  filnavn: z23.string(),
  format: z23.enum(["manedsmal", "erp_rapport"]),
  ar: arSchema,
  /**
   * The last month with genuine actuals, or null when the file has none.
   * Everything after it is left alone — importing an export's placeholder zeros
   * would book the rest of the year at nil and erase the forecast (§3.2).
   */
  sisteManedMedRegnskap: manedSchema.nullable(),
  /** Normally December: a budget is planned for the whole year. */
  sisteManedMedBudsjett: manedSchema.nullable(),
  budsjettkilde: budsjettkildeSchema,
  /** The unit the file was read as, and the one the virksomhet stores in. */
  filEnhet: valutaenhetSchema,
  virksomhetEnhet: valutaenhetSchema,
  treff: z23.array(importTreffSchema),
  utenTreff: z23.array(importUtenTreffSchema),
  /** Account numbers the file lists more than once — reported, never merged. */
  duplikater: z23.array(z23.string()),
  /** Rows the analysis could not use, with the reason. Never silently dropped. */
  ignorerteRader: z23.array(ignorertRadSchema),
  /** Sums over the rows that would be imported, in the virksomhet's unit. */
  sumRegnskap: belopSchema,
  sumBudsjett: belopSchema,
  /**
   * What the OTHER budget column would have summed to, in the same unit.
   *
   * «Budsjett» and «Opprinnelig budsjett» are different figures that differ by a
   * few per cent, so picking the wrong one produces a total that looks entirely
   * plausible. Showing both makes the choice something the user can check rather
   * than something they have to remember.
   */
  alternativArsbudsjett: belopSchema.nullable(),
  /**
   * Findings from cross-checking the file against itself — the file's own year
   * column against the twelve months, and each subtotal row against the accounts
   * under it. A finding of severity `feil` means the wrong columns were read.
   */
  funn: z23.array(
    z23.object({
      alvor: z23.enum(["feil", "advarsel", "ok", "info"]),
      tittel: z23.string(),
      detalj: z23.string(),
      eksempler: z23.array(z23.string())
    })
  )
});
var importhandlingSchema = z23.discriminatedUnion("handling", [
  z23.object({
    handling: z23.literal("oppdater"),
    underkontoId: uuidSchema,
    kontonummer: z23.string().min(1).max(20),
    regnskap: z23.array(importManedSchema).max(12),
    budsjett: z23.array(importManedSchema).max(12)
  }),
  z23.object({
    handling: z23.literal("opprett"),
    /** An artsgruppe that already exists. */
    artsgruppeId: uuidSchema,
    kontonummer: z23.string().min(1).max(20),
    navn: z23.string().min(1).max(150),
    regnskap: z23.array(importManedSchema).max(12),
    budsjett: z23.array(importManedSchema).max(12)
  }),
  z23.object({
    /**
     * Creates the artsgruppe as well, then the account under it.
     *
     * A separate variant rather than a nullable artsgruppeId on the one above,
     * so that creating a level of the kontoplan is something the request states
     * outright. The hovedkonto must already exist: a hovedkonto carries a
     * `kategori` that decides the sign convention for everything beneath it, and
     * no accounting export says which one it is — guessing that is how an entire
     * branch ends up with its income and expenditure the wrong way round.
     */
    handling: z23.literal("opprett_med_artsgruppe"),
    hovedkontoId: uuidSchema,
    nyArtsgruppeKontonummer: z23.string().min(1).max(20),
    nyArtsgruppeNavn: z23.string().min(1).max(150),
    kontonummer: z23.string().min(1).max(20),
    navn: z23.string().min(1).max(150),
    regnskap: z23.array(importManedSchema).max(12),
    budsjett: z23.array(importManedSchema).max(12)
  }),
  z23.object({
    /**
     * Bygger hele grenen: hovedkonto, artsgruppe og konto.
     *
     * Dette er den FØRSTE importen til en tom kontoplan. Uten den finnes det
     * ingen vei inn: variantene over krever alle at noe over kontoen allerede
     * er der, og ingenting annet i løsningen oppretter en hovedkonto.
     *
     * Merk hva som er nytt her og hva som ikke er det. Kontonummeret og navnet
     * kommer fra filen, som alltid. `nyHovedkontoKategori` gjør det IKKE — den
     * avgjør fortegnskonvensjonen for alt under seg, og ingen regnskapseksport
     * sier hvilken den er. Den er derfor et påkrevd felt kalleren må ha fått en
     * person til å velge, ikke noe som utledes av et kontonummer som «pleier å
     * være» lønn. Å gjette den er hvordan en hel gren ender opp med inntekter
     * og utgifter byttet om.
     */
    handling: z23.literal("opprett_med_hovedkonto"),
    nyHovedkontoKontonummer: z23.string().min(1).max(20),
    nyHovedkontoNavn: z23.string().min(1).max(150),
    nyHovedkontoKategori: kategoriSchema,
    nyArtsgruppeKontonummer: z23.string().min(1).max(20),
    nyArtsgruppeNavn: z23.string().min(1).max(150),
    kontonummer: z23.string().min(1).max(20),
    navn: z23.string().min(1).max(150),
    regnskap: z23.array(importManedSchema).max(12),
    budsjett: z23.array(importManedSchema).max(12)
  })
]);
var utforImportSchema = z23.object({
  ar: arSchema,
  filnavn: z23.string().max(255),
  budsjettkilde: budsjettkildeSchema,
  /** Set false to bring in the actuals only and leave the budget as it stands. */
  importerBudsjett: z23.boolean().default(true),
  rader: z23.array(importhandlingSchema).min(1).max(5e3)
});
var importresultatSchema = z23.object({
  ar: arSchema,
  /** Months actually written. Only these are touched; the rest of the year is left alone. */
  berorteManederRegnskap: z23.array(manedSchema),
  berorteManederBudsjett: z23.array(manedSchema),
  antallRegnskapslinjer: z23.number().int(),
  antallBudsjettlinjer: z23.number().int(),
  antallOppdaterteKontoer: z23.number().int(),
  antallNyeKontoer: z23.number().int(),
  /** Artsgrupper opprettet av importen. Rapportert, aldri stilltiende gjort. */
  antallNyeArtsgrupper: z23.number().int(),
  /** Hovedkontoer opprettet fra filens eget hierarki. Nesten alltid 0, unntatt ved den aller første importen til en tom kontoplan. */
  antallNyeHovedkontoer: z23.number().int().default(0),
  sumRegnskap: belopSchema,
  sumBudsjett: belopSchema,
  /**
   * Prognoseoverstyringer som ble slettet fordi importen bokførte månedene de
   * gjaldt. Rapportert, ikke stilltiende gjort: brukeren skal få vite at
   * prognosen hun la inn for september er erstattet av regnskapet.
   */
  forkastedeOverstyringer: z23.number().int(),
  /**
   * Budsjettet i filen ble ikke tatt inn, fordi budsjettet for året er laget
   * og vedtatt i løsningen. Se `domain/budsjettibruk.ts`.
   */
  budsjettBeskyttet: z23.boolean().default(false)
});

// src/schemas/investeringsoppfolging.ts
var hendelse9 = z24.object({ tidspunkt: z24.iso.datetime(), av: z24.string().nullable() });
var omrade = z24.object({ id: uuidSchema, navn: z24.string() });
var tolv = z24.array(belopSchema.nullable()).length(12);
var investeringsstatusSchema = z24.enum(INVESTERINGSSTATUSER);
var investeringSporringSchema = z24.object({ ar: arSporringSchema });
var investeringSchema = z24.object({
  id: uuidSchema,
  /** Koden i fagsystemet. */
  kode: z24.string(),
  navn: z24.string(),
  /** Null er et prosjekt for hele kommunen. */
  omrade: omrade.nullable(),
  kostnadsramme: belopSchema.nullable(),
  avsluttet: z24.boolean(),
  versjon: z24.number().int(),
  /** Årets budsjett. */
  budsjett: belopSchema,
  /** Regnskapet i år, til og med siste førte måned. */
  regnskap: belopSchema,
  /** Det prosjektet har kostet i årene før, så langt løsningen har tall for dem. */
  tidligereAr: belopSchema,
  maneder: z24.object({ budsjett: tolv, regnskap: tolv }),
  prognose: z24.object({
    periode: manedSchema,
    arsprognose: belopSchema,
    sluttprognose: belopSchema.nullable(),
    forklaring: z24.string().nullable(),
    meldt: hendelse9
  }).nullable(),
  status: investeringsstatusSchema,
  avvik: belopSchema.nullable(),
  ubrukt: belopSchema,
  forbruksprosent: z24.number().int().nullable(),
  sluttavvik: belopSchema.nullable(),
  sprekker: z24.boolean(),
  /** Perioden prognosen skal meldes for nå. Null når den er meldt, eller regnskapet ikke er ført. */
  skalMeldes: manedSchema.nullable(),
  kanMelde: z24.boolean()
});
var investeringsoppfolgingSchema = z24.object({
  ar: arSchema,
  /** Siste måned noe prosjekt har regnskap for. */
  sisteForteManed: manedSchema.nullable(),
  rytme: z24.enum(PROGNOSERYTMER),
  /** Terskelen for forklaring, som i driften. Null er at ingen forklaring kreves. */
  terskel: z24.object({ prosent: z24.number(), belop: belopSchema }).nullable(),
  /** Økonomisjefen og controllerne importerer og setter område og ramme. */
  kanStyre: z24.boolean(),
  /** Områdene et prosjekt kan høre til. Bare for den som styrer. */
  omrader: z24.array(omrade),
  prosjekter: z24.array(investeringSchema),
  sum: z24.object({
    budsjett: belopSchema,
    regnskap: belopSchema,
    /** Summen av avvikene over budsjett, i de prosjektene som er meldt. */
    merforbruk: belopSchema,
    ubrukt: belopSchema
  })
});
var meldInvesteringsprognoseSchema = z24.object({
  ar: arSchema,
  arsprognose: belopSchema,
  sluttprognose: belopSchema.nullable().default(null),
  forklaring: z24.string().trim().max(2e3).nullable().default(null)
});
var endreInvesteringSchema = z24.object({
  navn: z24.string().trim().min(1).max(200),
  omradeId: uuidSchema.nullable(),
  kostnadsramme: belopSchema.nullable(),
  avsluttet: z24.boolean(),
  versjon: z24.number().int()
});
var investeringsimportAnalyseSchema = z24.object({
  filnavn: z24.string(),
  ar: arSchema,
  filEnhet: valutaenhetSchema,
  budsjettkilde: budsjettkildeSchema,
  sisteManedMedRegnskap: manedSchema.nullable(),
  rader: z24.array(
    z24.object({
      kode: z24.string(),
      navn: z24.string(),
      /** Prosjektet koden treffer, eller null når det er nytt. */
      finnes: z24.object({ id: uuidSchema, navn: z24.string(), omrade: omrade.nullable() }).nullable(),
      budsjett: tolv,
      regnskap: tolv,
      sumBudsjett: belopSchema,
      sumRegnskap: belopSchema
    })
  ),
  /** Koder filen har mer enn én gang. Rapportert, aldri slått sammen. */
  duplikater: z24.array(z24.string()),
  ignorerteRader: z24.array(ignorertRadSchema),
  sumBudsjett: belopSchema,
  sumRegnskap: belopSchema,
  /** Områdene et nytt prosjekt kan legges i. */
  omrader: z24.array(omrade)
});
var utforInvesteringsimportSchema = z24.object({
  ar: arSchema,
  filnavn: z24.string().max(255),
  sisteManedMedRegnskap: manedSchema.nullable(),
  rader: z24.array(
    z24.object({
      kode: z24.string().trim().min(1).max(60),
      navn: z24.string().trim().min(1).max(200),
      /** Området et nytt prosjekt legges i. Et prosjekt som finnes, beholder sitt. */
      omradeId: uuidSchema.nullable().default(null),
      budsjett: tolv,
      regnskap: tolv
    })
  ).min(1).max(2e3)
});
var investeringsimportresultatSchema = z24.object({
  ar: arSchema,
  nye: z24.number().int(),
  oppdaterte: z24.number().int(),
  sumBudsjett: belopSchema,
  sumRegnskap: belopSchema
});
var investeringsoppgaverSchema = z24.object({
  ar: arSchema,
  rytme: z24.enum(PROGNOSERYTMER),
  periode: manedSchema.nullable(),
  prosjekter: z24.array(z24.object({ id: uuidSchema, kode: z24.string(), navn: z24.string() }))
});

// src/schemas/vedlikehold.ts
import { z as z25 } from "zod";
var person2 = z25.object({ id: uuidSchema, navn: z25.string() });
var hendelse10 = z25.object({ tidspunkt: z25.iso.datetime(), av: z25.string().nullable() });
var fagomradeSchema = z25.enum(FAGOMRADER);
var alvorlighetSchema = z25.enum(ALVORLIGHETER);
var avviksstatusSchema = z25.enum(AVVIKSSTATUSER);
var posisjonSchema = z25.object({
  breddegrad: z25.number().min(-90).max(90),
  lengdegrad: z25.number().min(-180).max(180)
});
var flybilderSchema = z25.object({
  kilde: z25.enum(["norgeibilder", "esri"]),
  /** Leaflets mal: {z}, {x} og {y}. */
  url: z25.url(),
  kreditering: z25.string(),
  maksZoom: z25.number().int().min(1).max(22)
});
var driftsobjektSchema = z25.object({
  id: uuidSchema,
  fagomrade: fagomradeSchema,
  navn: z25.string(),
  adresse: z25.string().nullable(),
  gnr: z25.number().int().nullable(),
  bnr: z25.number().int().nullable(),
  byggear: z25.number().int().nullable(),
  /** Kvadratmeter. */
  bruttoareal: z25.number().int().nullable(),
  posisjon: posisjonSchema.nullable(),
  /** Driftsavdelingen som forvalter objektet. */
  avdeling: person2.nullable(),
  /** Virksomheten som bruker det. */
  brukesAv: person2.nullable(),
  aktiv: z25.boolean(),
  apneAvvik: z25.number().int(),
  /** Kontrollene på objektet som er over fristen, og som forfaller snart. */
  kontroller: z25.object({ overFristen: z25.number().int(), snart: z25.number().int() }),
  /** Endre objektet og behandle avvikene på det. */
  kanForvalte: z25.boolean(),
  versjon: z25.number().int()
});
var avvikSchema = z25.object({
  id: uuidSchema,
  fagomrade: fagomradeSchema,
  objekt: z25.object({ id: uuidSchema, navn: z25.string() }).nullable(),
  posisjon: posisjonSchema,
  type: z25.string(),
  /** Det som telles i etterkant; se `domain/avvikskatalog.ts`. */
  undertype: z25.string().nullable(),
  alvorlighet: alvorlighetSchema,
  tittel: z25.string(),
  beskrivelse: z25.string(),
  status: avviksstatusSchema,
  /** Når det skal være rettet. Tomt for avvik meldt før fristene kom. */
  frist: isodatoSchema.nullable(),
  /** Midlertidig sikret: faren er håndtert, selv om avviket ikke er rettet. */
  sikret: hendelse10.nullable(),
  meldt: hendelse10,
  statusEndret: hendelse10.nullable(),
  lukketKommentar: z25.string().nullable(),
  kanBehandle: z25.boolean(),
  versjon: z25.number().int()
});
var driftsoversiktSchema = z25.object({
  /** Fagområdene kalleren arbeider i; avgjør hva som kan registreres. */
  fagomrader: z25.array(fagomradeSchema),
  objekter: z25.array(driftsobjektSchema),
  /** Åpne avvik, og de som er lukket de siste 30 dagene. */
  avvik: z25.array(avvikSchema),
  /** Avdelingene kalleren kan legge nye objekter i. */
  avdelingvalg: z25.array(person2.extend({ fagomrade: fagomradeSchema })),
  /** Virksomhetene et objekt kan brukes av. */
  brukesAvValg: z25.array(person2),
  /** Kommunens administrator og økonomisjefen setter avdelingenes fagområde. */
  kanAdministrere: z25.boolean(),
  avdelinger: z25.array(person2.extend({ omrade: z25.string(), fagomrade: fagomradeSchema.nullable() })),
  /** Kartene kommunen har slått på i tillegg til Norgeskart og OpenStreetMap. */
  kart: z25.object({
    google: z25.boolean(),
    /** Flybilder, der vi har en kilde. Tomt er ikke tilgjengelig. */
    flybilder: flybilderSchema.nullable()
  })
});
var lagreDriftsobjektSchema = z25.object({
  fagomrade: fagomradeSchema,
  navn: z25.string().trim().min(1).max(200),
  adresse: z25.string().trim().max(200).nullable().default(null),
  gnr: z25.number().int().positive().nullable().default(null),
  bnr: z25.number().int().positive().nullable().default(null),
  byggear: z25.number().int().min(1e3).max(2100).nullable().default(null),
  bruttoareal: z25.number().int().positive().nullable().default(null),
  posisjon: posisjonSchema.nullable().default(null),
  avdelingId: uuidSchema.nullable().default(null),
  brukesAvId: uuidSchema.nullable().default(null),
  aktiv: z25.boolean().default(true),
  /** Påkrevd ved endring; ignoreres når objektet opprettes. */
  versjon: z25.number().int().default(0)
});
var nyttAvvikSchema = z25.object({
  fagomrade: fagomradeSchema,
  objektId: uuidSchema.nullable().default(null),
  posisjon: posisjonSchema,
  type: z25.string().min(1).max(20),
  undertype: z25.string().min(1).max(30).nullable().default(null),
  alvorlighet: alvorlighetSchema,
  tittel: z25.string().trim().min(1).max(150),
  beskrivelse: z25.string().trim().max(4e3).default("")
}).refine((a) => erAvvikstype(a.fagomrade, a.type), {
  message: "Typen h\xF8rer ikke til fagomr\xE5det.",
  path: ["type"]
}).refine((a) => erUndertype(a.fagomrade, a.type, a.undertype), {
  message: "Undertypen h\xF8rer ikke til typen.",
  path: ["undertype"]
});
var endreAvvikSchema = z25.object({
  tittel: z25.string().trim().min(1).max(150),
  beskrivelse: z25.string().trim().max(4e3),
  type: z25.string().min(1).max(20),
  undertype: z25.string().min(1).max(30).nullable(),
  alvorlighet: alvorlighetSchema,
  frist: isodatoSchema,
  sikret: z25.boolean(),
  versjon: z25.number().int()
});
var endreAvviksstatusSchema = z25.object({
  status: avviksstatusSchema,
  kommentar: z25.string().trim().max(1e3).nullable().default(null),
  versjon: z25.number().int()
});
var settFagomradeSchema = z25.object({ fagomrade: fagomradeSchema.nullable() });
var ref = z25.object({ id: uuidSchema, navn: z25.string() });
var fristtilstandSchema = z25.enum(FRISTTILSTANDER);
var kontrollresultatSchema = z25.enum(KONTROLLRESULTATER);
var tilstandsgradSchema = z25.literal(TILSTANDSGRADER);
var punktresultatSchema = z25.enum(PUNKTRESULTATER);
var sjekkpunktSchema = z25.object({
  nokkel: z25.string(),
  tekst: z25.string(),
  undertype: z25.string().nullable(),
  alvorlighet: alvorlighetSchema.optional()
});
var kontrolltypeSchema = z25.object({
  nokkel: z25.string(),
  navn: z25.string(),
  fagomrade: fagomradeSchema,
  intervallManeder: z25.number().int(),
  hjemmel: z25.string().nullable(),
  avvikstype: z25.string(),
  aktiv: z25.boolean(),
  egen: z25.boolean(),
  /** Sjekklisten. Tom for kommunens egne typer. */
  punkter: z25.array(sjekkpunktSchema).readonly()
});
var utforelseSchema = z25.object({
  id: uuidSchema,
  utfort: isodatoSchema,
  resultat: kontrollresultatSchema,
  /** Den som gjorde kontrollen: en ansatt eller et firma. */
  utfortAv: z25.string().nullable(),
  kommentar: z25.string().nullable(),
  /** Avviket mangelen ble meldt som; det første, når sjekklisten fant flere. */
  avvikId: uuidSchema.nullable(),
  registrertAv: z25.string().nullable(),
  /** Svarene på sjekklisten, med teksten slik den var da. Tom uten sjekkliste. */
  punkter: z25.array(
    z25.object({
      nokkel: z25.string(),
      tekst: z25.string(),
      resultat: punktresultatSchema,
      merknad: z25.string().nullable(),
      avvikId: uuidSchema.nullable()
    })
  )
});
var kontrollSchema = z25.object({
  id: uuidSchema,
  objekt: ref,
  type: z25.object({
    nokkel: z25.string(),
    navn: z25.string(),
    hjemmel: z25.string().nullable(),
    punkter: z25.array(sjekkpunktSchema).readonly()
  }),
  intervallManeder: z25.number().int(),
  nesteFrist: isodatoSchema,
  fristtilstand: fristtilstandSchema,
  aktiv: z25.boolean(),
  /** De siste, nyeste først. */
  sisteUtforelser: z25.array(utforelseSchema),
  /** Registrere utførte kontroller og endre fristen: den som forvalter objektet. */
  kanRegistrere: z25.boolean(),
  versjon: z25.number().int()
});
var tilstandSchema = z25.object({
  bygningsdel: z25.string(),
  tilstandsgrad: tilstandsgradSchema,
  konsekvensgrad: tilstandsgradSchema.nullable(),
  beskrivelse: z25.string(),
  tiltak: z25.string().nullable(),
  tiltakAr: z25.number().int().nullable(),
  kostnad: belopSchema.nullable(),
  vurdert: z25.object({ dato: isodatoSchema, av: z25.string().nullable() }),
  versjon: z25.number().int()
});
var objektdetaljSchema = z25.object({
  objekt: driftsobjektSchema,
  kontroller: z25.array(kontrollSchema),
  /** Bare delene som er vurdert. */
  tilstand: z25.array(tilstandSchema),
  /** Kontrolltypene som kan legges til: i bruk, i fagområdet, og ikke lagt til ennå. */
  typevalg: z25.array(kontrolltypeSchema)
});
var kontrolloversiktSchema = z25.object({
  /** Kontrollene på objektene kalleren ser, med den nærmeste fristen først. */
  kontroller: z25.array(kontrollSchema),
  typer: z25.array(kontrolltypeSchema),
  /** Endre katalogen og legge til egne typer. */
  kanAdministrere: z25.boolean()
});
var kontrolloppgaverSchema = z25.object({
  kontroller: z25.array(
    z25.object({
      id: uuidSchema,
      objekt: ref,
      type: z25.string(),
      nesteFrist: isodatoSchema,
      fristtilstand: fristtilstandSchema
    })
  )
});
var vedlikeholdsplanSchema = z25.object({
  ar: z25.array(
    z25.object({
      /** Tomt er tiltakene uten år. */
      ar: z25.number().int().nullable(),
      sum: belopSchema,
      tiltak: z25.array(
        z25.object({
          objekt: ref,
          bygningsdel: z25.string(),
          tilstandsgrad: tilstandsgradSchema,
          konsekvensgrad: tilstandsgradSchema.nullable(),
          tiltak: z25.string(),
          kostnad: belopSchema.nullable()
        })
      )
    })
  ),
  total: belopSchema
});
var nyKontrollSchema = z25.object({
  typeNokkel: z25.string().min(1).max(30),
  nesteFrist: isodatoSchema
});
var endreKontrollSchema = z25.object({
  nesteFrist: isodatoSchema,
  aktiv: z25.boolean(),
  versjon: z25.number().int()
});
var nyUtforelseSchema = z25.object({
  utfort: isodatoSchema,
  resultat: kontrollresultatSchema,
  utfortAv: z25.string().trim().max(150).nullable().default(null),
  kommentar: z25.string().trim().max(1e3).nullable().default(null),
  /** Ved mangler: meld dem som et avvik på objektet. Bare uten sjekkliste. */
  avvik: z25.object({ tittel: z25.string().trim().min(1).max(150), alvorlighet: alvorlighetSchema }).nullable().default(null),
  /**
   * Svarene på sjekklisten. Hvert punkt med avvik meldes som et eget avvik på
   * objektet, med merknaden som beskrivelse.
   */
  punkter: z25.array(
    z25.object({
      nokkel: z25.string().min(1).max(30),
      resultat: punktresultatSchema,
      merknad: z25.string().trim().max(1e3).nullable().default(null)
    })
  ).max(30).default([]),
  versjon: z25.number().int()
}).refine((u2) => u2.avvik === null || u2.resultat === "mangler", {
  message: "Et avvik meldes bare n\xE5r kontrollen fant mangler.",
  path: ["avvik"]
}).refine((u2) => u2.punkter.length === 0 || u2.avvik === null, {
  message: "Med sjekkliste meldes avvikene fra punktene.",
  path: ["avvik"]
}).refine((u2) => u2.punkter.length === 0 || sjekklisteresultat(u2.punkter) === u2.resultat, {
  message: "Resultatet m\xE5 stemme med sjekklisten.",
  path: ["resultat"]
});
var lagreTilstandSchema = z25.object({
  tilstandsgrad: tilstandsgradSchema,
  konsekvensgrad: tilstandsgradSchema.nullable().default(null),
  beskrivelse: z25.string().trim().max(1e3).default(""),
  tiltak: z25.string().trim().max(300).nullable().default(null),
  tiltakAr: z25.number().int().min(2e3).max(2100).nullable().default(null),
  kostnad: belopSchema.refine((b) => !b.startsWith("-"), "Kostnaden kan ikke v\xE6re negativ.").nullable().default(null),
  /** 0 for en del som ikke er vurdert før. */
  versjon: z25.number().int().default(0)
});
var bygningsdelSchema = z25.string().refine(erBygningsdel, "Ukjent bygningsdel.");
var lagreKontrolltypeSchema = z25.object({
  navn: z25.string().trim().min(1).max(100),
  intervallManeder: z25.number().int().min(1).max(120),
  hjemmel: z25.string().trim().max(200).nullable().default(null),
  aktiv: z25.boolean()
});
var nyKontrolltypeSchema = z25.object({
  navn: z25.string().trim().min(1).max(100),
  fagomrade: fagomradeSchema,
  intervallManeder: z25.number().int().min(1).max(120),
  hjemmel: z25.string().trim().max(200).nullable().default(null),
  avvikstype: z25.string().min(1).max(20)
}).refine((t2) => erAvvikstype(t2.fagomrade, t2.avvikstype), {
  message: "Avvikstypen h\xF8rer ikke til fagomr\xE5det.",
  path: ["avvikstype"]
});
var telling2 = {
  meldt: z25.number().int(),
  apne: z25.number().int(),
  forfalte: z25.number().int(),
  kostnad: belopSchema
};
var avviksoppfolgingSchema = z25.object({
  fra: isodatoSchema,
  til: isodatoSchema,
  apne: z25.number().int(),
  forfalte: z25.number().int(),
  sikret: z25.number().int(),
  meldt: z25.number().int(),
  lukket: z25.number().int(),
  lukketInnenFrist: z25.number().int(),
  lukketMedFrist: z25.number().int(),
  snittDagerTilLukket: z25.number().nullable(),
  kostnad: belopSchema,
  perType: z25.array(z25.object({ fagomrade: fagomradeSchema, type: z25.string(), ...telling2 })),
  perObjekt: z25.array(z25.object({ objekt: ref.nullable(), ...telling2 })),
  gjentakende: z25.array(
    z25.object({
      objekt: ref,
      fagomrade: fagomradeSchema,
      type: z25.string(),
      undertype: z25.string().nullable(),
      antall: z25.number().int(),
      sist: isodatoSchema
    })
  ),
  perManed: z25.array(
    z25.object({ maned: z25.string(), meldt: z25.number().int(), lukket: z25.number().int() })
  )
});

// src/schemas/sak.ts
import { z as z26 } from "zod";
var person3 = z26.object({ id: uuidSchema, navn: z26.string() });
var hendelse11 = z26.object({ tidspunkt: z26.iso.datetime(), av: z26.string().nullable() });
var sakstypeSchema = z26.enum(SAKSTYPER);
var prioritetSchema = z26.enum(PRIORITETER);
var reaksjonSchema = z26.enum(REAKSJONER);
var statustoneSchema = z26.enum(["noytral", "info", "varsel", "positiv", "negativ"]);
var vedleggSchema = z26.object({
  id: uuidSchema,
  filnavn: z26.string(),
  mimetype: z26.string(),
  storrelse: z26.number().int(),
  lastetOpp: hendelse11,
  /** Innlegget vedlegget ble delt i, eller tomt for vedlegg rett på saken. */
  innleggId: uuidSchema.nullable(),
  kanSlette: z26.boolean()
});
var innleggSchema = z26.object({
  id: uuidSchema,
  /** En kommentar noen skrev, eller noe som skjedde med saken. */
  type: z26.enum(["kommentar", "hendelse"]),
  tekst: z26.string(),
  forfatter: person3.nullable(),
  tidspunkt: z26.iso.datetime(),
  omtalte: z26.array(person3),
  vedlegg: z26.array(uuidSchema),
  reaksjoner: z26.array(
    z26.object({ emoji: reaksjonSchema, antall: z26.number().int(), minEgen: z26.boolean() })
  ),
  kanSlette: z26.boolean()
});
var deloppgaveSchema = z26.object({
  id: uuidSchema,
  tekst: z26.string(),
  ferdig: z26.boolean(),
  ferdigAv: hendelse11.nullable()
});
var tidsforingSchema = z26.object({
  id: uuidSchema,
  person: person3.nullable(),
  dato: isodatoSchema,
  minutter: z26.number().int(),
  kommentar: z26.string().nullable(),
  kanSlette: z26.boolean()
});
var sakSchema = z26.object({
  sakstype: sakstypeSchema,
  sakId: uuidSchema,
  /** Saks-ID-en, som «AVV-12». */
  nummer: z26.string(),
  tittel: z26.string(),
  beskrivelse: z26.string(),
  status: z26.object({ navn: z26.string(), tone: statustoneSchema }),
  /** Det modulen vil vise om saken: «Meldt av», «Frist», «Objekt». */
  felt: z26.array(z26.object({ navn: z26.string(), verdi: z26.string() })),
  /** Hvor saken hører hjemme i appen, for «Til avviket». */
  lenke: z26.string().nullable(),
  prioritet: prioritetSchema.nullable(),
  etiketter: z26.array(z26.string()),
  ansvarlige: z26.array(person3),
  deloppgaver: z26.array(deloppgaveSchema),
  vedlegg: z26.array(vedleggSchema),
  innlegg: z26.array(innleggSchema),
  tid: z26.object({ totalMinutter: z26.number().int(), foringer: z26.array(tidsforingSchema) }),
  /** Endre prioritet, etiketter, ansvarlige og deloppgaver. */
  kanEndre: z26.boolean(),
  /** Skrive i diskusjonen og legge ved filer. Alle som ser saken. */
  kanBidra: z26.boolean(),
  /** Krysse av deloppgaver og føre tid: den som kan endre saken, og de ansvarlige. */
  kanUtfore: z26.boolean(),
  /** Dem som kan være ansvarlige og omtales: de som kan se saken. */
  personer: z26.array(person3),
  versjon: z26.number().int()
});
var saksoppgaverSchema = z26.object({
  saker: z26.array(
    z26.object({
      sakstype: sakstypeSchema,
      sakId: uuidSchema,
      nummer: z26.string(),
      tittel: z26.string(),
      /** Hvorfor saken er her: du er ansvarlig, eller noen omtalte deg. */
      grunn: z26.enum(["ansvarlig", "omtalt"]),
      tidspunkt: z26.iso.datetime()
    })
  )
});
var nyttInnleggSchema = z26.object({
  tekst: z26.string().trim().max(4e3).default(""),
  omtalte: z26.array(uuidSchema).max(20).default([]),
  /** Vedlegg som er lastet opp til saken og skal vises i innlegget. */
  vedlegg: z26.array(uuidSchema).max(10).default([])
}).refine((i) => i.tekst !== "" || i.vedlegg.length > 0, {
  message: "Skriv noe, eller legg ved en fil.",
  path: ["tekst"]
});
var veksleReaksjonSchema = z26.object({ emoji: reaksjonSchema });
var lagreSaksdetaljerSchema = z26.object({
  prioritet: prioritetSchema.nullable(),
  etiketter: z26.array(z26.string().trim().max(30)).max(10),
  ansvarlige: z26.array(uuidSchema).max(20),
  versjon: z26.number().int()
});
var nyDeloppgaveSchema = z26.object({ tekst: z26.string().trim().min(1).max(200) });
var endreDeloppgaveSchema = z26.object({
  tekst: z26.string().trim().min(1).max(200),
  ferdig: z26.boolean()
});
var nyTidsforingSchema = z26.object({
  dato: isodatoSchema,
  minutter: z26.number().int().min(1).max(24 * 60),
  kommentar: z26.string().trim().max(200).nullable().default(null)
});

// src/schemas/arbeidsordre.ts
import { z as z28 } from "zod";

// src/schemas/entreprenor.ts
import { z as z27 } from "zod";
var firmarolleSchema = z27.enum(FIRMAROLLER);
var firmasvarSchema = z27.enum(FIRMASVAR);
var firmabrukerSchema = z27.object({
  id: uuidSchema,
  navn: z27.string(),
  epost: z27.string(),
  rolle: firmarolleSchema,
  aktiv: z27.boolean(),
  /** Om kontoen er tatt i bruk: personen har logget inn minst én gang. */
  harLoggetInn: z27.boolean(),
  sistInnlogget: z27.iso.datetime().nullable()
});
var entreprenorfirmaSchema = z27.object({
  id: uuidSchema,
  navn: z27.string(),
  orgnr: z27.string().nullable(),
  epost: z27.string().nullable(),
  telefon: z27.string().nullable(),
  aktiv: z27.boolean(),
  /** Åpne ordrer firmaet har nå. */
  apneOrdrer: z27.number().int(),
  brukere: z27.array(firmabrukerSchema),
  versjon: z27.number().int()
});
var entreprenoroversiktSchema = z27.object({
  firmaer: z27.array(entreprenorfirmaSchema)
});
var valgfri = (maks) => z27.string().trim().max(maks).nullable().default(null).transform((v) => v === "" ? null : v);
var lagreFirmaSchema = z27.object({
  navn: z27.string().trim().min(1, "Skriv navnet p\xE5 firmaet.").max(150),
  orgnr: z27.string().trim().transform((v) => v.replace(/\s/g, "")).refine((v) => v === "" || erOrgnr(v), "Organisasjonsnummeret er ikke gyldig.").nullable().default(null).transform((v) => v === "" ? null : v),
  epost: valgfri(190).refine(
    (v) => v === null || z27.email().safeParse(v).success,
    "E-postadressen er ikke gyldig."
  ),
  telefon: valgfri(30),
  aktiv: z27.boolean().default(true),
  /** 0 for et nytt firma. */
  versjon: z27.number().int().default(0)
});
var nyFirmabrukerSchema = z27.object({
  navn: z27.string().trim().min(1, "Skriv navnet.").max(150),
  epost: z27.email("E-postadressen er ikke gyldig.").max(190),
  rolle: firmarolleSchema
});
var endreFirmabrukerSchema = z27.object({
  rolle: firmarolleSchema,
  aktiv: z27.boolean()
});
var settUtforerSchema = z27.object({
  /** Firmaet som skal utføre ordren, eller tomt for kommunens egne folk. */
  firmaId: uuidSchema.nullable(),
  versjon: z27.number().int()
});
var svarPaOrdreSchema = z27.object({
  svar: firmasvarSchema,
  kommentar: z27.string().trim().max(1e3).nullable().default(null),
  versjon: z27.number().int()
});

// src/schemas/arbeidsordre.ts
var person4 = z28.object({ id: uuidSchema, navn: z28.string() });
var hendelse12 = z28.object({ tidspunkt: z28.iso.datetime(), av: z28.string().nullable() });
var ikkeNegativ = belopSchema.refine(
  (b) => !b.startsWith("-"),
  "Bel\xF8pet kan ikke v\xE6re negativt."
);
var arbeidsordrestatusSchema = z28.enum(ARBEIDSORDRESTATUSER);
var opphavSchema = z28.enum(OPPHAV);
var kostnadstypeSchema = z28.enum(KOSTNADSTYPER);
var antallSchema = z28.string().trim().regex(/^\d{1,7}([.,]\d{1,2})?$/, "Antall m\xE5 v\xE6re et tall med h\xF8yst to desimaler.");
var arbeidsordreSchema = z28.object({
  id: uuidSchema,
  /** Saks-ID-en, «AO-3». */
  nummer: z28.string(),
  tittel: z28.string(),
  fagomrade: z28.enum(FAGOMRADER),
  objekt: person4.nullable(),
  status: arbeidsordrestatusSchema,
  frist: isodatoSchema.nullable(),
  opphav: z28.object({
    type: opphavSchema,
    avvikId: uuidSchema.nullable(),
    kontrollId: uuidSchema.nullable(),
    bygningsdel: z28.string().nullable(),
    /** «Avvik: Lekkasje i taket». */
    tekst: z28.string()
  }),
  estimat: belopSchema.nullable(),
  /** Summen av kostnadslinjene. */
  faktisk: belopSchema,
  opprettet: hendelse12,
  utfort: hendelse12.nullable(),
  fakturaKontrollert: hendelse12.nullable(),
  ansvarlige: z28.array(person4),
  /** Firmaet som skal utføre ordren, og svaret derfra. Tomt er kommunens egne folk. */
  utforer: z28.object({
    firma: person4,
    svar: z28.object({
      svar: firmasvarSchema,
      tidspunkt: z28.iso.datetime(),
      av: z28.string().nullable(),
      kommentar: z28.string().nullable()
    }).nullable()
  }).nullable(),
  kanEndre: z28.boolean()
});
var belopsrad = z28.object({
  planlagt: belopSchema,
  estimat: belopSchema,
  faktisk: belopSchema,
  ordrer: z28.number().int()
});
var arbeidsordreoversiktSchema = z28.object({
  ordrer: z28.array(arbeidsordreSchema),
  /** Per år og bygg: det planen sa, estimatet, og det det faktisk kostet. */
  kostnader: z28.array(
    z28.object({
      ar: z28.number().int(),
      rader: z28.array(belopsrad.extend({ objekt: person4.nullable() })),
      sum: belopsrad
    })
  ),
  /** Byggene kalleren kan lage ordrer på, og fagområdene for ordrer uten bygg. */
  objekter: z28.array(person4.extend({ fagomrade: z28.enum(FAGOMRADER) })),
  fagomrader: z28.array(z28.enum(FAGOMRADER)),
  /** De aktive firmaene en ordre kan sendes til, for den som kan sende den. */
  firmaer: z28.array(person4)
});
var kostnadslinjeSchema = z28.object({
  id: uuidSchema,
  type: kostnadstypeSchema,
  beskrivelse: z28.string(),
  antall: z28.string(),
  enhet: z28.string(),
  enhetspris: belopSchema,
  sum: belopSchema,
  registrert: hendelse12,
  kanSlette: z28.boolean()
});
var arbeidsordredetaljSchema = arbeidsordreSchema.extend({
  beskrivelse: z28.string(),
  utfortKommentar: z28.string().nullable(),
  linjer: z28.array(kostnadslinjeSchema),
  kostnad: z28.object({ arbeid: belopSchema, materiell: belopSchema, total: belopSchema }),
  /** Faktisk over estimatet, i prosent. */
  overEstimat: z28.number().int().nullable(),
  /** Hvor jobben er: adressen og posisjonen til bygget. */
  sted: z28.object({
    adresse: z28.string().nullable(),
    posisjon: z28.object({ breddegrad: z28.number(), lengdegrad: z28.number() }).nullable()
  }).nullable(),
  /**
   * Melde status og føre kostnader: den som kan endre, de ansvarlige, og
   * utføreren i firmaet når oppdraget er påtatt.
   */
  kanUtfore: z28.boolean(),
  /** Påta seg eller avslå oppdraget: utføreren i firmaet, før det er svart. */
  kanSvare: z28.boolean(),
  versjon: z28.number().int()
});
var felt = {
  tittel: z28.string().trim().min(1, "Skriv hva som skal gj\xF8res.").max(150),
  beskrivelse: z28.string().trim().max(4e3).default(""),
  frist: isodatoSchema.nullable().default(null),
  estimat: ikkeNegativ.nullable().default(null)
};
var nyArbeidsordreSchema = z28.object({
  /** Hvor ordren kommer fra. Bygget og fagområdet følger av det. */
  fra: z28.discriminatedUnion("type", [
    z28.object({
      type: z28.literal("manuell"),
      objektId: uuidSchema.nullable(),
      fagomrade: z28.enum(FAGOMRADER)
    }),
    z28.object({ type: z28.literal("avvik"), avvikId: uuidSchema }),
    z28.object({ type: z28.literal("kontroll"), kontrollId: uuidSchema }),
    z28.object({
      type: z28.literal("tilstand"),
      objektId: uuidSchema,
      bygningsdel: z28.string().refine(erBygningsdel, "Ukjent bygningsdel.")
    })
  ]),
  ...felt
});
var endreArbeidsordreSchema = z28.object({ ...felt, versjon: z28.number().int() });
var endreArbeidsordrestatusSchema = z28.object({
  status: arbeidsordrestatusSchema,
  /** Hva som ble gjort, når ordren meldes utført. */
  kommentar: z28.string().trim().max(1e3).nullable().default(null),
  versjon: z28.number().int()
});
var nyKostnadslinjeSchema = z28.object({
  type: kostnadstypeSchema,
  beskrivelse: z28.string().trim().min(1, "Skriv hva det gjelder.").max(200),
  antall: antallSchema.refine((a) => !/^0+([.,]0+)?$/.test(a), "Antallet m\xE5 v\xE6re mer enn null."),
  enhet: z28.string().trim().min(1).max(10),
  enhetspris: ikkeNegativ
});
var fakturakontrollSchema = z28.object({
  kontrollert: z28.boolean(),
  versjon: z28.number().int()
});

// src/schemas/budsjettrevisjon.ts
import { z as z29 } from "zod";
var hendelse13 = z29.object({ tidspunkt: z29.iso.datetime(), av: z29.string().nullable() });
var revisjonslinjeSchema = z29.object({
  id: uuidSchema,
  virksomhet: z29.object({ id: uuidSchema, navn: z29.string(), omradeId: uuidSchema }),
  underkonto: z29.object({ id: uuidSchema, kontonummer: z29.string().nullable(), navn: z29.string() }),
  tekst: z29.string(),
  maneder: tolvSchema,
  sum: belopSchema
});
var budsjettrevisjonSchema = z29.object({
  id: uuidSchema,
  nummer: z29.number().int().min(1),
  navn: z29.string(),
  /** Kommunestyrets vedtak. Null mens revisjonen er et utkast. */
  vedtak: z29.object({ dato: z29.iso.date(), saksnummer: z29.string().nullable(), registrert: hendelse13 }).nullable(),
  /** Endringen for året per område. */
  omrader: z29.array(z29.object({ id: uuidSchema, navn: z29.string(), endring: belopSchema })),
  /** Summen av alle linjene. Null er finansiert: inntektene står med minus. */
  netto: belopSchema,
  linjer: z29.array(revisjonslinjeSchema),
  /**
   * Rapporten revisjonen legges fram i, til politisk behandling, og
   * behandlingen når den er registrert. Null før den er lagt fram.
   */
  rapport: z29.object({
    id: uuidSchema,
    navn: z29.string(),
    behandling: z29.object({ dato: z29.iso.date(), saksnummer: z29.string().nullable() }).nullable()
  }).nullable(),
  laget: hendelse13,
  kanEndre: z29.boolean()
});
var budsjettrevisjonerSchema = z29.object({
  revisjoner: z29.array(budsjettrevisjonSchema),
  /** Om kalleren kan lage revisjoner: den som styrer, når budsjettet er tatt i bruk. */
  kanLage: z29.boolean(),
  /** Måneds-, tertial- og årsrapportene for året, som en revisjon kan legges fram i. */
  rapporter: z29.array(z29.object({ id: uuidSchema, navn: z29.string() })),
  /** Virksomhetene linjene kan gjelde. */
  virksomheter: z29.array(z29.object({ id: uuidSchema, navn: z29.string(), omradeId: uuidSchema }))
});
var nyBudsjettrevisjonSchema = z29.object({ navn: z29.string().trim().min(1).max(150) });
var nyRevisjonslinjeSchema = z29.object({
  virksomhetId: uuidSchema,
  underkontoId: uuidSchema,
  tekst: z29.string().trim().min(1).max(300),
  maneder: tolvSchema
});
var registrerRevisjonsvedtakSchema = z29.object({
  dato: z29.iso.date(),
  saksnummer: z29.string().trim().max(50).nullable().default(null),
  /** Registrer selv om revisjonen ikke er finansiert. Uten den avvises det. */
  tillatUbalanse: z29.boolean().default(false)
});
var settRevisjonsrapportSchema = z29.object({
  /** Null tar revisjonen ut av rapporten. */
  rapportId: uuidSchema.nullable()
});

// src/schemas/administrasjon.ts
import { z as z30 } from "zod";
var virksomhetSchema = z30.object({
  id: uuidSchema,
  navn: z30.string(),
  kommunalomradeId: uuidSchema,
  kommunalomradeNavn: z30.string(),
  valutaEnhet: z30.enum(["kr", "kr_1000"]),
  aktiv: z30.boolean()
});
var funksjonsflaggSchema = z30.object({
  nokkel: z30.string(),
  beskrivelse: z30.string(),
  aktivert: z30.boolean(),
  /**
   * Hva flagget er satt til for alle som ikke har valgt selv.
   *
   * Skilt fra `aktivert`, slik at skjermbildet kan si forskjell på «denne
   * kommunen har slått den på» og «den er på for alle». Nesten alle
   * standardverdier er av, så en modul som står på uten en overstyring ville
   * ellers vært umulig å forklare.
   */
  standardAktivert: z30.boolean(),
  /** True når kommunen har en egen rad som overstyrer standarden. */
  erOverstyrt: z30.boolean()
});
var settFunksjonsflaggSchema = z30.object({ aktivert: z30.boolean() });
var revisjonsloggOppforingSchema = z30.object({
  id: uuidSchema,
  tidspunkt: z30.iso.datetime(),
  brukerId: uuidSchema.nullable(),
  brukerNavn: z30.string().nullable(),
  handling: z30.string().describe('f.eks. "prognose.overstyring_satt"'),
  objektType: z30.string(),
  objektId: z30.string().nullable(),
  virksomhetId: uuidSchema.nullable(),
  sporingsId: z30.string().nullable()
});
var revisjonsloggSporringSchema = pagineringSchema.extend({
  virksomhetId: uuidSchema.optional(),
  handling: z30.string().optional(),
  fra: z30.iso.datetime().optional(),
  til: z30.iso.datetime().optional()
});
var systemrolleSchema = z30.enum(SYSTEMROLLER);
var tilgangSchema = z30.object({
  virksomhetId: uuidSchema,
  virksomhetNavn: z30.string(),
  rolle: rolleSchema
});
var omradetilgangSchema = z30.object({
  kommunalomradeId: uuidSchema,
  kommunalomradeNavn: z30.string(),
  rolle: rolleSchema
});
var administrasjonsbrukerSchema = z30.object({
  id: uuidSchema,
  navn: z30.string(),
  epost: z30.string(),
  systemrolle: systemrolleSchema,
  aktiv: z30.boolean(),
  /** Ser økonomirapporten for hele kommunen. Se `megSchema.erOkonomisjef`. */
  erOkonomisjef: z30.boolean(),
  /** Områdene brukeren er controller for. Tom når brukeren ikke er controller. */
  controllerOmrader: z30.array(uuidSchema),
  /** Utvalgene brukeren sitter i som folkevalgt. Tom når brukeren ikke er folkevalgt. */
  utvalg: z30.array(uuidSchema).default([]),
  /**
   * Whether the account has ever been bound to an identity. Not the same as
   * `aktiv`: an invited account is active and unbound, which is exactly the
   * state a person is in between being invited and logging in the first time.
   */
  harLoggetInn: z30.boolean(),
  sistInnlogget: z30.iso.datetime().nullable(),
  /**
   * Virksomhetene brukeren er tildelt DIREKTE.
   *
   * Ikke de som følger av et område. Skjermen skal vise hva noen har bestemt,
   * ikke summen av det og det som er utledet — ellers kan man ikke se forskjell
   * på en tildeling man kan ta bort og en som følger av noe annet.
   */
  tilganger: z30.array(tilgangSchema),
  omradetilganger: z30.array(omradetilgangSchema).default([])
});
var invitasjonSchema = z30.object({
  id: uuidSchema,
  epost: z30.string(),
  navn: z30.string(),
  rolle: rolleSchema,
  virksomhetId: uuidSchema.nullable(),
  virksomhetNavn: z30.string().nullable(),
  status: z30.enum(["sendt", "godtatt", "tilbaketrukket", "utlopt"]),
  utloper: z30.iso.datetime(),
  invitertAvNavn: z30.string().nullable(),
  opprettet: z30.iso.datetime()
});
var identitetsleverandorSchema = z30.object({
  id: uuidSchema,
  alias: z30.string().min(1).max(100),
  type: identitetsleverandorTypeSchema,
  visningsnavn: z30.string().min(1).max(150),
  /** Tilbys først på innloggingsskjermen for denne kommunen. */
  standard: z30.boolean(),
  aktiv: z30.boolean()
});
var opprettIdentitetsleverandorSchema = z30.object({
  // Samme tegnsett som Keycloak tillater i et alias. Mellomrom og æøå går ikke,
  // og det er bedre å si det her enn å la innloggingen feile stille senere.
  alias: z30.string().min(1).max(100).regex(
    /^[a-z0-9][a-z0-9._-]*$/,
    "Aliaset kan bare inneholde a-z, 0-9, punktum, bindestrek og understrek."
  ),
  type: identitetsleverandorTypeSchema,
  visningsnavn: z30.string().min(1).max(150),
  standard: z30.boolean().default(false)
});
var endreIdentitetsleverandorSchema = z30.object({
  visningsnavn: z30.string().min(1).max(150).optional(),
  standard: z30.boolean().optional(),
  aktiv: z30.boolean().optional()
}).refine((d) => Object.keys(d).length > 0, { message: "Ingenting \xE5 endre." });
var administrasjonsvirksomhetSchema = z30.object({
  id: uuidSchema,
  navn: z30.string(),
  kommunalomradeId: uuidSchema,
  orgNummer: z30.string().nullable(),
  valutaEnhet: valutaEnhetSchema,
  standardDetaljniva: z30.number().int().min(1).max(3),
  aktiv: z30.boolean(),
  /** Om det finnes budsjett- eller regnskapslinjer på den. Låser valutaenheten. */
  harTall: z30.boolean(),
  /**
   * Hvor mange som har tilgang. En nyopprettet virksomhet har NULL — den
   * dukker ikke opp i virksomhetsvelgeren for noen, heller ikke for den som
   * opprettet den, fordi lista filtreres på tilgang. Uten dette tallet ser det
   * ut som om opprettelsen ikke virket.
   */
  antallMedTilgang: z30.number().int()
});
var administrasjonsomradeSchema = z30.object({
  id: uuidSchema,
  navn: z30.string(),
  aktiv: z30.boolean(),
  virksomheter: z30.array(administrasjonsvirksomhetSchema)
});
var opprettKommunalomradeSchema = z30.object({
  navn: z30.string().min(1).max(150)
});
var endreKommunalomradeSchema = z30.object({
  navn: z30.string().min(1).max(150).optional(),
  aktiv: z30.boolean().optional()
}).refine((d) => Object.keys(d).length > 0, { message: "Ingenting \xE5 endre." });
var opprettVirksomhetSchema = z30.object({
  navn: z30.string().min(1).max(150),
  kommunalomradeId: uuidSchema,
  orgNummer: z30.string().max(20).nullable().default(null),
  // Ingen standardverdi: dette er valget som gjør tall tusen ganger feil hvis
  // det er galt, og et felt uten standard er et felt noen må ta stilling til.
  valutaEnhet: valutaEnhetSchema,
  standardDetaljniva: z30.number().int().min(1).max(3).default(2)
});
var endreVirksomhetSchema = z30.object({
  navn: z30.string().min(1).max(150).optional(),
  kommunalomradeId: uuidSchema.optional(),
  orgNummer: z30.string().max(20).nullable().optional(),
  valutaEnhet: valutaEnhetSchema.optional(),
  standardDetaljniva: z30.number().int().min(1).max(3).optional(),
  aktiv: z30.boolean().optional()
}).refine((d) => Object.keys(d).length > 0, { message: "Ingenting \xE5 endre." });
var utvalgSchema = z30.object({
  id: uuidSchema,
  navn: z30.string(),
  aktiv: z30.boolean(),
  antallMedlemmer: z30.number().int().min(0)
});
var opprettUtvalgSchema = z30.object({ navn: z30.string().trim().min(1).max(150) });
var endreUtvalgSchema = z30.object({
  navn: z30.string().trim().min(1).max(150).optional(),
  aktiv: z30.boolean().optional()
}).refine((d) => d.navn !== void 0 || d.aktiv !== void 0, "Ingenting \xE5 endre.");
var administrasjonsoversiktSchema = z30.object({
  brukere: z30.array(administrasjonsbrukerSchema),
  invitasjoner: z30.array(invitasjonSchema),
  virksomheter: z30.array(z30.object({ id: uuidSchema, navn: z30.string() })),
  /** What the caller may hand out — a kommuneadmin cannot appoint platform admins. */
  tildelbareSystemroller: z30.array(systemrolleSchema),
  identitetsleverandorer: z30.array(identitetsleverandorSchema).default([]),
  /** Hele organisasjonen, aktiv og inaktiv, til administrasjonsseksjonen. */
  kommunalomrader: z30.array(administrasjonsomradeSchema).default([]),
  /** De politiske utvalgene, aktive og inaktive. */
  utvalg: z30.array(utvalgSchema).default([])
});
var inviterBrukerSchema = z30.object({
  epost: z30.string().max(190).regex(/^[^@\s]+@[^@\s]+\.[^@\s]+$/, "Skriv en gyldig e-postadresse."),
  navn: z30.string().min(1).max(150),
  systemrolle: systemrolleSchema.default("bruker"),
  /** Optional first grant, so an invitation can be useful the moment it is used. */
  virksomhetId: uuidSchema.nullable().default(null),
  rolle: rolleSchema.default("leser"),
  /** Days until the invitation stops admitting anyone. */
  gyldigeDager: z30.number().int().min(1).max(90).default(14)
});
var invitasjonOpprettetSchema = z30.object({
  invitasjon: invitasjonSchema
});
var endreBrukerSchema = z30.object({
  navn: z30.string().min(1).max(150).optional(),
  systemrolle: systemrolleSchema.optional(),
  aktiv: z30.boolean().optional(),
  erOkonomisjef: z30.boolean().optional(),
  /** Hele settet av områder brukeren er controller for. Tom liste fjerner rollen. */
  controllerOmrader: z30.array(uuidSchema).max(100).optional(),
  /** Hele settet av utvalg brukeren sitter i som folkevalgt. Tom liste fjerner rollen. */
  utvalg: z30.array(uuidSchema).max(100).optional()
});
var settTilgangerSchema = z30.object({
  tilganger: z30.array(z30.object({ virksomhetId: uuidSchema, rolle: rolleSchema })),
  /**
   * Områdene brukeren skal ha. Når feltet ER med, er det HELE settet — en
   * tildeling som ikke står i lista fjernes, som for virksomhetene.
   *
   * `.optional()` og ikke `.default([])`, og forskjellen er ikke kosmetisk: en
   * standardverdi på tom liste ville betydd «ingen områder» for enhver klient
   * som ikke sender feltet, og dermed SLETTET tildelinger under en utrulling
   * der noen har en gammel fane åpen. Udefinert betyr uendret.
   */
  omradetilganger: z30.array(z30.object({ kommunalomradeId: uuidSchema, rolle: rolleSchema })).optional()
});
var revisjonsloggSvarSchema = z30.object({
  oppforinger: z30.array(revisjonsloggOppforingSchema),
  side: z30.number().int(),
  antall: z30.number().int(),
  totalt: z30.number().int(),
  /** The distinct actions present, so the filter offers what exists. */
  handlinger: z30.array(z30.string())
});

// src/schemas/bemanning.ts
import { z as z31 } from "zod";
var tallSchema = z31.string().regex(/^-?\d{1,9}(\.\d{1,3})?$/, "M\xE5 v\xE6re et tall med inntil tre desimaler");
var bemanningsmaalSchema = z31.object({
  budsjetterteArsverk: tallSchema,
  besatteArsverk: tallSchema,
  overtidstimer: tallSchema,
  fravarsdagsverk: tallSchema,
  korttidsdagsverk: tallSchema,
  muligeDagsverk: tallSchema
});
var utledetSchema = z31.object({
  vakans: tallSchema,
  vakansgrad: tallSchema.nullable(),
  sykefravar: tallSchema.nullable(),
  korttidsfravar: tallSchema.nullable()
});
var bemanningsmanedSchema = bemanningsmaalSchema.extend({
  maned: manedSchema,
  ...utledetSchema.shape
});
var bemanningsgruppeSchema = bemanningsmaalSchema.extend({
  stillingsgruppeId: uuidSchema,
  kode: z31.string(),
  navn: z31.string(),
  ...utledetSchema.shape,
  /** Årsverk for året er snittet av månedene, ikke summen. */
  snittBudsjetterteArsverk: tallSchema,
  snittBesatteArsverk: tallSchema,
  maneder: z31.array(bemanningsmanedSchema)
});
var bemanningsoversiktSchema = z31.object({
  virksomhetId: uuidSchema,
  virksomhetNavn: z31.string(),
  ar: arSchema,
  /** Måneder det finnes registreringer for. Resten er ikke ført, ikke null. */
  registrerteManeder: z31.array(manedSchema),
  grupper: z31.array(bemanningsgruppeSchema),
  sum: bemanningsmaalSchema.extend({
    ...utledetSchema.shape,
    snittBudsjetterteArsverk: tallSchema,
    snittBesatteArsverk: tallSchema,
    maneder: z31.array(bemanningsmanedSchema)
  })
});
var bemanningSporringSchema = z31.object({ ar: arSporringSchema });
var stillingsgruppeSchema = z31.object({
  id: uuidSchema,
  kode: z31.string(),
  navn: z31.string(),
  aktiv: z31.boolean()
});
var bemanningsimportradSchema = bemanningsmaalSchema.extend({
  linje: z31.number().int(),
  kode: z31.string(),
  navn: z31.string(),
  maned: manedSchema,
  /** Stillingsgruppen koden traff, eller null når kommunen ikke har den. */
  stillingsgruppeId: uuidSchema.nullable(),
  kjentNavn: z31.string().nullable()
});
var ignorertBemanningsradSchema = z31.object({
  linje: z31.number().int(),
  innhold: z31.string(),
  arsak: z31.string()
});
var bemanningsanalyseSchema = z31.object({
  filnavn: z31.string(),
  ar: arSchema,
  manederIFilen: z31.array(manedSchema),
  rader: z31.array(bemanningsimportradSchema),
  /** Koder filen har som kommunen ikke fører. Opprettes bare hvis noen ber om det. */
  ukjenteGrupper: z31.array(
    z31.object({ kode: z31.string(), navn: z31.string(), antallRader: z31.number().int() })
  ),
  /** Kolonner filen hadde som ikke ble gjenkjent. Rapportert, aldri gjettet på. */
  ukjenteKolonner: z31.array(z31.string()),
  ignorerteRader: z31.array(ignorertBemanningsradSchema)
});
var analyserBemanningSchema = z31.object({ ar: arSporringSchema });
var bemanningshandlingSchema = z31.discriminatedUnion("handling", [
  z31.object({
    handling: z31.literal("skriv"),
    stillingsgruppeId: uuidSchema,
    maned: manedSchema,
    ...bemanningsmaalSchema.shape
  }),
  z31.object({
    /**
     * Oppretter stillingsgruppen først, så raden. Et eget, navngitt valg —
     * aldri noe importen gjør av seg selv. En parallell «SPL Sykepleier» ved
     * siden av kommunens egen er slik et register stille deler seg i to.
     */
    handling: z31.literal("skriv_med_ny_gruppe"),
    kode: z31.string().min(1).max(20),
    navn: z31.string().min(1).max(150),
    maned: manedSchema,
    ...bemanningsmaalSchema.shape
  })
]);
var utforBemanningsimportSchema = z31.object({
  ar: arSchema,
  filnavn: z31.string().max(255),
  rader: z31.array(bemanningshandlingSchema).min(1).max(5e3)
});
var bemanningsresultatSchema = z31.object({
  ar: arSchema,
  berorteManeder: z31.array(manedSchema),
  antallLinjer: z31.number().int(),
  antallNyeGrupper: z31.number().int()
});

// src/schemas/lonn.ts
import { z as z32 } from "zod";
var LONNSUTFALL = ["ny", "uendret", "endret", "avsluttet", "avvist"];
var LONNSHANDLINGER = ["ny", "endret", "avsluttet"];
var lonnsradSchema = z32.object({
  linje: z32.number().int(),
  ansattnummer: z32.string().min(1),
  navn: z32.string().min(1),
  /** Lønnssystemets egen id for forholdet. Uten den må vi finne stillingen på gruppen. */
  eksternId: z32.string().nullable(),
  gruppekode: z32.string().min(1),
  stillingsprosent: tallSchema,
  arslonnFullStilling: belopSchema,
  fraDato: isodatoSchema,
  tilDato: isodatoSchema.nullable()
});
var lonnsanalyseradSchema = lonnsradSchema.extend({
  /** Stillingsgruppen koden traff, eller null når kommunen ikke fører den. */
  stillingsgruppeId: uuidSchema.nullable(),
  kjentGruppenavn: z32.string().nullable(),
  utfall: z32.enum(LONNSUTFALL),
  /** Forholdet raden traff hos oss. Null for `ny` og for de fleste avviste. */
  forholdId: uuidSchema.nullable(),
  /** Feltene som er ulike. Bare ved `endret`. */
  endrede: z32.array(z32.string()),
  /** Datoen den gamle raden lukkes på — dagen før virkningsdatoen. */
  lukkesDato: isodatoSchema.nullable(),
  /** Hvorfor raden ble avvist. Null ellers. */
  grunn: z32.string().nullable(),
  /**
   * Verdiene stillingen har hos oss i dag. Bare ved `endret`.
   *
   * Uten dem kan gjennomgangen si AT lønnen endres, men ikke fra hva — og en
   * forhåndsvisning man ikke kan kjenne igjen tallene i, er en bekreftelsesknapp
   * med ekstra steg. Dette er en ansatt i kallerens egen virksomhet, og lønnen
   * hennes er nettopp det modulen er til for å vise.
   */
  forrige: z32.object({
    gruppekode: z32.string(),
    stillingsprosent: tallSchema,
    arslonnFullStilling: belopSchema
  }).nullable()
});
var ignorertLonnsradSchema = z32.object({
  linje: z32.number().int(),
  innhold: z32.string(),
  arsak: z32.string()
});
var lonnsanalyseSchema = z32.object({
  filnavn: z32.string(),
  virkningsdato: isodatoSchema,
  rader: z32.array(lonnsanalyseradSchema),
  /** Hvor mange rader som fikk hvert utfall. Det tallet avgjør om noen tør trykke. */
  oppsummering: z32.object({
    ny: z32.number().int(),
    uendret: z32.number().int(),
    endret: z32.number().int(),
    avsluttet: z32.number().int(),
    avvist: z32.number().int()
  }),
  /** Stillingskoder filen har som kommunen ikke fører. Importen finner dem aldri opp. */
  ukjenteGrupper: z32.array(z32.object({ kode: z32.string(), antallRader: z32.number().int() })),
  /** Kolonner filen hadde som ikke ble gjenkjent. Rapportert, aldri gjettet på. */
  ukjenteKolonner: z32.array(z32.string()),
  /** Kolonner vi må ha, som filen ikke hadde. Da leses ingen rader i det hele tatt. */
  manglendeKolonner: z32.array(z32.string()),
  ignorerteRader: z32.array(ignorertLonnsradSchema),
  /**
   * Hvordan datoer med skråstrek ble lest, når filen ikke avgjorde det selv
   * (bare datoer som 01/08/2020). Null når det ikke var noe å si fra om.
   */
  datomerknad: z32.string().nullable()
});
var analyserLonnSchema = z32.object({ virkningsdato: isodatoSchema });
var lonnsimportradSchema = lonnsradSchema.extend({
  forventetUtfall: z32.enum(LONNSHANDLINGER),
  // Kolonnebreddene i databasen, uttrykt der de hører hjemme: på veien INN.
  ansattnummer: z32.string().min(1).max(50),
  navn: z32.string().min(1).max(150),
  eksternId: z32.string().max(100).nullable(),
  gruppekode: z32.string().min(1).max(20)
});
var utforLonnsimportSchema = z32.object({
  filnavn: z32.string().max(255),
  virkningsdato: isodatoSchema,
  rader: z32.array(lonnsimportradSchema).min(1).max(5e3)
});
var lonnsimportresultatSchema = z32.object({
  virkningsdato: isodatoSchema,
  antallNyeAnsatte: z32.number().int(),
  antallNyeForhold: z32.number().int(),
  /** Stillinger som fikk en ny periode, og der den gamle ble lukket dagen før. */
  antallVersjonerte: z32.number().int(),
  antallAvsluttede: z32.number().int()
});
var lonnslesingOppforingSchema = z32.object({
  id: uuidSchema,
  tidspunkt: z32.iso.datetime(),
  brukerId: uuidSchema,
  brukerNavn: z32.string(),
  virksomhetId: uuidSchema,
  virksomhetNavn: z32.string(),
  formaal: z32.string().describe('f.eks. "lonn.import_gjennomgang"'),
  ansattIder: z32.array(uuidSchema),
  /** Alltid `ansattIder.length`. Ligger ikke lagret, nettopp for ikke å kunne avvike. */
  antall: z32.number().int(),
  sporingsId: z32.string().nullable()
});
var lonnslesingSporringSchema = pagineringSchema.extend({
  /** Hvem som leste. */
  brukerId: uuidSchema.optional(),
  /** Spørsmålet sporet finnes for: hvem har sett DENNE ansatte. */
  ansattId: uuidSchema.optional(),
  fra: z32.iso.datetime().optional(),
  til: z32.iso.datetime().optional()
});
var lonnslesingSvarSchema = z32.object({
  oppforinger: z32.array(lonnslesingOppforingSchema),
  side: z32.number().int(),
  antall: z32.number().int(),
  totalt: z32.number().int()
});
var lonnsforutsetningSchema = z32.object({
  ar: arSchema,
  arbeidsgiveravgiftProsent: prosentSchema,
  feriepengeProsent: prosentSchema,
  pensjonProsent: prosentSchema,
  /** Glidning gjennom året, utenom det sentrale oppgjøret (beslutning 8). */
  glidningProsent: prosentSchema,
  sattAv: uuidSchema.nullable(),
  sattAvNavn: z32.string().nullable(),
  sattTidspunkt: z32.iso.datetime()
});
var settLonnsforutsetningSchema = z32.object({
  ar: arSchema,
  arbeidsgiveravgiftProsent: prosentSchema,
  feriepengeProsent: prosentSchema,
  pensjonProsent: prosentSchema,
  glidningProsent: prosentSchema
});
var OPPGJORSTILSTANDER = ["forutsatt", "vedtatt"];
var oppgjorstilstandSchema = z32.enum(OPPGJORSTILSTANDER);
var lonnsoppgjorSchema = z32.object({
  id: uuidSchema,
  ar: arSchema,
  /** Null betyr «gjelder alle stillingsgrupper». */
  stillingsgruppeId: uuidSchema.nullable(),
  stillingsgruppeNavn: z32.string().nullable(),
  virkningDato: isodatoSchema,
  prosent: prosentSchema,
  tilstand: oppgjorstilstandSchema,
  sattAv: uuidSchema.nullable(),
  sattAvNavn: z32.string().nullable(),
  sattTidspunkt: z32.iso.datetime()
});
var settLonnsoppgjorSchema = z32.object({
  id: uuidSchema.optional(),
  ar: arSchema,
  stillingsgruppeId: uuidSchema.nullable(),
  virkningDato: isodatoSchema,
  prosent: prosentSchema,
  tilstand: oppgjorstilstandSchema
});
var lonnsgrunnlagSchema = z32.object({
  ar: arSchema,
  /** Null når kommunen ikke har satt satsene for året ennå. */
  forutsetning: lonnsforutsetningSchema.nullable(),
  oppgjor: z32.array(lonnsoppgjorSchema)
});
var lonnsgrunnlagSporringSchema = z32.object({
  ar: z32.coerce.number().int().min(2e3).max(2100)
});
var stillingshjemmelSchema = z32.object({
  id: uuidSchema,
  stillingsgruppeId: uuidSchema,
  gruppekode: z32.string(),
  gruppenavn: z32.string(),
  betegnelse: z32.string(),
  stillingsprosent: tallSchema,
  fraDato: isodatoSchema,
  /** Null betyr løpende. En hjemmel som er inndratt får en sluttdato. */
  tilDato: isodatoSchema.nullable()
});
var settStillingshjemmelSchema = z32.object({
  id: uuidSchema.optional(),
  stillingsgruppeId: uuidSchema,
  betegnelse: z32.string().min(1).max(150),
  stillingsprosent: tallSchema,
  fraDato: isodatoSchema,
  tilDato: isodatoSchema.nullable()
});
var hjemmelinnehaverSchema = z32.object({
  forholdId: uuidSchema,
  ansattId: uuidSchema,
  navn: z32.string().nullable(),
  stillingsprosent: tallSchema,
  fraDato: isodatoSchema,
  tilDato: isodatoSchema.nullable()
});
var hjemmelstatusSchema = stillingshjemmelSchema.extend({
  /** Summen av forholdene som fyller hjemmelen på referansedatoen. */
  dekket: tallSchema,
  /**
   * Hjemmelens prosent minus dekningen, eller NULL når det ikke lar seg måle.
   *
   * Null betyr «ingen kobling ført», og er det samme grepet som
   * `perGruppe.vakans` gjør uten hjemler: uten vedtatte stillinger finnes det
   * ingenting å måle mot, og uten koblinger finnes det ingenting å måle HVEM
   * mot. Et forhold som ikke er plassert på noen hjemmel teller fortsatt i
   * gruppens besatte årsverk, så en hjemmel som meldte seg ledig bare fordi
   * ingen hadde koblet noen dit, ville motsagt tallet rett over på skjermen.
   *
   * Negativ ved en overlevering — to som overlapper er et faktum.
   */
  ledig: tallSchema.nullable(),
  /** Første dag i den PÅGÅENDE underdekningen, ikke den første noen gang. */
  ledigSiden: isodatoSchema.nullable(),
  /** En oppsigelse som er kjent, men ennå ikke har virket. */
  blirLedigFra: isodatoSchema.nullable(),
  innehavere: z32.array(hjemmelinnehaverSchema)
});
var settHjemmelskoblingSchema = z32.object({
  forholdId: uuidSchema,
  stillingshjemmelId: uuidSchema.nullable()
});
var hjemmelkandidatSchema = z32.object({
  forholdId: uuidSchema,
  ansattId: uuidSchema,
  navn: z32.string().nullable(),
  ansattnummer: z32.string().nullable(),
  stillingsprosent: tallSchema,
  fraDato: isodatoSchema,
  tilDato: isodatoSchema.nullable(),
  /** Betegnelsen på hjemmelen forholdet alt er knyttet til, om noen. */
  opptattAv: z32.string().nullable(),
  foreslatt: z32.boolean()
});
var arsverkslinjeSchema = z32.object({
  maned: manedSchema,
  budsjetterte: tallSchema,
  besatte: tallSchema,
  /** Null når gruppen ikke har hjemler — da finnes det ingen vakans å måle. */
  vakans: tallSchema.nullable()
});
var kostnadSchema = z32.object({
  lonn: belopSchema,
  feriepenger: belopSchema,
  pensjon: belopSchema,
  arbeidsgiveravgift: belopSchema,
  sum: belopSchema
});
var stillingsperiodeSchema = z32.object({
  id: uuidSchema,
  gruppekode: z32.string(),
  gruppenavn: z32.string(),
  stillingsprosent: tallSchema,
  arslonnFullStilling: belopSchema,
  fraDato: isodatoSchema,
  tilDato: isodatoSchema.nullable()
});
var stillingslinjeSchema = z32.object({
  ansattId: uuidSchema,
  /**
   * Null for en anonymisert ansatt (beslutning 9).
   *
   * Raden blir stående etter ett år fordi besatte årsverk og lønnskost utledes
   * av den — historikken skal fortsatt regne riktig, det skal bare ikke lenger
   * stå hvem den handlet om.
   */
  navn: z32.string().nullable(),
  ansattnummer: z32.string().nullable(),
  perioder: z32.array(stillingsperiodeSchema),
  /** Summen av stillingsprosentene som løper ved utgangen av året. */
  samletStillingsprosent: tallSchema,
  /** Null når kommunen ikke har satt satsene for året. */
  kostnad: kostnadSchema.nullable()
});
var stillingsgruppesumSchema = z32.object({
  stillingsgruppeId: uuidSchema,
  gruppekode: z32.string(),
  gruppenavn: z32.string(),
  antallPersoner: z32.number().int(),
  samletStillingsprosent: tallSchema,
  kostnad: kostnadSchema.nullable(),
  /** Snitt over året, utledet av hjemlene (beslutning 7). */
  budsjetterteArsverk: tallSchema,
  /** Snitt over året, utledet av stillingsforholdene. */
  besatteArsverk: tallSchema,
  /**
   * Budsjetterte minus besatte. Null når gruppen ikke har hjemler i det hele
   * tatt — da er det ingen vakans å måle, og et negativt tall ville påstått at
   * de som er i stilling er overtallige.
   */
  vakans: tallSchema.nullable()
});
var stillingsoversiktSchema = z32.object({
  virksomhetId: uuidSchema,
  virksomhetNavn: z32.string(),
  ar: arSchema,
  /**
   * Sant når satsene for året ikke er satt.
   *
   * Da står kostnadene som null framfor som nuller. En kostnad på 0 kr ser ut
   * som et svar, og «vi vet ikke ennå» er noe annet enn «det koster ingenting».
   */
  manglerSatser: z32.boolean(),
  linjer: z32.array(stillingslinjeSchema),
  perGruppe: z32.array(stillingsgruppesumSchema),
  sum: kostnadSchema.nullable(),
  /** Samlet kostnad per måned. Tom når satsene mangler. */
  perManed: z32.array(z32.object({ maned: manedSchema, sum: belopSchema })),
  /** De vedtatte stillingene, hver med hvem som fyller den og hva som står tomt. */
  hjemler: z32.array(hjemmelstatusSchema),
  /**
   * Dagen dekningen er regnet på.
   *
   * «Ledig siden» er et utsagn om NÅ, mens resten av oversikten gjelder et år.
   * Ser man på et tidligere år, er referansedatoen siste dag i det året — ellers
   * ville hver eneste hjemmel i 2025 stått som inndratt og uten vakans.
   */
  referansedato: isodatoSchema,
  /** Årsverk måned for måned, begge sider av vakansen. */
  arsverk: z32.array(arsverkslinjeSchema)
});
var HJEMMELUTFALL = ["ny", "uendret", "endret", "avvist"];
var HJEMMELHANDLINGER = ["ny", "endret"];
var hjemmelradSchema = z32.object({
  linje: z32.number().int(),
  betegnelse: z32.string().min(1),
  gruppekode: z32.string().min(1),
  stillingsprosent: tallSchema,
  fraDato: isodatoSchema,
  tilDato: isodatoSchema.nullable()
});
var hjemmelanalyseradSchema = hjemmelradSchema.extend({
  stillingsgruppeId: uuidSchema.nullable(),
  kjentGruppenavn: z32.string().nullable(),
  utfall: z32.enum(HJEMMELUTFALL),
  /** Hjemmelen raden traff hos oss. Null for `ny` og for de fleste avviste. */
  hjemmelId: uuidSchema.nullable(),
  /** Feltene som er ulike. Bare ved `endret`. */
  endrede: z32.array(z32.string()),
  grunn: z32.string().nullable()
});
var hjemmelanalyseSchema = z32.object({
  filnavn: z32.string(),
  rader: z32.array(hjemmelanalyseradSchema),
  oppsummering: z32.object({
    ny: z32.number().int(),
    uendret: z32.number().int(),
    endret: z32.number().int(),
    avvist: z32.number().int()
  }),
  ukjenteGrupper: z32.array(z32.object({ kode: z32.string(), antallRader: z32.number().int() })),
  ukjenteKolonner: z32.array(z32.string()),
  manglendeKolonner: z32.array(z32.string()),
  ignorerteRader: z32.array(ignorertLonnsradSchema),
  /**
   * Hvordan datoer med skråstrek ble lest, når filen ikke avgjorde det selv
   * (bare datoer som 01/08/2020). Null når det ikke var noe å si fra om.
   */
  datomerknad: z32.string().nullable()
});
var hjemmelimportradSchema = hjemmelradSchema.extend({
  forventetUtfall: z32.enum(HJEMMELHANDLINGER),
  betegnelse: z32.string().min(1).max(150),
  gruppekode: z32.string().min(1).max(20)
});
var utforHjemmelimportSchema = z32.object({
  filnavn: z32.string().max(255),
  rader: z32.array(hjemmelimportradSchema).min(1).max(2e3)
});
var hjemmelimportresultatSchema = z32.object({
  antallNye: z32.number().int(),
  antallEndrede: z32.number().int()
});
var koblingskontoSchema = z32.object({
  underkontoId: uuidSchema,
  kontonummer: z32.string(),
  kontonavn: z32.string(),
  /** Årssummen koblingen ville lagt på kontoen, i virksomhetens enhet. */
  belop: belopSchema
});
var lonnsprognosekoblingSchema = z32.object({
  ar: arSchema,
  /**
   * Av som standard. Å koble lønnsbudsjettet til prognosen er en beslutning
   * kommunen tar, ikke noe som skjer fordi kontoene ble pekt ut.
   */
  aktiv: z32.boolean(),
  lonnUnderkontoId: uuidSchema.nullable(),
  feriepengerUnderkontoId: uuidSchema.nullable(),
  pensjonUnderkontoId: uuidSchema.nullable(),
  arbeidsgiveravgiftUnderkontoId: uuidSchema.nullable(),
  /**
   * Om koblingen LAR SEG slå på: alle fire postene må ha en konto, og det må
   * finnes satser for året. Uten satser regnes ingen kroner, og en kobling som
   * la null på fire kontoer ville sett ut som et lønnsbudsjett på null.
   */
  kanAktiveres: z32.boolean(),
  /** Hvorfor den ikke lar seg slå på. Null når den gjør det. */
  hindring: z32.string().nullable(),
  /**
   * Hva koblingen ville lagt på hver konto i år.
   *
   * Vises FØR noen slår den på: et lønnsbudsjett som dukker opp uten at man så
   * hvor det havnet, er vanskeligere å stole på enn et man har sett.
   */
  perKonto: z32.array(koblingskontoSchema)
});
var settLonnsprognosekoblingSchema = z32.object({
  ar: arSchema,
  aktiv: z32.boolean(),
  lonnUnderkontoId: uuidSchema.nullable(),
  feriepengerUnderkontoId: uuidSchema.nullable(),
  pensjonUnderkontoId: uuidSchema.nullable(),
  arbeidsgiveravgiftUnderkontoId: uuidSchema.nullable()
});

// src/schemas/innkjop.ts
import { z as z33 } from "zod";
var orgnummerSchema = z33.string().regex(/^\d{9}$/, "Organisasjonsnummer m\xE5 v\xE6re ni siffer.");
var leverandorSchema = z33.object({
  id: uuidSchema,
  orgNummer: orgnummerSchema,
  navn: z33.string()
});
var journalradSchema = z33.object({
  linje: z33.number().int(),
  orgnummer: orgnummerSchema,
  leverandor: z33.string().min(1).max(200),
  kontonummer: z33.string().min(1).max(20),
  belop: belopSchema,
  /** ISO-dato. Måneden utledes av den, så den er påkrevd. */
  dato: isodatoSchema,
  fakturanummer: z33.string().max(50).nullable(),
  maned: manedSchema
});
var avvistradSchema = z33.object({
  linje: z33.number().int(),
  feil: z33.enum([
    "ugyldig_orgnummer",
    "mangler_konto",
    "ugyldig_belop",
    "mangler_leverandornavn",
    "ukjent_konto",
    "mangler_dato",
    "feil_ar"
  ]),
  verdi: z33.string()
});
var innkjopsanalyseSchema = z33.object({
  ar: arSchema,
  /** Radene som kan importeres, med kontoen slått opp i kontoplanen. */
  rader: z33.array(journalradSchema),
  /**
   * Radene som IKKE kan importeres, med grunn og linjenummer.
   *
   * De telles og vises. En import der fjorten linjer stille forsvant gir en
   * leverandøroversikt som ser komplett ut og ikke er det.
   */
  avviste: z33.array(avvistradSchema),
  /** Leverandører filen har som ikke finnes i registeret fra før. */
  nyeLeverandorer: z33.array(z33.object({ orgNummer: orgnummerSchema, navn: z33.string() })),
  antallKjenteLeverandorer: z33.number().int(),
  berorteManeder: z33.array(manedSchema),
  sumBelop: belopSchema,
  /**
   * Hvordan datoer med skråstrek ble lest, når filen ikke avgjorde det selv
   * (bare datoer som 01/08/2020). Null når det ikke var noe å si fra om.
   */
  datomerknad: z33.string().nullable()
});
var importerInnkjopSchema = z33.object({
  ar: arSchema,
  filnavn: z33.string().min(1).max(255),
  rader: z33.array(journalradSchema).min(1)
});
var innkjopsresultatSchema = z33.object({
  antallLinjer: z33.number().int(),
  antallNyeLeverandorer: z33.number().int(),
  berorteManeder: z33.array(manedSchema),
  sumBelop: belopSchema
});
var leverandorforbrukSchema = z33.object({
  leverandorId: uuidSchema,
  orgNummer: orgnummerSchema,
  navn: z33.string(),
  belop: belopSchema,
  antallLinjer: z33.number().int(),
  /** Andel av samlet innkjøp, i tusendels prosent. Null når alt er null. */
  andelProsent: z33.string().nullable()
});
var kontoforbrukSchema = z33.object({
  underkontoId: uuidSchema,
  kontonummer: z33.string().nullable(),
  kontonavn: z33.string(),
  /** Regnskapet. Fasit for hva som er brukt. */
  bokfort: belopSchema,
  /** Summen av innkjøpslinjene på kontoen. */
  forklart: belopSchema,
  /** Bokført minus forklart. Positiv: forbruk uten fakturalinjer bak seg. */
  uforklart: belopSchema
});
var innkjopsoversiktSchema = z33.object({
  virksomhetId: uuidSchema,
  virksomhetNavn: z33.string(),
  ar: arSporringSchema,
  leverandorer: z33.array(leverandorforbrukSchema),
  kontoer: z33.array(kontoforbrukSchema),
  sumInnkjop: belopSchema,
  sumBokfort: belopSchema,
  sumUforklart: belopSchema,
  /**
   * Hvor mye av det bokførte forbruket fakturalinjene gjør rede for.
   *
   * Regnet av summene, aldri som snitt av kontoenes andeler. Null når
   * ingenting er bokført.
   */
  andelForklart: z33.string().nullable()
});
var innkjopSporringSchema = z33.object({ ar: arSporringSchema });
export {
  AGGREGERINGER,
  AKSJONSSTATUSER,
  ALVORLIGHETER,
  ALVORLIGHETSKRITERIUM,
  ALVORLIGHETSNAVN,
  ANTALL_MANEDER,
  ARBEIDSORDREHANDLING,
  ARBEIDSORDRESTATUSER,
  ARBEIDSORDRESTATUSNAVN,
  AVVIKSIKONER,
  AVVIKSKATALOG,
  AVVIKSSTATUSER,
  AVVIKSSTATUSNAVN,
  AVVIKSTYPER,
  BELOP_SKALA,
  BILDETYPER,
  BUDSJETTFASER,
  BYGNINGSDELER,
  BYGNINGSDELGRUPPER,
  DATAKILDER,
  DIMENSJONER,
  DIMENSJONSNAVN,
  DISK_ADVARSEL_ANDEL,
  DISK_ADVARSEL_BYTES,
  DISK_KRITISK_ANDEL,
  DISK_KRITISK_BYTES,
  DRIFTSTATUSER,
  ELEMENTNAVN,
  ELEMENTTYPER,
  ENDRINGSKATEGORIER,
  ENHETSDESIMALER,
  ENHETSSYMBOL,
  FAGOMRADENAVN,
  FAGOMRADER,
  FASENAVN,
  FERIEMANED,
  FIGURFLATE,
  FIGURKAKE,
  FIGURTYPER,
  FIRMAROLLENAVN,
  FIRMAROLLER,
  FIRMASVAR,
  FIRMASVARNAVN,
  FORSLAGSTYPER,
  FRISTDAGER,
  FRISTDAG_MAKS,
  FRISTDAG_MIN,
  FRISTTILSTANDER,
  FRISTTILSTANDNAVN,
  FUNKSJONSFLAGG,
  GAPDEKNINGER,
  GAPDEKNINGSNAVN,
  GEVINSTVURDERINGER,
  GEVINSTVURDERINGSNAVN,
  GRUPPEROLLER,
  GRUPPETYPENAVN,
  GRUPPETYPER,
  HJEMMELHANDLINGER,
  HJEMMELUTFALL,
  HOVEDKONTO_KATEGORIER,
  IDENTITETSLEVERANDOR_TYPER,
  INVESTERINGSSTATUSER,
  INVESTERINGSSTATUSNAVN,
  KAPITTELTYPENAVN,
  KAPITTELTYPER,
  KONSEKVENSGRADNAVN,
  KONTROLLKATALOG,
  KONTROLLRESULTATER,
  KONTROLLRESULTATNAVN,
  KORTE_MANEDSNAVN,
  KOSTNADSTYPENAVN,
  KOSTNADSTYPER,
  KPI_KATEGORIER,
  KPI_TYPER,
  LEVERINGSSTATUSER,
  LONNSHANDLINGER,
  LONNSPOSTER,
  LONNSUTFALL,
  MAALEENHETER,
  MAALTALL_SKALA,
  MAKS_AVDRAGSTID,
  MAKS_BILDE,
  MAKS_CELLE,
  MAKS_KAPITLER,
  MAKS_KATEGORIER,
  MAKS_KOLONNER,
  MAKS_RADER,
  MAKS_SERIER,
  MAKS_UNDEROVERSKRIFTER,
  MAKS_VEDLEGG,
  MANEDER,
  MANEDSETIKETTER,
  MANEDSNAVN,
  MILEPAELSTATUSER,
  MILEPAELSTATUSNAVN,
  MINSTE_HISTORIKK,
  MOMSANDEL,
  NIVAER,
  NIVA_ARTSGRUPPE,
  NIVA_HOVEDKONTO,
  NIVA_UNDERKONTO,
  NULL_BELOP,
  NULL_FIREAAR,
  NULL_MAALTALL,
  NULL_TOLV,
  OMRADETILSTANDER,
  OPPGJORSTILSTANDER,
  OPPHAV,
  OPPHAVNAVN,
  OVERFORINGSAVVIK,
  PERIODETYPER,
  PLANAR,
  PORTEFOLJESTATUSER,
  PORTEFOLJESTATUSNAVN,
  PRIORITETER,
  PRIORITETSNAVN,
  PROGNOSERYTMER,
  PROGNOSE_METODER,
  PROSJEKTFASENAVN,
  PROSJEKTFASER,
  PROSJEKTVURDERINGER,
  PROSJEKTVURDERINGSNAVN,
  PUNKTRESULTATER,
  PUNKTRESULTATNAVN,
  RAPPORTTYPER,
  REAKSJONER,
  RETNINGER,
  RISIKONAVN,
  RISIKONIVAER,
  ROLLER,
  SAKSPREFIKS,
  SAKSTYPENAVN,
  SAKSTYPER,
  SERIEFARGER,
  STANDARD_AVDRAGSTID,
  STANDARD_FORKLARINGSTERSKEL,
  STATUSER,
  SYSTEMROLLER,
  TEKSTFELT,
  TILSTANDSGRADER,
  TILSTANDSGRADNAVN,
  TILTAKSSTATUSER,
  TILTAKSTYPER,
  TOM_MAANED,
  TREFFGRENSE_PROSENT,
  TREFFRETNINGER,
  TREFFRETNINGSNAVN,
  UTVALGSROLLER,
  UTVIKLINGER,
  VALUTA_ENHETER,
  VARSELGRENSE,
  VARSEL_DAGER,
  VEDLEGGSTYPER,
  administrasjonsbrukerSchema,
  administrasjonsomradeSchema,
  administrasjonsoversiktSchema,
  administrasjonsvirksomhetSchema,
  aggreger,
  aggregeringSchema,
  aksjonsoppgaverSchema,
  aksjonspunktSchema,
  aksjonsstatusSchema,
  aktivitetVariablerSchema,
  alvorlighetSchema,
  analyserBemanningSchema,
  analyserImportSchema,
  analyserJournal,
  analyserLonnSchema,
  analyserRutenett,
  andelAvManeden,
  andelIProsent,
  ankerSchema,
  ankernokkel,
  antallFraTekst,
  antallSchema,
  antallTilTekst,
  arSchema,
  arSporringSchema,
  arbeidsordreSchema,
  arbeidsordredetaljSchema,
  arbeidsordreoversiktSchema,
  arbeidsordrestatusSchema,
  arssum,
  arssumPerKonto,
  arsverkForManed,
  arsverkPerManed,
  arsverkslinjeSchema,
  artsgruppeSchema,
  avstem,
  avvikErForfalt,
  avvikSchema,
  avviksendringer,
  avviksforklaringSchema,
  avviksoppfolging,
  avviksoppfolgingSchema,
  avviksstatusSchema,
  avvikstype,
  avvistradSchema,
  basisUrlSchema,
  behandleForslagSchema,
  behandleLeveringSchema,
  belopFraDesimaltekst,
  belopFraTall,
  belopSchema,
  belopTilDesimaltekst,
  belopTilTall,
  bemanningSporringSchema,
  bemanningsanalyseSchema,
  bemanningsgruppeSchema,
  bemanningshandlingSchema,
  bemanningsimportradSchema,
  bemanningsmaalSchema,
  bemanningsmanedSchema,
  bemanningsoversiktSchema,
  bemanningsresultatSchema,
  beregnArskostnad,
  beregnAvvik,
  beregnAvvikProsent,
  beregnBasis,
  beregnBasisPrognose,
  beregnEndring,
  beregnPrognoseTabell,
  beregnPrognoseTotalt,
  beregnRegnskapHittilIAr,
  beregnStatus,
  budsjettendringSchema,
  budsjettfaseSchema,
  budsjettfordelingSchema,
  budsjettforslagSchema,
  budsjettforslageneSchema,
  budsjettibrukSchema,
  budsjettkildeSchema,
  budsjettomradeSchema,
  budsjettoppgaverSchema,
  budsjettprosessSchema,
  budsjettprosesserSchema,
  budsjettrevisjonSchema,
  budsjettrevisjonerSchema,
  budsjetttiltakSchema,
  byggOkonomirapport,
  byggOversikt,
  bygningsdelSchema,
  dagenEtter,
  dagenFor,
  dagerIManeden,
  dagerTil,
  datoIOslo,
  dekningstekst,
  deloppgaveSchema,
  detaljavvik,
  detaljbudsjettSchema,
  detaljnivaSchema,
  detaljradSchema,
  dimensjonskoblingSchema,
  dimensjonsnokkel,
  dimensjonsverdiSchema,
  driftSchema,
  driftsbelastning,
  driftsobjektSchema,
  driftsoversiktSchema,
  driftstatusSchema,
  eksportbelop,
  elementinnholdSchema,
  elementmerke,
  endreAksjonspunktSchema,
  endreArbeidsordreSchema,
  endreArbeidsordrestatusSchema,
  endreAvvikSchema,
  endreAvviksstatusSchema,
  endreBrukerSchema,
  endreBudsjettSchema,
  endreBudsjettforslagSchema,
  endreBudsjetttiltakSchema,
  endreDeloppgaveSchema,
  endreDimensjonskoblingSchema,
  endreElementSchema,
  endreFirmabrukerSchema,
  endreGruppeSchema,
  endreIdentitetsleverandorSchema,
  endreInvesteringSchema,
  endreInvesteringsprosjektSchema,
  endreKommunalomradeSchema,
  endreKontrollSchema,
  endreUtvalgSchema,
  endreVirksomhetSchema,
  endredeAnkre,
  endringskategoriSchema,
  entreprenorKanGaTil,
  entreprenorfirmaSchema,
  entreprenoroversiktSchema,
  erApenForPrognose,
  erApenOrdre,
  erAvvikstype,
  erBeregnet,
  erBilde,
  erBygningsdel,
  erDimensjon,
  erFeil,
  erForfalt,
  erForsinket,
  erForsinketMilepael,
  erGyldigOrgnummer,
  erJournalfeil,
  erKatalognokkel,
  erKomplettSjekkliste,
  erManed,
  erMed,
  erOrgnr,
  erOverBudsjett,
  erPeriodetype,
  erReaksjon,
  erSakstype,
  erUndertype,
  fagomradeSchema,
  fagomraderFor,
  fakturakontrollSchema,
  fastsettSchema,
  feilSvarSchema,
  fellesVisningSchema,
  feriepengemaned,
  figurSvg,
  figurX,
  figurnavnHvert,
  figurskala,
  figurstolper,
  filstorrelse,
  finansiellOversiktSchema,
  finansiellOversiktSporringSchema,
  finansiering,
  finnForelder,
  finnForfedre,
  finnHjemmelkolonner,
  finnKolonner,
  finnKpiType,
  finnLonnskolonner,
  finnTilsvarende,
  finnUgyldigeTildelinger,
  finnVirksomhet,
  fireaarSchema,
  firmabrukerSchema,
  firmarolleSchema,
  firmasvarSchema,
  flatUtTil,
  flybilderSchema,
  flyttElementSchema,
  foldTekst,
  fordelEtterVekter,
  fordelJevnt,
  fordelOverstyring,
  fordelPaKontoer,
  foreslaKontoer,
  foreslattAlvorlighet,
  forklaringsplikt,
  forklaringsterskelSchema,
  formaterBelop,
  formaterBytes,
  formaterFigurtall,
  formaterMaaltall,
  formaterMedEnhet,
  formaterOrgnummer,
  forslagSchema,
  forslagsbalanse,
  forslagslinjeSchema,
  forslagsramme,
  forslagstypeSchema,
  framskrivningVariablerSchema,
  framstillingSchema,
  fremdrift,
  fristdagSchema,
  fristtilstand,
  fristtilstandSchema,
  funksjonsflaggSchema,
  gevinststatus,
  gevinstvurderingSchema,
  gjeldendeLevering,
  gjeldendeOppgjor,
  gjenkjennFiltype,
  gjenstaendeGevinst,
  gruppeSchema,
  gruppekandidaterSchema,
  gruppeomfangSchema,
  gruppeoversiktSchema,
  grupperolleSchema,
  gruppetypeSchema,
  gyldigAnker,
  harLeder,
  harNummer,
  harOmradetilgang,
  hemmeligheterSchema,
  hjemmelanalyseSchema,
  hjemmelanalyseradSchema,
  hjemmelimportradSchema,
  hjemmelimportresultatSchema,
  hjemmelinnehaverSchema,
  hjemmelkandidatSchema,
  hjemmelnokkel,
  hjemmelradSchema,
  hjemmelsdekning,
  hjemmelstatusSchema,
  hovedkontoSchema,
  iBalanse,
  identitetsleverandorSchema,
  identitetsleverandorTypeSchema,
  ignorertBemanningsradSchema,
  ignorertLonnsradSchema,
  ignorertRadSchema,
  importAnalyseSchema,
  importManedSchema,
  importTreffSchema,
  importUtenTreffSchema,
  importerInnkjopSchema,
  importhandlingSchema,
  importresultatSchema,
  innholdskapittelSchema,
  innkjopSporringSchema,
  innkjopsanalyseSchema,
  innkjopsoversiktSchema,
  innkjopsresultatSchema,
  innleggSchema,
  integrasjonSchema,
  integrasjonsfeltSchema,
  integrasjonsoppsettSchema,
  integrasjonssystemSchema,
  investeringKreverForklaring,
  investeringSchema,
  investeringSkalMeldes,
  investeringSporringSchema,
  investeringsbudsjettSchema,
  investeringsimportAnalyseSchema,
  investeringsimportresultatSchema,
  investeringsoppfolgingSchema,
  investeringsoppgaverSchema,
  investeringsoversikt,
  investeringsprosjektSchema,
  investeringsstatusSchema,
  invitasjonOpprettetSchema,
  invitasjonSchema,
  inviterBrukerSchema,
  isodatoSchema,
  journalradSchema,
  kakestykker,
  kanAdministrere,
  kanBehandleFrittAvvik,
  kanBehandleLeveringer,
  kanBudsjettereVirksomhet,
  kanDetaljere,
  kanEndreForslag,
  kanEndreProsjekt,
  kanEndreRevisjon,
  kanEndreTiltak,
  kanFordele,
  kanForvalte,
  kanGaTil,
  kanLeverePeriode,
  kanMeldeInvestering,
  kanOppretteGruppe,
  kanOppretteNoenGruppe,
  kanRedigereVisning,
  kanSummeres,
  kanSvarePolitisk,
  kanTaIBruk,
  kanTildeleSystemrolle,
  kanVaereMedlem,
  kapitalkostnad,
  kapitlerSchema,
  kapittelSchema,
  kapitteltyperFor,
  kategoriSchema,
  klientfeilSchema,
  klientfeilSvarSchema,
  koblingskontoSchema,
  kommentarsvarSchema,
  kommenterLeveringSchema,
  kommuneoppforingSchema,
  kontoforbrukSchema,
  kontoplanSchema,
  kontovalgSchema,
  kontrollSchema,
  kontroller,
  kontrolloppgaverSchema,
  kontrolloversiktSchema,
  kontrollresultatSchema,
  kontrolltypeSchema,
  kontrolltyper,
  konverterBelop,
  kostnad,
  kostnadSchema,
  kostnadslinjeSchema,
  kostnadsoversikt,
  kostnadstypeSchema,
  kpiKategoriSchema,
  kpiKildeSchema,
  kpiKortSchema,
  kpiParametreFlatSchema,
  kpiParametreLagretSchema,
  kpiPeriodeSchema,
  kpiPunktSchema,
  kpiSporringSchema,
  kpiStatusSchema,
  kpiTypeValgSchema,
  kpiUtvalgSchema,
  kpiVisningSchema,
  kronetekst,
  kvitterVarselSchema,
  lagreDriftsobjektSchema,
  lagreFellesVisningSchema,
  lagreFirmaSchema,
  lagreKontrolltypeSchema,
  lagreKpiSchema,
  lagreMaalingSchema,
  lagreMilepaelSchema,
  lagreMoteSchema,
  lagreOppsettSchema,
  lagrePrognoseSchema,
  lagreProsjektinfoSchema,
  lagreSaksdetaljerSchema,
  lagreTekstSchema,
  lagreTilstandSchema,
  lagreTiltakSchema,
  lagreVedtattKuttSchema,
  lagretRolleutvalgSchema,
  leggSammen,
  lesBemanningsfil,
  lesElementmerke,
  lesHjemmelfil,
  lesLimtInnTabell,
  lesLonnsfil,
  leverPrognoseSchema,
  leverandorSchema,
  leverandorforbrukSchema,
  leveringsfrist,
  leveringskommentarSchema,
  leveringsoppsettSchema,
  leveringsperiode,
  leveringsstatusSchema,
  levertForklaringSchema,
  linjestykker,
  linjesum,
  lonnsanalyseSchema,
  lonnsanalyseradSchema,
  lonnsforutsetningSchema,
  lonnsgrunnlagSchema,
  lonnsgrunnlagSporringSchema,
  lonnsimportradSchema,
  lonnsimportresultatSchema,
  lonnslesingOppforingSchema,
  lonnslesingSporringSchema,
  lonnslesingSvarSchema,
  lonnsoppgjorSchema,
  lonnsprognosekoblingSchema,
  lonnsradSchema,
  losAr,
  losManed,
  losOppKontovalg,
  losOppRolle,
  losOppStandard,
  lukkedeManeder,
  maaleenhetSchema,
  maaltallFraBelop,
  maaltallFraDesimaltekst,
  maaltallInnSchema,
  maaltallSchema,
  maaltallTilDesimaltekst,
  manedFraIndeks,
  manedSchema,
  manedSporringSchema,
  manglerForklaring,
  markerFerdigSchema,
  medElementer,
  median,
  megSchema,
  meldGevinstSchema,
  meldInvesteringsprognoseSchema,
  metodevalgSchema,
  milepaelSchema,
  milepaelstatusSchema,
  minsteBokforte,
  moteSchema,
  nesteArbeidsordrestatuser,
  nesteFrist,
  nivaSchema,
  normaliser,
  normaliserDimensjonsverdi,
  normaliserManed,
  normaliserOrgnummer,
  normaliserRad,
  normaliserTall,
  normtallVariablerSchema,
  nyArbeidsordreSchema,
  nyBudsjettendringSchema,
  nyBudsjettrevisjonSchema,
  nyDeloppgaveSchema,
  nyDimensjonskoblingSchema,
  nyFirmabrukerSchema,
  nyForslagslinjeSchema,
  nyGruppeSchema,
  nyIntegrasjonSchema,
  nyKommentarSchema,
  nyKommuneSchema,
  nyKontrollSchema,
  nyKontrolltypeSchema,
  nyKostnadslinjeSchema,
  nyRevisjonslinjeSchema,
  nyTidsforingSchema,
  nyUtforelseSchema,
  nyttAksjonspunktSchema,
  nyttAvvikSchema,
  nyttBudsjettforslagSchema,
  nyttBudsjetttiltakSchema,
  nyttElementSchema,
  nyttForslagSchema,
  nyttInnleggSchema,
  nyttInvesteringsprosjektSchema,
  nyttKapittel,
  nyttKommentarsvarSchema,
  nyttMoteSchema,
  objektdetaljSchema,
  okonomirapportSchema,
  okonomisporringSchema,
  omradeTilgangSchema,
  omradeleveringerSchema,
  omradeneSer,
  omradeoversiktSchema,
  omradeoversiktSporringSchema,
  omraderamme,
  omradetilgangSchema,
  omradetilstand,
  omradetilstandSchema,
  omradevirksomhetSchema,
  omtalte,
  oppfolgingFra,
  oppgaveSchema,
  oppgaverSchema,
  oppgjorstilstandSchema,
  opphavSchema,
  opplastetBildeSchema,
  opprettBudsjettSchema,
  opprettIdentitetsleverandorSchema,
  opprettKommunalomradeSchema,
  opprettRapportSchema,
  opprettUtvalgSchema,
  opprettVirksomhetSchema,
  opprettetBudsjettforslagSchema,
  oppsettfeil,
  ordrear,
  orgnummerSchema,
  overEstimat,
  overfinansiert,
  overforingsavvik,
  oversiktManedSchema,
  oversiktRadSchema,
  oversiktTallSchema,
  oversiktsdataSchema,
  oversiktsoppsettSchema,
  overstyringSchema,
  pagineringSchema,
  periodenavn,
  periodetilstand,
  periodetypeSchema,
  periodiser,
  periodiserFra,
  planar,
  planlagtGevinstHittil,
  plasser,
  pleietyngdeSchema,
  politiskKommentarSchema,
  portefoljeSchema,
  portefoljeprosjektSchema,
  portefoljestatusSchema,
  posisjonSchema,
  posteringsvarselSchema,
  posteringsvarselSporringSchema,
  posteringsvarslerSchema,
  prioritetSchema,
  prisjuster,
  prognoseHovedkontoSchema,
  prognoseManedSchema,
  prognoseMetoderSchema,
  prognoseRadSchema,
  prognoseSporringSchema,
  prognoseVisningSchema,
  prognoseendringSchema,
  prognoseendringer,
  prognoseendringerSchema,
  prognoseleveringSchema,
  prognoseleveringerSchema,
  prognoserytmeSchema,
  prosentSchema,
  prosjektfaseSchema,
  prosjektfremdrift,
  prosjektfremdriftSchema,
  punktavvikstittel,
  punktresultatSchema,
  rapportelementSchema,
  rapportinnholdSchema,
  rapportmalSchema,
  rapportnavn,
  rapportnummer,
  rapportomfangSchema,
  rapportoppgaverSchema,
  rapportperiode,
  rapportsporringSchema,
  rapporttypeSchema,
  rapportutkastSchema,
  rapportversjonSchema,
  rapportversjonsoppforingSchema,
  reaksjonSchema,
  registrerBehandlingSchema,
  registrerRevisjonsvedtakSchema,
  registrerVedtakSchema,
  registrerteManeder,
  regnForslag,
  regnProsjekt,
  retningSchema,
  revisjonslinjeSchema,
  revisjonsloggOppforingSchema,
  revisjonsloggSporringSchema,
  revisjonsloggSvarSchema,
  revisjonssum,
  risikonivaSchema,
  rolleSchema,
  rullerendeVariablerSchema,
  ryddEtiketter,
  sakSchema,
  saksnummer,
  saksoppgaverSchema,
  sakstypeSchema,
  sammenlignUnderkonto,
  sammenligningsperiode,
  sammenligningstekst,
  sendTilBehandlingSchema,
  serFrittAvvik,
  serObjekt,
  serPortefoljeprosjekt,
  serUtsendelse,
  seriefargeHex,
  settBudsjettfordelingSchema,
  settDetaljradSchema,
  settFagomradeSchema,
  settForslagsvalgSchema,
  settFristSchema,
  settFunksjonsflaggSchema,
  settHemmelighetSchema,
  settHjemmelskoblingSchema,
  settIntegrasjonAktivSchema,
  settKoblingerSchema,
  settLanerenteSchema,
  settLonnsforutsetningSchema,
  settLonnsoppgjorSchema,
  settLonnsprognosekoblingSchema,
  settMedlemmerSchema,
  settRevisjonsrapportSchema,
  settStillingshjemmelSchema,
  settTilgangerSchema,
  settUtforerSchema,
  sideSvarSchema,
  sisteBokfortManed,
  sjekklisteresultat,
  sjekkpunktSchema,
  skalerBelop,
  snittArsverk,
  snittArsverkAvManeder,
  somKontooverskrift,
  sorterAvvik,
  standardFrist,
  standardmal,
  statustoneSchema,
  stillingsgruppeSchema,
  stillingsgruppesumSchema,
  stillingshjemmelSchema,
  stillingslinjeSchema,
  stillingsoversiktSchema,
  stillingsperiodeSchema,
  styrerRapporten,
  summer2 as summer,
  summerBelop,
  summerFireaar,
  summerGap,
  summerGevinster,
  summerKostnad,
  summerManeder,
  summerRader,
  summerTabell,
  summerTall,
  summerTolv,
  summerValgte,
  svarPaOrdreSchema,
  systemrolleSchema,
  taIBrukSchema,
  tallFraCelle,
  tallFraTekst,
  tallSchema,
  tekstblokker,
  tekstfelt,
  tekstfeltSchema,
  tellPortefolje,
  terskelErGyldig,
  tidsforingSchema,
  tidstekst,
  tilgangSchema,
  tilstandSchema,
  tilstandsgradSchema,
  tiltakSchema,
  tiltakSporringSchema,
  tiltakStatusSchema,
  tiltakVisningSchema,
  tiltaksdekningSchema,
  tiltakseffekt,
  tiltaksgap,
  tiltaksstatusInnSchema,
  tiltaksstatusSchema,
  tiltakstypeSchema,
  tolkDato,
  tolkDesimal,
  tolkManed,
  tolkNorsktall,
  tolvSchema,
  treffsikkerhet,
  treffsikkerhetSchema,
  trengerForklaring,
  typefeil,
  ubalanse,
  udisponert,
  underkontoSchema,
  undertypenavn,
  utforBemanningsimportSchema,
  utforHjemmelimportSchema,
  utforImportSchema,
  utforInvesteringsimportSchema,
  utforLonnsimportSchema,
  utforelseSchema,
  utforertekst,
  utled,
  utledBudsjettoppgaver,
  utledNivaer,
  utledOppgaver,
  utledPolitiskeOppgaver,
  utledRapportoppgaver,
  utledVirksomhetstilganger,
  utledetSchema,
  utsendelseDetaljSchema,
  utsendelseSchema,
  utsendelserSchema,
  utvalgSchema,
  utvalgsrolleSchema,
  utviklingSchema,
  uuidSchema,
  uvanligPostering,
  uvanligePosteringer,
  vakansPerManed,
  valutaEnhetSchema,
  valutaenhetSchema,
  variantSchema,
  varseloppgaverSchema,
  vedleggSchema,
  vedlikeholdsplan,
  vedlikeholdsplanSchema,
  vedtattKuttSchema,
  vedtatteKuttOmradeSchema,
  vedtatteKuttSchema,
  vedtatteKuttSporringSchema,
  veksleReaksjonSchema,
  vekstfaktor,
  villeLaastUteKommunen,
  virksomhetSchema,
  virksomhetTilgangSchema,
  vurderDatoformat,
  vurderDiskplass,
  vurderInvestering,
  vurderPortefoljeprosjekt,
  vurderRad
};
//# sourceMappingURL=index.mjs.map